import React, { useState, useEffect, useMemo } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WaxProvider, useWax } from './context/WaxContext';
import Landing from './components/Landing';
import { LogOut, ArrowDownLeft, RefreshCw, Zap, Coins } from 'lucide-react';
import WaxPayment from './components/WaxPayment';
import GamePage from './components/GamePage';
import AtaviForestPage from './components/AtaviForestPage';
import LeaderboardPage from './components/LeaderboardPage';
import { getSessionKit } from './wax/session';
import AtaviGuide from './components/AtaviGuide';
import AtaviForestPlantModal from './components/AtaviForestPlantModal';

// ─── Shared Types ──────────────────────────────────────────────────────────────

export interface WaxTx {
  timestamp: string;
  trx_id: string;
  amount: number;
  from: string;
  memo: string;
  plots_count?: number;
}

// ─── Config ───────────────────────────────────────────────────────────────────

const ATAVI_LOCKED = false;
const WAX_ACCOUNT = 'samatvam.gm';
const HYPERION = 'https://wax.eosusa.io';
const INVESTED_WAX_FALLBACK = 46; // fallback until DB config loads
const EXCLUDED_SENDERS = new Set([
  'swap.alcor', 'reward.alcor', 'wallet.taco',
  'eosio.voters', 'swap.taco', 'dapp.fusion', 'ccomtranswax'
]);

// ─── WAX Payment Confirmation ─────────────────────────────────────────────────
function confirmWaxSpend(amount: string): boolean {
  return window.confirm(
    `WAX Payment Confirmation\n\nYou are about to spend ${amount} — real cryptocurrency.\n\nBlockchain transactions are irreversible and cannot be refunded. By proceeding you confirm you are 18 or over and waive your 14-day digital content cancellation right under the UK Consumer Rights Act 2015.\n\nProceed?`
  );
}

// ─── WAX Balance Hook ─────────────────────────────────────────────────────────

function useWaxBalance() {
  const [balance, setBalance] = useState<string | null>(null);

  useEffect(() => {
    const fetch$ = async () => {
      try {
        const res = await fetch(`${HYPERION}/v1/chain/get_account`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ account_name: WAX_ACCOUNT })
        });
        const data = await res.json();
        setBalance(data.core_liquid_balance ?? '0.00000000 WAX');
      } catch {
        setBalance(null);
      }
    };
    fetch$();
    const interval = setInterval(fetch$, 60000);
    return () => clearInterval(interval);
  }, []);

  return balance;
}

// ─── Shared Incoming WAX Hook ─────────────────────────────────────────────────

function useWaxIncoming() {
  const [txs, setTxs] = useState<WaxTx[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState('');
  const [error, setError] = useState('');

  // Fetch all land from our DB (authoritative, accumulates forever)
  const fetchFromDb = async (): Promise<WaxTx[] | null> => {
    try {
      const res = await fetch('/samatvam/api/samatvam/land');
      const data = await res.json();
      if (data.success && data.txs.length > 0) return data.txs as WaxTx[];
    } catch {}
    return null;
  };

  // Sync Hyperion txs into our DB (fire-and-forget)
  const syncToDb = (incoming: WaxTx[]) => {
    if (incoming.length === 0) return;
    fetch('/samatvam/api/samatvam/land/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ txs: incoming }),
    }).catch(() => {});
  };

  const fetchTxs = async () => {
    try {
      setLoading(true);
      setError('');

      // Always try to sync fresh data from Hyperion into DB
      const res = await fetch(
        `${HYPERION}/v2/history/get_actions?account=${WAX_ACCOUNT}&limit=300&sort=desc&act.account=eosio.token&act.name=transfer`
      );
      const data = await res.json();
      const incoming: WaxTx[] = (data.actions || [])
        .filter((a: any) =>
          a.act?.data?.to === WAX_ACCOUNT &&
          a.act?.data?.symbol === 'WAX' &&
          !EXCLUDED_SENDERS.has(a.act?.data?.from)
        )
        .map((a: any) => ({
          timestamp: a.timestamp,
          trx_id: a.trx_id,
          amount: a.act.data.amount,
          from: a.act.data.from,
          memo: a.act.data.memo || '—'
        }));

      // Sync all Hyperion txs into DB
      syncToDb(incoming);

      // Use DB as authoritative source (has ALL txs, not just last 300)
      // DB returns oldest-first; reverse to match the desc order the rest of the app expects
      const dbTxs = await fetchFromDb();
      const finalTxs = dbTxs
        ? [...dbTxs].reverse()   // DB is asc → reverse to desc for consistency
        : incoming;              // fallback if DB empty

      setTxs(finalTxs);
      setLastUpdated(new Date().toLocaleTimeString());
      console.log(`Land: ${finalTxs.length} txs (${dbTxs ? 'DB' : 'Hyperion'})`);
    } catch (err) {
      console.error('WAX fetch error:', err);
      setError('Could not reach Hyperion API');
      // Try DB fallback even if Hyperion failed
      const dbTxs = await fetchFromDb();
      if (dbTxs) { setTxs([...dbTxs].reverse()); setError(''); }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTxs();
    const interval = setInterval(fetchTxs, 60000);
    return () => clearInterval(interval);
  }, []);

  return { txs, loading, lastUpdated, error, fetchTxs };
}

// ─── Rules & Economy Section ──────────────────────────────────────────────────

const SEED_RULES = [
  { emoji:'🌱', name:'Amaranth',     cost:10,  food:1  },
  { emoji:'🌾', name:'Vetiver',      cost:20,  food:2  },
  { emoji:'🌵', name:'Prickly Pear', cost:35,  food:3  },
  { emoji:'🌿', name:'Moringa',      cost:50,  food:5  },
  { emoji:'🫐', name:'Jujube',       cost:70,  food:7  },
  { emoji:'🌳', name:'Acacia',       cost:90,  food:9  },
  { emoji:'🌲', name:'Mesquite',     cost:120, food:11 },
  { emoji:'🌴', name:'Neem',         cost:160, food:14 },
];

// ─── Game Config Hook ─────────────────────────────────────────────────────────

function useGameConfig() {
  const [investedWax, setInvestedWax] = React.useState<number>(INVESTED_WAX_FALLBACK);
  const refresh = React.useCallback(() => {
    fetch('/samatvam/api/samatvam/config')
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setInvestedWax(d.invested_wax);
        }
      })
      .catch(() => {});
  }, []);
  React.useEffect(() => { refresh(); }, [refresh]);
  return { investedWax, refresh };
}

function useGameTotals() {
  const [totalFood, setTotalFood] = React.useState<number>(0);
  const [totalBhoomi, setTotalBhoomi] = React.useState<number>(0);
  React.useEffect(() => {
    const load = () => {
      fetch('/samatvam/api/samatvam/totals')
        .then(r => r.json())
        .then(d => { if (d.success) { setTotalFood(d.total_food); setTotalBhoomi(d.total_bhoomi); } })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 60_000); // refresh every minute
    return () => clearInterval(t);
  }, []);
  return { totalFood, totalBhoomi };
}

// ─── Admin Panel (ahantravyuh@gmail.com only) ─────────────────────────────────

function AdminPanel({ token, onOpenCityMapEditor, onOpenAdminZones, onOpenAdminLabels }: { token: string; onOpenCityMapEditor: () => void; onOpenAdminZones: () => void; onOpenAdminLabels: () => void }) {
  const { investedWax, refresh } = useGameConfig();
  const [waxInput, setWaxInput] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const update = async (key: string, value: string) => {
    const val = parseFloat(value);
    if (isNaN(val) || val < 0) { setStatus('Enter a valid number'); return; }
    setSaving(true); setStatus('');
    try {
      const r = await fetch('/samatvam/api/samatvam/admin/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ key, value: val }),
      });
      const d = await r.json();
      if (d.success) {
        setStatus(`✅ Invested WAX updated to ${d.value}`);
        setWaxInput('');
        refresh();
      } else setStatus(`❌ ${d.error}`);
    } catch { setStatus('❌ Network error'); }
    setSaving(false);
  };

  return (
    <div className="mb-6 rounded-2xl border border-red-500/20 bg-slate-900/80 overflow-hidden">
      <div className="px-5 py-3 border-b border-red-500/10 flex items-center gap-2">
        <span className="text-red-400 text-xs">🔐</span>
        <span className="text-xs font-bold uppercase tracking-widest text-red-400/70">Admin Panel</span>
      </div>
      <div className="p-5 flex flex-col gap-4">
        {/* Invested WAX */}
        <div>
          <p className="text-xs text-slate-500 mb-2">Invested WAX: <span className="text-blue-400 font-bold">{investedWax} WAX</span></p>
          <div className="flex gap-3 items-center">
            <input type="number" step="1" min="0" placeholder="New invested value (e.g. 50)"
              value={waxInput} onChange={e => setWaxInput(e.target.value)}
              className="flex-1 bg-slate-800 border border-slate-600 rounded-xl px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500/50" />
            <button onClick={() => update('invested_wax', waxInput)} disabled={saving}
              className="px-5 py-2 rounded-xl bg-blue-500/20 border border-blue-500/30 text-blue-300 text-sm font-bold hover:bg-blue-500/30 transition-all disabled:opacity-50">
              {saving ? 'Saving…' : 'Update'}
            </button>
          </div>
        </div>
        {status && <p className="text-xs text-slate-400">{status}</p>}

        {/* City Map Editor */}
        <div className="border-t border-red-500/10 pt-4 flex flex-col gap-3">
          <button onClick={onOpenCityMapEditor}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/60 border border-white/10 hover:border-amber-500/40 hover:bg-amber-500/5 transition-all text-left group">
            <span className="text-2xl">🗺️</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white group-hover:text-amber-300 transition-colors">City Map Editor</p>
              <p className="text-xs text-slate-500">Design the 100×100 Samatvam colony layout</p>
            </div>
            <span className="text-slate-600 group-hover:text-amber-400 transition-colors text-base">→</span>
          </button>
          <button onClick={onOpenAdminZones}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/60 border border-white/10 hover:border-emerald-500/40 hover:bg-emerald-500/5 transition-all text-left group">
            <span className="text-2xl">📍</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white group-hover:text-emerald-300 transition-colors">Task Zone Manager</p>
              <p className="text-xs text-slate-500">Place and manage clickable task zones on the city map</p>
            </div>
            <span className="text-slate-600 group-hover:text-emerald-400 transition-colors text-base">→</span>
          </button>
          <button onClick={onOpenAdminLabels}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/60 border border-white/10 hover:border-violet-500/40 hover:bg-violet-500/5 transition-all text-left group">
            <span className="text-2xl">🏷️</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white group-hover:text-violet-300 transition-colors">Map Label Manager</p>
              <p className="text-xs text-slate-500">Place and manage hover labels on the city map</p>
            </div>
            <span className="text-slate-600 group-hover:text-violet-400 transition-colors text-base">→</span>
          </button>
        </div>

        {/* Council Worker Manager */}
        <WorkerAdminPanel token={token} />

      </div>
    </div>
  );
}

// ─── Admin Page (separate full page) ─────────────────────────────────────────
function AdminPage({ onBack, token, onOpenCityMapEditor, onOpenAdminZones, onOpenAdminLabels }: {
  onBack: () => void;
  token: string;
  onOpenCityMapEditor: () => void;
  onOpenAdminZones: () => void;
  onOpenAdminLabels: () => void;
}) {
  // ── Anna Mint Pending ──────────────────────────────────────────────────────
  type AnnaMint = { id: number; wax_account: string; food_packets_spent: number; asset_id: string | null; tx_id: string | null; status: string; initiated_at: string; completed_at: string | null; error_msg: string | null };
  const [annaPending,    setAnnaPending]    = React.useState<AnnaMint[]>([]);
  const [annaLoading,    setAnnaLoading]    = React.useState(false);
  const [annaAdminMsg,   setAnnaAdminMsg]   = React.useState('');
  const [recheckingId,   setRecheckingId]   = React.useState<number | null>(null);

  const loadAnnaPending = React.useCallback(async () => {
    setAnnaLoading(true);
    try {
      const r = await fetch('/samatvam/api/samatvam/admin/anna/pending', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (d.success) setAnnaPending(d.mints);
    } catch {}
    setAnnaLoading(false);
  }, [token]);

  React.useEffect(() => { loadAnnaPending(); }, [loadAnnaPending]);

  const recheckAnna = async (log_id: number) => {
    setRecheckingId(log_id); setAnnaAdminMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/admin/anna/recheck', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ log_id }),
      });
      const d = await r.json();
      setAnnaAdminMsg(d.success ? `✅ ${d.message}` : `❌ ${d.error}`);
      setTimeout(() => loadAnnaPending(), 130_000); // refresh after 2min 10s
    } catch { setAnnaAdminMsg('❌ Network error'); }
    setRecheckingId(null);
  };

  type TaskStatus = {
    task_id: string;
    label: string;
    description: string;
    food_req: number;
    cart_req: boolean;
    machine_req: boolean;
    map_x: number;
    map_y: number;
    is_active: boolean;
    completed: boolean;
    completed_by: string | null;
    completed_at: string | null;
  };
  const [tasks, setTasks] = React.useState<TaskStatus[]>([]);
  const [taskMsg, setTaskMsg] = React.useState('');
  const [taskLoading, setTaskLoading] = React.useState(false);

  const loadTasks = React.useCallback(async () => {
    try {
      const r = await fetch('/samatvam/api/samatvam/admin/tasks', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (d.success) setTasks(d.tasks);
    } catch {}
  }, [token]);

  React.useEffect(() => { loadTasks(); }, [loadTasks]);

  const triggerTask = async (task_id: string) => {
    setTaskLoading(true); setTaskMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/admin/task/trigger', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ task_id }),
      });
      const d = await r.json();
      if (d.success) { setTaskMsg(`✅ Task triggered: ${task_id}`); loadTasks(); }
      else setTaskMsg(`❌ ${d.error}`);
    } catch { setTaskMsg('❌ Network error'); }
    setTaskLoading(false);
  };

  const deactivateTask = async (task_id: string) => {
    setTaskLoading(true); setTaskMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/admin/task/deactivate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ task_id }),
      });
      const d = await r.json();
      if (d.success) { setTaskMsg(`✅ Task deactivated: ${task_id}`); loadTasks(); }
      else setTaskMsg(`❌ ${d.error}`);
    } catch { setTaskMsg('❌ Network error'); }
    setTaskLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-slate-950/95 backdrop-blur-sm border-b border-red-500/15 px-4 py-3 flex items-center gap-3">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm font-semibold px-3 py-1.5 rounded-lg hover:bg-white/5">
          ← Back
        </button>
        <div className="flex items-center gap-2">
          <span className="text-red-400 text-sm">🔐</span>
          <span className="text-sm font-black uppercase tracking-widest text-red-400">Admin Command Center</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">

        {/* Existing Admin Panel content */}
        <AdminPanel token={token} onOpenCityMapEditor={onOpenCityMapEditor} onOpenAdminZones={onOpenAdminZones} onOpenAdminLabels={onOpenAdminLabels} />

        {/* ── City Task Control ── */}
        <div className="rounded-2xl border border-amber-500/20 bg-slate-900/80 overflow-hidden">
          <div className="px-5 py-3 border-b border-amber-500/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-amber-400 text-xs">🗺️</span>
              <span className="text-xs font-bold uppercase tracking-widest text-amber-400/70">City Task Control</span>
            </div>
            <button onClick={loadTasks} className="text-xs text-slate-500 hover:text-slate-300 transition-colors px-2 py-1 rounded-lg hover:bg-white/5">↺ Refresh</button>
          </div>
          <div className="p-5 space-y-4">
            {taskMsg && (
              <p className={`text-xs px-3 py-2 rounded-lg ${taskMsg.startsWith('✅') ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>{taskMsg}</p>
            )}
            {tasks.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-4">Loading templates…</p>
            ) : tasks.map((t, idx) => {
              const statusColor = t.completed ? 'emerald' : t.is_active ? 'amber' : 'slate';
              const statusLabel = t.completed ? 'Completed' : t.is_active ? 'Active' : 'Inactive';
              return (
                <div key={t.task_id} className={`rounded-xl border p-4 space-y-3 ${
                  t.completed ? 'border-emerald-500/25 bg-emerald-950/20' :
                  t.is_active ? 'border-amber-500/30 bg-amber-950/20' :
                  'border-white/8 bg-slate-800/40'
                }`}>
                  {/* Card header */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Template {idx + 1}</span>
                        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                          t.completed ? 'bg-emerald-500/15 border-emerald-400/30 text-emerald-300' :
                          t.is_active ? 'bg-amber-500/15 border-amber-400/30 text-amber-300' :
                          'bg-slate-700/40 border-slate-600/30 text-slate-400'
                        }`}>{statusLabel}</span>
                      </div>
                      <p className="text-sm font-bold text-white mt-1">{t.label}</p>
                      <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{t.description}</p>
                    </div>
                  </div>

                  {/* Requirements row */}
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="px-2 py-1 rounded-lg bg-green-500/10 border border-green-500/20 text-green-300">{t.food_req} Food Packets</span>
                    {t.cart_req && <span className="px-2 py-1 rounded-lg bg-orange-500/10 border border-orange-500/20 text-orange-300">Hand Cart</span>}
                    {t.machine_req && <span className="px-2 py-1 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-300">Water Machine</span>}
                    <span className="px-2 py-1 rounded-lg bg-slate-700/40 border border-slate-600/20 text-slate-400 font-mono">({t.map_x}, {t.map_y})</span>
                  </div>

                  {/* Completion info */}
                  {t.completed && t.completed_by && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/8 border border-emerald-500/15">
                      <span className="text-emerald-400 text-xs">✅</span>
                      <span className="text-xs text-slate-400">Completed by</span>
                      <span className="text-xs font-bold text-emerald-300 font-mono">{t.completed_by}</span>
                      {t.completed_at && <span className="text-xs text-slate-600 ml-auto">{new Date(t.completed_at).toLocaleDateString()}</span>}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex gap-2 pt-1">
                    {!t.is_active && (
                      <button
                        onClick={() => triggerTask(t.task_id)}
                        disabled={taskLoading}
                        className="flex-1 py-2 rounded-xl bg-amber-500/20 border border-amber-500/30 text-amber-300 text-xs font-bold hover:bg-amber-500/30 transition-all disabled:opacity-50"
                      >
                        ▶ Trigger Task
                      </button>
                    )}
                    {(t.is_active || t.completed) && (
                      <button
                        onClick={() => deactivateTask(t.task_id)}
                        disabled={taskLoading}
                        className="flex-1 py-2 rounded-xl bg-red-500/15 border border-red-500/25 text-red-400 text-xs font-bold hover:bg-red-500/25 transition-all disabled:opacity-50"
                      >
                        ■ Deactivate
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Anna Mint — Pending / Failed ── */}
        <div className="rounded-2xl border border-yellow-500/20 bg-slate-900/80 overflow-hidden">
          <div className="px-5 py-3 border-b border-yellow-500/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-yellow-400 text-xs">🌾</span>
              <span className="text-xs font-bold uppercase tracking-widest text-yellow-400/70">Anna NFT Mints — Pending / Failed</span>
            </div>
            <button onClick={loadAnnaPending} className="text-xs text-slate-500 hover:text-slate-300 transition-colors px-2 py-1 rounded-lg hover:bg-white/5">↺ Refresh</button>
          </div>
          <div className="p-5 space-y-3">
            {annaAdminMsg && (
              <p className={`text-xs px-3 py-2 rounded-lg ${annaAdminMsg.startsWith('✅') ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>{annaAdminMsg}</p>
            )}
            {annaLoading && <p className="text-xs text-slate-500 text-center py-4">Loading…</p>}
            {!annaLoading && annaPending.length === 0 && (
              <p className="text-xs text-slate-500 text-center py-4">No pending or failed Anna mints ✅</p>
            )}
            {annaPending.map(m => (
              <div key={m.id} className="rounded-xl border border-yellow-500/10 bg-slate-800/50 p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${m.status === 'pending' ? 'bg-yellow-500/15 text-yellow-400' : m.status === 'failed' ? 'bg-red-500/15 text-red-400' : 'bg-slate-500/15 text-slate-400'}`}>{m.status.toUpperCase()}</span>
                    <span className="text-sm font-bold text-white">{m.wax_account}</span>
                  </div>
                  <span className="text-xs text-slate-500">log#{m.id}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-400">
                  <div>Initiated: {new Date(m.initiated_at).toLocaleString()}</div>
                  <div>Packets: {m.food_packets_spent.toLocaleString()}</div>
                  {m.asset_id && <div>Asset: {m.asset_id}</div>}
                  {m.tx_id && <div className="truncate">TX: <span className="font-mono">{m.tx_id.slice(0, 16)}…</span></div>}
                  {m.error_msg && <div className="col-span-2 text-red-400/70">{m.error_msg}</div>}
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => recheckAnna(m.id)}
                    disabled={recheckingId === m.id}
                    className="text-xs px-3 py-1.5 rounded-lg font-bold bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 hover:bg-yellow-500/20 transition-colors disabled:opacity-50"
                  >
                    {recheckingId === m.id ? '⏳ Re-checking…' : '↺ Re-check / Retry'}
                  </button>
                  {m.tx_id && (
                    <a href={`https://waxblock.io/transaction/${m.tx_id}`} target="_blank" rel="noreferrer"
                      className="text-xs px-3 py-1.5 rounded-lg font-bold bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 transition-colors">
                      View TX →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── Worker Admin Panel ───────────────────────────────────────────────────────
function WorkerAdminPanel({ token }: { token: string }) {
  const [workers, setWorkers] = React.useState<CouncilWorker[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  // Create form
  const [newName, setNewName] = React.useState('Worker');
  const [newAssigned, setNewAssigned] = React.useState('');
  const [newPosX, setNewPosX] = React.useState('1015');
  const [newPosY, setNewPosY] = React.useState('765');
  const [workersOpen, setWorkersOpen] = React.useState(false);
  // Distribute
  const [distPreview, setDistPreview] = React.useState<{ wax_account: string; still_owed: number }[] | null>(null);
  const [distLoading, setDistLoading] = React.useState(false);
  const [distMsg, setDistMsg] = React.useState('');
  // Move form per worker
  const [moveTarget, setMoveTarget] = React.useState<number | null>(null);
  const [moveDx, setMoveDx] = React.useState('');
  const [moveDy, setMoveDy] = React.useState('');
  const [moveDur, setMoveDur] = React.useState('60');

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = React.useCallback(() => {
    fetch('/samatvam/api/samatvam/workers').then(r => r.json())
      .then(d => { if (d.success) setWorkers(d.workers); });
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const createWorker = async () => {
    setLoading(true); setMsg('');
    const r = await fetch('/samatvam/api/samatvam/admin/workers', {
      method: 'POST', headers,
      body: JSON.stringify({ worker_name: newName, wax_account: newAssigned, pos_x: parseInt(newPosX), pos_y: parseInt(newPosY) }),
    });
    const d = await r.json();
    setMsg(d.success ? `✅ Created ${newName}` : `❌ ${d.error}`);
    if (d.success) { load(); setNewName('Worker'); setNewAssigned(''); }
    setLoading(false);
  };

  const loadDistPreview = async () => {
    setDistLoading(true); setDistMsg(''); setDistPreview(null);
    const r = await fetch('/samatvam/api/samatvam/workers/allocation');
    const d = await r.json();
    if (d.success) {
      const eligible = d.rows.filter((row: any) => row.still_owed > 0);
      setDistPreview(eligible);
      if (eligible.length === 0) setDistMsg('✅ No players owed workers right now.');
    } else { setDistMsg('❌ Failed to load allocation'); }
    setDistLoading(false);
  };

  const runDistribute = async () => {
    setDistLoading(true); setDistMsg('');
    const r = await fetch('/samatvam/api/samatvam/admin/workers/distribute', { method: 'POST', headers });
    const d = await r.json();
    if (d.success) {
      setDistMsg(`✅ Gave 1 worker to ${d.distributed} player${d.distributed !== 1 ? 's' : ''}: ${d.players.join(', ')}`);
      setDistPreview(null);
      load();
    } else { setDistMsg(`❌ ${d.error}`); }
    setDistLoading(false);
  };

  const setState = async (id: number, state: 0 | 1 | 2) => {
    await fetch(`/samatvam/api/samatvam/admin/workers/${id}`, { method: 'PATCH', headers, body: JSON.stringify({ state }) });
    load();
  };

  const startMove = async (id: number) => {
    if (!moveDx || !moveDy) { setMsg('Enter destination x,y'); return; }
    setLoading(true);
    await fetch(`/samatvam/api/samatvam/admin/workers/${id}`, {
      method: 'PATCH', headers,
      body: JSON.stringify({ state: 1, dest_x: parseInt(moveDx), dest_y: parseInt(moveDy), move_duration_sec: parseInt(moveDur) || 60 }),
    });
    setMsg(`✅ Worker ${id} moving to (${moveDx}, ${moveDy})`);
    setMoveTarget(null); setMoveDx(''); setMoveDy('');
    load(); setLoading(false);
  };

  const deleteWorker = async (id: number) => {
    await fetch(`/samatvam/api/samatvam/admin/workers/${id}`, { method: 'DELETE', headers });
    load();
  };

  const stateLabel = (s: number) => s === 0 ? 'Idle' : s === 1 ? 'Moving' : 'Working';
  const stateColor = (s: number) => s === 0 ? 'text-slate-400' : s === 1 ? 'text-cyan-400' : 'text-green-400';

  return (
    <div className="border-t border-red-500/10 pt-4">
      <p className="text-xs font-bold uppercase tracking-widest text-amber-400/60 mb-3">🧑 Council Worker Manager</p>

      {/* Create */}
      <div className="bg-slate-800/50 rounded-xl p-3 mb-3 flex flex-col gap-2">
        <p className="text-xs text-slate-500 font-bold">Add Worker</p>
        <div className="grid grid-cols-2 gap-2">
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Name"
            className="bg-slate-700 border border-slate-600 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none" />
          <input value={newAssigned} onChange={e => setNewAssigned(e.target.value)} placeholder="Assigned to (wax account)"
            className="bg-slate-700 border border-slate-600 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none" />
          <input value={newPosX} onChange={e => setNewPosX(e.target.value)} placeholder="X"
            className="bg-slate-700 border border-slate-600 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none" />
          <input value={newPosY} onChange={e => setNewPosY(e.target.value)} placeholder="Y"
            className="bg-slate-700 border border-slate-600 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none" />
        </div>
        <button onClick={createWorker} disabled={loading}
          className="w-full py-1.5 rounded-lg bg-amber-500/20 border border-amber-500/30 text-amber-300 text-xs font-bold hover:bg-amber-500/30 transition-all disabled:opacity-50">
          {loading ? 'Creating…' : '+ Add Worker'}
        </button>
      </div>

      {/* Distribute Workers */}
      <div className="bg-slate-800/50 rounded-xl p-3 mb-3 flex flex-col gap-2">
        <p className="text-xs text-slate-500 font-bold">Distribute Workers (1 per eligible player)</p>
        <div className="flex gap-2">
          <button onClick={loadDistPreview} disabled={distLoading}
            className="flex-1 py-1.5 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-300 text-xs font-bold hover:bg-blue-500/30 transition-all disabled:opacity-50">
            {distLoading ? '…' : '🔍 Preview'}
          </button>
          {distPreview && distPreview.length > 0 && (
            <button onClick={runDistribute} disabled={distLoading}
              className="flex-1 py-1.5 rounded-lg bg-green-500/20 border border-green-500/30 text-green-300 text-xs font-bold hover:bg-green-500/30 transition-all disabled:opacity-50">
              {distLoading ? '…' : `✅ Give to ${distPreview.length}`}
            </button>
          )}
        </div>
        {distPreview && distPreview.length > 0 && (
          <div className="flex flex-col gap-1 mt-1">
            {distPreview.map((r: any) => (
              <div key={r.wax_account} className="flex justify-between text-[10px] px-2 py-1 rounded bg-slate-700/40 border border-white/5">
                <span className="text-slate-300 font-mono">{r.wax_account}</span>
                <span className="text-amber-400">owes +{r.still_owed}</span>
              </div>
            ))}
          </div>
        )}
        {distMsg && <p className="text-xs text-slate-400">{distMsg}</p>}
      </div>

      {/* Worker list */}
      <button
        onClick={() => setWorkersOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-slate-800/40 border border-white/8 text-xs font-bold text-slate-300 hover:text-white hover:border-white/15 transition-all mb-1"
      >
        <span>Workers ({workers.length})</span>
        <span style={{ fontSize: 10, opacity: 0.6 }}>{workersOpen ? '▲ collapse' : '▼ expand'}</span>
      </button>
      <div className="flex flex-col gap-2" style={{ display: workersOpen ? 'flex' : 'none' }}>
        {workers.map(w => (
          <div key={w.id} className="bg-slate-800/40 rounded-xl p-2.5 border border-white/5">
            <div className="flex items-center justify-between mb-1.5">
              <div>
                <span className="text-white text-xs font-bold">{w.worker_name}</span>
                {w.wax_account && <span className="text-slate-500 text-xs ml-1.5">→ {w.wax_account}</span>}
              </div>
              <span className={`text-xs font-bold ${stateColor(w.state)}`}>{stateLabel(w.state)}</span>
            </div>
            <div className="text-slate-600 text-[10px] mb-2">pos ({w.pos_x}, {w.pos_y}){w.dest_x != null ? ` → (${w.dest_x}, ${w.dest_y})` : ''}</div>
            {/* State buttons */}
            <div className="flex gap-1.5 flex-wrap">
              {([0,1,2] as const).map(s => (
                <button key={s} onClick={() => s === 1 ? setMoveTarget(w.id) : setState(w.id, s)}
                  className={`px-2 py-1 rounded-lg text-[10px] font-bold border transition-all ${w.state === s ? 'border-white/30 text-white bg-white/10' : 'border-white/10 text-slate-500 hover:border-white/20 hover:text-slate-300'}`}>
                  {stateLabel(s)}
                </button>
              ))}
              <button onClick={() => deleteWorker(w.id)}
                className="ml-auto px-2 py-1 rounded-lg text-[10px] font-bold border border-red-500/20 text-red-400/60 hover:border-red-500/40 hover:text-red-400 transition-all">
                ✕
              </button>
            </div>
            {/* Move form */}
            {moveTarget === w.id && (
              <div className="mt-2 flex gap-1.5 flex-wrap">
                <input value={moveDx} onChange={e => setMoveDx(e.target.value)} placeholder="Dest X"
                  className="w-16 bg-slate-700 border border-cyan-500/30 rounded-lg px-2 py-1 text-white text-[10px] focus:outline-none" />
                <input value={moveDy} onChange={e => setMoveDy(e.target.value)} placeholder="Dest Y"
                  className="w-16 bg-slate-700 border border-cyan-500/30 rounded-lg px-2 py-1 text-white text-[10px] focus:outline-none" />
                <input value={moveDur} onChange={e => setMoveDur(e.target.value)} placeholder="Sec"
                  className="w-14 bg-slate-700 border border-slate-600 rounded-lg px-2 py-1 text-white text-[10px] focus:outline-none" />
                <button onClick={() => startMove(w.id)} disabled={loading}
                  className="px-2 py-1 rounded-lg bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 text-[10px] font-bold hover:bg-cyan-500/30 transition-all disabled:opacity-50">
                  Go
                </button>
                <button onClick={() => setMoveTarget(null)}
                  className="px-2 py-1 rounded-lg border border-white/10 text-slate-500 text-[10px] hover:text-white transition-all">
                  ✕
                </button>
              </div>
            )}
          </div>
        ))}
        {workers.length === 0 && <p className="text-xs text-slate-600">No workers yet.</p>}
      </div>

      {msg && <p className="text-xs text-slate-400 mt-2">{msg}</p>}
    </div>
  );
}


function RulesSection() {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="bg-slate-800/60 backdrop-blur-sm border border-white/10 rounded-2xl mb-6 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-white/3 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-amber-500/15 border border-amber-500/25 rounded-xl flex items-center justify-center text-base">📜</div>
          <div>
            <span className="text-white font-bold text-base">Rules &amp; Economy</span>
            <span className="text-slate-500 text-xs ml-3">Season 0 · Phase 1</span>
          </div>
        </div>
        <span className="text-slate-500 text-lg transition-transform duration-200" style={{ display: 'inline-block', transform: open ? 'rotate(90deg)' : 'none' }}>›</span>
      </button>

      {open && (
        <div className="px-6 pb-6 border-t border-white/6">

          {/* Land */}
          <div className="mt-5 mb-5">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Land</p>
            <div className="flex items-start gap-3 bg-amber-500/6 border border-amber-500/15 rounded-xl px-4 py-3">
              <span className="text-xl mt-0.5">🏜️</span>
              <div>
                <p className="text-white font-bold text-sm">Every owned plot generates <span className="text-amber-400">0.3 Bhoomi / hr</span></p>
                <p className="text-slate-400 text-xs mt-1">Bhoomi accrues passively 24/7 as long as you own the land. More plots = more Bhoomi per hour.</p>
              </div>
            </div>
          </div>

          {/* General rules */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">General</p>
            <div className="space-y-2">
              {[
                { icon:'🌐', text:'Land plots are permanent — recorded on the WAX blockchain. 1 WAX = 1 plot.' },
                { icon:'🔒', text:'Only the WAX wallet that bought a plot can plant or uproot on it.' },
                { icon:'💾', text:'Bhoomi and Food balances are saved to the global database every 60 seconds.' },
                { icon:'🌱', text:'Trees generate Food from the moment they are planted — growth stages are visual only in Phase 1.' },
                { icon:'🎯', text:'Total supply: 10,000 plots. Once all are sold, no new land can be created this season.' },
              ].map(r => (
                <div key={r.text} className="flex items-start gap-3 text-sm px-4 py-2.5 rounded-xl bg-white/3 border border-white/6">
                  <span className="text-base flex-shrink-0 mt-0.5">{r.icon}</span>
                  <span className="text-slate-300 leading-relaxed">{r.text}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      )}
    </div>
  );
}

// ─── Colony Leaderboard (Dashboard) ──────────────────────────────────────────

const ACCOUNT_COLORS_DB = [
  '#f59e0b','#14b8a6','#8b5cf6','#f43f5e','#10b981',
  '#3b82f6','#facc15','#ec4899','#06b6d4','#a78bfa',
  '#fb923c','#34d399','#60a5fa','#f472b6','#4ade80',
];
const MEDALS_DB = ['🥇','🥈','🥉'];

function ColonyLeaderboard({ txs }: { txs: WaxTx[] }) {
  const [rows, setRows] = React.useState<any[]>([]);
  const { waxActor } = useWax();

  const acctColor = React.useMemo(() => {
    const ordered = [...txs].reverse();
    const order: string[] = [];
    ordered.forEach(tx => { if (!order.includes(tx.from)) order.push(tx.from); });
    const map: Record<string, string> = {};
    order.forEach((a, i) => { map[a] = ACCOUNT_COLORS_DB[i % ACCOUNT_COLORS_DB.length]; });
    return map;
  }, [txs]);

  const fetchRows = React.useCallback(async () => {
    try {
      const res = await fetch('/samatvam/api/samatvam/leaderboard');
      const data = await res.json();
      if (data.success) setRows(data.rows);
    } catch {}
  }, []);

  React.useEffect(() => {
    fetchRows();
    const t = setInterval(fetchRows, 60_000);
    return () => clearInterval(t);
  }, [fetchRows]);

  if (rows.length === 0) return null;

  return (
    <div className="bg-slate-800/60 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-amber-500/15 border border-amber-500/25 rounded-xl flex items-center justify-center text-lg">🏆</div>
          <div>
            <h2 className="text-lg font-bold text-white">Colony Leaderboard</h2>
            <p className="text-xs text-slate-400">Bhoomi from land · Food from trees · live</p>
          </div>
        </div>
        <span className="text-xs font-bold uppercase tracking-widest bg-amber-500/12 border border-amber-400/25 text-amber-300 px-3 py-1 rounded-full">Season 0 · Phase 1</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-2 px-2 text-slate-400 font-semibold text-xs uppercase tracking-wide w-10">#</th>
              <th className="text-left py-2 px-2 text-slate-400 font-semibold text-xs uppercase tracking-wide">Account</th>
              <th className="text-right py-2 px-2 text-slate-400 font-semibold text-xs uppercase tracking-wide">Plots</th>
              <th className="text-right py-2 px-2 text-amber-600/80 font-semibold text-xs uppercase tracking-wide">🪙 Bhoomi</th>
              <th className="text-right py-2 px-2 text-amber-700/60 font-semibold text-xs uppercase tracking-wide hidden md:table-cell">+/hr</th>
              <th className="text-right py-2 px-2 text-pink-400/80 font-semibold text-xs uppercase tracking-wide">🍎 Food</th>
              <th className="text-right py-2 px-2 text-pink-700/60 font-semibold text-xs uppercase tracking-wide hidden md:table-cell">+/hr</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row: any, i: number) => {
              const isMe = row.wax_account === waxActor;
              const bh = parseFloat(row.bhoomi_score) || 0;
              const bhR = parseFloat(row.bhoomi_rate) || 0;
              const fd = parseFloat(row.food_score) || 0;
              const fdR = parseFloat(row.food_rate) || 0;
              const color = acctColor[row.wax_account] || '#475569';
              return (
                <tr key={row.wax_account}
                  className={`border-b border-white/5 transition-colors ${isMe ? 'bg-green-500/5 border-l-2 border-l-green-500/50' : 'hover:bg-white/3'}`}
                >
                  <td className="py-2.5 px-2 font-black text-sm">
                    {i < 3 ? <span>{MEDALS_DB[i]}</span> : <span className="text-slate-500">{i + 1}</span>}
                  </td>
                  <td className="py-2.5 px-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color, boxShadow: isMe ? `0 0 6px ${color}` : 'none' }} />
                      <span className={`font-mono text-xs ${isMe ? 'text-green-400 font-bold' : 'text-slate-300'}`}>
                        {row.wax_account}{isMe ? ' (you)' : ''}
                      </span>
                    </div>
                  </td>
                  <td className="py-2.5 px-2 text-right font-bold text-amber-400 text-xs">{row.plots_count || 0}</td>
                  <td className="py-2.5 px-2 text-right font-black text-sm" style={{ color: bh > 0 ? '#d97706' : '#334155' }}>
                    {bh > 0 ? bh.toFixed(1) : '—'}
                  </td>
                  <td className="py-2.5 px-2 text-right text-xs hidden md:table-cell" style={{ color: bhR > 0 ? '#92400e' : '#334155' }}>
                    {bhR > 0 ? `+${bhR.toFixed(1)}` : '—'}
                  </td>
                  <td className="py-2.5 px-2 text-right font-black text-sm" style={{ color: fd > 0 ? '#f472b6' : '#334155' }}>
                    {fd > 0 ? fd.toFixed(1) : '—'}
                  </td>
                  <td className="py-2.5 px-2 text-right text-xs hidden md:table-cell" style={{ color: fdR > 0 ? '#9d174d' : '#334155' }}>
                    {fdR > 0 ? `+${fdR.toFixed(1)}` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Activity Monitor ─────────────────────────────────────────────────────────

const ACTIVITY_META: Record<string, { label: string; color: string; bg: string; border: string; dot: string }> = {
  redemption: { label: 'Redemption', color: 'text-rose-300',   bg: 'bg-rose-500/15',   border: 'border-rose-500/30',   dot: 'bg-rose-400' },
  conversion: { label: 'Conversion', color: 'text-violet-300', bg: 'bg-violet-500/15', border: 'border-violet-500/30', dot: 'bg-violet-400' },
  craft:       { label: 'Crafted',    color: 'text-emerald-300',bg: 'bg-emerald-500/15',border: 'border-emerald-500/30',dot: 'bg-emerald-400' },
};

const ITEM_LABELS: Record<string, string> = {
  water_harvester: 'Water Harvester',
  handcart: 'Hand Cart',
};

function ActivityMonitor() {
  const [rows, setRows] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [lastRefresh, setLastRefresh] = React.useState(Date.now());

  const load = React.useCallback(() => {
    fetch('/samatvam/api/samatvam/activity')
      .then(r => r.json())
      .then(d => { if (d.success) setRows(d.rows); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { load(); }, [load, lastRefresh]);
  React.useEffect(() => {
    const t = setInterval(() => setLastRefresh(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const fmt = (ts: string) => {
    const d = new Date(ts);
    const now = Date.now();
    const diff = Math.floor((now - d.getTime()) / 1000);
    if (diff < 60)  return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  // Summary stats
  const totalRedemptions = rows.filter(r => r.activity_type === 'redemption').length;
  const totalConversions = rows.filter(r => r.activity_type === 'conversion').length;
  const totalCrafts = rows.filter(r => r.activity_type === 'craft').length;

  return (
    <div className="bg-slate-800/60 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden mb-6">
      {/* Header */}
      <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center text-base" style={{ background: 'linear-gradient(135deg,#7c3aed,#0ea5e9)' }}>⚡</div>
          <div>
            <h2 className="text-white font-black text-base leading-tight">Activity Monitor</h2>
            <p className="text-slate-500 text-xs">Live feed · auto-refreshes every 30s</p>
          </div>
        </div>
        <button
          onClick={() => { setLoading(true); setLastRefresh(Date.now()); }}
          className="text-slate-500 hover:text-slate-300 transition-colors p-1.5 rounded-lg hover:bg-white/5"
          title="Refresh"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
            <path d="M21 3v5h-5"/>
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
            <path d="M8 16H3v5"/>
          </svg>
        </button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-4 divide-x divide-white/5 border-b border-white/8">
        {[
          { label: 'Redemptions', value: totalRedemptions, color: 'text-rose-400' },
          { label: 'Conversions', value: totalConversions, color: 'text-violet-400' },
          { label: 'Items Crafted', value: totalCrafts, color: 'text-emerald-400' },
        ].map(s => (
          <div key={s.label} className="px-3 py-2.5 text-center">
            <div className={`text-lg font-black ${s.color}`}>{s.value}</div>
            <div className="text-slate-600 text-[10px] font-bold uppercase tracking-wide leading-tight mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Feed */}
      {loading ? (
        <div className="p-4 space-y-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-12 bg-white/5 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="py-12 text-center text-slate-500 text-sm">No activity yet</div>
      ) : (
        <div className="divide-y divide-white/5">
          {rows.map((row, i) => {
            const meta = ACTIVITY_META[row.activity_type] ?? ACTIVITY_META.conversion;
            return (
              <div key={i} className="flex items-center gap-3 px-4 py-3 hover:bg-white/3 transition-colors">
                {/* Dot */}
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${meta.dot}`} />

                {/* Badge */}
                <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border flex-shrink-0 ${meta.color} ${meta.bg} ${meta.border}`}>
                  {meta.label}
                </span>

                {/* Account */}
                <span className="font-mono text-xs text-teal-400 flex-shrink-0">{row.wax_account}</span>

                {/* Details */}
                <div className="flex-1 flex items-center gap-2 min-w-0 flex-wrap">
                  {row.activity_type === 'redemption' && (
                    <>
                      <span className="text-xs text-slate-400">{parseFloat(row.food_amount).toFixed(1)} Food redeemed</span>
                      <span className="text-xs text-slate-600 truncate">{row.destination ? `→ ${row.destination}` : ''}</span>
                    </>
                  )}
                  {row.activity_type === 'conversion' && (
                    <>
                      <span className="text-xs text-slate-400">{parseFloat(row.food_amount).toFixed(1)} Food converted</span>
                    </>
                  )}
                  {row.activity_type === 'craft' && (
                    <>
                      <span className="text-xs font-bold text-emerald-300">{ITEM_LABELS[row.item] ?? row.item}</span>
                      {row.wax_equivalent && (
                        <span className="text-xs text-slate-600">≈ {row.wax_equivalent} WAX</span>
                      )}
                    </>
                  )}
                </div>

                {/* TX link */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {row.tx_id && (
                    <a
                      href={`https://waxblock.io/transaction/${row.tx_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-mono text-[10px] text-cyan-500 hover:text-cyan-300 underline transition-colors"
                    >
                      {row.tx_id.slice(0, 8)}…
                    </a>
                  )}
                  <span className="text-slate-600 text-[11px] whitespace-nowrap">{fmt(row.event_at)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── WAX Transactions Table ───────────────────────────────────────────────────

function WaxTransactions({ txs, loading, lastUpdated, error, onRefresh }: {
  txs: WaxTx[];
  loading: boolean;
  lastUpdated: string;
  error: string;
  onRefresh: () => void;
}) {
  const formatTime = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })
      + ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const shortTrx = (id: string) => id.slice(0, 8) + '...' + id.slice(-6);

  return (
    <div className="bg-slate-800/60 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
            <ArrowDownLeft className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Incoming WAX Payments</h2>
            <p className="text-xs text-slate-400">{WAX_ACCOUNT} · auto-refreshes every 60s</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {lastUpdated && <span className="text-xs text-slate-500 hidden md:block">Updated {lastUpdated}</span>}
          <button
            onClick={onRefresh}
            disabled={loading}
            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors disabled:opacity-50 border border-white/10"
          >
            <RefreshCw className={`w-4 h-4 text-amber-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-400/20 text-red-300 p-3 rounded-lg text-sm mb-4">{error}</div>
      )}

      {loading && txs.length === 0 ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-10 bg-white/5 rounded-lg animate-pulse"></div>
          ))}
        </div>
      ) : txs.length === 0 ? (
        <div className="text-center py-8 text-slate-500">No incoming WAX transactions found</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-2 px-2 text-slate-400 font-semibold text-xs uppercase tracking-wide">Time</th>
                <th className="text-right py-2 px-2 text-slate-400 font-semibold text-xs uppercase tracking-wide">Amount</th>
                <th className="text-left py-2 px-2 text-slate-400 font-semibold text-xs uppercase tracking-wide">From</th>
                <th className="text-left py-2 px-2 text-slate-400 font-semibold text-xs uppercase tracking-wide hidden md:table-cell">Memo</th>
                <th className="text-left py-2 px-2 text-slate-400 font-semibold text-xs uppercase tracking-wide hidden lg:table-cell">Tx ID</th>
              </tr>
            </thead>
            <tbody>
              {txs.map((tx, i) => (
                <tr key={tx.trx_id + i} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="py-2.5 px-2 text-slate-400 text-xs whitespace-nowrap">{formatTime(tx.timestamp)}</td>
                  <td className="py-2.5 px-2 text-right">
                    <span className="font-bold text-amber-400">{tx.amount.toFixed(4)}</span>
                    <span className="text-xs text-slate-500 ml-1">WAX</span>
                  </td>
                  <td className="py-2.5 px-2 font-mono text-xs text-teal-400">{tx.from}</td>
                  <td className="py-2.5 px-2 text-slate-500 text-xs hidden md:table-cell max-w-xs truncate">{tx.memo}</td>
                  <td className="py-2.5 px-2 font-mono text-xs text-slate-600 hidden lg:table-cell">
                    <a
                      href={`https://waxblock.io/transaction/${tx.trx_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-amber-400 transition-colors underline decoration-dotted"
                    >
                      {shortTrx(tx.trx_id)}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Water Harvester Shareholders ────────────────────────────────────────────
function WaterHarvesterShareholders() {
  const [shareholders, setShareholders] = React.useState<any[]>([]);
  React.useEffect(() => {
    fetch('/samatvam/api/samatvam/task-shareholders/water-harvesting-zone')
      .then(r => r.json())
      .then(d => { if (d.success) setShareholders(d.shareholders); })
      .catch(() => {});
  }, []);

  if (!shareholders.length) return null;

  const medals = ['🥇','🥈','🥉'];
  const rankColors = ['#fcd34d','#94a3b8','#d97706'];

  return (
    <div className="mb-6 rounded-2xl overflow-hidden relative" style={{ background: 'linear-gradient(135deg, rgba(8,47,73,0.8) 0%, rgba(15,23,42,0.95) 60%, rgba(8,47,73,0.6) 100%)', border: '1px solid rgba(56,189,248,0.25)' }}>
      {/* Glow */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-96 h-40 rounded-full blur-3xl" style={{ background: 'rgba(56,189,248,0.08)' }} />
        <div className="absolute -bottom-10 -right-10 w-56 h-56 rounded-full blur-3xl" style={{ background: 'rgba(16,185,129,0.06)' }} />
      </div>

      <div className="relative p-6">
        {/* Header */}
        <div className="flex flex-wrap items-start gap-4 mb-5">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-2xl">💧</span>
              <span className="text-[10px] font-black uppercase tracking-[0.2em] px-2.5 py-1 rounded-full" style={{ background: 'rgba(56,189,248,0.15)', border: '1px solid rgba(56,189,248,0.3)', color: '#7dd3fc' }}>Task 2 Complete</span>
              <span className="text-[10px] font-black uppercase tracking-[0.2em] px-2.5 py-1 rounded-full" style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)', color: '#6ee7b7' }}>✅ Water Harvesting Zone</span>
            </div>
            <h2 className="text-xl font-black text-white tracking-tight leading-tight">🎉 Congratulations to all the</h2>
            <h2 className="text-xl font-black tracking-tight leading-tight" style={{ color: '#7dd3fc' }}>Water Harvester Shareholders!</h2>
            <p className="text-sm mt-1.5" style={{ color: 'rgba(148,163,184,0.7)' }}>Together you delivered <span className="text-white font-bold">20,000 food packets</span> to power the city's first water harvesting system. 🌊</p>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-4xl mb-1">🏆</div>
            <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: 'rgba(255,255,255,0.3)' }}>7 Shareholders</p>
            <p className="text-[10px]" style={{ color: 'rgba(56,189,248,0.5)' }}>20,000 food delivered</p>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(56,189,248,0.12)' }}>
          {/* Table header */}
          <div className="grid grid-cols-12 px-4 py-2" style={{ background: 'rgba(56,189,248,0.08)', borderBottom: '1px solid rgba(56,189,248,0.12)' }}>
            <div className="col-span-1 text-[10px] font-black uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.3)' }}>#</div>
            <div className="col-span-5 text-[10px] font-black uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.3)' }}>Wallet</div>
            <div className="col-span-3 text-right text-[10px] font-black uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.3)' }}>Food</div>
            <div className="col-span-3 text-right text-[10px] font-black uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.3)' }}>Share</div>
          </div>

          {shareholders.map((s, i) => (
            <div key={s.wax_account} className="grid grid-cols-12 px-4 py-3 items-center" style={{ borderBottom: i < shareholders.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none', background: i === 0 ? 'rgba(252,211,77,0.04)' : 'transparent' }}>
              {/* Rank */}
              <div className="col-span-1">
                {i < 3
                  ? <span className="text-base">{medals[i]}</span>
                  : <span className="text-xs font-black" style={{ color: 'rgba(255,255,255,0.25)' }}>{s.rank}</span>
                }
              </div>
              {/* Account */}
              <div className="col-span-5 flex items-center gap-2">
                <span className="text-sm font-mono font-bold truncate" style={{ color: i < 3 ? rankColors[i] : '#cbd5e1' }}>{s.wax_account}</span>
              </div>
              {/* Food */}
              <div className="col-span-3 text-right">
                <span className="text-sm font-black" style={{ color: '#7dd3fc' }}>{Number(s.food_contributed).toLocaleString()}</span>
                <span className="text-[10px] ml-1" style={{ color: 'rgba(255,255,255,0.3)' }}>pkts</span>
              </div>
              {/* Share + bar */}
              <div className="col-span-3 text-right">
                <span className="text-sm font-black" style={{ color: '#6ee7b7' }}>{s.share_pct}%</span>
                <div className="mt-1 h-1 rounded-full w-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
                  <div className="h-1 rounded-full" style={{ width: `${s.share_pct}%`, background: 'linear-gradient(90deg, #0ea5e9, #10b981)' }} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer note */}
        <p className="text-center text-[11px] mt-4" style={{ color: 'rgba(255,255,255,0.2)' }}>
          💙 Your contribution is permanently recorded on the Samatvam ledger · Shareholders will receive future benefits as the water system develops
        </p>
      </div>
    </div>
  );
}

// ─── Dashboard ─────────────────────────────────────────────────────────────────

function Dashboard({ txs, loading, lastUpdated, error, fetchTxs, waxBalance, onOpenGame, onOpenWarehouse, onOpenSamrajya, onOpenExchange, onOpenCrafting, onOpenSeason0Rank, onOpenCityMap, onOpenCityMapEditor, onOpenAdminZones, onOpenAdminLabels, onOpenAtavi, onOpenAdminPage, onOpenCityChampions, onOpenPrivacy, onOpenVayal, onOpenMyNfts, onOpenVayalTest, onOpenAtaviForest, onOpenEdinburgh }: {
  txs: WaxTx[];
  loading: boolean;
  lastUpdated: string;
  error: string;
  fetchTxs: () => void;
  waxBalance: string | null;
  onOpenGame: () => void;
  onOpenWarehouse: () => void;
  onOpenSamrajya: () => void;
  onOpenExchange: () => void;
  onOpenCrafting: () => void;
  onOpenSeason0Rank: () => void;
  onOpenCityMap: () => void;
  onOpenCityMapEditor: () => void;
  onOpenAdminZones: () => void;
  onOpenAdminLabels: () => void;
  onOpenAtavi: () => void;
  onOpenAdminPage: () => void;
  onOpenCityChampions: () => void;
  onOpenPrivacy: () => void;
  onOpenVayal: () => void;
  onOpenMyNfts: () => void;
  onOpenVayalTest: () => void;
  onOpenAtaviForest: () => void;
  onOpenEdinburgh: () => void;
}) {
  const { user, logout } = useAuth();
  const { investedWax } = useGameConfig();
  const { totalFood } = useGameTotals();
  const { waxActor, waxConnecting, connect, disconnect } = useWax();
  const token = localStorage.getItem('token') || '';
  const isAdmin = user?.email === 'ahantravyuh@gmail.com';

  const [task1Total, setTask1Total] = React.useState(0);
  React.useEffect(() => {
    fetch('/samatvam/api/samatvam/map/task1-leaderboard')
      .then(r => r.json())
      .then(d => { if (d.success) setTask1Total(d.total); })
      .catch(() => {});
  }, []);

  // WAX wallet — from global context
  const { waxSession: dashWaxSession, waxActor: dashWaxActor, connect: dashConnect, disconnect: dashDisconnect } = useWax();

  const handleReset = async () => {
    if (!window.confirm('This will log you out of Ahantravyuh and your WAX wallet, and clear all browser data.\n\nYour game data in the database is completely safe.\n\nContinue?')) return;
    try { await dashDisconnect(); } catch {}
    logout();
    localStorage.clear();
    window.location.reload();
  };
  const [dashSeedQty, setDashSeedQty] = React.useState('1');
  const [dashSeedStatus, setDashSeedStatus] = React.useState<'idle'|'sending'|'verifying'|'done'|'error'>('idle');
  const [dashSeedMsg, setDashSeedMsg] = React.useState('');
  const [dashSeedCount, setDashSeedCount] = React.useState(0);
  const [dashSeedsSown, setDashSeedsSown] = React.useState(0);
  React.useEffect(() => {
    if (!dashWaxActor) return;
    fetch(`/samatvam/api/samatvam/seeds/${dashWaxActor}`).then(r=>r.json()).then(d=>{ if(d.success) setDashSeedCount(d.seeds_in_wallet||0); }).catch(()=>{});
    fetch('/samatvam/api/samatvam/land/rental-trees').then(r=>r.json()).then(rt=>{ if(rt.success) setDashSeedsSown(rt.trees.filter((t:any)=>t.planted_by===dashWaxActor).length); }).catch(()=>{});
  }, [dashWaxActor]);

  const handleDashWalletConnect = async () => {
    setDashSeedStatus('idle'); setDashSeedMsg('');
    try {
      await dashConnect();
    } catch (err: any) {
      setDashSeedMsg(err.message || 'Connection cancelled');
    }
  };

  const handleDashWalletDisconnect = async () => {
    await dashDisconnect();
    setDashSeedCount(0);
    setDashSeedsSown(0);
    setDashSeedStatus('idle');
    setDashSeedMsg('');
  };

  const handleBuySeedDashboard = async () => {
    const qty = parseInt(dashSeedQty) || 1;
    if (!dashWaxSession || !dashWaxActor) { setDashSeedMsg('Connect your WAX wallet first (use the Atavi Forest)'); return; }
    if (!confirmWaxSpend(`${qty} WAX`)) return;
    setDashSeedStatus('sending'); setDashSeedMsg('');
    try {
      const result = await dashWaxSession.transact({
        actions: [{
          account: 'eosio.token',
          name: 'transfer',
          authorization: [{ actor: dashWaxSession.actor, permission: 'active' }],
          data: { from: dashWaxSession.actor, to: 'samatvam.gm', quantity: `${qty}.00000000 WAX`, memo: `season0:seed:${qty}` },
        }],
      });
      const txId = result?.resolved?.transaction?.id || (result as any)?.transaction_id || '';
      setDashSeedStatus('verifying'); setDashSeedMsg('Transaction sent — checking blockchain…');
      const prevCount = dashSeedCount;
      const deadline = Date.now() + 60_000;
      let done = false;
      while (Date.now() < deadline && !done) {
        await new Promise(r => setTimeout(r, 5000));
        try {
          const r = await fetch(`/samatvam/api/samatvam/seeds/${dashWaxActor}`);
          const d = await r.json();
          if (d.success && (d.seeds_in_wallet || 0) > prevCount) {
            setDashSeedCount(d.seeds_in_wallet || 0);
            setDashSeedStatus('done');
            setDashSeedMsg(`${qty} seed${qty > 1 ? 's' : ''} added! You now have ${d.seeds_in_wallet} seed${d.seeds_in_wallet !== 1 ? 's' : ''}.`);
            fetch('/samatvam/api/samatvam/land/rental-trees').then(r=>r.json()).then(rt=>{ if(rt.success) setDashSeedsSown(rt.trees.filter((t:any)=>t.planted_by===dashWaxActor).length); }).catch(()=>{});
            done = true;
          }
        } catch { /* network hiccup */ }
        if (!done) {
          const secsLeft = Math.round((deadline - Date.now()) / 1000);
          setDashSeedMsg(`Waiting for blockchain confirmation… (${secsLeft}s)`);
        }
      }
      if (!done) {
        setDashSeedStatus('error');
        setDashSeedMsg(`Could not confirm after 60s. Save your tx ID: ${txId}`);
      }
    } catch (err: any) {
      setDashSeedStatus('error');
      setDashSeedMsg(err.message || 'Transaction failed');
    }
  };

  const [isMobile, setIsMobile] = React.useState(()=>window.innerWidth<768);
  React.useEffect(()=>{
    const h=()=>setIsMobile(window.innerWidth<768);
    window.addEventListener('resize',h);
    return()=>window.removeEventListener('resize',h);
  },[]);
  const [openSections, setOpenSections] = React.useState<Record<string,boolean>>({});
  const toggleSection = (k:string)=>setOpenSections(s=>({...s,[k]:!s[k]}));

  return (
    <div className="min-h-screen bg-slate-900 relative">

      {/* ── New City Announcement ── */}
      <div style={{ background: 'linear-gradient(90deg, rgba(52,211,153,0.12), rgba(52,211,153,0.05))', borderBottom: '1px solid rgba(52,211,153,0.25)', padding: '12px 20px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>🏙️</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, color: '#34d399', fontSize: 14, marginBottom: 4, letterSpacing: 0.3 }}>
            New City is now opening — Season 1 begins
          </div>
          <div style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.6 }}>
            The Samatvam city has completed its first phase of construction. New territories, council roles, and resource systems are now live.
            Explore the city map, plant your Atavi Forest, and stake your claim in the emerging colony economy.
          </div>
          <div style={{ marginTop: 6, color: '#64748b', fontSize: 12 }}>
            Samatvam is in pre-alpha — new features are added frequently. Join the Discord for updates and community coordination.
          </div>
        </div>
      </div>

      {/* ── Hero Section ── */}
      <div className="relative h-[480px] overflow-hidden bg-slate-900">

        <img
          src="/samatvam/city.jpg"
          alt="Samatvam city left"
          className="absolute top-0 left-0 h-full object-cover object-center"
          style={{
            width: '52%',
            WebkitMaskImage: 'linear-gradient(to right, black 92%, transparent 100%)',
            maskImage: 'linear-gradient(to right, black 92%, transparent 100%)'
          }}
        />
        <img
          src="/samatvam/city2.jpg"
          alt="Samatvam city right"
          className="absolute top-0 right-0 h-full object-cover object-center"
          style={{
            width: '52%',
            WebkitMaskImage: 'linear-gradient(to left, black 92%, transparent 100%)',
            maskImage: 'linear-gradient(to left, black 92%, transparent 100%)'
          }}
        />
        <div
          className="absolute top-0 h-full pointer-events-none"
          style={{
            left: '48%', width: '4%',
            backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 20%, black 80%, transparent 100%)',
            maskImage: 'linear-gradient(to right, transparent 0%, black 20%, black 80%, transparent 100%)'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/30 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-transparent to-transparent" style={{ width: '30%' }}></div>

        {/* Nav */}
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-8 py-5">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-amber-500/20 backdrop-blur-sm border border-amber-400/30 rounded-xl flex items-center justify-center">
              <span className="text-lg">🏙️</span>
            </div>
            <span className="text-white font-bold text-lg tracking-tight">Samatvam</span>
          </div>
          <button
            onClick={onOpenGame}
            className="flex items-center space-x-2 bg-green-500/10 hover:bg-green-500/20 border border-green-500/30 hover:border-green-400/50 px-4 py-2 rounded-xl transition-all text-sm font-bold text-green-300 hover:text-green-200"
          >
            <span>🌱</span>
            <span>Play Game</span>
          </button>
          <button
            onClick={logout}
            className="flex items-center space-x-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-xl transition-all text-sm font-semibold text-white"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>

        {/* Hero text */}
        <div className="absolute bottom-0 left-0 px-8 pb-10">
          <p className="text-amber-400 text-sm font-semibold tracking-widest uppercase mb-2">
            Sustainable City Building Digital Twin
          </p>
          <h1 className="text-5xl md:text-6xl font-black text-white mb-3 leading-none tracking-tight">
            Samatvam
          </h1>
          <p className="text-slate-300 text-base max-w-lg leading-relaxed">
            Build from desert wasteland to a thriving, balanced city. Every action in the real world shapes this digital twin.
          </p>
          <p className="text-slate-400 text-sm mt-2">Welcome back, <span className="text-amber-400 font-semibold">{user?.name}</span></p>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="px-6 md:px-10 pb-12 -mt-2">
        <div className="w-[95%] mx-auto">

          {/* Wallet cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8 -mt-8 relative z-10">
            <div className="bg-slate-800/80 backdrop-blur-sm border border-amber-500/20 rounded-2xl p-4 shadow-xl">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-slate-400 text-xs font-semibold uppercase tracking-wide truncate">AHN Balance</p>
                  <p className="text-2xl font-black text-amber-400 mt-1 truncate">{user?.ahant_balance?.toFixed(2) ?? '0.00'}</p>
                  <p className="text-slate-500 text-xs mt-0.5">Ahantra</p>
                </div>
                <div className="w-10 h-10 flex-shrink-0 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-center justify-center">
                  <Coins className="w-5 h-5 text-amber-400" />
                </div>
              </div>
            </div>
            <div className="bg-slate-800/80 backdrop-blur-sm border border-teal-500/20 rounded-2xl p-4 shadow-xl">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-slate-400 text-xs font-semibold uppercase tracking-wide truncate">URJA Balance</p>
                  <p className="text-2xl font-black text-teal-400 mt-1 truncate">{user?.urja_balance?.toFixed(2) ?? '0.00'}</p>
                  <p className="text-slate-500 text-xs mt-0.5">Energy</p>
                </div>
                <div className="w-10 h-10 flex-shrink-0 bg-teal-500/10 border border-teal-500/20 rounded-xl flex items-center justify-center">
                  <Zap className="w-5 h-5 text-teal-400" />
                </div>
              </div>
            </div>
            <div className="bg-slate-800/80 backdrop-blur-sm border border-blue-500/20 rounded-2xl p-4 shadow-xl">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-slate-400 text-xs font-semibold uppercase tracking-wide">WAX Fund <span className="text-slate-600 normal-case text-xs">(Risk Lvl 9)</span></p>
                  <p className="text-2xl font-black text-blue-400 mt-1 truncate">
                    {investedWax ? investedWax.toFixed(4) : '—'}
                  </p>
                  <p className="text-slate-500 text-xs mt-0.5 truncate">{WAX_ACCOUNT}</p>
                  <p className="text-blue-400/60 text-xs mt-0.5">Economy value</p>
                </div>
                <div className="w-10 h-10 flex-shrink-0 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center justify-center">
                  <span className="text-xl">⬡</span>
                </div>
              </div>
            </div>

            {/* WAX Wallet Login */}
            <div className="bg-slate-800/80 backdrop-blur-sm border border-purple-500/20 rounded-2xl p-4 shadow-xl">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-slate-400 text-xs font-semibold uppercase tracking-wide">WAX Wallet</p>
                  {waxActor ? (
                    <>
                      <p className="text-sm font-black text-purple-300 mt-1 truncate">{waxActor}</p>
                      <button
                        onClick={disconnect}
                        className="text-slate-500 hover:text-red-400 text-xs mt-1 transition-colors"
                      >
                        Disconnect
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-slate-600 text-xs mt-1">Not connected</p>
                      <button
                        onClick={connect}
                        disabled={waxConnecting}
                        className="mt-2 w-full bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/40 hover:border-purple-400/60 text-purple-300 text-xs font-bold py-1.5 px-3 rounded-lg transition-all disabled:opacity-50"
                      >
                        {waxConnecting ? 'Connecting…' : 'Connect Wallet'}
                      </button>
                    </>
                  )}
                </div>
                <div className="w-10 h-10 flex-shrink-0 bg-purple-500/10 border border-purple-500/20 rounded-xl flex items-center justify-center">
                  <span className="text-xl">⬡</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Vayal + On-Chain: side by side on desktop ── */}
          <div className="flex flex-col lg:flex-row gap-4 mb-6">

          {/* ── Vayal Minting Announcement ────────────────────────────── */}
          <div
            onClick={onOpenVayal}
            className="cursor-pointer group relative w-full lg:w-1/2 rounded-3xl overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(120,53,15,0.55) 0%, rgba(78,32,2,0.7) 35%, rgba(23,18,2,0.97) 100%)',
              border: '1px solid rgba(251,191,36,0.35)',
              boxShadow: '0 0 60px rgba(251,191,36,0.08), 0 2px 40px rgba(0,0,0,0.5)',
            }}>

            {/* glow orbs */}
            <div className="absolute -top-10 -left-10 w-56 h-56 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.12) 0%, transparent 70%)' }} />
            <div className="absolute -bottom-10 right-10 w-72 h-72 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(217,119,6,0.1) 0%, transparent 70%)' }} />
            <div className="absolute inset-0 rounded-3xl pointer-events-none transition-opacity duration-500 opacity-0 group-hover:opacity-100"
              style={{ boxShadow: 'inset 0 0 60px rgba(251,191,36,0.06)' }} />

            <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4 px-4 py-4 sm:px-6 sm:py-5">

              {/* Left: icon + text */}
              <div className="flex items-start gap-4 flex-1 min-w-0">
                <div className="flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center text-3xl"
                  style={{ background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.3)' }}>
                  🌾
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] font-black uppercase tracking-[0.18em] px-2.5 py-1 rounded-full flex items-center gap-1.5"
                      style={{ background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.35)', color: '#fcd34d' }}>
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse inline-block" />
                      Now Open
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Season 0</span>
                  </div>
                  <p className="text-white font-black text-xl sm:text-2xl leading-tight tracking-tight">
                    Vayal Farmland Minting
                  </p>
                  <p className="text-amber-200/60 text-xs sm:text-sm mt-1 leading-relaxed">
                    Craft farmland from Food Packets, Shram, or Bhoomi — no WAX required for the base tier.
                  </p>

                  {/* 4 tier pills */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {[
                      { label: 'Food Only',   color: '#86efac', bg: 'rgba(34,197,94,0.12)',   border: 'rgba(34,197,94,0.3)'   },
                      { label: '+ Shram',     color: '#7dd3fc', bg: 'rgba(56,189,248,0.12)',  border: 'rgba(56,189,248,0.3)'  },
                      { label: '+ WARI',      color: '#7dd3fc', bg: 'rgba(56,189,248,0.12)',  border: 'rgba(56,189,248,0.3)'  },
                      { label: '+ Bhoomi',    color: '#c4b5fd', bg: 'rgba(139,92,246,0.12)',  border: 'rgba(139,92,246,0.3)'  },
                    ].map(t => (
                      <span key={t.label}
                        className="text-[10px] font-bold px-2.5 py-1 rounded-full"
                        style={{ background: t.bg, border: `1px solid ${t.border}`, color: t.color }}>
                        {t.label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: CTA */}
              <div className="flex-shrink-0 w-full sm:w-auto">
                <div className="flex items-center justify-center gap-2 px-5 py-3 rounded-2xl font-black text-sm text-amber-900 transition-all duration-300 group-hover:scale-105"
                  style={{ background: 'linear-gradient(135deg, #fcd34d, #f59e0b)', boxShadow: '0 4px 20px rgba(251,191,36,0.3)' }}>
                  Open Vayal
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </div>
              </div>

            </div>
          </div>

          {/* ── On-Chain Portfolio Card ── */}
          <div
            onClick={onOpenMyNfts}
            className="cursor-pointer group w-full lg:w-1/2 rounded-2xl overflow-hidden relative"
            style={{ background: 'linear-gradient(120deg, rgba(2,6,23,0.97) 0%, rgba(15,23,42,0.95) 60%, rgba(30,15,40,0.97) 100%)', border: '1px solid rgba(139,92,246,0.25)' }}>

            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full pointer-events-none blur-3xl"
              style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.09) 0%, transparent 70%)' }} />
            <div className="absolute -bottom-10 -left-10 w-48 h-48 rounded-full pointer-events-none blur-3xl"
              style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.06) 0%, transparent 70%)' }} />

            <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5 px-6 py-5">

              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center text-2xl"
                  style={{ background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.25)' }}>
                  ◈
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] font-black uppercase tracking-[0.18em] px-2.5 py-0.5 rounded-full"
                      style={{ background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.3)', color: '#c4b5fd' }}>
                      On-Chain
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">In-Game / On-Chain Assets</span>
                  </div>
                  <p className="text-white font-black text-lg leading-tight tracking-tight">
                    What you build here, you own.
                  </p>
                  <p className="text-slate-500 text-xs mt-1 leading-relaxed">
                    Assets live on the WAX blockchain — wallet-bound, tradeable, and outside our control once minted.
                  </p>
                </div>
              </div>

              <div className="flex-shrink-0 flex flex-row sm:flex-col items-start sm:items-end gap-2 w-full sm:w-auto">
                {[
                  { label: 'Collection', value: 'ahantravyuhw', color: '#a78bfa' },
                  { label: 'Token', value: 'VAYAL · ahantravh.gm', color: '#fcd34d' },
                  { label: 'Withdraw', value: '1% fee · instant queue', color: '#86efac' },
                ].map(s => (
                  <div key={s.label} className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-600 uppercase tracking-widest">{s.label}</span>
                    <span className="text-[11px] font-bold" style={{ color: s.color }}>{s.value}</span>
                  </div>
                ))}
                <div className="mt-1 flex items-center gap-1.5 text-[11px] font-bold transition-colors group-hover:text-white text-slate-500">
                  View your assets
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </div>
              </div>

            </div>
          </div>

          </div>{/* end side-by-side row */}

          {/* ── City Evolution: Stage 0 → Stage 1 ── */}
          {true && <div className="mb-6 rounded-3xl overflow-hidden relative" style={{ border: '1px solid rgba(255,255,255,0.12)', background: '#0f172a' }}>

            {/* Header */}
            <div className="px-6 pt-5 pb-3 flex items-center justify-between">
              <div>
                <p style={{ color: '#f59e0b', fontSize: 10, fontWeight: 900, letterSpacing: 3, textTransform: 'uppercase', margin: 0 }}>City Evolution</p>
                <p style={{ color: 'white', fontSize: 18, fontWeight: 900, margin: '2px 0 0' }}>Stage 0 → Stage 1</p>
              </div>
              <div style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 20, padding: '4px 14px' }}>
                <span style={{ color: '#fbbf24', fontSize: 11, fontWeight: 800 }}>🏗️ Stage 1 Unlocks After Task 10</span>
              </div>
            </div>

            {/* Full-width map with bus */}
            <div className="relative w-full">
              {/* city3.jpg as full background — no crop */}
              <img
                src="/samatvam/city3.jpg"
                alt="City Map"
                className="w-full"
                style={{ display: 'block', height: 'auto' }}
              />

              {/* Subtle dark vignette edges */}
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(15,23,42,0.35) 0%, transparent 20%, transparent 75%, rgba(15,23,42,0.55) 100%)' }} />

              {/* Eco bus — driving from City 1 toward City 2, positioned on the road ~48% from left */}
              <img
                src="/samatvam/eco-bus.jpg"
                alt="Eco Bus"
                style={{
                  position: 'absolute',
                  left: '46%',
                  top: '52%',
                  transform: 'translateY(-50%)',
                  width: 100,
                  height: 'auto',
                  mixBlendMode: 'multiply',
                  filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))',
                  borderRadius: 6,
                }}
              />

              {/* City 1 label — left urban cluster ~28% left, ~55% top */}
              <div style={{
                position: 'absolute',
                left: '22%',
                top: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(16,185,129,0.85)',
                backdropFilter: 'blur(6px)',
                border: '1px solid rgba(110,231,183,0.5)',
                borderRadius: 8,
                padding: '5px 12px',
                whiteSpace: 'nowrap',
              }}>
                <p style={{ color: 'white', fontSize: 10, fontWeight: 900, margin: 0, letterSpacing: 1 }}>🏙️ Current City</p>
                <p style={{ color: '#d1fae5', fontSize: 8, fontWeight: 700, margin: '1px 0 0', letterSpacing: 0.5 }}>Stage 0 · Active Now</p>
              </div>

              {/* Clickable zone over Edinburgh building cluster */}
              <div onClick={() => onOpenEdinburgh()} style={{
                position: 'absolute',
                left: '58%',
                top: '25%',
                width: '24%',
                height: '40%',
                cursor: 'pointer',
                zIndex: 2,
              }} />

              {/* City 2 label — right cluster near lake ~68% left, ~40% top */}
              <div onClick={() => onOpenEdinburgh()} style={{
                position: 'absolute',
                left: '68%',
                top: '35%',
                transform: 'translateX(-50%)',
                background: 'rgba(251,191,36,0.85)',
                backdropFilter: 'blur(6px)',
                border: '1px solid rgba(253,230,138,0.5)',
                borderRadius: 8,
                padding: '5px 12px',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                zIndex: 3,
              }}>
                <p style={{ color: '#000', fontSize: 10, fontWeight: 900, margin: 0, letterSpacing: 1 }}>🏙️ Edinburgh</p>
                <p style={{ color: '#78350f', fontSize: 8, fontWeight: 700, margin: '1px 0 0', letterSpacing: 0.5 }}>Stage 1 · Coming Soon</p>
              </div>
            </div>

            {/* Edinburgh image modal */}

            {/* Footer bar */}
            <div className="px-6 py-3 flex items-center gap-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <span style={{ fontSize: 18 }}>🚌</span>
              <p style={{ color: '#94a3b8', fontSize: 14, fontWeight: 700, margin: 0, flex: 1 }}>
                The new city will open as soon as all 10 Stage 0 tasks are completed.
              </p>
              <button onClick={onOpenCityMap} style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: '5px 14px', color: '#f59e0b', fontSize: 10, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                View Stage 0 Map →
              </button>
            </div>
          </div>}

          {/* ── Primary Nav Row: Empire · Season 1 Map · Atavi Forest ── */}
          {isMobile&&(
            <div className="mb-4 flex flex-col gap-2">
              {([
                {k:'empire', icon:'🏰', label:'My Empire', sub:'Your plots, trees & infrastructure', action:onOpenSamrajya, color:'rgba(99,102,241,0.15)', border:'rgba(99,102,241,0.3)'},
                {k:'citymap', icon:'🏙️', label:'Season 1 Map', sub:'Task 1 active — place food packets at the drop zone', action:onOpenCityMap, color:'rgba(245,158,11,0.1)', border:'rgba(245,158,11,0.3)'},
                {k:'ataviforest', icon:'⬡', label:'Atavi', sub:'10,000 plots · 20 golden epicenter · Isometric map', action:onOpenAtaviForest, color:'rgba(251,191,36,0.05)', border:'rgba(251,191,36,0.25)'},
                {k:'vayal', icon:'🌾', label:'Vayal Farmland', sub:'25,000 farmland · Craft from Food, Shram or Bhoomi', action:onOpenVayal, color:'rgba(120,53,15,0.25)', border:'rgba(251,191,36,0.35)'},
                {k:'atavi', icon:'🌳', label:'Forest', sub:'10,000 plots · Plant trees · Harvest logs · Earn Food', action:onOpenGame, color:'rgba(16,185,129,0.1)', border:'rgba(16,185,129,0.3)'},
                {k:'vayaltest', icon:'🧪', label:'Vayal Test Map', sub:'Tile testing sandbox · Separate images', action:onOpenVayalTest, color:'rgba(249,115,22,0.1)', border:'rgba(249,115,22,0.3)'},
              ] as const).map(s=>(
                <div key={s.k} style={{borderRadius:12,border:`1px solid ${s.border}`,background:s.color,overflow:'hidden'}}>
                  <button onClick={()=>toggleSection(s.k)} style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 14px',background:'none',border:'none',cursor:'pointer',textAlign:'left'}}>
                    <span style={{display:'flex',alignItems:'center',gap:10}}>
                      <span style={{fontSize:20}}>{s.icon}</span>
                      <span style={{color:'white',fontWeight:800,fontSize:14}}>{s.label}</span>
                    </span>
                    <span style={{color:'#64748b',fontSize:11}}>{openSections[s.k]?'▲':'▼'}</span>
                  </button>
                  {openSections[s.k]&&(
                    <div style={{padding:'0 14px 14px',borderTop:'1px solid rgba(255,255,255,0.06)'}}>
                      <p style={{fontSize:12,color:'#94a3b8',marginBottom:12,marginTop:10,lineHeight:1.5}}>{s.sub}</p>
                      <button onClick={s.action} style={{width:'100%',padding:'9px',borderRadius:9,background:'rgba(255,255,255,0.08)',border:'1px solid rgba(255,255,255,0.12)',color:'white',fontSize:12,fontWeight:700,cursor:'pointer'}}>Open →</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {!isMobile&&<div className="flex gap-4 mb-6">

            {/* Samrajya (Empire) */}
            <button onClick={onOpenSamrajya}
              className="relative flex-1 rounded-2xl overflow-hidden border border-indigo-400/50 hover:border-indigo-300/70 min-h-[180px] cursor-pointer group transition-all duration-300 hover:shadow-2xl hover:shadow-indigo-500/20 text-left"
              style={{ background: 'linear-gradient(135deg, rgba(30,5,80,0.97) 0%, rgba(15,5,40,0.99) 60%, rgba(5,3,15,1) 100%)' }}>
              <div className="absolute inset-0 rounded-2xl border border-indigo-400/15 pointer-events-none" />
              <div className="absolute top-0 right-0 w-40 h-40 bg-indigo-500/12 rounded-full blur-3xl pointer-events-none group-hover:bg-indigo-500/20 transition-all duration-500" />
              <div className="relative h-full flex flex-col justify-between p-6">
                <div className="flex items-start gap-5">
                  <div className="text-6xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300">🏰</div>
                  <div className="flex-1 min-w-0">
                    <span className="text-[9px] font-bold uppercase tracking-widest bg-indigo-400/20 border border-indigo-400/40 text-indigo-300 px-2 py-0.5 rounded-full">Season 0 · Live</span>
                    <p className="text-indigo-100 font-black text-xl tracking-tight leading-tight mt-2">Samrajya</p>
                    <p className="text-indigo-300/60 text-xs mt-1 leading-relaxed">Your plots, trees &amp; infrastructure in one place</p>
                  </div>
                </div>
                <div>
                  <p className="text-indigo-200 font-black text-base mb-2">My Empire</p>
                  <div className="w-full bg-indigo-500/20 border border-indigo-400/40 group-hover:bg-indigo-500/35 text-indigo-200 group-hover:text-indigo-100 py-2 rounded-xl font-black text-xs text-center transition-all duration-200">
                    Open Empire →
                  </div>
                </div>
              </div>
            </button>

            {/* Season 1 Map — LIVE */}
            <button onClick={onOpenCityMap}
              className="relative flex-1 rounded-2xl overflow-hidden border border-amber-400/50 hover:border-amber-300/70 min-h-[180px] cursor-pointer group transition-all duration-300 hover:shadow-2xl hover:shadow-amber-500/20 text-left"
              style={{ background: 'linear-gradient(135deg, rgba(55,30,0,0.97) 0%, rgba(25,14,2,0.99) 60%, rgba(8,5,0,1) 100%)' }}>
              <div className="absolute inset-0 rounded-2xl border border-amber-400/15 pointer-events-none" />
              <div className="absolute top-0 right-0 w-40 h-40 bg-amber-500/12 rounded-full blur-3xl pointer-events-none group-hover:bg-amber-500/20 transition-all duration-500" />
              <div className="relative h-full flex flex-col justify-between p-6">
                <div className="flex items-start gap-5">
                  <div className="text-6xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300">🏙️</div>
                  <div className="flex-1 min-w-0">
                    <span className="text-[9px] font-bold uppercase tracking-widest bg-amber-400/20 border border-amber-400/40 text-amber-300 px-2 py-0.5 rounded-full">Season 1 · Live</span>
                    <p className="text-amber-100 font-black text-xl tracking-tight leading-tight mt-2">City Map</p>
                    <p className="text-amber-300/60 text-xs mt-1 leading-relaxed">Task 1 active — place food packets at the drop zone</p>
                  </div>
                </div>
                <div>
                  <p className="text-amber-200 font-black text-base mb-2">Season 1 Map</p>
                  <div className="w-full bg-amber-500/20 border border-amber-400/40 group-hover:bg-amber-500/35 text-amber-200 group-hover:text-amber-100 py-2 rounded-xl font-black text-xs text-center transition-all duration-200">
                    Open Map →
                  </div>
                </div>
              </div>
            </button>

            {/* Atavi (new isometric forest) */}
            <div onClick={onOpenAtaviForest}
              className="cursor-pointer group relative flex-1 rounded-2xl overflow-hidden border border-yellow-500/25 hover:border-yellow-400/50 transition-all duration-300 hover:shadow-2xl hover:shadow-yellow-500/15 min-h-[180px]"
              style={{ background: 'linear-gradient(135deg, rgba(46,35,5,0.9) 0%, rgba(23,18,2,0.95) 60%, rgba(2,6,23,1) 100%)' }}
            >
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 right-0 w-40 h-40 bg-yellow-500/8 rounded-full blur-3xl group-hover:bg-yellow-500/15 transition-all duration-500" />
              </div>
              <div className="relative h-full flex flex-col justify-between p-6">
                <div className="flex items-start gap-5">
                  <div className="text-6xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300">⬡</div>
                  <div className="flex-1 min-w-0">
                    <span className="text-[10px] font-bold uppercase tracking-widest bg-yellow-500/15 border border-yellow-400/20 text-yellow-300 px-2 py-0.5 rounded-full">Season 0 · Live</span>
                    <p className="text-white font-black text-xl tracking-tight leading-tight mt-2">Atavi</p>
                    <p className="text-yellow-400/60 text-xs mt-1 leading-relaxed">10,000 plots · 20 golden epicenter · Isometric map</p>
                  </div>
                </div>
                <div>
                  <p className="text-yellow-200 font-black text-base mb-2">Atavi</p>
                  <div className="w-full bg-yellow-500/20 border border-yellow-400/40 group-hover:bg-yellow-500/35 text-yellow-200 group-hover:text-yellow-100 py-2 rounded-xl font-black text-xs text-center transition-all duration-200">
                    Open Map →
                  </div>
                </div>
              </div>
            </div>

            {/* Vayal Farmlands */}
            <div onClick={onOpenVayal}
              className="cursor-pointer group relative flex-1 rounded-2xl overflow-hidden border border-yellow-500/25 hover:border-yellow-400/50 transition-all duration-300 hover:shadow-2xl hover:shadow-yellow-500/15 min-h-[180px]"
              style={{ background: 'linear-gradient(135deg, rgba(46,30,5,0.9) 0%, rgba(23,18,2,0.95) 60%, rgba(2,6,23,1) 100%)' }}
            >
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 right-0 w-40 h-40 bg-yellow-500/8 rounded-full blur-3xl group-hover:bg-yellow-500/15 transition-all duration-500" />
              </div>
              <div className="relative h-full flex flex-col justify-between p-6">
                <div className="flex items-start gap-5">
                  <div className="text-6xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300">🌾</div>
                  <div className="flex-1 min-w-0">
                    <span className="text-[10px] font-bold uppercase tracking-widest bg-yellow-500/15 border border-yellow-400/20 text-yellow-300 px-2 py-0.5 rounded-full">Season 1 · Coming</span>
                    <p className="text-white font-black text-xl tracking-tight leading-tight mt-2">Vayal Farmlands</p>
                    <p className="text-yellow-400/60 text-xs mt-1 leading-relaxed">25,000 farmlands · 10 growth stages · Craft</p>
                  </div>
                </div>
                <div>
                  <p className="text-yellow-200 font-black text-base mb-2">Vayal Farmlands</p>
                  <div className="w-full bg-yellow-500/20 border border-yellow-400/40 group-hover:bg-yellow-500/35 text-yellow-200 group-hover:text-yellow-100 py-2 rounded-xl font-black text-xs text-center transition-all duration-200">
                    Open Map →
                  </div>
                </div>
              </div>
            </div>

            {/* Khanij (old Atavi rental system — disabled) */}
            <div
              className="relative flex-1 rounded-2xl overflow-hidden border border-slate-700/40 min-h-[180px] opacity-50 cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, rgba(15,23,42,0.9) 0%, rgba(2,6,23,1) 100%)' }}
            >
              <div className="relative h-full flex flex-col justify-between p-6">
                <div className="flex items-start gap-5">
                  <div className="text-6xl flex-shrink-0 grayscale">⛏️</div>
                  <div className="flex-1 min-w-0">
                    <span className="text-[10px] font-bold uppercase tracking-widest bg-slate-500/15 border border-slate-400/20 text-slate-400 px-2 py-0.5 rounded-full">Coming Soon</span>
                    <p className="text-slate-300 font-black text-xl tracking-tight leading-tight mt-2">Khanij</p>
                    <p className="text-slate-500 text-xs mt-1 leading-relaxed">Metal Mining</p>
                  </div>
                </div>
                <div>
                  <p className="text-slate-400 font-black text-base mb-2">Khanij</p>
                  <div className="w-full bg-slate-700/50 text-slate-500 py-2 rounded-xl font-black text-xs text-center">
                    Disabled
                  </div>
                </div>
              </div>
            </div>

          </div>}

          {/* ── Seed panel + Discord — 50/50 row ── */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">

            {/* ── LEFT 50%: Seed buy panel ── */}
            <div className="relative flex-1 rounded-2xl overflow-hidden flex flex-col"
              style={{ background: 'linear-gradient(135deg, #052e16 0%, #064e3b 45%, #052e16 100%)', border: '1.5px solid rgba(52,211,153,0.45)', boxShadow: '0 0 40px rgba(16,185,129,0.15)', minHeight: '200px' }}>
              <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(52,211,153,0.12) 0%, transparent 70%)' }} />
              <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{ border: '1px solid rgba(52,211,153,0.1)', animation: 'pulse 2.5s cubic-bezier(0.4,0,0.6,1) infinite' }} />

              <div className="relative flex flex-col h-full px-6 py-5 gap-4">
                {/* Header */}
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
                    style={{ background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.3)' }}>🌱</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-black text-lg leading-tight">Buy Seeds · Start Farming</span>
                      <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full"
                        style={{ background: 'rgba(251,191,36,0.2)', border: '1px solid rgba(251,191,36,0.4)', color: '#fde68a' }}>✦ New Player? Start Here</span>
                    </div>
                    <p className="text-emerald-400/70 text-xs mt-0.5">1 WAX = 1 seed · Plant on any land · Earn 0.8× food rate</p>
                  </div>
                </div>

                {/* Seed stats */}
                <div className="flex gap-3">
                  {[{e:'💼',v:dashSeedCount,l:'In Wallet'},{e:'🌿',v:dashSeedsSown,l:'Sown'},{e:'🌱',v:dashSeedCount+dashSeedsSown,l:'Total'}].map(s=>(
                    <div key={s.l} className="flex-1 rounded-xl py-2.5 flex flex-col items-center gap-0.5"
                      style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.15)' }}>
                      <span className="text-base">{s.e}</span>
                      <span className="text-xl font-black text-emerald-300 leading-none">{s.v}</span>
                      <span className="text-[10px] text-slate-500 uppercase tracking-wide">{s.l}</span>
                    </div>
                  ))}
                </div>

                {/* Wallet connect or buy form */}
                <div className="flex-1 flex flex-col justify-end">
                  {!dashWaxActor ? (
                    <div className="flex flex-col gap-2">
                      <p className="text-slate-400 text-sm">Connect your WAX wallet to buy seeds</p>
                      <p className="text-slate-600 text-xs">Cloud Wallet &amp; Anchor supported</p>
                      <button onClick={handleDashWalletConnect}
                        className="w-full py-3 rounded-xl font-black text-sm text-white transition-all hover:scale-[1.02] active:scale-95"
                        style={{ background: 'linear-gradient(135deg, #10b981, #059669)', boxShadow: '0 4px 20px rgba(16,185,129,0.3)' }}>
                        🔗 Connect WAX Wallet
                      </button>
                      {dashSeedMsg && <div className="text-xs text-red-400">{dashSeedMsg}</div>}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-green-400" style={{ boxShadow: '0 0 6px #4ade80' }} />
                          <span className="text-emerald-300 text-xs font-mono font-bold">{dashWaxActor}</span>
                        </div>
                        <button onClick={handleDashWalletDisconnect}
                          style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '4px 10px', color: '#f87171', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                          Disconnect
                        </button>
                      </div>
                      <div className="flex gap-1.5 flex-wrap items-center">
                        {['1','5','10','25'].map(v => (
                          <button key={v} onClick={() => { setDashSeedQty(v); setDashSeedStatus('idle'); setDashSeedMsg(''); }}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                            style={{
                              background: dashSeedQty===v ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.06)',
                              border: dashSeedQty===v ? '1px solid rgba(52,211,153,0.55)' : '1px solid rgba(255,255,255,0.1)',
                              color: dashSeedQty===v ? '#6ee7b7' : '#64748b',
                            }}>{v}</button>
                        ))}
                        <input type="number" value={dashSeedQty} min="1"
                          onChange={e => { setDashSeedQty(e.target.value); setDashSeedStatus('idle'); setDashSeedMsg(''); }}
                          className="w-14 px-2 py-1.5 rounded-lg text-xs text-white text-center outline-none"
                          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }} />
                        <span className="text-emerald-400 text-xs font-bold">{parseInt(dashSeedQty)||1} WAX</span>
                      </div>
                      <button onClick={handleBuySeedDashboard}
                        disabled={ATAVI_LOCKED || dashSeedStatus==='sending'||dashSeedStatus==='verifying'}
                        className="w-full py-3 rounded-xl font-black text-sm text-white transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:hover:scale-100"
                        style={{ background: ATAVI_LOCKED ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, #10b981, #059669)', boxShadow: ATAVI_LOCKED ? 'none' : '0 4px 16px rgba(16,185,129,0.28)' }}>
                        {ATAVI_LOCKED ? '🔒 Seed buying paused' : dashSeedStatus==='sending' ? '⏳ Sending…' : dashSeedStatus==='verifying' ? '🔍 Verifying on blockchain…' : `🌱 Buy ${parseInt(dashSeedQty)||1} Seed${(parseInt(dashSeedQty)||1)>1?'s':''} · ${parseInt(dashSeedQty)||1} WAX`}
                      </button>
                      {dashSeedMsg && <div className={`text-xs ${dashSeedStatus==='error'?'text-red-400':dashSeedStatus==='done'?'text-green-400':'text-emerald-400'}`}>{dashSeedMsg}</div>}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── RIGHT 50%: Discord panel ── */}
            <a href="https://discord.gg/vurmGpX9RW" target="_blank" rel="noreferrer"
              className="relative flex-1 rounded-2xl overflow-hidden flex flex-col justify-between group hover:opacity-95 transition-opacity"
              style={{ background: 'linear-gradient(135deg, #5865F2 0%, #4752c4 60%, #3c45c0 100%)', minHeight: '200px', textDecoration: 'none' }}>
              <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(120deg, transparent 25%, rgba(255,255,255,0.06) 50%, transparent 75%)' }} />
              <div className="absolute top-0 right-0 w-56 h-56 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)', transform: 'translate(25%, -35%)' }} />

              <div className="relative flex flex-col h-full px-6 py-5 gap-4">
                {/* Header */}
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-white/15 transition-colors">
                    <svg width="26" height="20" viewBox="0 0 71 55" fill="white" xmlns="http://www.w3.org/2000/svg">
                      <path d="M60.1 4.9A58.6 58.6 0 0 0 45.5.7a.2.2 0 0 0-.2.1 40.8 40.8 0 0 0-1.8 3.7 54.1 54.1 0 0 0-16.2 0A37.5 37.5 0 0 0 25.4.8a.2.2 0 0 0-.2-.1A58.4 58.4 0 0 0 10.6 4.9a.2.2 0 0 0-.1.1C1.6 18.1-.9 31 .3 43.6a.2.2 0 0 0 .1.2 58.8 58.8 0 0 0 17.7 8.9.2.2 0 0 0 .2-.1 42 42 0 0 0 3.6-5.9.2.2 0 0 0-.1-.3 38.7 38.7 0 0 1-5.5-2.6.2.2 0 0 1 0-.4l1.1-.9a.2.2 0 0 1 .2 0c11.5 5.3 24 5.3 35.4 0a.2.2 0 0 1 .2 0l1.1.9a.2.2 0 0 1 0 .4 36 36 0 0 1-5.5 2.6.2.2 0 0 0-.1.3 47.1 47.1 0 0 0 3.6 5.9.2.2 0 0 0 .2.1 58.6 58.6 0 0 0 17.7-8.9.2.2 0 0 0 .1-.2C73 29.1 69.2 16.4 60.2 5a.2.2 0 0 0-.1-.1ZM23.7 36a6.8 6.8 0 0 1-6.4-7.2 6.8 6.8 0 0 1 6.4-7.1 6.8 6.8 0 0 1 6.4 7.1A6.8 6.8 0 0 1 23.7 36Zm23.6 0a6.8 6.8 0 0 1-6.4-7.2 6.8 6.8 0 0 1 6.4-7.1 6.8 6.8 0 0 1 6.4 7.1A6.8 6.8 0 0 1 47.3 36Z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-white/55 text-[10px] font-black uppercase tracking-widest">Community</p>
                    <p className="text-white font-black text-lg leading-tight">Join the Samatvam Discord</p>
                  </div>
                </div>

                {/* Topic tags */}
                <div className="flex-1 flex flex-col gap-2">
                  {['Upcoming seasons & events','Colony strategies','Rental & farming tips','Announcements'].map(tag => (
                    <div key={tag} className="flex items-center gap-2">
                      <span className="text-white/25 text-sm">›</span>
                      <span className="text-white/65 text-sm font-medium">{tag}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="flex items-center gap-2 bg-white/15 group-hover:bg-white/25 transition-colors rounded-xl px-4 py-2.5 self-start">
                  <span className="text-white font-black text-sm">Join Server</span>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </div>
              </div>
            </a>

          </div>

          {/* ── Quickstart ── */}
          <div className="flex gap-4 mb-6">
            <div className="relative bg-slate-900/80 backdrop-blur-sm border border-amber-400/25 rounded-2xl p-6 overflow-hidden shadow-xl shadow-amber-500/5 w-full">
              <div className="absolute -inset-px rounded-2xl border border-amber-400/30 animate-pulse pointer-events-none" />
              <div className="absolute top-0 right-0 w-48 h-48 bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-9 h-9 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/25">
                    <span className="text-base">⚡</span>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="text-white font-black text-base tracking-tight">Quickstart</h3>
                      <span className="text-[9px] font-bold uppercase tracking-widest bg-amber-500/15 border border-amber-400/25 text-amber-300 px-2 py-0.5 rounded-full">Season Zero</span>
                    </div>
                    <p className="text-slate-500 text-xs font-semibold uppercase tracking-wide mt-0.5">What you can do right now</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-start space-x-3 bg-white/4 border border-white/8 rounded-xl px-4 py-3">
                    <span className="text-amber-400 text-sm mt-0.5 flex-shrink-0">▸</span>
                    <span className="text-slate-300 text-sm leading-relaxed">
                      <a href="#buy-land" onClick={e => { e.preventDefault(); document.getElementById('buy-land')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                        className="text-amber-400 font-bold underline decoration-dotted hover:text-amber-300 transition-colors">Buy land grids</a> — <span className="text-amber-400 font-bold">1 WAX each</span>. Each plot is yours permanently on the grid.
                    </span>
                  </div>
                  <div className="flex items-start space-x-3 bg-white/4 border border-white/8 rounded-xl px-4 py-3">
                    <span className="text-amber-400 text-sm mt-0.5 flex-shrink-0">▸</span>
                    <span className="text-slate-300 text-sm leading-relaxed">Leaderboard toppers will get <span className="text-amber-400 font-bold">daily prizes/salaries</span> — automatically sent to your wallet.</span>
                  </div>
                  <div className="flex items-start space-x-3 bg-white/4 border border-white/8 rounded-xl px-4 py-3">
                    <span className="text-amber-400 text-sm mt-0.5 flex-shrink-0">▸</span>
                    <span className="text-slate-300 text-sm leading-relaxed">Open the <span className="text-green-400 font-bold">Atavi Forest</span> to see all 10,000 plots and plant pioneer species on your land.</span>
                  </div>
                  <div className="flex items-start space-x-3 bg-white/4 border border-white/8 rounded-xl px-4 py-3">
                    <span className="text-amber-400 text-sm mt-0.5 flex-shrink-0">▸</span>
                    <span className="text-slate-300 text-sm leading-relaxed">Grow trees, accumulate <span className="text-amber-400 font-bold">Food tokens</span>, and contribute to city tasks to earn Bhoomi and platform rewards.</span>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* ── Marketplace + Crafting ── */}
          <div className="mb-6 flex flex-col md:flex-row gap-4">

            {/* Marketplace */}
            <div className="flex-1 rounded-2xl overflow-hidden border border-purple-500/30 hover:border-purple-400/50 transition-all duration-300"
              style={{ background: 'linear-gradient(135deg, rgba(59,7,100,0.45) 0%, rgba(15,23,42,0.95) 60%)' }}>
              <div className="flex flex-col h-full p-5 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                    style={{ background: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.3)' }}>
                    🏪
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-purple-400/70">Marketplace</p>
                    <p className="text-white font-black text-lg leading-tight">
                      10 Food <span className="text-purple-400">→</span> 1 Packet
                    </p>
                  </div>
                </div>
                <p className="text-slate-400 text-xs leading-relaxed">
                  Craft Food Packets in your Warehouse, load them onto your <span className="text-amber-300 font-bold">Hand Cart</span>, and bring them to the marketplace to sell for <span className="text-purple-300 font-bold">Bhoomi</span>.
                </p>
                <div className="rounded-xl p-3 border border-amber-500/20 flex items-center gap-3" style={{ background: 'rgba(120,53,15,0.18)' }}>
                  <span className="text-xl">🛒</span>
                  <div>
                    <p className="text-xs font-bold text-amber-300">Hand Cart required — 10 WAX</p>
                    <p className="text-xs text-slate-400 mt-0.5">Carries <span className="text-amber-200 font-bold">100 packets</span> per trip · Buy in your Samrajya</p>
                  </div>
                </div>
                <div className="mt-auto flex items-center justify-between">
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-widest">Phase</div>
                    <div className="text-2xl font-black text-purple-400">3 <span className="text-sm font-normal text-slate-500">coming next</span></div>
                  </div>
                  <button onClick={onOpenWarehouse}
                    className="px-4 py-2 rounded-xl text-xs font-bold text-purple-300 border border-purple-500/40 bg-purple-950/40 hover:bg-purple-500/20 transition-all cursor-pointer">
                    Open Warehouse →
                  </button>
                </div>
              </div>
            </div>

            {/* Crafting Workshop */}
            <div
              onClick={onOpenCrafting}
              className="cursor-pointer group flex-1 rounded-2xl overflow-hidden border border-violet-500/30 hover:border-violet-400/50 transition-all duration-300"
              style={{ background: 'linear-gradient(135deg, rgba(46,16,101,0.45) 0%, rgba(15,23,42,0.95) 60%)' }}>
              <div className="flex flex-col h-full p-5 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                    style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)' }}>
                    ⚒️
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-amber-400/80">Active · Workshop</p>
                    <p className="text-white font-black text-lg leading-tight">Crafting</p>
                  </div>
                </div>
                <p className="text-slate-400 text-xs leading-relaxed">
                  Craft Biorefinery materials from your logs. Use them to run batches and produce <span className="text-green-300 font-bold">Bioethanol</span>.
                </p>
                <div className="rounded-xl p-3 border border-amber-700/30 flex items-center gap-3" style={{ background: 'rgba(120,53,15,0.25)' }}>
                  <span className="text-xl">🌲</span>
                  <div>
                    <p className="text-xs font-bold text-amber-300">Logs → Materials</p>
                    <p className="text-xs text-slate-400 mt-0.5">100 Mesquite → Firewood · 100 Moringa → Yeast Culture</p>
                  </div>
                </div>
                <div className="mt-auto flex items-center justify-between">
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-widest">Status</div>
                    <div className="text-2xl font-black text-amber-400">Active</div>
                  </div>
                  <div className="px-4 py-2 rounded-xl text-xs font-bold text-amber-300 border border-amber-500/40 bg-amber-950/40 group-hover:bg-amber-500/20 transition-all">
                    Open Workshop →
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* ── Atavi Forest Guide ── */}
          <div className="mb-6">
            <div
              onClick={onOpenAtavi}
              className="cursor-pointer group rounded-2xl border border-emerald-500/30 hover:border-emerald-400/60 transition-all duration-300 overflow-hidden"
              style={{ background: 'linear-gradient(135deg, rgba(6,78,59,0.45) 0%, rgba(15,23,42,0.95) 70%)' }}
            >
              <div className="px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                    style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)' }}>
                    🌳
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-emerald-400/70">Guide · Forest</p>
                    <p className="text-white font-black text-base leading-tight">Atavi</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <p className="text-slate-400 text-xs hidden sm:block">Trees · Food · Logs · Harvest</p>
                  <span className="text-emerald-400 group-hover:translate-x-1 transition-transform text-lg">›</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Infrastructure Announcement ── */}
          <div className="mb-6 rounded-2xl overflow-hidden border border-amber-500/20"
            style={{ background: 'linear-gradient(135deg, rgba(30,20,5,0.9) 0%, rgba(15,23,42,0.97) 100%)' }}>
            <div className="px-5 py-3 border-b border-amber-500/10 flex items-center gap-2">
              <span className="text-amber-400 text-xs">🏗️</span>
              <span className="text-xs font-bold uppercase tracking-widest text-amber-400/70">New · Infrastructure</span>
              <span className="ml-auto text-[9px] font-bold uppercase tracking-widest border border-amber-500/30 text-amber-400/60 px-2 py-0.5 rounded-full">Now Available</span>
            </div>
            <div className="p-5 flex flex-col md:flex-row gap-4">

              {/* Water Harvesting Machine */}
              <div className="flex-1 rounded-xl border border-sky-500/20 overflow-hidden"
                style={{ background: 'linear-gradient(135deg, rgba(8,47,73,0.6) 0%, rgba(15,23,42,0.9) 100%)' }}>
                <div className="flex items-center gap-4 p-4">
                  <img src="/samatvam/images/water-machine.jpg" alt="Water Harvesting Machine"
                    className="w-20 h-20 rounded-xl object-cover border border-sky-500/30 flex-shrink-0" />
                  <div>
                    <div className="text-sky-300 font-black text-sm">Water Harvesting Machine</div>
                    <div className="text-white/70 text-xs mt-1 leading-relaxed">
                      Boosts your land's food production by <span className="text-green-400 font-bold">+10%</span> permanently.
                      Harvests moisture from the air to irrigate your crops around the clock.
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-sky-500/40 text-sky-300 bg-sky-900/30">Buy · 5 WAX</span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-violet-500/30 text-violet-300 bg-violet-900/20">Craft from Food tokens · Live ⚒️</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Hand Cart */}
              <div className="flex-1 rounded-xl border border-amber-500/20 overflow-hidden"
                style={{ background: 'linear-gradient(135deg, rgba(78,52,8,0.5) 0%, rgba(15,23,42,0.9) 100%)' }}>
                <div className="flex items-center gap-4 p-4">
                  <img src="/samatvam/images/handcart.jpg" alt="Hand Cart"
                    className="w-20 h-20 rounded-xl object-cover border border-amber-500/30 flex-shrink-0" />
                  <div>
                    <div className="text-amber-300 font-black text-sm">Hand Cart</div>
                    <div className="text-white/70 text-xs mt-1 leading-relaxed">
                      Required to transport food packets to the marketplace. Carries <span className="text-amber-300 font-bold">100 packets</span> per trip — your gateway to the Samatvam economy.
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-500/40 text-amber-300 bg-amber-900/30">Buy · 10 WAX</span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-violet-500/30 text-violet-300 bg-violet-900/20">Craft from Food tokens · Live ⚒️</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>
            <div className="px-5 pb-4 text-xs text-slate-500 text-center">
              Buy directly with WAX in <span className="text-indigo-400 font-bold">Samrajya</span> · or craft using Food tokens in the <span className="text-violet-400 font-bold">Crafting Workshop</span> — both live now
            </div>
          </div>

          {/* Buy Land + Rules — 50/50 */}
          <div className="flex flex-col md:flex-row gap-6 mb-6 items-start">
            <div id="buy-land" className="flex-1 min-w-0">
              <WaxPayment />
            </div>
            <div className="flex-1 min-w-0">
              <RulesSection />
            </div>
          </div>

          {/* Admin Panel (owner only) — card link */}
          {isAdmin && (
            <button
              onClick={onOpenAdminPage}
              className="w-full flex items-center gap-4 px-5 py-4 mb-6 rounded-2xl bg-red-950/40 border border-red-500/25 hover:border-red-400/50 hover:bg-red-900/40 transition-all text-left group shadow-lg"
            >
              <div className="w-10 h-10 rounded-xl bg-red-500/20 border border-red-500/30 flex items-center justify-center flex-shrink-0 group-hover:bg-red-500/30 transition-colors">
                <span className="text-lg">🔐</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-black text-red-300 uppercase tracking-widest group-hover:text-red-200 transition-colors">Admin Command Center</p>
                <p className="text-xs text-red-500/70 mt-0.5">Config · Tasks · Workers · Exchange · Map</p>
              </div>
              <span className="text-red-600 group-hover:text-red-400 transition-colors text-lg font-bold">→</span>
            </button>
          )}

          {/* ── Season 0 City Map Guide ── */}
          <div className="mb-6 bg-gradient-to-br from-amber-950/40 via-slate-800/60 to-slate-800/40 backdrop-blur-sm border border-amber-500/20 rounded-2xl overflow-hidden">
            <div className="px-6 pt-6 pb-4 border-b border-amber-500/10">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500/30 to-orange-500/20 border border-amber-400/30 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-lg">🏛</span>
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h2 className="text-white font-black text-base">Season 0 — City Task Guide</h2>
                    <span className="text-[9px] font-black uppercase tracking-widest bg-amber-500/20 border border-amber-400/30 text-amber-300 px-2 py-0.5 rounded-full">10 Tasks</span>
                  </div>
                  <p className="text-xs text-amber-400/70 mt-0.5">One winner per task · Completed tasks unlock the next</p>
                </div>
              </div>
            </div>

            <div className="px-6 py-5">
              {/* How it works */}
              <div className="grid grid-cols-3 gap-3 mb-5">
                {[
                  { icon: '🍎', title: 'Food Packets', body: 'Earn from trees. Harvest from your plots. Consumed on task completion.' },
                  { icon: '🛒', title: 'Hand Cart', body: 'Buy for 10 WAX or craft. Required for most tasks — shows you can transport.' },
                  { icon: '💧', title: 'Water Machine', body: 'Buy for 15 WAX or craft. Required for water and high-tier tasks.' },
                ].map(c => (
                  <div key={c.title} className="bg-white/3 border border-white/8 rounded-xl p-3">
                    <div className="text-xl mb-1">{c.icon}</div>
                    <div className="text-xs font-black text-white mb-1">{c.title}</div>
                    <div className="text-[11px] text-slate-400 leading-relaxed">{c.body}</div>
                  </div>
                ))}
              </div>

              {/* Task table */}
              <div className="overflow-x-auto rounded-xl border border-white/8">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-white/5 border-b border-white/8">
                      <th className="text-left px-4 py-2.5 text-slate-400 font-black uppercase tracking-widest text-[10px]">#</th>
                      <th className="text-left px-4 py-2.5 text-slate-400 font-black uppercase tracking-widest text-[10px]">Task</th>
                      <th className="text-center px-3 py-2.5 text-slate-400 font-black uppercase tracking-widest text-[10px]">Food</th>
                      <th className="text-center px-3 py-2.5 text-slate-400 font-black uppercase tracking-widest text-[10px]">Cart</th>
                      <th className="text-center px-3 py-2.5 text-slate-400 font-black uppercase tracking-widest text-[10px]">Machine</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { n: 1,  id: 'secure-land-for-farming',      label: 'Secure Land for Farming',          food: 500,  cart: true,  machine: true  },
                      { n: 2,  id: 'remove-biowaste-from-hospital', label: 'Remove Biowaste from Hospital',     food: 400,  cart: true,  machine: false },
                      { n: 3,  id: 'restore-market-district',       label: 'Restore Market District',          food: 300,  cart: true,  machine: false },
                      { n: 4,  id: 'build-water-point',             label: 'Build Water Distribution Point',   food: 400,  cart: false, machine: true  },
                      { n: 5,  id: 'setup-trade-post',              label: 'Set Up Trade Post',                food: 500,  cart: true,  machine: false },
                      { n: 6,  id: 'supply-desert-outpost',         label: 'Supply Desert Outpost',            food: 600,  cart: true,  machine: false },
                      { n: 7,  id: 'establish-community-kitchen',   label: 'Establish Community Kitchen',      food: 700,  cart: true,  machine: true  },
                      { n: 8,  id: 'fortify-city-walls',            label: 'Fortify City Walls',               food: 800,  cart: true,  machine: false },
                      { n: 9,  id: 'plant-city-park',               label: 'Plant Trees at City Park',         food: 900,  cart: false, machine: true  },
                      { n: 10, id: 'repair-mining-road',            label: 'Repair Road to Mining Site',       food: 1000, cart: true,  machine: true  },
                    ].map((t, i) => (
                      <tr key={t.id} className={`border-b border-white/5 ${i % 2 === 0 ? 'bg-white/2' : ''} hover:bg-amber-500/5 transition-colors`}>
                        <td className="px-4 py-3 text-slate-500 font-black">{t.n}</td>
                        <td className="px-4 py-3 text-white font-semibold">{t.label}</td>
                        <td className="px-3 py-3 text-center">
                          <span className="text-amber-300 font-black">{t.food.toLocaleString()}</span>
                        </td>
                        <td className="px-3 py-3 text-center">{t.cart ? <span className="text-green-400 font-black">✓</span> : <span className="text-slate-600">—</span>}</td>
                        <td className="px-3 py-3 text-center">{t.machine ? <span className="text-sky-400 font-black">✓</span> : <span className="text-slate-600">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-[11px] text-slate-500 mt-3 text-center">Tasks activate one at a time · Visible on the city map when active · First to meet all requirements wins</p>
            </div>
          </div>

          {/* ── Water Harvester Shareholders ── */}
          <WaterHarvesterShareholders />

          {/* ── Vayal NFT Launch Banner ── */}
          <div className="mb-6 rounded-2xl overflow-hidden relative" style={{ background: 'linear-gradient(135deg, rgba(20,83,45,0.6) 0%, rgba(15,23,42,0.9) 50%, rgba(101,44,3,0.5) 100%)', border: '1px solid rgba(134,239,172,0.2)' }}>
            {/* Glow blobs */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              <div className="absolute -top-12 -left-12 w-64 h-64 rounded-full blur-3xl" style={{ background: 'rgba(134,239,172,0.07)' }} />
              <div className="absolute -bottom-12 -right-12 w-64 h-64 rounded-full blur-3xl" style={{ background: 'rgba(251,191,36,0.07)' }} />
            </div>

            <div className="relative p-6">
              {/* Header row */}
              <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] px-2.5 py-1 rounded-full" style={{ background: 'rgba(134,239,172,0.15)', border: '1px solid rgba(134,239,172,0.3)', color: '#86efac' }}>Coming Soon</span>
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] px-2.5 py-1 rounded-full" style={{ background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.25)', color: '#fcd34d' }}>First NFTs on AhantraVyuh</span>
                  </div>
                  <h2 className="text-2xl font-black text-white tracking-tight leading-tight">Vayal</h2>
                  <p className="text-sm mt-1" style={{ color: 'rgba(134,239,172,0.7)' }}>The Farmland Zone</p>
                </div>
                <div className="text-right">
                  <p className="text-base font-black leading-tight" style={{ color: '#86efac' }}>Craft from Bhoomi</p>
                  <p className="text-base font-black leading-tight mb-2" style={{ color: '#86efac' }}>&amp; Shram tokens</p>
                  <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.35)' }}>or buy with 10 WAX directly</p>
                </div>
              </div>

              {/* NFT feature pills */}
              <div className="flex flex-wrap gap-2 mb-5">
                {[
                  { icon: '🔄', label: 'Tradable' },
                  { icon: '⬆️', label: 'Upgradable' },
                  { icon: '🔨', label: 'Craftable' },
                  { icon: '🌾', label: '10-Stage Growth' },
                  { icon: '⏱', label: 'Real-Time Cycle' },
                  { icon: '🪙', label: 'Earns Vayal Token' },
                ].map(f => (
                  <span key={f.label} className="flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#cbd5e1' }}>
                    <span>{f.icon}</span>{f.label}
                  </span>
                ))}
              </div>

              {/* Stage progression strip */}
              <div className="mb-3">
                <p className="text-[10px] font-black uppercase tracking-[0.15em] mb-2" style={{ color: 'rgba(255,255,255,0.3)' }}>10-Stage Field Progression</p>
                <div className="flex gap-1.5 items-end overflow-x-auto pb-1">
                  {[
                    { stage: 1, label: 'Bare' },
                    { stage: 2, label: 'Tilled' },
                    { stage: 3, label: 'Sown' },
                    { stage: 4, label: 'Sprout' },
                    { stage: 5, label: 'Early' },
                    { stage: 6, label: 'Mid' },
                    { stage: 7, label: 'Advanced' },
                    { stage: 8, label: 'Harvest' },
                    { stage: 9, label: 'Cutting' },
                    { stage: 10, label: 'Replenish' },
                  ].map((s, idx) => (
                    <div key={s.stage} className="flex flex-col items-center flex-shrink-0" style={{ width: 'clamp(52px, 9vw, 88px)' }}>
                      <div className="relative w-full rounded-lg overflow-hidden" style={{ aspectRatio: '1/1', border: s.stage === 8 ? '2px solid rgba(134,239,172,0.6)' : '1px solid rgba(255,255,255,0.1)' }}>
                        <img
                          src={`${process.env.PUBLIC_URL}/images/farmland-stage-${s.stage}.jpg`}
                          alt={s.label}
                          className="w-full h-full object-cover"
                        />
                        {/* Stage number badge */}
                        <div className="absolute top-1 left-1 w-4 h-4 rounded-sm flex items-center justify-center text-[9px] font-black" style={{ background: 'rgba(0,0,0,0.6)', color: '#fcd34d' }}>{s.stage}</div>
                      </div>
                      {/* Arrow between tiles */}
                      <div className="flex items-center w-full mt-1">
                        <p className="text-center w-full text-[8.5px] font-semibold truncate" style={{ color: 'rgba(255,255,255,0.4)' }}>{s.label}</p>
                      </div>
                      {idx < 9 && (
                        <div className="absolute" style={{ display: 'none' }} />
                      )}
                    </div>
                  ))}
                </div>
                {/* Arrow row below tiles */}
                <div className="flex items-center mt-1 gap-1.5 overflow-x-auto">
                  {Array.from({ length: 9 }).map((_, i) => (
                    <div key={i} className="flex items-center flex-shrink-0" style={{ width: 'clamp(52px, 9vw, 88px)' }}>
                      <div className="flex-1 h-px" style={{ background: 'rgba(134,239,172,0.25)' }} />
                      <span className="text-[10px] px-0.5" style={{ color: 'rgba(134,239,172,0.4)' }}>▶</span>
                    </div>
                  ))}
                  <div className="flex-shrink-0" style={{ width: 'clamp(52px, 9vw, 88px)' }} />
                </div>
              </div>

              {/* Bottom row */}
              <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <div>
                  <p className="text-xs font-bold" style={{ color: 'rgba(255,255,255,0.5)' }}>25,000 farm units · Craft or buy · Click-to-advance stages</p>
                  <p className="text-[11px] mt-0.5" style={{ color: 'rgba(134,239,172,0.5)' }}>Each farm unit is a living NFT — plant, grow, harvest, trade.</p>
                </div>
                <span className="text-xs font-black px-4 py-2 rounded-xl cursor-default" style={{ background: 'rgba(134,239,172,0.1)', border: '1px solid rgba(134,239,172,0.25)', color: '#86efac' }}>
                  🌾 Season 0 — Opening Soon
                </span>
              </div>
            </div>
          </div>

          {/* Colony Leaderboard */}
          <ColonyLeaderboard txs={txs} />

          {/* ── Roadmap — full width, after leaderboard ── */}
          <div className="mb-6 bg-slate-800/40 backdrop-blur-sm border border-white/8 rounded-2xl p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-9 h-9 bg-gradient-to-br from-slate-600 to-slate-700 rounded-xl flex items-center justify-center">
                <span className="text-base">🗺</span>
              </div>
              <div>
                <h2 className="text-white font-black text-base tracking-tight">Samatvam Roadmap</h2>
                <p className="text-slate-500 text-xs uppercase tracking-widest font-semibold">Season 0 — Build the chain</p>
              </div>
            </div>

            <div className="relative pl-6">
              <div className="absolute left-0 top-2 bottom-2 w-px bg-gradient-to-b from-amber-500/60 via-green-500/30 via-sky-500/20 to-transparent" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {/* Phase 0 — LIVE */}
                <div className="relative">
                  <div className="absolute -left-6 top-2 w-3 h-3 rounded-full bg-amber-400 shadow-lg shadow-amber-500/60 ring-4 ring-amber-500/20" />
                  <div className="relative bg-gradient-to-r from-amber-950/60 to-slate-800/60 border border-amber-400/25 rounded-xl p-4 overflow-hidden">
                    <div className="absolute -inset-px rounded-xl border border-amber-400/20 animate-pulse pointer-events-none" />
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="text-white font-black text-sm">Season 0 · Phase 0</span>
                          <span className="text-[9px] font-black uppercase tracking-widest bg-amber-500 text-black px-2 py-0.5 rounded-full">Live</span>
                        </div>
                        <p className="text-amber-300/80 text-xs font-semibold mt-0.5">Land Genesis Sale</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-amber-400 font-black text-lg">1,000</p>
                        <p className="text-slate-500 text-[9px] uppercase">plots</p>
                      </div>
                    </div>
                    <ul className="space-y-1">
                      <li className="flex items-start space-x-2"><span className="text-amber-400 text-xs mt-0.5">▸</span><span className="text-slate-300 text-xs"><span className="text-amber-400 font-bold">1,000 land plots</span> — 1 WAX each, permanent ownership</span></li>
                      <li className="flex items-start space-x-2"><span className="text-amber-400 text-xs mt-0.5">▸</span><span className="text-slate-300 text-xs">Daily prize draw — WAX / NFT sent automatically to winner's wallet</span></li>
                    </ul>
                  </div>
                </div>

                {/* Phase 1 — LIVE */}
                <div className="relative">
                  <div className="absolute -left-6 top-2 w-3 h-3 rounded-full bg-green-400 shadow-lg shadow-green-500/60 ring-4 ring-green-500/20" />
                  <div className="relative bg-gradient-to-r from-green-950/60 to-slate-800/60 border border-green-400/25 rounded-xl p-4 overflow-hidden">
                    <div className="absolute -inset-px rounded-xl border border-green-400/20 animate-pulse pointer-events-none" />
                    <div className="mb-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-white font-black text-sm">Season 0 · Phase 1</span>
                        <span className="text-[9px] font-black uppercase tracking-widest bg-green-500 text-black px-2 py-0.5 rounded-full">Live</span>
                      </div>
                      <p className="text-green-300/80 text-xs font-semibold mt-0.5">Land Economy · Trees · Food</p>
                    </div>
                    <ul className="space-y-1">
                      <li className="flex items-start space-x-2"><span className="text-green-400 text-xs mt-0.5">▸</span><span className="text-slate-300 text-xs">Land generates <span className="text-green-400 font-bold">0.3 Bhoomi/hr</span> — passively, 24/7</span></li>
                      <li className="flex items-start space-x-2"><span className="text-green-400 text-xs mt-0.5">▸</span><span className="text-slate-300 text-xs">Spend Bhoomi to plant 8 species of pioneer trees</span></li>
                      <li className="flex items-start space-x-2"><span className="text-green-400 text-xs mt-0.5">▸</span><span className="text-slate-300 text-xs">Trees generate <span className="text-amber-400 font-bold">Food tokens</span> continuously</span></li>
                      <li className="flex items-start space-x-2"><span className="text-green-400 text-xs mt-0.5">▸</span><span className="text-slate-300 text-xs">Food tokens used for city tasks and infrastructure crafting</span></li>
                    </ul>
                  </div>
                </div>

                {/* Phase 2 — LIVE */}
                <div className="relative">
                  <div className="absolute -left-6 top-2 w-3 h-3 rounded-full bg-sky-400 shadow-lg shadow-sky-500/60 ring-4 ring-sky-500/20" />
                  <div className="relative bg-gradient-to-r from-sky-950/60 to-slate-800/60 border border-sky-400/25 rounded-xl p-4 overflow-hidden">
                    <div className="absolute -inset-px rounded-xl border border-sky-400/20 animate-pulse pointer-events-none" />
                    <div className="mb-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-white font-black text-sm">Season 0 · Phase 2</span>
                        <span className="text-[9px] font-black uppercase tracking-widest bg-sky-500 text-black px-2 py-0.5 rounded-full">Live</span>
                      </div>
                      <p className="text-sky-300/80 text-xs font-semibold mt-0.5">Water Harvesting Machine</p>
                    </div>
                    <ul className="space-y-1">
                      <li className="flex items-start space-x-2"><span className="text-sky-400 text-xs mt-0.5">▸</span><span className="text-slate-300 text-xs">Install a <span className="text-sky-300 font-bold">Water Harvesting Machine</span> — one-time 5 WAX</span></li>
                      <li className="flex items-start space-x-2"><span className="text-sky-400 text-xs mt-0.5">▸</span><span className="text-slate-300 text-xs">Boosts all your trees' Food production by <span className="text-sky-300 font-bold">+10%</span> permanently</span></li>
                      <li className="flex items-start space-x-2"><span className="text-sky-400 text-xs mt-0.5">▸</span><span className="text-slate-300 text-xs">Capital investment in infrastructure — no running cost</span></li>
                      <li className="flex items-start space-x-2"><span className="text-sky-400 text-xs mt-0.5">▸</span><span className="text-slate-300 text-xs">Manage from your <span className="text-indigo-300 font-bold">Samrajya</span> page</span></li>
                    </ul>
                  </div>
                </div>

                {/* Phase 3 — coming */}
                <div className="relative opacity-60">
                  <div className="absolute -left-6 top-2 w-3 h-3 rounded-full border-2 border-purple-500/50 bg-slate-900" />
                  <div className="border border-purple-500/15 rounded-xl p-4 bg-purple-950/10">
                    <div className="mb-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-slate-300 font-black text-sm">Season 0 · Phase 3</span>
                        <span className="text-[9px] font-bold uppercase tracking-widest border border-purple-500/30 text-purple-400/70 px-2 py-0.5 rounded-full">Coming Next</span>
                      </div>
                      <p className="text-purple-300/70 text-xs font-semibold mt-0.5">Hand Cart &amp; Marketplace</p>
                    </div>
                    <ul className="space-y-1">
                      <li className="flex items-start space-x-2"><span className="text-purple-400/70 text-xs mt-0.5">▸</span><span className="text-slate-400 text-xs">Craft <span className="text-purple-300 font-bold">Food Packets</span> from 10 Food tokens (Warehouse)</span></li>
                      <li className="flex items-start space-x-2"><span className="text-purple-400/70 text-xs mt-0.5">▸</span><span className="text-slate-400 text-xs">Buy a <span className="text-amber-300 font-bold">Hand Cart</span> (10 WAX, memo: season0:handcart) — carries 100 packets per trip</span></li>
                      <li className="flex items-start space-x-2"><span className="text-purple-400/70 text-xs mt-0.5">▸</span><span className="text-slate-400 text-xs">Transport packets to marketplace — sell for <span className="text-amber-300 font-bold">Bhoomi</span></span></li>
                      <li className="flex items-start space-x-2"><span className="text-purple-400/70 text-xs mt-0.5">▸</span><span className="text-slate-400 text-xs">Hand Cart required — no transport, no marketplace listing</span></li>
                    </ul>
                  </div>
                </div>

                {/* Season 1 — future */}
                <div className="relative opacity-30 md:col-span-2">
                  <div className="absolute -left-6 top-2 w-3 h-3 rounded-full border-2 border-slate-700 bg-slate-900" style={{ borderStyle: 'dashed' }} />
                  <div className="border border-white/5 rounded-xl p-4 flex items-center gap-4">
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-slate-500 font-black text-sm">Season 1</span>
                        <span className="text-[9px] font-bold uppercase tracking-widest border border-slate-700 text-slate-600 px-2 py-0.5 rounded-full">Future</span>
                      </div>
                      <p className="text-slate-600 text-xs">Each tree produces its real-world product. Advanced economy, cross-chain integration, expanded species ecosystem.</p>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* ── Season 0 Rankings — archived notice ── */}
          <div className="mb-6 rounded-2xl border border-slate-700/40 overflow-hidden" style={{ background: 'rgba(15,23,42,0.5)' }}>
            <div className="px-5 py-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0 opacity-50" style={{ background: 'rgba(148,163,184,0.08)', border: '1px solid rgba(148,163,184,0.15)' }}>🏆</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-slate-400 font-black text-sm">Season 0 Rankings</span>
                  <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: 'rgba(148,163,184,0.1)', border: '1px solid rgba(148,163,184,0.2)', color: '#64748b' }}>Tabulating</span>
                </div>
                <p className="text-slate-600 text-xs leading-relaxed">Final Season 0 rankings are being tabulated. Results will be published when Season 0 officially closes. Rankings were based on plot proximity to city origin (0,0) plus infrastructure bonuses.</p>
              </div>
            </div>
          </div>

          {/* ── City Champions card ── */}
          <button onClick={onOpenCityChampions} className="w-full mb-6 rounded-2xl border border-amber-500/25 overflow-hidden text-left hover:border-amber-400/50 transition-all group" style={{ background: 'rgba(120,53,15,0.15)' }}>
            <div className="px-5 py-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0 group-hover:scale-105 transition-transform" style={{ background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.25)' }}>🏛</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-amber-300 font-black text-sm">City Champions</span>
                  <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.25)', color: '#fcd34d' }}>Live</span>
                </div>
                <p className="text-slate-400 text-xs leading-relaxed">Who completed which city tasks · Season Zero scoreboard</p>
              </div>
              <span className="text-amber-500 group-hover:text-amber-300 transition-colors text-lg font-bold">→</span>
            </div>
          </button>

          {/* Activity Monitor — collapsible */}
          <div className="mb-6 rounded-2xl border border-slate-700/50 overflow-hidden" style={{ background: 'rgba(15,23,42,0.6)' }}>
            <button onClick={()=>toggleSection('activity')} className="w-full flex items-center justify-between px-5 py-4 text-left" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <span className="flex items-center gap-3">
                <span className="text-xl">📊</span>
                <span className="text-white font-bold text-sm">Activity Monitor</span>
              </span>
              <span className="text-slate-500 text-xs">{openSections['activity'] ? '▲' : '▼'}</span>
            </button>
            {openSections['activity'] && <ActivityMonitor />}
          </div>

          {/* Transactions table — collapsible */}
          <div className="mb-6 rounded-2xl border border-slate-700/50 overflow-hidden" style={{ background: 'rgba(15,23,42,0.6)' }}>
            <button onClick={()=>toggleSection('waxtxs')} className="w-full flex items-center justify-between px-5 py-4 text-left" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <span className="flex items-center gap-3">
                <span className="text-xl">💸</span>
                <span className="text-white font-bold text-sm">Incoming WAX Payments</span>
              </span>
              <span className="text-slate-500 text-xs">{openSections['waxtxs'] ? '▲' : '▼'}</span>
            </button>
            {openSections['waxtxs'] && <WaxTransactions
              txs={txs}
              loading={loading}
              lastUpdated={lastUpdated}
              error={error}
              onRefresh={fetchTxs}
            />}
          </div>

          {/* ── Privacy Policy card ── */}
          <button onClick={onOpenPrivacy} className="w-full mt-6 rounded-2xl border border-slate-700/40 overflow-hidden text-left hover:border-slate-600/60 transition-all group" style={{ background: 'rgba(15,23,42,0.5)' }}>
            <div className="px-5 py-4 flex items-center gap-4">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center text-base flex-shrink-0" style={{ background: 'rgba(148,163,184,0.08)', border: '1px solid rgba(148,163,184,0.15)' }}>🔒</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-slate-400 font-black text-sm group-hover:text-slate-300 transition-colors">Privacy Policy</span>
                </div>
                <p className="text-slate-600 text-xs">AhantraVyuh Ltd · SC866275 · UK GDPR compliant</p>
              </div>
              <span className="text-slate-600 group-hover:text-slate-400 transition-colors text-sm font-bold">→</span>
            </div>
          </button>

          {/* ── Vayal Test Map ── */}
          <button onClick={onOpenVayalTest} className="w-full mt-4 rounded-2xl border overflow-hidden text-left hover:border-orange-600/40 transition-all group" style={{ background: 'rgba(249,115,22,0.05)', borderColor: 'rgba(249,115,22,0.2)' }}>
            <div className="px-5 py-4 flex items-center gap-4">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center text-base flex-shrink-0" style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.25)' }}>🧪</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-orange-400 font-black text-sm group-hover:text-orange-300 transition-colors">Vayal Test Map</span>
                </div>
                <p className="text-slate-600 text-xs">Tile testing sandbox · Separate images · Does not affect main map</p>
              </div>
              <span className="text-slate-600 group-hover:text-orange-400 transition-colors text-sm font-bold">→</span>
            </div>
          </button>

          {/* ── Reset / Clean Slate ── */}
          <div className="mt-8 pt-6 border-t border-slate-800/60 text-center">
            <button
              onClick={handleReset}
              className="text-xs text-slate-600 hover:text-red-400 transition-colors underline underline-offset-2"
            >
              Reset — clear all sessions &amp; browser data
            </button>
            <p className="text-[10px] text-slate-700 mt-1">Logs out of Ahantravyuh and WAX · Your game data in the database is safe</p>
          </div>

        </div>
      </div>
    </div>
  );
}

// ─── Root ──────────────────────────────────────────────────────────────────────

// ─── Samrajya Page ────────────────────────────────────────────────────────────

interface SamrajyaData {
  plots_owned: number;
  trees_planted: number;
  species: { name: string; emoji: string; count: number }[];
  bhoomi_score: number;
  bhoomi_rate: number;
  edinburgh_bhoomi_rate: number;
  edinburgh_pine_bhoomi_rate: number;
  edinburgh_birch_bhoomi_rate: number;
  food_score: number;
  food_rate: number;
  rental_rate: number;
  rental_tree_count: number;
  food_packets: number;
  shram_balance: number;
  wari_balance: number;
  wari_rate: number;
  vayal_count: number;
  vayal_balance: number;
  vayal_rate: number;
  jivurja_balance: number;
  jivurja_rate: number;
  sugar_packets: number;
  firewood: number;
  yeast_culture: number;
  machine_installed: boolean;
  machine_owned: boolean;
  machine_count: number;
  machine_placed: boolean;
  handcart_owned: boolean;
  handcart_count: number;
  handcart_placed: boolean;
  owned_books: { slug: string; title: string; emoji: string; type: string }[];
}

const TREE_HARVEST_INFO: Record<number, { name: string; emoji: string; logs: number }> = {
  5: { name: 'Amaranth',     emoji: '🌿', logs: 20 },
  7: { name: 'Vetiver',      emoji: '🌾', logs: 15 },
  4: { name: 'Prickly Pear', emoji: '🌵', logs: 12 },
  3: { name: 'Moringa',      emoji: '🌳', logs:  9 },
  8: { name: 'Jujube',       emoji: '🫐', logs:  7 },
  2: { name: 'Acacia',       emoji: '🌲', logs:  5 },
  1: { name: 'Mesquite',     emoji: '🌴', logs:  4 },
  6: { name: 'Neem',         emoji: '🌴', logs:  3 },
};

function SamrajyaPage({ onBack, onOpenWarehouse, onOpenUniversity, onOpenAtaviForest, initialTab }: { onBack: () => void; onOpenWarehouse: () => void; onOpenUniversity: () => void; onOpenAtaviForest: () => void; initialTab?: 'overview'|'infra'|'forest'|'knowledge'|'vayal'|'atavi'|'team' }) {
  // WAX wallet — from global context (must be before useState that uses it)
  const { waxSession, waxActor, waxConnecting, connect: rawWaxConnect, disconnect: rawWaxDisconnect } = useWax();

  const [myAcct, setMyAcct] = React.useState(() => waxActor || '');
  const [data, setData] = React.useState<SamrajyaData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [logs, setLogs] = React.useState<Record<number, number>>({});
  const [bhoomiLog, setBhoomiLog] = React.useState<{ amount_added: string; balance_before: string; balance_after: string; created_at: string }[]>([]);

  const handleWaxConnect = async () => {
    try {
      await rawWaxConnect();
      // If no account loaded yet, use WAX actor as account (actor available after connect)
    } catch { /* cancelled */ }
  };

  const handleWaxDisconnect = async () => {
    await rawWaxDisconnect();
  };
  const [machineStatus, setMachineStatus] = React.useState<'idle' | 'sending' | 'verifying' | 'done' | 'error'>('idle');
  const [machineMsg, setMachineMsg] = React.useState('');
  const [handcartStatus, setHandcartStatus] = React.useState<'idle' | 'sending' | 'verifying' | 'done' | 'error'>('idle');
  const [handcartMsg, setHandcartMsg] = React.useState('');
  const [placeItemBusy, setPlaceItemBusy] = React.useState<Record<string, boolean>>({});
  const [placeItemMsg, setPlaceItemMsg] = React.useState<Record<string, string>>({});
  const [seedStatus, setSeedStatus] = React.useState<'idle' | 'sending' | 'verifying' | 'done' | 'error'>('idle');
  const [seedMsg, setSeedMsg] = React.useState('');
  const [seedQty, setSeedQty] = React.useState('1');
  const [seedCount, setSeedCount] = React.useState(0);
  const [seedsSown, setSeedsSown] = React.useState(0);
  const [remnantPulling, setRemnantPulling] = React.useState(false);
  const [remnantMsg, setRemnantMsg] = React.useState('');

  // Plant on random plot state
  const [plantSpecies, setPlantSpecies] = React.useState<number | null>(null);
  const [plantStatus, setPlantStatus] = React.useState<'idle' | 'planting' | 'done' | 'error'>('idle');
  const [plantMsg, setPlantMsg] = React.useState('');
  const [plantResult, setPlantResult] = React.useState<{ plot_index: number; plot_owner: string; species_index: number } | null>(null);

  React.useEffect(() => {
    if (!waxActor) return;
    fetch(`/samatvam/api/samatvam/seeds/${waxActor}`).then(r => r.json()).then(d => { if (d.success) setSeedCount(d.seeds_in_wallet || 0); }).catch(() => {});
    fetch('/samatvam/api/samatvam/land/rental-trees').then(r => r.json()).then(d => { if (d.success) setSeedsSown(d.trees.filter((t: any) => t.planted_by === waxActor).length); }).catch(() => {});
    fetch(`/samatvam/api/samatvam/vayal/withdraw-history/${waxActor}`).then(r => r.json()).then(d => { if (d.success) setVayalWithdrawHistory(d.rows); }).catch(() => {});
    if (!myAcct) setMyAcct(waxActor);
    fetchData(waxActor);
  }, [waxActor]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = React.useCallback((acct: string) => {
    if (!acct) return;
    setLoading(true);
    Promise.all([
      fetch(`/samatvam/api/samatvam/samrajya/${acct}`).then(r => r.json()),
      fetch(`/samatvam/api/samatvam/logs/${acct}`).then(r => r.json()),
      fetch(`/samatvam/api/samatvam/bhoomi-log/${acct}`).then(r => r.json()),
    ]).then(([d, l, bl]) => {
      if (d.success) setData(d);
      if (l.success) setLogs(l.logs);
      if (bl.success) setBhoomiLog(bl.log);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleBuyMachine = async () => {
    if (!waxSession || !waxActor) { setMachineMsg('Connect your WAX wallet first (use the Atavi Forest)'); return; }
    if (!confirmWaxSpend('5 WAX')) return;
    setMachineStatus('sending'); setMachineMsg('');
    try {
      const result = await waxSession.transact({
        actions: [{
          account: 'eosio.token',
          name: 'transfer',
          authorization: [{ actor: waxSession.actor, permission: 'active' }],
          data: { from: waxSession.actor, to: 'samatvam.gm', quantity: '5.00000000 WAX', memo: 'season0:machine' },
        }],
      });
      const txId = result?.resolved?.transaction?.id || (result as any)?.transaction_id || '';
      setMachineStatus('verifying'); setMachineMsg('Transaction sent — checking blockchain…');
      // Poll every 5s for up to 60s
      const deadline = Date.now() + 60_000;
      let done = false;
      while (Date.now() < deadline && !done) {
        await new Promise(r => setTimeout(r, 5000));
        try {
          const reg = await fetch('/samatvam/api/samatvam/infrastructure/buy-machine', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ wax_account: waxActor, tx_id: txId }),
          });
          const regData = await reg.json();
          if (regData.success) {
            setMachineStatus('done');
            setMachineMsg('Water harvesting machine installed! Food production +10%');
            fetchData(myAcct);
            done = true;
          } else if (regData.error === 'Machine already installed') {
            setMachineStatus('done');
            setMachineMsg('Machine already installed!');
            done = true;
          }
          // any other error — keep polling until deadline
        } catch { /* network hiccup — keep trying */ }
        if (!done) {
          const secsLeft = Math.round((deadline - Date.now()) / 1000);
          setMachineMsg(`Waiting for blockchain confirmation… (${secsLeft}s)`);
        }
      }
      if (!done) {
        setMachineStatus('error');
        setMachineMsg(`Could not confirm after 60s. Save your tx ID and contact support: ${txId}`);
      }
    } catch (err: any) {
      setMachineStatus('error');
      setMachineMsg(err.message || 'Transaction failed');
    }
  };

  const handleBuyHandcart = async () => {
    if (!waxSession || !waxActor) { setHandcartMsg('Connect your WAX wallet first (use the Atavi Forest)'); return; }
    if (!confirmWaxSpend('10 WAX')) return;
    setHandcartStatus('sending'); setHandcartMsg('');
    try {
      const result = await waxSession.transact({
        actions: [{
          account: 'eosio.token',
          name: 'transfer',
          authorization: [{ actor: waxSession.actor, permission: 'active' }],
          data: { from: waxSession.actor, to: 'samatvam.gm', quantity: '10.00000000 WAX', memo: 'season0:handcart' },
        }],
      });
      const txId = result?.resolved?.transaction?.id || (result as any)?.transaction_id || '';
      setHandcartStatus('verifying'); setHandcartMsg('Transaction sent — checking blockchain…');
      // Poll every 5s for up to 60s
      const deadline = Date.now() + 60_000;
      let done = false;
      while (Date.now() < deadline && !done) {
        await new Promise(r => setTimeout(r, 5000));
        try {
          const reg = await fetch('/samatvam/api/samatvam/infrastructure/buy-handcart', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ wax_account: waxActor, tx_id: txId }),
          });
          const regData = await reg.json();
          if (regData.success) {
            setHandcartStatus('done');
            setHandcartMsg('Hand cart ready! You can now harvest logs from mature trees.');
            fetchData(myAcct);
            done = true;
          } else if (regData.error === 'Handcart already owned') {
            setHandcartStatus('done');
            setHandcartMsg('Hand cart already registered!');
            done = true;
          }
          // any other error — keep polling until deadline
        } catch { /* network hiccup — keep trying */ }
        if (!done) {
          const secsLeft = Math.round((deadline - Date.now()) / 1000);
          setHandcartMsg(`Waiting for blockchain confirmation… (${secsLeft}s)`);
        }
      }
      if (!done) {
        setHandcartStatus('error');
        setHandcartMsg(`Could not confirm after 60s. Save your tx ID and contact support: ${txId}`);
      }
    } catch (err: any) {
      setHandcartStatus('error');
      setHandcartMsg(err.message || 'Transaction failed');
    }
  };

  const handlePlaceItem = async (type: 'handcart' | 'machine') => {
    if (!waxActor) return;
    setPlaceItemBusy(p => ({ ...p, [type]: true }));
    setPlaceItemMsg(p => ({ ...p, [type]: '' }));
    try {
      const r = await fetch('/samatvam/api/samatvam/infrastructure/place-item', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxActor, type }),
      }).then(x => x.json());
      if (r.success) {
        setPlaceItemMsg(p => ({ ...p, [type]: 'Placed successfully' }));
        fetchData(waxActor);
      } else {
        setPlaceItemMsg(p => ({ ...p, [type]: r.error || 'Failed' }));
      }
    } catch { setPlaceItemMsg(p => ({ ...p, [type]: 'Network error' })); }
    setPlaceItemBusy(p => ({ ...p, [type]: false }));
  };

  const handleRemoveItem = async (type: 'handcart' | 'machine') => {
    if (!waxActor) return;
    setPlaceItemBusy(p => ({ ...p, [type]: true }));
    setPlaceItemMsg(p => ({ ...p, [type]: '' }));
    try {
      const r = await fetch('/samatvam/api/samatvam/infrastructure/remove-item', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxActor, type }),
      }).then(x => x.json());
      if (r.success) {
        setPlaceItemMsg(p => ({ ...p, [type]: 'Removed' }));
        fetchData(waxActor);
      } else {
        setPlaceItemMsg(p => ({ ...p, [type]: r.error || 'Failed' }));
      }
    } catch { setPlaceItemMsg(p => ({ ...p, [type]: 'Network error' })); }
    setPlaceItemBusy(p => ({ ...p, [type]: false }));
  };

  const handleBuySeed = async () => {
    const qty = parseInt(seedQty) || 1;
    if (!waxSession || !waxActor) { setSeedMsg('Connect your WAX wallet first (use the Atavi Forest)'); return; }
    if (!confirmWaxSpend(`${qty} WAX`)) return;
    setSeedStatus('sending'); setSeedMsg('');
    try {
      const result = await waxSession.transact({
        actions: [{
          account: 'eosio.token',
          name: 'transfer',
          authorization: [{ actor: waxSession.actor, permission: 'active' }],
          data: { from: waxSession.actor, to: 'samatvam.gm', quantity: `${qty}.00000000 WAX`, memo: `season0:seed:${qty}` },
        }],
      });
      const txId = result?.resolved?.transaction?.id || (result as any)?.transaction_id || '';
      setSeedStatus('verifying'); setSeedMsg('Transaction sent — checking blockchain…');
      const prevCount = seedCount;
      const deadline = Date.now() + 60_000;
      let done = false;
      while (Date.now() < deadline && !done) {
        await new Promise(r => setTimeout(r, 5000));
        try {
          const r = await fetch(`/samatvam/api/samatvam/seeds/${waxActor}`);
          const d = await r.json();
          if (d.success && (d.seeds_in_wallet || 0) > prevCount) {
            setSeedCount(d.seeds_in_wallet || 0);
            setSeedStatus('done');
            setSeedMsg(`${qty} seed${qty > 1 ? 's' : ''} added! You now have ${d.seeds_in_wallet} seed${d.seeds_in_wallet !== 1 ? 's' : ''}.`);
            fetch('/samatvam/api/samatvam/land/rental-trees').then(r => r.json()).then(rt => { if (rt.success) setSeedsSown(rt.trees.filter((t: any) => t.planted_by === waxActor).length); }).catch(() => {});
            done = true;
          }
        } catch { /* network hiccup — keep trying */ }
        if (!done) {
          const secsLeft = Math.round((deadline - Date.now()) / 1000);
          setSeedMsg(`Waiting for blockchain confirmation… (${secsLeft}s)`);
        }
      }
      if (!done) {
        setSeedStatus('error');
        setSeedMsg(`Could not confirm after 60s. Save your tx ID and contact support: ${txId}`);
      }
    } catch (err: any) {
      setSeedStatus('error');
      setSeedMsg(err.message || 'Transaction failed');
    }
  };

  const handleBuyVayalWax = async () => {
    if (!waxSession || !waxActor) { setWaxMintMsg('Connect your WAX wallet first (use the Atavi Forest)'); return; }
    const qty = Math.max(1, parseInt(waxMintQty) || 1);
    const totalWax = qty * 10;
    const memo = qty === 1 ? 'vayal:mint:direct' : `vayal:mint:direct:${qty}`;
    if (!confirmWaxSpend(`${totalWax} WAX`)) return;
    setWaxMintStatus('sending'); setWaxMintMsg('');
    try {
      const result = await waxSession.transact({
        actions: [{
          account: 'eosio.token',
          name: 'transfer',
          authorization: [{ actor: waxSession.actor, permission: 'active' }],
          data: { from: waxSession.actor, to: 'samatvam.gm', quantity: `${totalWax.toFixed(8)} WAX`, memo },
        }],
      });
      const txId = result?.resolved?.transaction?.id || (result as any)?.transaction_id || '';
      setWaxMintStatus('verifying'); setWaxMintMsg('Transaction sent — checking blockchain…');
      const deadline = Date.now() + 60_000;
      let done = false;
      while (Date.now() < deadline && !done) {
        await new Promise(r => setTimeout(r, 5000));
        try {
          const reg = await fetch('/samatvam/api/samatvam/vayal/mint-direct', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ wax_account: waxActor, tx_id: txId, quantity: qty }),
          });
          const regData = await reg.json();
          if (regData.success) {
            setWaxMintStatus('done');
            setWaxMintMsg(regData.message || (qty === 1 ? 'Vayal farmland minted! It will appear on the Vayal map shortly.' : `${qty} Vayal farmlands queued for your account — they will appear on the map shortly.`));
            done = true;
          } else if (regData.error?.includes('already')) {
            setWaxMintStatus('done');
            setWaxMintMsg('Already registered!');
            done = true;
          }
        } catch {}
        if (!done) {
          const secsLeft = Math.round((deadline - Date.now()) / 1000);
          setWaxMintMsg(`Waiting for blockchain confirmation… (${secsLeft}s)`);
        }
      }
      if (!done) {
        setWaxMintStatus('error');
        setWaxMintMsg(`Could not confirm after 60s. Save your tx ID and contact support: ${txId}`);
      }
    } catch (err: any) {
      setWaxMintStatus('error');
      setWaxMintMsg(err.message || 'Transaction failed or cancelled');
    }
  };

  const handlePlantRandom = async () => {
    if (!myAcct) { setPlantMsg('No account found — open Samrajya from your account'); return; }
    if (!plantSpecies) { setPlantMsg('Pick a species first'); return; }
    setPlantStatus('planting'); setPlantMsg(''); setPlantResult(null);
    try {
      const r = await fetch('/samatvam/api/samatvam/plot/plant-random', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: myAcct, species_index: plantSpecies }),
      });
      const d = await r.json();
      if (d.success) {
        setPlantStatus('done');
        setPlantResult({ plot_index: d.plot_index, plot_owner: d.plot_owner, species_index: d.species_index });
        setSeedCount(c => Math.max(0, c - 1));
        setSeedsSown(s => s + 1);
      } else {
        setPlantStatus('error');
        setPlantMsg(d.error || 'Failed to plant');
      }
    } catch {
      setPlantStatus('error');
      setPlantMsg('Network error');
    }
  };

  React.useEffect(() => {
    // Prefer localStorage account (set by old WAX connect flow); fall back to global WaxContext actor
    const acct = localStorage.getItem('samatvam_active_account') || waxActor || '';
    if (acct) { setMyAcct(acct); fetchData(acct); }
    else setLoading(false);
    const t = setInterval(() => {
      const a = localStorage.getItem('samatvam_active_account') || waxActor || '';
      if (a) fetchData(a);
    }, 60_000);
    return () => clearInterval(t);
  }, [fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

  const [tab, setTab] = React.useState<'overview'|'infra'|'forest'|'knowledge'|'vayal'|'atavi'|'team'>(initialTab ?? 'overview');
  const [showAtaviForestPlant, setShowAtaviForestPlant] = React.useState(false);
  const [logsOpen, setLogsOpen] = React.useState(false);
  const [ataviStats, setAtaviStats] = React.useState<{ forest_plots: number; owned_land_trees: number; rental_on_land: number; trees_on_rental_land: number; total_seeds_bought: number; seeds_sown: number; seeds_in_wallet: number; food_rate_own: number; food_rate_rental_bhoomi: number; food_rate_rental_seed: number; food_rate_rent_income: number; food_rate_total: number } | null>(null);
  React.useEffect(() => {
    if (tab !== 'atavi' || !myAcct) return;
    fetch(`/samatvam/api/samatvam/atavi-forest/player-stats/${myAcct}`)
      .then(r => r.json()).then(d => { if (d.success) setAtaviStats(d); }).catch(() => {});
  }, [tab, myAcct]);

  const [teamData, setTeamData] = React.useState<{ idle_workers: any[]; training_workers: any[]; operatives: any[] } | null>(null);
  const [teamLoading, setTeamLoading] = React.useState(false);
  React.useEffect(() => {
    if (tab !== 'team' || !myAcct) return;
    setTeamLoading(true);
    fetch(`/samatvam/api/samatvam/academy/status/${encodeURIComponent(myAcct)}`)
      .then(r => r.json()).then(d => { if (d.success) setTeamData(d); }).catch(() => {})
      .finally(() => setTeamLoading(false));
  }, [tab, myAcct]);

  // ── Bulk plant queue state ──
  const [queueSpecies, setQueueSpecies] = React.useState('neem');
  const [queueQty, setQueueQty] = React.useState(1);
  const [queueSubmitting, setQueueSubmitting] = React.useState(false);
  const [queueJob, setQueueJob] = React.useState<{ id:number; species:string; quantity:number; planted:number; status:string; error:string|null } | null>(null);
  const [queueMsg, setQueueMsg] = React.useState('');

  // ── Bhoomi bulk plant state (new atavi forest) ──
  const [bhoomiBulkSpecies, setBhoomiBulkSpecies] = React.useState('amaranth');
  const [bhoomiBulkQty, setBhoomiBulkQty] = React.useState(1);
  const [bhoomiBulkSubmitting, setBhoomiBulkSubmitting] = React.useState(false);
  const [bhoomiBulkMsg, setBhoomiBulkMsg] = React.useState('');
  const [bhoomiBulkJob, setBhoomiBulkJob] = React.useState<{ id:number; species:string; quantity:number; planted:number; status:string; error:string|null } | null>(null);
  const [plantOwnedBusy, setPlantOwnedBusy] = React.useState(false);
  const [plantOwnedMsg, setPlantOwnedMsg] = React.useState('');
  const [rentalSpecies, setRentalSpecies] = React.useState('amaranth');
  const [rentalQty, setRentalQty] = React.useState(1);
  const [rentalSubmitting, setRentalSubmitting] = React.useState(false);
  const [rentalMsg, setRentalMsg] = React.useState('');
  const [rentalJob, setRentalJob] = React.useState<{ id:number; species:string; quantity:number; planted:number; status:string; error:string|null } | null>(null);
  const [seedPlantSpecies, setSeedPlantSpecies] = React.useState('amaranth');
  const [seedPlantQty, setSeedPlantQty] = React.useState(1);
  const [seedPlantSubmitting, setSeedPlantSubmitting] = React.useState(false);
  const [seedPlantMsg, setSeedPlantMsg] = React.useState('');
  const [seedPlantJob, setSeedPlantJob] = React.useState<{ id:number; species:string; quantity:number; planted:number; status:string; error:string|null } | null>(null);

  // ── Harvest All state (new atavi forest) ──
  const [harvestAllBusy, setHarvestAllBusy] = React.useState(false);
  const [harvestAllMsg, setHarvestAllMsg] = React.useState('');

  // ── Harvest All state (old plot forest) ──
  const [plotHarvestAllBusy, setPlotHarvestAllBusy] = React.useState(false);
  const [plotHarvestAllMsg, setPlotHarvestAllMsg] = React.useState('');

  // ── Harvest panel state ──
  const [matureCounts, setMatureCounts] = React.useState<Record<string, { own: number; rental_bhoomi: number; rental_seed: number }>>({});
  const [matureCountsLoading, setMatureCountsLoading] = React.useState(false);
  const [harvestSpecies, setHarvestSpecies] = React.useState('amaranth');
  const [harvestQty, setHarvestQty] = React.useState(0); // 0 = all mature
  const [harvestOwnBusy, setHarvestOwnBusy] = React.useState(false);
  const [harvestOwnMsg, setHarvestOwnMsg] = React.useState('');
  const [harvestBhoomiBusy, setHarvestBhoomiBusy] = React.useState(false);
  const [harvestBhoomiMsg, setHarvestBhoomiMsg] = React.useState('');
  const [harvestSeedBusy, setHarvestSeedBusy] = React.useState(false);
  const [harvestSeedMsg, setHarvestSeedMsg] = React.useState('');
  // legacy refs used by old handlers still in scope
  const [harvestSpeciesBusy] = React.useState(false);
  const [harvestRentalBusy] = React.useState(false);

  const [growingCounts, setGrowingCounts] = React.useState<Record<string, { own: number; rental_bhoomi: number; rental_seed: number }>>({});
  const [growingCountsLoading, setGrowingCountsLoading] = React.useState(false);
  const [growingFilter, setGrowingFilter] = React.useState<'all' | 'seeds' | 'bhoomi'>('all');

  const fetchMatureCounts = React.useCallback(() => {
    if (!myAcct) return;
    setMatureCountsLoading(true);
    fetch(`/samatvam/api/samatvam/atavi-forest/mature-counts/${myAcct}`)
      .then(r => r.json())
      .then(d => { if (d.success) setMatureCounts(d.counts); })
      .catch(() => {})
      .finally(() => setMatureCountsLoading(false));
  }, [myAcct]);

  const fetchGrowingCounts = React.useCallback(() => {
    if (!myAcct) return;
    setGrowingCountsLoading(true);
    fetch(`/samatvam/api/samatvam/atavi-forest/growing-counts/${myAcct}`)
      .then(r => r.json())
      .then(d => { if (d.success) setGrowingCounts(d.counts); })
      .catch(() => {})
      .finally(() => setGrowingCountsLoading(false));
  }, [myAcct]);

  React.useEffect(() => {
    if (myAcct && tab === 'atavi') { fetchMatureCounts(); fetchGrowingCounts(); }
  }, [myAcct, tab, fetchMatureCounts, fetchGrowingCounts]);

  // Poll queue status while active
  React.useEffect(() => {
    if (!myAcct || tab !== 'atavi') return;
    const poll = () => {
      fetch(`/samatvam/api/samatvam/atavi-forest/queue-status/${myAcct}`)
        .then(r => r.json()).then(d => {
          if (d.success && d.job) {
            setQueueJob(d.job);
            setBhoomiBulkJob(prev => (prev && prev.id === d.job.id) ? d.job : prev);
          }
        }).catch(() => {});
      fetch(`/samatvam/api/samatvam/atavi-forest/queue-status/${myAcct}?type=bhoomi_rental`)
        .then(r => r.json()).then(d => {
          if (d.success && d.job) setRentalJob(d.job);
        }).catch(() => {});
      fetch(`/samatvam/api/samatvam/atavi-forest/queue-status/${myAcct}?type=seed`)
        .then(r => r.json()).then(d => {
          if (d.success && d.job) setSeedPlantJob(d.job);
        }).catch(() => {});
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => clearInterval(id);
  }, [myAcct, tab]);

  const handleQueuePlant = async () => {
    if (!myAcct || queueSubmitting || queueQty < 1) return;
    setQueueSubmitting(true); setQueueMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/atavi-forest/queue-plant', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: myAcct, species: queueSpecies, quantity: queueQty }),
      });
      const d = await r.json();
      if (d.success) {
        setQueueMsg(`✅ Queued ${queueQty} ${queueSpecies} seeds — ${d.seeds_remaining} seeds remaining`);
        // Refresh atavi stats
        fetch(`/samatvam/api/samatvam/atavi-forest/player-stats/${myAcct}`)
          .then(r => r.json()).then(d => { if (d.success) setAtaviStats(d); }).catch(() => {});
      } else {
        setQueueMsg(`❌ ${d.error}`);
      }
    } catch { setQueueMsg('❌ Network error'); }
    finally { setQueueSubmitting(false); }
  };

  const BHOOMI_SEED_COST: Record<string, number> = {
    amaranth: 10, vetiver: 20, opuntia: 35, moringa: 50,
    jujube: 70, acacia: 90, mesquite: 120, neem: 160,
  };

  const handleBhoomiBulkPlant = async () => {
    if (!myAcct || bhoomiBulkSubmitting || bhoomiBulkQty < 1) return;
    setBhoomiBulkSubmitting(true); setBhoomiBulkMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/atavi-forest/queue-plant-bhoomi', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: myAcct, species: bhoomiBulkSpecies, quantity: bhoomiBulkQty }),
      });
      const d = await r.json();
      if (d.success) {
        setBhoomiBulkMsg(`✅ Queued ${bhoomiBulkQty} ${bhoomiBulkSpecies} — 🪙 ${d.bhoomi_spent} spent · ${d.bhoomi_remaining.toFixed(1)} remaining`);
        setBhoomiBulkJob({ id: d.job_id, species: bhoomiBulkSpecies, quantity: bhoomiBulkQty, planted: 0, status: 'pending', error: null });
        fetch(`/samatvam/api/samatvam/atavi-forest/player-stats/${myAcct}`)
          .then(r => r.json()).then(d => { if (d.success) setAtaviStats(d); }).catch(() => {});
        fetch(`/samatvam/api/samatvam/samrajya/player/${myAcct}`)
          .then(r => r.json()).then(d => { if (d.success) setData(d); }).catch(() => {});
      } else {
        setBhoomiBulkMsg(`❌ ${d.error}`);
      }
    } catch { setBhoomiBulkMsg('❌ Network error'); }
    finally { setBhoomiBulkSubmitting(false); }
  };

  const handlePlotHarvestAll = async () => {
    if (!myAcct || plotHarvestAllBusy) return;
    setPlotHarvestAllBusy(true); setPlotHarvestAllMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/plot/harvest-all', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: myAcct }),
      });
      const d = await r.json();
      if (d.success) {
        if (d.harvested === 0) {
          setPlotHarvestAllMsg('⏳ No mature trees to harvest yet');
        } else {
          setPlotHarvestAllMsg(`✅ Harvested ${d.harvested} trees · +${d.total_logs} logs${d.seeds_returned > 0 ? ` · +${d.seeds_returned} seeds returned` : ''}`);
        }
      } else {
        setPlotHarvestAllMsg(`❌ ${d.error}`);
      }
    } catch { setPlotHarvestAllMsg('❌ Network error'); }
    finally { setPlotHarvestAllBusy(false); }
  };

  const handleHarvestAll = async () => {
    if (!myAcct || harvestAllBusy) return;
    setHarvestAllBusy(true); setHarvestAllMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/atavi-forest/harvest-all', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: myAcct }),
      });
      const d = await r.json();
      if (d.success) {
        if (d.harvested === 0) {
          setHarvestAllMsg('⏳ No mature trees to harvest yet');
        } else {
          setHarvestAllMsg(`✅ Harvested ${d.harvested} trees · +${d.total_logs} logs${d.seeds_returned > 0 ? ` · +${d.seeds_returned} seeds returned` : ''}`);
          fetch(`/samatvam/api/samatvam/atavi-forest/player-stats/${myAcct}`)
            .then(r => r.json()).then(d => { if (d.success) setAtaviStats(d); }).catch(() => {});
        }
      } else {
        setHarvestAllMsg(`❌ ${d.error}`);
      }
    } catch { setHarvestAllMsg('❌ Network error'); }
    finally { setHarvestAllBusy(false); }
  };

  const handleHarvestOwn = async () => {
    if (!myAcct || harvestOwnBusy) return;
    setHarvestOwnBusy(true); setHarvestOwnMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/atavi-forest/harvest-own', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: myAcct, species: harvestSpecies, qty: harvestQty }),
      });
      const d = await r.json();
      if (d.success) {
        if (d.harvested === 0) setHarvestOwnMsg('⏳ No mature trees on own land');
        else { setHarvestOwnMsg(`✅ ${d.harvested} trees · +${d.total_logs} logs`); fetchMatureCounts(); }
      } else setHarvestOwnMsg(`❌ ${d.error}`);
    } catch { setHarvestOwnMsg('❌ Network error'); }
    finally { setHarvestOwnBusy(false); }
  };

  const handleHarvestBhoomiRental = async () => {
    if (!myAcct || harvestBhoomiBusy) return;
    setHarvestBhoomiBusy(true); setHarvestBhoomiMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/atavi-forest/harvest-rental-bhoomi', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: myAcct, species: harvestSpecies, qty: harvestQty }),
      });
      const d = await r.json();
      if (d.success) {
        if (d.harvested === 0) setHarvestBhoomiMsg('⏳ No mature bhoomi trees on rental land');
        else { setHarvestBhoomiMsg(`✅ ${d.harvested} trees · +${d.total_logs} logs`); fetchMatureCounts(); }
      } else setHarvestBhoomiMsg(`❌ ${d.error}`);
    } catch { setHarvestBhoomiMsg('❌ Network error'); }
    finally { setHarvestBhoomiBusy(false); }
  };

  const handleHarvestSeedRental = async () => {
    if (!myAcct || harvestSeedBusy) return;
    setHarvestSeedBusy(true); setHarvestSeedMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/atavi-forest/harvest-rental-seed', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: myAcct, species: harvestSpecies, qty: harvestQty }),
      });
      const d = await r.json();
      if (d.success) {
        if (d.harvested === 0) setHarvestSeedMsg('⏳ No mature seed trees on rental land');
        else {
          setHarvestSeedMsg(`✅ ${d.harvested} trees · +${d.total_logs} logs · +${d.seeds_returned} seeds back`);
          fetchMatureCounts();
          fetch(`/samatvam/api/samatvam/seeds/${myAcct}`).then(r => r.json()).then(d => { if (d.success) setSeedCount(d.seeds_in_wallet || 0); }).catch(() => {});
        }
      } else setHarvestSeedMsg(`❌ ${d.error}`);
    } catch { setHarvestSeedMsg('❌ Network error'); }
    finally { setHarvestSeedBusy(false); }
  };

  const [craftTier, setCraftTier] = React.useState<number | null>(null);
  const [craftStatus, setCraftStatus] = React.useState<'idle'|'sending'|'done'|'error'>('idle');
  const [craftMsg, setCraftMsg] = React.useState('');
  const [craftQty, setCraftQty] = React.useState('1');
  const [showWaxMintModal, setShowWaxMintModal] = React.useState(false);
  const [waxMintStatus, setWaxMintStatus] = React.useState<'idle'|'sending'|'verifying'|'done'|'error'>('idle');
  const [waxMintMsg, setWaxMintMsg] = React.useState('');
  const [waxMintQty, setWaxMintQty] = React.useState('1');
  const [vayalWithdrawAmount, setVayalWithdrawAmount] = React.useState('');
  const [vayalWithdrawStatus, setVayalWithdrawStatus] = React.useState<'idle'|'sending'|'done'|'error'>('idle');
  const [vayalWithdrawMsg, setVayalWithdrawMsg] = React.useState('');
  const [vayalWithdrawHistory, setVayalWithdrawHistory] = React.useState<any[]>([]);

  const handleVayalWithdraw = async () => {
    const account = waxActor;
    if (!account) { setVayalWithdrawMsg('No WAX wallet connected'); setVayalWithdrawStatus('error'); return; }
    const amount = parseFloat(vayalWithdrawAmount);
    if (isNaN(amount) || amount < 1) { setVayalWithdrawMsg('Minimum withdrawal is 1 VAYAL'); setVayalWithdrawStatus('error'); return; }
    const bal = data?.vayal_balance ?? 0;
    if (amount > bal) { setVayalWithdrawMsg(`Insufficient balance — you have ${bal.toFixed(2)} VAYAL`); setVayalWithdrawStatus('error'); return; }
    setVayalWithdrawStatus('sending');
    setVayalWithdrawMsg('Submitting withdrawal…');
    try {
      const r = await fetch('/samatvam/api/samatvam/vayal/withdraw', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: account, vayal_amount: amount, destination_wallet: account }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.error || 'Withdrawal failed');
      setVayalWithdrawStatus('done');
      setVayalWithdrawMsg(`Queued! ${d.payout_vayal.toFixed(8)} VAYAL will arrive in your WAX wallet shortly.`);
      setVayalWithdrawAmount('');
      fetchData(account);
      // Refresh history
      const hr = await fetch(`/samatvam/api/samatvam/vayal/withdraw-history/${account}`);
      const hd = await hr.json();
      if (hd.success) setVayalWithdrawHistory(hd.rows);
    } catch (err: any) {
      setVayalWithdrawStatus('error');
      setVayalWithdrawMsg(err.message || 'Withdrawal failed');
    }
  };

  const handleCraftMint = async (tier: number) => {
    const account = waxActor;
    if (!account) { setCraftMsg('No WAX wallet connected — connect via Atavi Forest first'); setCraftStatus('error'); return; }
    const qty = Math.max(1, parseInt(craftQty) || 1);
    setCraftStatus('sending'); setCraftMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/vayal/mint-batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account, tier, quantity: qty }),
      });
      const d = await r.json();
      if (d.success) {
        setCraftStatus('done');
        setCraftMsg(d.message || (d.queued ? `${qty} Vayal farmlands queued for your account — they will appear on the map shortly.` : `Farmland minted at (${d.plot_x}, ${d.plot_y})!`));
        fetchData(account);
      } else {
        setCraftStatus('error');
        setCraftMsg(d.error || 'Mint failed');
      }
    } catch (err: any) {
      setCraftStatus('error');
      setCraftMsg(err.message || 'Network error');
    }
  };

  const StatCard = ({ label, value, sub, emoji, color }: { label: string; value: string | number; sub?: string; emoji: string; color: string }) => (
    <div className="rounded-2xl p-3 sm:p-5 flex flex-col gap-1.5 sm:gap-2" style={{ background: 'rgba(15,23,42,0.85)', border: `1px solid ${color}28` }}>
      <div className="flex items-center justify-between">
        <span className="text-xl sm:text-2xl">{emoji}</span>
        <div className="text-[9px] sm:text-xs font-bold uppercase tracking-widest text-right leading-tight" style={{ color: `${color}99` }}>{label}</div>
      </div>
      <div className="text-xl sm:text-3xl font-black text-white leading-none">{value}</div>
      {sub && <div className="text-[10px] sm:text-xs mt-0.5" style={{ color: `${color}70` }}>{sub}</div>}
    </div>
  );

  const TABS = [
    { id: 'overview',   label: 'Overview',       emoji: '🏰' },
    { id: 'infra',      label: 'Infrastructure', emoji: '⚙️' },
    { id: 'forest',     label: 'Forest',         emoji: '🌳' },
    { id: 'knowledge',  label: 'Knowledge',      emoji: '📚' },
    { id: 'vayal',      label: 'Vayal',          emoji: '🌾' },
    { id: 'atavi',      label: 'Atavi',          emoji: '🌿' },
    { id: 'team',       label: 'My Team',        emoji: '🚌' },
  ] as const;

  return (
    <div className="min-h-screen bg-slate-900 text-white" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>

      {/* Header */}
      <div className="sticky top-0 z-40 backdrop-blur-md border-b border-slate-800/60" style={{ background: 'rgba(2,6,23,0.95)' }}>
        <div className="w-full px-6 py-3 flex items-center gap-3">
          <button onClick={onBack} className="text-slate-400 hover:text-white transition-colors p-1">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
          </button>
          <div>
            <h1 className="text-white font-black text-base leading-tight tracking-widest">SAMRAJYA</h1>
            <p className="text-violet-400/70 text-xs">Your Empire</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {myAcct && (
              <div className="px-3 py-1 rounded-full text-xs font-bold border"
                style={{ background: 'rgba(139,92,246,0.1)', borderColor: 'rgba(139,92,246,0.3)', color: '#a78bfa' }}>
                {myAcct.length > 20 ? `${myAcct.slice(0, 12)}…` : myAcct}
              </div>
            )}
            {waxActor ? (
              <div className="flex items-center gap-2">
                <span style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, padding: '4px 8px', fontFamily: 'monospace' }}>
                  ⬡ {waxActor.length > 12 ? waxActor.slice(0,12)+'…' : waxActor}
                </span>
                <button onClick={handleWaxDisconnect} style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '4px 8px', color: '#f87171', cursor: 'pointer', fontSize: 10, fontWeight: 700 }}>✕</button>
              </div>
            ) : (
              <button onClick={handleWaxConnect} disabled={waxConnecting} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 10, border: '1px solid rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.08)', color: '#fcd34d', fontSize: 11, fontWeight: 700, cursor: waxConnecting ? 'not-allowed' : 'pointer', opacity: waxConnecting ? 0.6 : 1 }}>
                <span>⬡</span>{waxConnecting ? '…' : 'Connect WAX'}
              </button>
            )}
          </div>
        </div>

        {/* Tab bar — only when data loaded */}
        {myAcct && data && (
          <div className="w-full px-6 pb-0 flex gap-1 border-t border-slate-800/40">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id as any)}
                className={`flex items-center gap-1.5 px-2 sm:px-4 py-2.5 text-xs font-bold uppercase tracking-widest transition-all border-b-2 ${
                  tab === t.id
                    ? 'text-white border-violet-400'
                    : 'text-slate-500 border-transparent hover:text-slate-300'
                }`}>
                <span>{t.emoji}</span><span className="hidden sm:inline">{t.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="w-full px-6 py-6">

        {/* Not connected */}
        {!myAcct && (
          <div className="rounded-2xl border border-slate-700/40 bg-slate-800/40 p-14 flex flex-col items-center gap-4 text-center">
            <div className="text-5xl">🏰</div>
            <p className="text-white font-black text-lg">No empire yet</p>
            <p className="text-slate-400 text-sm">Connect your WAX wallet to view your Samrajya.</p>
            <button onClick={handleWaxConnect} disabled={waxConnecting}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px', borderRadius: 12, border: '1px solid rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.1)', color: '#fcd34d', fontSize: 14, fontWeight: 800, cursor: waxConnecting ? 'not-allowed' : 'pointer', opacity: waxConnecting ? 0.6 : 1 }}>
              <span style={{ fontSize: 18 }}>⬡</span>{waxConnecting ? 'Connecting…' : 'Connect WAX Wallet'}
            </button>
            <button onClick={onBack} className="px-6 py-2.5 rounded-xl font-bold text-sm text-slate-400 hover:text-white">← Back to Dashboard</button>
          </div>
        )}

        {myAcct && loading && (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" />
          </div>
        )}

        {myAcct && !loading && data && (
          <>
            {/* ── OVERVIEW TAB ──────────────────────────────────────── */}
            {tab === 'overview' && (
              <div className="flex flex-col gap-6">

                {/* ── SECTION: LAND & TREES ── */}
                <div className="rounded-2xl border border-slate-700/30 overflow-hidden" style={{ background: 'rgba(15,23,42,0.85)' }}>
                  <div className="px-5 py-3 border-b border-slate-800/50">
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-400">🗺️ Land &amp; Trees</span>
                  </div>
                  <div className="p-5 grid grid-cols-3 gap-4">
                    {[
                      { label: 'Plots',           value: data.plots_owned,  color: '#f59e0b', emoji: '🗺️', sub: 'atavi forest' },
                      { label: 'Trees Planted',   value: data.trees_planted, color: '#22c55e', emoji: '🌳', sub: `${data.plots_owned - data.trees_planted} bare` },
                      { label: 'Vayal Farmland',  value: data.vayal_count,  color: '#34d399', emoji: '🌾', sub: 'farmland plots' },
                    ].map(c => (
                      <div key={c.label} className="rounded-xl p-4 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${c.color}20` }}>
                        <div className="text-2xl mb-1">{c.emoji}</div>
                        <div className="text-2xl font-black" style={{ color: c.color }}>{c.value}</div>
                        <div className="text-xs font-bold text-slate-300 mt-1">{c.label}</div>
                        <div className="text-[10px] text-slate-600 mt-0.5">{c.sub}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── SECTION: CURRENCIES ── */}
                <div className="rounded-2xl border border-slate-700/30 overflow-hidden" style={{ background: 'rgba(15,23,42,0.85)' }}>
                  <div className="px-5 py-3 border-b border-slate-800/50">
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-400">💰 Currencies &amp; Tokens</span>
                  </div>
                  <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {/* Bhoomi — custom card with Atavi/Edinburgh Pine/Edinburgh Birch breakdown */}
                    <div className="rounded-xl p-4 flex flex-col" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid #f59e0b20' }}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">🪙</span>
                        <span className="text-xs font-bold text-slate-400">Bhoomi</span>
                      </div>
                      <div className="text-xl font-black mb-0.5" style={{ color: '#f59e0b' }}>{data.bhoomi_score.toFixed(4)}</div>
                      <div className="text-[10px] font-semibold mb-2" style={{ color: '#f59e0b80' }}>+{data.bhoomi_rate.toFixed(2)}/hr</div>
                      <div className="text-[9px] leading-relaxed space-y-0.5" style={{ color: '#64748b' }}>
                        <div>Atavi +{(data.bhoomi_rate - data.edinburgh_bhoomi_rate).toFixed(2)}/hr</div>
                        <div style={{ color: data.edinburgh_pine_bhoomi_rate > 0 ? '#4ade80' : '#334155' }}>
                          Edin Pine +{data.edinburgh_pine_bhoomi_rate.toFixed(2)}/hr
                        </div>
                        <div style={{ color: data.edinburgh_birch_bhoomi_rate > 0 ? '#a3e635' : '#334155' }}>
                          Edin Birch +{data.edinburgh_birch_bhoomi_rate.toFixed(2)}/hr
                        </div>
                      </div>
                    </div>
                    {[
                      { label: 'Food',     value: data.food_score,     rate: data.food_rate,     color: '#22c55e', emoji: '🍎', rateUnit: '/hr', sub: data.machine_placed ? '+10% 💧 active' : 'tree token' },
                      { label: 'WARI',     value: data.wari_balance,   rate: data.wari_rate,     color: '#38bdf8', emoji: '💧', rateUnit: '/hr', sub: 'water resource' },
                      { label: 'VAYAL',    value: data.vayal_balance,  rate: data.vayal_rate,    color: '#34d399', emoji: '🌾', rateUnit: '/hr', sub: 'farmland token' },
                      { label: 'JIVURJA', value: data.jivurja_balance, rate: data.jivurja_rate,  color: '#a78bfa', emoji: '⚡', rateUnit: '/hr', sub: 'biorefinery share' },
                      { label: 'Shram',    value: data.shram_balance,  rate: null,               color: '#fb923c', emoji: '⚒️', rateUnit: null,  sub: '1/worker/day' },
                    ].map(c => (
                      <div key={c.label} className="rounded-xl p-4 flex flex-col" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${c.color}20` }}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">{c.emoji}</span>
                          <span className="text-xs font-bold text-slate-400">{c.label}</span>
                        </div>
                        <div className="text-xl font-black mb-0.5" style={{ color: c.color }}>{c.value.toFixed(4)}</div>
                        {c.rate !== null
                          ? <div className="text-[10px] font-semibold" style={{ color: `${c.color}80` }}>+{c.rate.toFixed(4)}{c.rateUnit}</div>
                          : <div className="text-[10px] text-slate-600">no auto-rate</div>}
                        <div className="text-[10px] text-slate-600 mt-1">{c.sub}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── SECTION: BHOOMI ACCRUAL LOG ── */}
                <div className="rounded-2xl border border-slate-700/30 overflow-hidden" style={{ background: 'rgba(15,23,42,0.85)' }}>
                  <div className="px-5 py-3 border-b border-slate-800/50">
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-400">🪙 Bhoomi Accrual Log</span>
                  </div>
                  {bhoomiLog.length === 0 ? (
                    <div className="px-5 py-4 text-xs text-slate-600">No bhoomi accrued yet — check back in a few minutes.</div>
                  ) : (
                    <div className="divide-y divide-slate-800/40">
                      {bhoomiLog.map((entry, i) => {
                        const mins = Math.round((Date.now() - new Date(entry.created_at).getTime()) / 60000);
                        const timeAgo = mins < 60 ? `${mins}m ago` : `${Math.round(mins/60)}h ago`;
                        return (
                          <div key={i} className="px-5 py-3 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                              <span className="text-emerald-400 font-black text-sm">+{parseFloat(entry.amount_added).toFixed(2)}</span>
                              <span className="text-slate-500 text-xs">bhoomi added</span>
                            </div>
                            <div className="text-xs font-mono text-slate-400">
                              <span style={{ color: '#94a3b8' }}>{parseFloat(entry.balance_before).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              <span className="text-slate-600 mx-1.5">→</span>
                              <span style={{ color: '#fbbf24' }}>{parseFloat(entry.balance_after).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <span className="text-[10px] text-slate-600 shrink-0">{timeAgo}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* ── SECTION: INVENTORY ── */}
                <div className="rounded-2xl border border-slate-700/30 overflow-hidden" style={{ background: 'rgba(15,23,42,0.85)' }}>
                  <div className="px-5 py-3 border-b border-slate-800/50 flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-400">📦 Inventory</span>
                    <button onClick={onOpenWarehouse} className="text-xs text-violet-400 hover:text-violet-300 font-bold transition-colors">Open Warehouse →</button>
                  </div>
                  <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {[
                      { label: 'Food Packets',  value: data.food_packets,  color: '#a78bfa', emoji: '📦', sub: 'ready to sell' },
                      { label: 'Sugar Packets', value: data.sugar_packets, color: '#fb923c', emoji: '🍬', sub: 'biorefinery input' },
                      { label: 'Firewood',      value: data.firewood,      color: '#f59e0b', emoji: '🪵', sub: '100 mesquite logs each' },
                      { label: 'Yeast Culture', value: data.yeast_culture, color: '#34d399', emoji: '🧫', sub: '100 moringa bundles each' },
                      { label: 'Seeds (wallet)',value: seedCount,           color: '#c4b5fd', emoji: '🌱', sub: 'rental farming' },
                      { label: 'Seeds (sown)',  value: seedsSown,           color: '#86efac', emoji: '🌿', sub: 'active on plots' },
                    ].map(c => (
                      <div key={c.label} className="rounded-xl p-4 flex flex-col" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${c.color}20` }}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">{c.emoji}</span>
                          <span className="text-xs font-bold text-slate-400">{c.label}</span>
                        </div>
                        <div className="text-xl font-black" style={{ color: c.color }}>{c.value}</div>
                        <div className="text-[10px] text-slate-600 mt-1">{c.sub}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── SECTION: LOGS BY SPECIES ── */}
                <div className="rounded-2xl border border-slate-700/30 overflow-hidden" style={{ background: 'rgba(15,23,42,0.85)' }}>
                  <div className="px-5 py-3 border-b border-slate-800/50">
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-400">🪵 Logs &amp; Bundles</span>
                  </div>
                  <div className="p-5 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
                    {[
                      { idx: 1, name: 'Mesquite',     emoji: '🌲', color: '#a3e635' },
                      { idx: 2, name: 'Acacia',       emoji: '🌳', color: '#86efac' },
                      { idx: 3, name: 'Moringa',      emoji: '🌿', color: '#6ee7b7' },
                      { idx: 4, name: 'Prickly Pear', emoji: '🌵', color: '#34d399' },
                      { idx: 5, name: 'Amaranth',     emoji: '🌱', color: '#4ade80' },
                      { idx: 6, name: 'Neem',         emoji: '🌴', color: '#22c55e' },
                      { idx: 7, name: 'Vetiver',      emoji: '🌾', color: '#16a34a' },
                      { idx: 8, name: 'Jujube',       emoji: '🫐', color: '#15803d' },
                    ].map(sp => (
                      <div key={sp.idx} className="rounded-xl p-3 flex flex-col items-center text-center" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${sp.color}20` }}>
                        <span className="text-xl mb-1">{sp.emoji}</span>
                        <div className="text-lg font-black" style={{ color: sp.color }}>{logs[sp.idx] ?? 0}</div>
                        <div className="text-[10px] font-semibold text-slate-400 mt-0.5">{sp.name}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── SECTION: EQUIPMENT ── */}
                <div className="rounded-2xl border border-slate-700/30 overflow-hidden" style={{ background: 'rgba(15,23,42,0.85)' }}>
                  <div className="px-5 py-3 border-b border-slate-800/50 flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-400">⚙️ Equipment</span>
                    <button onClick={() => setTab('infra')} className="text-xs text-violet-400 hover:text-violet-300 font-bold transition-colors">Manage →</button>
                  </div>
                  <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { label: 'Water Harvesting Machine', emoji: '💧', owned: data.machine_owned, count: data.machine_count, placed: data.machine_placed, effect: '+10% food rate', notOwned: '5 WAX · buy in Infrastructure' },
                      { label: 'Hand Cart',                emoji: '🛒', owned: data.handcart_owned, count: data.handcart_count, placed: data.handcart_placed, effect: 'faster warehouse packing', notOwned: '10 WAX · buy in Infrastructure' },
                    ].map(eq => (
                      <div key={eq.label} className="rounded-xl p-4 flex items-center gap-4"
                        style={{ background: eq.owned ? 'rgba(34,197,94,0.05)' : 'rgba(255,255,255,0.02)', border: eq.owned ? '1px solid rgba(34,197,94,0.2)' : '1px solid rgba(255,255,255,0.06)' }}>
                        <span className="text-3xl">{eq.emoji}</span>
                        <div className="flex-1">
                          <div className="text-sm font-bold text-slate-200">{eq.label}</div>
                          {eq.owned
                            ? <div className="text-xs text-green-400 mt-0.5">✓ ×{eq.count} owned{eq.placed ? ' · active' : ' · not placed'} · {eq.effect}</div>
                            : <div className="text-xs text-slate-600 mt-0.5">{eq.notOwned}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── SECTION: SEEDS BUY PANEL ── */}
                <div className="rounded-2xl overflow-hidden border transition-all duration-300"
                  style={{ borderColor: 'rgba(139,92,246,0.35)', background: 'linear-gradient(135deg, rgba(46,16,101,0.35) 0%, rgba(15,23,42,0.95) 100%)' }}>
                  <div className="px-5 py-3 border-b flex items-center justify-between"
                    style={{ borderColor: 'rgba(139,92,246,0.18)' }}>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#c4b5fd' }}>🌱 Seeds — Rental Farming</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {seedsSown > 0 && <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)', color: '#86efac' }}>{seedsSown} sown</span>}
                      {seedCount > 0 && <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(139,92,246,0.2)', border: '1px solid rgba(167,139,250,0.35)', color: '#c4b5fd' }}>{seedCount} in wallet</span>}
                    </div>
                  </div>
                  <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="text-white font-bold mb-2">Plant trees on any land</div>
                      <div className="text-slate-400 text-sm leading-relaxed mb-4">
                        Buy seeds to rent space on landowners' plots. You earn <span className="text-purple-300 font-semibold">0.8× food rate</span>; 0.2× flows to the landowner. Seeds are returned when you harvest a tree for logs.
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { label: 'Total Seeds', value: seedCount + seedsSown, color: '#c4b5fd', emoji: '🌱' },
                          { label: 'In Wallet',   value: seedCount,             color: '#a78bfa', emoji: '💼' },
                          { label: 'Sown',        value: seedsSown,             color: '#86efac', emoji: '🌿' },
                        ].map(s => (
                          <div key={s.label} className="rounded-xl p-3 text-center"
                            style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${s.color}20` }}>
                            <div className="text-lg">{s.emoji}</div>
                            <div className="text-xl font-black mt-1" style={{ color: s.color }}>{s.value}</div>
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mt-0.5">{s.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Buy Seeds · 1 WAX each</div>
                      <div className="flex gap-1.5 mb-3 flex-wrap">
                        {['1','5','10','25'].map(v => (
                          <button key={v} onClick={() => { setSeedQty(v); setSeedStatus('idle'); setSeedMsg(''); }}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                            style={{
                              background: seedQty === v ? 'rgba(139,92,246,0.35)' : 'rgba(255,255,255,0.05)',
                              border: seedQty === v ? '1px solid rgba(167,139,250,0.5)' : '1px solid rgba(255,255,255,0.08)',
                              color: seedQty === v ? '#c4b5fd' : '#64748b',
                            }}>
                            {v}
                          </button>
                        ))}
                        <input type="number" value={seedQty} min="1" onChange={e => { setSeedQty(e.target.value); setSeedStatus('idle'); setSeedMsg(''); }}
                          className="w-16 px-2 py-1.5 rounded-lg text-xs text-white text-center outline-none"
                          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }} />
                        <span className="flex items-center text-purple-400 text-xs font-bold">{parseInt(seedQty) || 1} WAX</span>
                      </div>
                      <button onClick={handleBuySeed}
                        disabled={ATAVI_LOCKED || seedStatus === 'sending' || seedStatus === 'verifying'}
                        className="w-full py-3 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90 disabled:opacity-50"
                        style={{ background: ATAVI_LOCKED ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, #7c3aed, #6d28d9)' }}>
                        {ATAVI_LOCKED ? '🔒 Seed buying paused' : seedStatus === 'sending' ? 'Sending transaction…' : seedStatus === 'verifying' ? 'Verifying on blockchain…' : `Buy ${parseInt(seedQty) || 1} Seed${(parseInt(seedQty) || 1) > 1 ? 's' : ''} · ${parseInt(seedQty) || 1} WAX`}
                      </button>
                      {seedMsg && (
                        <div className={`mt-2 text-xs ${seedStatus === 'error' ? 'text-red-400' : seedStatus === 'done' ? 'text-green-400' : 'text-purple-400'}`}>
                          {seedMsg}
                        </div>
                      )}
                      {!waxActor && (
                        <div className="mt-3 text-xs text-slate-600">Connect WAX wallet in Atavi Forest to buy seeds.</div>
                      )}
                      {waxActor && (
                        <div className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                          <button
                            disabled={remnantPulling}
                            onClick={async () => {
                              if (!waxActor || remnantPulling) return;
                              setRemnantPulling(true); setRemnantMsg('');
                              try {
                                const r = await fetch('/samatvam/api/samatvam/bus/pull-remnant-seeds', {
                                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ wax_account: waxActor }),
                                });
                                const d = await r.json();
                                if (d.success) { setRemnantMsg(d.message); setSeedCount(c => c + (d.seeds_returned || 0)); }
                                else { setRemnantMsg(d.error || 'Failed.'); }
                              } catch { setRemnantMsg('Network error.'); }
                              setRemnantPulling(false);
                            }}
                            className="w-full py-2.5 rounded-xl font-bold text-sm transition-all"
                            style={{ background: 'rgba(134,239,172,0.1)', border: '1px solid rgba(134,239,172,0.3)', color: '#86efac', opacity: remnantPulling ? 0.6 : 1, cursor: remnantPulling ? 'not-allowed' : 'pointer' }}>
                            {remnantPulling ? 'Pulling…' : '⬇ Pull Stranded Seeds to Wallet'}
                          </button>
                          {remnantMsg && <div className="mt-2 text-xs text-center" style={{ color: remnantMsg.includes('returned') || remnantMsg.includes('No') ? '#4ade80' : '#f87171' }}>{remnantMsg}</div>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>


                {/* ── RENTAL INCOME BOX ── */}
                {((data.rental_rate ?? 0) > 0 || (data.rental_tree_count ?? 0) > 0) && (
                  <div className="rounded-2xl border p-5" style={{ background: 'linear-gradient(135deg, rgba(120,53,15,0.25) 0%, rgba(15,23,42,0.95) 100%)', borderColor: 'rgba(251,191,36,0.25)' }}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center text-lg" style={{ background: 'rgba(251,191,36,0.15)' }}>🏡</div>
                      <div>
                        <div className="text-sm font-bold text-amber-300">Rental Income</div>
                        <div className="text-xs text-slate-500">Passive earnings from farmers on your land</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-xl p-4 text-center" style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.15)' }}>
                        <div className="text-2xl font-black text-amber-300">{(data.rental_rate ?? 0).toFixed(3)}</div>
                        <div className="text-xs text-slate-500 mt-1 uppercase tracking-wide">Food / hr</div>
                        <div className="text-xs text-amber-500/60 mt-0.5">20% landlord share</div>
                      </div>
                      <div className="rounded-xl p-4 text-center" style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.15)' }}>
                        <div className="text-2xl font-black text-amber-300">{data.rental_tree_count ?? 0}</div>
                        <div className="text-xs text-slate-500 mt-1 uppercase tracking-wide">Renter Trees</div>
                        <div className="text-xs text-amber-500/60 mt-0.5">active on your plots</div>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            )}

            {/* ── INFRASTRUCTURE TAB ────────────────────────────────── */}
            {tab === 'infra' && (
              <div className="flex flex-col gap-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                  {/* Water Harvesting Machine */}
                  <div className="rounded-2xl overflow-hidden border transition-all duration-300"
                    style={{
                      borderColor: data.machine_owned ? 'rgba(34,197,94,0.4)' : 'rgba(56,189,248,0.3)',
                      background: data.machine_owned
                        ? 'linear-gradient(135deg, rgba(5,46,22,0.5) 0%, rgba(15,23,42,0.98) 100%)'
                        : 'linear-gradient(135deg, rgba(8,47,73,0.5) 0%, rgba(15,23,42,0.98) 100%)',
                    }}>
                    <div className="px-5 py-3 border-b flex items-center justify-between"
                      style={{ borderColor: data.machine_owned ? 'rgba(34,197,94,0.2)' : 'rgba(56,189,248,0.15)' }}>
                      <span className="text-xs font-bold uppercase tracking-widest" style={{ color: data.machine_owned ? '#86efac' : '#7dd3fc' }}>
                        Infrastructure · Phase 2
                      </span>
                      {data.machine_owned && (
                        <span className="text-xs font-bold text-green-300 px-2 py-0.5 rounded-full bg-green-900/40 border border-green-500/30">Active ×{data.machine_count}</span>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-start gap-4 mb-4">
                        <img src="/samatvam/images/water-machine.jpg" alt="Water Harvesting Machine"
                          className="w-24 h-24 rounded-2xl object-cover flex-shrink-0"
                          style={{ border: data.machine_owned ? '1px solid rgba(34,197,94,0.4)' : '1px solid rgba(56,189,248,0.2)' }} />
                        <div className="flex-1">
                          <div className="text-white font-black text-base mb-1">Water Harvesting Machine</div>
                          {data.machine_owned
                            ? <div className="text-green-400 text-sm font-semibold mb-2">✓ Installed — Food production +10%</div>
                            : <div className="text-sky-400 text-sm mb-2">+10% Food rate for all trees · One-time purchase</div>}
                          <div className="text-slate-400 text-xs leading-relaxed">
                            Harvests atmospheric moisture to boost your entire forest's food output. One machine covers all your plots permanently.
                          </div>
                        </div>
                      </div>
                      {data.machine_owned ? (
                        <>
                          <div className="rounded-xl p-3 flex items-center justify-between"
                            style={{ background: 'rgba(5,46,22,0.4)', border: '1px solid rgba(34,197,94,0.2)' }}>
                            <span className="text-green-300 text-sm">Boosting {data.trees_planted} trees · +{(data.food_rate - data.food_rate / 1.1).toFixed(3)} Food/hr bonus</span>
                            <span className="text-green-400 font-bold text-sm">×{data.machine_count} owned</span>
                          </div>
                          <div className="mt-2 flex gap-2 items-center">
                            {data.machine_placed ? (
                              <button onClick={() => handleRemoveItem('machine')} disabled={!!placeItemBusy['machine']}
                                className="flex-1 py-2 rounded-xl font-bold text-sm transition-all hover:opacity-90 disabled:opacity-50"
                                style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.35)', color: '#fca5a5' }}>
                                {placeItemBusy['machine'] ? 'Removing…' : '✕ Remove Machine'}
                              </button>
                            ) : (
                              <button onClick={() => handlePlaceItem('machine')} disabled={!!placeItemBusy['machine']}
                                className="flex-1 py-2 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90 disabled:opacity-50"
                                style={{ background: 'linear-gradient(135deg, #0ea5e9, #0284c7)' }}>
                                {placeItemBusy['machine'] ? 'Placing…' : '▶ Place Machine'}
                              </button>
                            )}
                          </div>
                          {placeItemMsg['machine'] && (
                            <div className="mt-1 text-xs text-sky-300">{placeItemMsg['machine']}</div>
                          )}
                        </>
                      ) : (
                        <button onClick={handleBuyMachine}
                          disabled={machineStatus === 'sending' || machineStatus === 'verifying'}
                          className="w-full py-3 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90 disabled:opacity-50"
                          style={{ background: 'linear-gradient(135deg, #0ea5e9, #0284c7)' }}>
                          {machineStatus === 'sending' ? 'Sending transaction…' : machineStatus === 'verifying' ? 'Verifying on blockchain…' : 'Buy Water Harvesting Machine · 5 WAX'}
                        </button>
                      )}
                      {machineMsg && (
                        <div className={`mt-2 text-xs ${machineStatus === 'error' ? 'text-red-400' : machineStatus === 'done' ? 'text-green-400' : 'text-sky-400'}`}>
                          {machineMsg}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Hand Cart */}
                  <div className="rounded-2xl overflow-hidden border transition-all duration-300"
                    style={{
                      borderColor: data.handcart_owned ? 'rgba(34,197,94,0.4)' : 'rgba(251,191,36,0.3)',
                      background: data.handcart_owned
                        ? 'linear-gradient(135deg, rgba(5,46,22,0.5) 0%, rgba(15,23,42,0.98) 100%)'
                        : 'linear-gradient(135deg, rgba(78,52,8,0.5) 0%, rgba(15,23,42,0.98) 100%)',
                    }}>
                    <div className="px-5 py-3 border-b flex items-center justify-between"
                      style={{ borderColor: data.handcart_owned ? 'rgba(34,197,94,0.2)' : 'rgba(251,191,36,0.15)' }}>
                      <span className="text-xs font-bold uppercase tracking-widest" style={{ color: data.handcart_owned ? '#86efac' : '#fbbf24' }}>
                        Transport · Phase 3
                      </span>
                      {data.handcart_owned && (
                        <span className="text-xs font-bold text-green-300 px-2 py-0.5 rounded-full bg-green-900/40 border border-green-500/30">Ready ×{data.handcart_count}</span>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-start gap-4 mb-4">
                        <img src="/samatvam/images/handcart.jpg" alt="Hand Cart"
                          className="w-24 h-24 rounded-2xl object-cover flex-shrink-0"
                          style={{ border: data.handcart_owned ? '1px solid rgba(34,197,94,0.4)' : '1px solid rgba(251,191,36,0.2)' }} />
                        <div className="flex-1">
                          <div className="text-white font-black text-base mb-1">Hand Cart</div>
                          {data.handcart_owned
                            ? <div className="text-green-400 text-sm font-semibold mb-2">✓ Ready — unlocks log harvesting</div>
                            : <div className="text-amber-400 text-sm mb-2">Required to harvest logs from mature trees</div>}
                          <div className="text-slate-400 text-xs leading-relaxed">
                            Needed to carry harvested logs from your forest plots. Without a Hand Cart, mature trees cannot be harvested.
                          </div>
                        </div>
                      </div>
                      {data.handcart_owned ? (
                        <>
                          <div className="rounded-xl p-3 flex items-center justify-between"
                            style={{ background: 'rgba(5,46,22,0.4)', border: '1px solid rgba(34,197,94,0.2)' }}>
                            <span className="text-green-300 text-sm">Log harvesting unlocked across all plots</span>
                            <span className="text-green-400 font-bold text-sm">×{data.handcart_count} owned</span>
                          </div>
                          <div className="mt-2 flex gap-2 items-center">
                            {data.handcart_placed ? (
                              <button onClick={() => handleRemoveItem('handcart')} disabled={!!placeItemBusy['handcart']}
                                className="flex-1 py-2 rounded-xl font-bold text-sm transition-all hover:opacity-90 disabled:opacity-50"
                                style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.35)', color: '#fca5a5' }}>
                                {placeItemBusy['handcart'] ? 'Removing…' : '✕ Remove Hand Cart'}
                              </button>
                            ) : (
                              <button onClick={() => handlePlaceItem('handcart')} disabled={!!placeItemBusy['handcart']}
                                className="flex-1 py-2 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90 disabled:opacity-50"
                                style={{ background: 'linear-gradient(135deg, #d97706, #b45309)' }}>
                                {placeItemBusy['handcart'] ? 'Placing…' : '▶ Place Hand Cart'}
                              </button>
                            )}
                          </div>
                          {placeItemMsg['handcart'] && (
                            <div className="mt-1 text-xs text-amber-300">{placeItemMsg['handcart']}</div>
                          )}
                        </>
                      ) : (
                        <button onClick={handleBuyHandcart}
                          disabled={handcartStatus === 'sending' || handcartStatus === 'verifying'}
                          className="w-full py-3 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90 disabled:opacity-50"
                          style={{ background: 'linear-gradient(135deg, #d97706, #b45309)' }}>
                          {handcartStatus === 'sending' ? 'Sending transaction…' : handcartStatus === 'verifying' ? 'Verifying on blockchain…' : 'Buy Hand Cart · 10 WAX'}
                        </button>
                      )}
                      {handcartMsg && (
                        <div className={`mt-2 text-xs ${handcartStatus === 'error' ? 'text-red-400' : handcartStatus === 'done' ? 'text-green-400' : 'text-amber-400'}`}>
                          {handcartMsg}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Info row */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { emoji: '💧', title: 'Water Machine Effect', body: 'Multiplies every tree\'s Food rate by 1.1×. A Neem earning 14 Food/hr becomes 15.4/hr. Applies to all current and future trees.', color: '#7dd3fc' },
                    { emoji: '🛒', title: 'Hand Cart Unlock', body: 'Without a Hand Cart the Harvest button is disabled on all plots. One cart covers every plot — you never need more than one for basic use.', color: '#fbbf24' },
                    { emoji: '📈', title: 'Season 0 Score', body: 'Water Machine adds +10 pts to your Season 0 rank. Hand Cart adds +20 pts. Scores are used for the end-of-season leaderboard rewards.', color: '#a78bfa' },
                  ].map(tip => (
                    <div key={tip.title} className="rounded-2xl p-5" style={{ background: 'rgba(15,23,42,0.7)', border: `1px solid ${tip.color}20` }}>
                      <div className="text-2xl mb-2">{tip.emoji}</div>
                      <div className="text-white font-bold text-sm mb-1.5">{tip.title}</div>
                      <div className="text-slate-400 text-xs leading-relaxed">{tip.body}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── FOREST TAB ────────────────────────────────────────── */}
            {tab === 'forest' && (
              <div className="flex flex-col gap-6">
                {/* Harvest All button */}
                <div className="rounded-2xl p-5" style={{ background: 'rgba(15,23,42,0.9)', border: '1px solid rgba(52,211,153,0.2)' }}>
                  <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#34d399' }}>🪓 Harvest All Mature Trees</p>
                  <button onClick={handlePlotHarvestAll} disabled={ATAVI_LOCKED || plotHarvestAllBusy}
                    style={{ width: '100%', padding: '11px 16px', borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: plotHarvestAllBusy ? 'default' : 'pointer', transition: 'all 0.2s',
                      background: (ATAVI_LOCKED || plotHarvestAllBusy) ? 'rgba(52,211,153,0.04)' : 'rgba(52,211,153,0.14)',
                      border: `1px solid rgba(52,211,153,${(ATAVI_LOCKED || plotHarvestAllBusy) ? 0.1 : 0.4})`,
                      color: (ATAVI_LOCKED || plotHarvestAllBusy) ? 'rgba(52,211,153,0.3)' : '#34d399' }}>
                    {ATAVI_LOCKED ? '🔒 Harvest paused' : plotHarvestAllBusy ? '⏳ Harvesting…' : '🪓 Harvest All — owner trees + my rental trees'}
                  </button>
                  {plotHarvestAllMsg && <p style={{ fontSize: 11, marginTop: 10, fontWeight: 700, color: plotHarvestAllMsg.startsWith('✅') ? '#6ee7b7' : plotHarvestAllMsg.startsWith('⏳') ? '#fbbf24' : '#f87171' }}>{plotHarvestAllMsg}</p>}
                </div>

                {data.species.length === 0 ? (
                  <div className="rounded-2xl border border-slate-700/40 p-16 text-center" style={{ background: 'rgba(15,23,42,0.8)' }}>
                    <div className="text-5xl mb-4">🌱</div>
                    <p className="text-white font-bold text-base mb-2">No trees planted yet</p>
                    <p className="text-slate-400 text-sm">Open the Atavi Forest to start planting on your plots.</p>
                  </div>
                ) : (
                  <>
                    {/* Species cards grid */}
                    <div>
                      <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Species Breakdown — {data.trees_planted} trees across {data.species.length} species</p>
                      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
                        {data.species.map(sp => {
                          const pct = Math.min(100, (sp.count / Math.max(...data.species.map(s => s.count))) * 100);
                          return (
                            <div key={sp.name} className="rounded-2xl p-5" style={{ background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(34,197,94,0.15)' }}>
                              <div className="flex items-center gap-3 mb-3">
                                <span className="text-3xl">{sp.emoji}</span>
                                <div>
                                  <div className="text-white font-bold text-sm">{sp.name}</div>
                                  <div className="text-green-400 font-black text-lg leading-none">×{sp.count}</div>
                                </div>
                              </div>
                              <div className="h-1.5 rounded-full bg-green-500/15 overflow-hidden">
                                <div className="h-full rounded-full bg-green-400 transition-all" style={{ width: `${pct}%` }} />
                              </div>
                              <div className="text-xs text-slate-600 mt-1.5">{Math.round(pct)}% of largest species</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Summary bar */}
                    <div className="rounded-2xl border border-slate-700/30 p-5 grid grid-cols-2 sm:grid-cols-4 gap-y-4 sm:gap-y-0 sm:divide-x divide-slate-800/60" style={{ background: 'rgba(15,23,42,0.7)' }}>
                      <div className="px-5 first:pl-0">
                        <div className="text-xs text-slate-500 uppercase tracking-widest mb-1">Total Trees</div>
                        <div className="text-white font-black text-2xl">{data.trees_planted}</div>
                      </div>
                      <div className="px-5">
                        <div className="text-xs text-slate-500 uppercase tracking-widest mb-1">Bare Plots</div>
                        <div className="text-amber-400 font-black text-2xl">{data.plots_owned - data.trees_planted}</div>
                      </div>
                      <div className="px-5">
                        <div className="text-xs text-slate-500 uppercase tracking-widest mb-1">Food / hr</div>
                        <div className="text-green-400 font-black text-2xl">{data.food_rate.toFixed(2)}</div>
                      </div>
                      <div className="px-5">
                        <div className="text-xs text-slate-500 uppercase tracking-widest mb-1">Species</div>
                        <div className="text-slate-200 font-black text-2xl">{data.species.length}</div>
                      </div>
                    </div>

                    {/* Logs inventory */}
                    {Object.values(logs).some(v => v > 0) && (
                      <div className="rounded-2xl border border-yellow-500/20 overflow-hidden" style={{ background: 'rgba(15,23,42,0.85)' }}>
                        <div className="px-5 py-3 border-b border-yellow-500/10 flex items-center gap-2">
                          <span>🪵</span>
                          <span className="text-xs font-bold uppercase tracking-widest text-yellow-400/70">Logs Inventory</span>
                          <span className="ml-auto text-xs text-slate-500">{Object.values(logs).reduce((a, b) => a + b, 0)} total logs</span>
                        </div>
                        <div className="divide-y divide-slate-800/60">
                          {Object.entries(logs).filter(([, qty]) => qty > 0).map(([si, qty]) => {
                            const info = TREE_HARVEST_INFO[parseInt(si)];
                            if (!info) return null;
                            return (
                              <div key={si} className="px-5 py-3 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <span className="text-xl">{info.emoji}</span>
                                  <div>
                                    <div className="text-white font-bold text-sm">{info.name}</div>
                                    <div className="text-slate-500 text-xs">{info.logs} logs/harvest</div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-yellow-300 font-black text-xl">{qty}</span>
                                  <span className="text-slate-600 text-xs">🪵</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ── KNOWLEDGE TAB ─────────────────────────────────────── */}
            {tab === 'knowledge' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                {/* Books — 2/3 width */}
                <div className="col-span-1 md:col-span-2">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Owned Books &amp; Recipes</p>
                  {data.owned_books && data.owned_books.length > 0 ? (
                    <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
                      {data.owned_books.map(bk => (
                        <div key={bk.slug} className="rounded-2xl p-4 flex items-center gap-4"
                          style={{ background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(99,102,241,0.2)' }}>
                          <span className="text-3xl flex-shrink-0">{bk.emoji}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-white text-sm font-bold truncate">{bk.title}</div>
                            <div className="text-xs text-slate-500 capitalize mt-0.5">{bk.type}</div>
                          </div>
                          <span className="text-green-400 text-sm font-black flex-shrink-0">✓</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-slate-700/40 p-10 text-center" style={{ background: 'rgba(15,23,42,0.8)' }}>
                      <div className="text-4xl mb-3">📖</div>
                      <p className="text-white font-bold text-sm mb-1">No books or recipes yet</p>
                      <p className="text-slate-500 text-xs">Visit the University to purchase learning resources.</p>
                    </div>
                  )}
                </div>

                {/* University CTA — 1/3 width */}
                <div className="flex flex-col gap-4">
                  <div className="rounded-2xl p-6 flex flex-col gap-4"
                    style={{ background: 'linear-gradient(135deg, rgba(49,46,129,0.5) 0%, rgba(15,23,42,0.95) 100%)', border: '1px solid rgba(99,102,241,0.25)' }}>
                    <div className="text-4xl">🎓</div>
                    <div>
                      <div className="text-white font-black text-lg mb-1">University</div>
                      <div className="text-slate-400 text-sm leading-relaxed">
                        Unlock books, recipes, and knowledge packs that boost your empire's capabilities.
                      </div>
                    </div>
                    <button onClick={onOpenUniversity}
                      className="w-full py-3 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90"
                      style={{ background: 'linear-gradient(135deg, #4338ca, #6d28d9)' }}>
                      Open University →
                    </button>
                  </div>
                  <div className="rounded-2xl p-5" style={{ background: 'rgba(15,23,42,0.7)', border: '1px solid rgba(99,102,241,0.15)' }}>
                    <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Stats</div>
                    <div className="flex items-center justify-between py-2 border-b border-slate-800/50">
                      <span className="text-slate-400 text-sm">Books owned</span>
                      <span className="text-indigo-300 font-black">{data.owned_books?.length ?? 0}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-slate-400 text-sm">Available to buy</span>
                      <button onClick={onOpenUniversity} className="text-indigo-400 text-sm font-bold hover:text-indigo-300 transition-colors">View →</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* ── VAYAL TAB ─────────────────────────────────────────── */}
            {tab === 'vayal' && (() => {
              const VAYAL_TIERS = [
                {
                  n: 1, emoji: '🥗', label: 'Food Only', color: '#86efac',
                  bg: 'rgba(34,197,94,0.08)', border: 'rgba(34,197,94,0.3)',
                  hdr: 'rgba(34,197,94,0.07)', hdrBorder: 'rgba(34,197,94,0.15)',
                  cost: '10,000 Food',
                  tokens: [{ val: '10,000', unit: 'Food Packets', c: '#86efac' }],
                  who: 'Open to all players',
                  desc: 'Craft Vayal farmland using 10,000 Food Packets accumulated from your planted trees.',
                  locked: false,
                },
                {
                  n: 2, emoji: '⚒️', label: '+ Shram', color: '#7dd3fc',
                  bg: 'rgba(56,189,248,0.08)', border: 'rgba(56,189,248,0.3)',
                  hdr: 'rgba(56,189,248,0.07)', hdrBorder: 'rgba(56,189,248,0.15)',
                  cost: '6,000 Food + 2 Shram',
                  tokens: [{ val: '6,000', unit: 'Food Packets', c: '#7dd3fc' }, { val: '2', unit: 'Shram', c: '#7dd3fc' }],
                  who: 'Council workers',
                  desc: 'For active city council workers who hold Shram tokens. Lower food cost in exchange for Shram contribution.',
                  locked: false,
                },
                {
                  n: 3, emoji: '💧', label: '+ WARI', color: '#7dd3fc',
                  bg: 'rgba(56,189,248,0.08)', border: 'rgba(56,189,248,0.3)',
                  hdr: 'rgba(56,189,248,0.07)', hdrBorder: 'rgba(56,189,248,0.15)',
                  cost: '3,000 Food + 1 Shram + 1 WARI',
                  tokens: [{ val: '3,000', unit: 'Food Packets', c: '#7dd3fc' }, { val: '1', unit: 'Shram', c: '#7dd3fc' }, { val: '1', unit: 'WARI', c: '#7dd3fc' }],
                  who: 'Task 2 shareholders',
                  desc: 'For Task 2 water harvesting shareholders who hold WARI tokens. Reduced food and Shram cost in exchange for WARI contribution.',
                  locked: false,
                },
                {
                  n: 4, emoji: '🌍', label: 'OG / Bhoomi', color: '#c4b5fd',
                  bg: 'rgba(139,92,246,0.08)', border: 'rgba(139,92,246,0.3)',
                  hdr: 'rgba(139,92,246,0.07)', hdrBorder: 'rgba(139,92,246,0.15)',
                  cost: '500 Food + 1,500 Bhoomi',
                  tokens: [{ val: '500', unit: 'Food Packets', c: '#c4b5fd' }, { val: '1,500', unit: 'Bhoomi', c: '#c4b5fd' }],
                  who: 'OG players',
                  desc: 'For players who have built up high Bhoomi scores. The Bhoomi contribution substitutes most of the food cost.',
                  locked: false,
                },
              ];
              const activeTier = craftTier !== null ? VAYAL_TIERS[craftTier - 1] : null;
              return (
                <div className="flex flex-col gap-6">

                  {/* Hero */}
                  <div className="rounded-2xl border px-6 py-5 flex items-center gap-6"
                    style={{ background: 'rgba(15,23,42,0.85)', borderColor: 'rgba(251,191,36,0.2)' }}>
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#fbbf24' }}>🌾 Vayal — Farmland</div>
                      <div className="text-white font-black text-xl">Your Vayal Farmland</div>
                      <div className="text-5xl font-black mt-1" style={{ color: '#fbbf24' }}>{data?.vayal_count ?? 0}</div>
                    </div>
                    <div className="ml-auto flex flex-col items-end gap-2">
                      <div className="text-xs px-3 py-1.5 rounded-full font-bold"
                        style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.2)', color: '#fcd34d' }}>
                        Season 0 · Crafting open
                      </div>
                      {(data?.vayal_count ?? 0) > 0 && (
                        <div className="flex gap-3">
                          <div className="text-center px-3 py-1.5 rounded-xl"
                            style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.15)' }}>
                            <div className="text-[10px] font-bold uppercase tracking-widest mb-0.5" style={{ color: '#86efac' }}>VAYAL Balance</div>
                            <div className="text-sm font-black" style={{ color: '#4ade80' }}>{(data?.vayal_balance ?? 0).toFixed(2)}</div>
                          </div>
                          <div className="text-center px-3 py-1.5 rounded-xl"
                            style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.15)' }}>
                            <div className="text-[10px] font-bold uppercase tracking-widest mb-0.5" style={{ color: '#86efac' }}>Per Hour</div>
                            <div className="text-sm font-black" style={{ color: '#4ade80' }}>+{(data?.vayal_rate ?? 0).toFixed(0)}/hr</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Tier cards + WAX card */}
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Select a crafting tier</p>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                      {VAYAL_TIERS.map(t => (
                        <button key={t.n}
                          onClick={() => { setCraftTier(t.n); setCraftStatus('idle'); setCraftMsg(''); }}
                          className={`rounded-2xl p-4 text-left transition-all hover:scale-[1.02] ${t.locked ? 'opacity-50' : ''}`}
                          style={{ background: t.bg, border: `1px solid ${t.border}` }}>
                          <div className="text-2xl mb-2">{t.emoji}</div>
                          <div className="text-[10px] font-bold uppercase tracking-widest mb-0.5" style={{ color: t.color }}>Tier {t.n}</div>
                          <div className="text-white font-bold text-sm mb-1">{t.label}</div>
                          <div className="text-slate-500 text-xs leading-snug">{t.cost}</div>
                          {t.locked && <div className="text-[10px] mt-1.5 font-bold text-red-400">Locked</div>}
                        </button>
                      ))}
                      {/* WAX direct purchase card */}
                      <button
                        onClick={() => { setCraftTier(null); setShowWaxMintModal(true); }}
                        className="rounded-2xl p-4 text-left transition-all hover:scale-[1.02]"
                        style={{ background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.3)' }}>
                        <div className="text-2xl mb-2">💎</div>
                        <div className="text-[10px] font-bold uppercase tracking-widest mb-0.5" style={{ color: '#fcd34d' }}>Direct</div>
                        <div className="text-white font-bold text-sm mb-1">WAX</div>
                        <div className="text-slate-500 text-xs leading-snug">10 WAX</div>
                      </button>
                    </div>
                  </div>

                  {/* ── Stage 1 → 2 Upgrade Tiers ── */}
                  <div className="mt-6">
                    <div className="rounded-2xl border px-6 py-4 mb-3"
                      style={{ background: 'rgba(15,23,42,0.85)', borderColor: 'rgba(234,179,8,0.2)' }}>
                      <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#fbbf24' }}>🚜 Stage 1 → 2 Upgrade</div>
                      <div className="text-white font-black text-lg">Tilled Soil</div>
                      <div className="text-slate-400 text-xs mt-1 leading-relaxed">Earthworks, water channels, soil preparation — turning bare desert into workable farmland. Requires timber from the Atavi forest. Rewards are 3× Stage 1.</div>
                    </div>
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Upgrade tiers — select a Stage 1 plot to upgrade</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        { emoji: '🥗', label: 'Food + Logs', color: '#86efac',
                          bg: 'rgba(34,197,94,0.08)', border: 'rgba(34,197,94,0.3)',
                          lines: ['25,000 Food Packets', '100 Logs'] },
                        { emoji: '⚒️', label: '+ Shram', color: '#7dd3fc',
                          bg: 'rgba(56,189,248,0.08)', border: 'rgba(56,189,248,0.3)',
                          lines: ['15,000 Food Packets', '5 Shram', '60 Logs'] },
                        { emoji: '💧', label: '+ WARI', color: '#7dd3fc',
                          bg: 'rgba(56,189,248,0.08)', border: 'rgba(56,189,248,0.3)',
                          lines: ['7,500 Food Packets', '2 Shram', '2 WARI', '30 Logs'] },
                        { emoji: '🌍', label: '+ Bhoomi', color: '#c4b5fd',
                          bg: 'rgba(139,92,246,0.08)', border: 'rgba(139,92,246,0.3)',
                          lines: ['1,250 Food Packets', '6,250 Bhoomi', '10 Logs'] },
                      ].map(t => (
                        <div key={t.label}
                          className="rounded-2xl p-4 text-left opacity-80"
                          style={{ background: t.bg, border: `1px solid ${t.border}` }}>
                          <div className="text-2xl mb-2">{t.emoji}</div>
                          <div className="text-white font-bold text-sm mb-2">{t.label}</div>
                          {t.lines.map(l => (
                            <div key={l} className="text-slate-400 text-xs leading-relaxed">{l}</div>
                          ))}
                          <div className="text-[10px] mt-2 font-bold text-amber-400/60">Coming Soon</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Info row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { emoji: '📈', title: 'Food Bonus', body: 'Each Vayal farmland stage boosts your food production rate. Higher stages give larger multipliers.', color: '#fbbf24' },
                      { emoji: '🔑', title: 'Stage Progression', body: 'Farmland starts at Stage 0 and can be upgraded up to Stage 10 using resources crafted within Samatvam.', color: '#c4b5fd' },
                    ].map(tip => (
                      <div key={tip.title} className="rounded-2xl p-5" style={{ background: 'rgba(15,23,42,0.7)', border: `1px solid ${tip.color}20` }}>
                        <div className="text-2xl mb-2">{tip.emoji}</div>
                        <div className="text-white font-bold text-sm mb-1.5">{tip.title}</div>
                        <div className="text-slate-400 text-xs leading-relaxed">{tip.body}</div>
                      </div>
                    ))}
                  </div>

                  {/* Per-tier modal */}
                  {activeTier && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
                      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
                      onClick={e => { if (e.target === e.currentTarget && craftStatus !== 'sending') { setCraftTier(null); setCraftStatus('idle'); setCraftMsg(''); } }}>
                      <div className="w-full max-w-md rounded-3xl overflow-hidden"
                        style={{ background: 'linear-gradient(160deg, #0f172a 0%, #020617 100%)', border: `1px solid ${activeTier.border}` }}>

                        {/* Header */}
                        <div className="px-6 py-4 flex items-center justify-between border-b"
                          style={{ background: activeTier.hdr, borderColor: activeTier.hdrBorder }}>
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{activeTier.emoji}</span>
                            <div>
                              <div className="text-white font-black text-base">Tier {activeTier.n} — {activeTier.label}</div>
                              <div className="text-xs mt-0.5" style={{ color: activeTier.color }}>{activeTier.who}</div>
                            </div>
                          </div>
                          <button onClick={() => { setCraftTier(null); setCraftStatus('idle'); setCraftMsg(''); }}
                            disabled={craftStatus === 'sending'}
                            className="w-8 h-8 rounded-xl flex items-center justify-center text-slate-400 hover:text-white transition-colors disabled:opacity-40"
                            style={{ background: 'rgba(255,255,255,0.06)' }}>✕</button>
                        </div>

                        <div className="p-6 flex flex-col gap-5">

                          {/* Quantity selector */}
                          <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest mb-2 text-slate-500">How many farmlands?</div>
                            <div className="flex gap-2 items-center flex-wrap">
                              {['1','2','5','10'].map(v => (
                                <button key={v} onClick={() => { setCraftQty(v); setCraftStatus('idle'); setCraftMsg(''); }}
                                  className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                                  style={{ background: craftQty===v ? `${activeTier.color}33` : 'rgba(255,255,255,0.05)', border: `1px solid ${craftQty===v ? activeTier.border : 'rgba(255,255,255,0.1)'}`, color: craftQty===v ? activeTier.color : '#64748b' }}>
                                  {v}
                                </button>
                              ))}
                              <input type="number" min="1" max="100" value={craftQty}
                                onChange={e => { setCraftQty(e.target.value); setCraftStatus('idle'); setCraftMsg(''); }}
                                className="w-16 px-2 py-1.5 rounded-lg text-xs text-white text-center outline-none"
                                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }} />
                            </div>
                          </div>

                          {/* Cost breakdown (multiplied by qty) */}
                          {(() => {
                            const qty = Math.max(1, parseInt(craftQty) || 1);
                            return (
                              <div className="flex items-center gap-6 flex-wrap">
                                {activeTier.tokens.map((tk, i) => {
                                  const baseVal = parseInt(tk.val.replace(/,/g, ''));
                                  const total = baseVal * qty;
                                  return (
                                    <React.Fragment key={tk.unit}>
                                      {i > 0 && <span className="text-slate-600 font-bold text-lg">+</span>}
                                      <div className="text-center">
                                        <div className="text-3xl font-black" style={{ color: tk.c }}>{total.toLocaleString()}</div>
                                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">{tk.unit}</div>
                                        {qty > 1 && <div className="text-[9px] text-slate-600">{baseVal.toLocaleString()} × {qty}</div>}
                                      </div>
                                    </React.Fragment>
                                  );
                                })}
                              </div>
                            );
                          })()}

                          {/* Your current balances */}
                          {data && (() => {
                            const qty = Math.max(1, parseInt(craftQty) || 1);
                            const balances: { unit: string; have: number; need: number }[] = [];
                            activeTier.tokens.forEach(tk => {
                              const baseVal = parseInt(tk.val.replace(/,/g, ''));
                              const total = baseVal * qty;
                              if (tk.unit === 'Food Packets') balances.push({ unit: 'Food Packets', have: data.food_packets, need: total });
                              if (tk.unit === 'Shram')        balances.push({ unit: 'Shram', have: Math.floor(data.shram_balance ?? 0), need: total });
                              if (tk.unit === 'WARI')         balances.push({ unit: 'WARI', have: Math.floor(data.wari_balance ?? 0), need: total });
                              if (tk.unit === 'Bhoomi')       balances.push({ unit: 'Bhoomi', have: Math.floor(data.bhoomi_score), need: total });
                            });
                            return (
                              <div className="rounded-xl px-4 py-3 flex flex-wrap gap-4"
                                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                                {balances.map(b => {
                                  const ok = b.have >= b.need;
                                  return (
                                    <div key={b.unit} className="flex flex-col gap-0.5">
                                      <div className="text-[10px] uppercase tracking-widest text-slate-500">{b.unit}</div>
                                      <div className="text-sm font-bold" style={{ color: ok ? '#86efac' : '#fca5a5' }}>
                                        {b.have.toLocaleString()} <span className="text-slate-600 font-normal">/ {b.need.toLocaleString()}</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}

                          {/* Description */}
                          <p className="text-slate-400 text-sm leading-relaxed">{activeTier.desc}</p>

                          {/* Status / error message */}
                          {craftMsg && craftTier === activeTier.n && (
                            <div className="rounded-xl px-4 py-3 text-sm"
                              style={{
                                background: craftStatus === 'error' ? 'rgba(239,68,68,0.08)' : craftStatus === 'done' ? 'rgba(34,197,94,0.08)' : 'rgba(251,191,36,0.08)',
                                border: `1px solid ${craftStatus === 'error' ? 'rgba(239,68,68,0.2)' : craftStatus === 'done' ? 'rgba(34,197,94,0.2)' : 'rgba(251,191,36,0.2)'}`,
                                color: craftStatus === 'error' ? '#fca5a5' : craftStatus === 'done' ? '#86efac' : '#fcd34d',
                              }}>
                              {craftMsg}
                            </div>
                          )}

                          {/* Mint button */}
                          {activeTier.locked ? (
                            <div className="rounded-xl px-4 py-3 text-sm text-center font-bold"
                              style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#fca5a5' }}>
                              Locked — WARI token not yet deployed
                            </div>
                          ) : craftStatus === 'done' && craftTier === activeTier.n ? (
                            <button
                              onClick={() => { setCraftTier(null); setCraftStatus('idle'); setCraftMsg(''); setCraftQty('1'); }}
                              className="w-full py-3 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90"
                              style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)' }}>
                              Done ✓
                            </button>
                          ) : (
                            <button
                              onClick={() => handleCraftMint(activeTier.n)}
                              disabled={craftStatus === 'sending' || (() => {
                                if (!data) return true;
                                const qty = Math.max(1, parseInt(craftQty) || 1);
                                return activeTier.tokens.some(tk => {
                                  const need = parseInt(tk.val.replace(/,/g, '')) * qty;
                                  if (tk.unit === 'Food Packets') return data.food_packets < need;
                                  if (tk.unit === 'Shram')        return Math.floor(data.shram_balance ?? 0) < need;
                                  if (tk.unit === 'WARI')         return Math.floor(data.wari_balance ?? 0) < need;
                                  if (tk.unit === 'Bhoomi')       return Math.floor(data.bhoomi_score) < need;
                                  return false;
                                });
                              })()}
                              className="w-full py-3 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                              style={{ background: `linear-gradient(135deg, ${activeTier.color}99, ${activeTier.color}66)` }}>
                              {craftStatus === 'sending' && craftTier === activeTier.n ? 'Minting…' : `Mint ${Math.max(1, parseInt(craftQty)||1)} Farmland${Math.max(1,parseInt(craftQty)||1)>1?'s':''}`}
                            </button>
                          )}

                        </div>
                      </div>
                    </div>
                  )}

                  {/* WAX direct mint modal */}
                  {showWaxMintModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
                      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
                      onClick={e => { if (e.target === e.currentTarget && waxMintStatus !== 'sending' && waxMintStatus !== 'verifying') { setShowWaxMintModal(false); setWaxMintStatus('idle'); setWaxMintMsg(''); setWaxMintQty('1'); } }}>
                      <div className="w-full max-w-md rounded-3xl overflow-hidden"
                        style={{ background: 'linear-gradient(160deg, #0f172a 0%, #020617 100%)', border: '1px solid rgba(234,179,8,0.35)' }}>

                        {/* Header */}
                        <div className="px-6 py-4 flex items-center justify-between border-b"
                          style={{ background: 'rgba(234,179,8,0.07)', borderColor: 'rgba(234,179,8,0.15)' }}>
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">💎</span>
                            <div>
                              <div className="text-white font-black text-base">Direct WAX Purchase</div>
                              <div className="text-xs mt-0.5" style={{ color: '#fcd34d' }}>No in-game resources required</div>
                            </div>
                          </div>
                          <button
                            onClick={() => { setShowWaxMintModal(false); setWaxMintStatus('idle'); setWaxMintMsg(''); setWaxMintQty('1'); }}
                            disabled={waxMintStatus === 'sending' || waxMintStatus === 'verifying'}
                            className="w-8 h-8 rounded-xl flex items-center justify-center text-slate-400 hover:text-white transition-colors disabled:opacity-40"
                            style={{ background: 'rgba(255,255,255,0.06)' }}>✕</button>
                        </div>

                        <div className="p-6 flex flex-col gap-5">

                          {/* Quantity selector */}
                          <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest mb-2 text-slate-500">How many farmlands?</div>
                            <div className="flex gap-2 items-center flex-wrap">
                              {['1','2','5','10'].map(v => (
                                <button key={v} onClick={() => { setWaxMintQty(v); setWaxMintStatus('idle'); setWaxMintMsg(''); }}
                                  className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                                  style={{ background: waxMintQty===v ? 'rgba(234,179,8,0.2)' : 'rgba(255,255,255,0.05)', border: `1px solid ${waxMintQty===v ? 'rgba(234,179,8,0.5)' : 'rgba(255,255,255,0.1)'}`, color: waxMintQty===v ? '#fcd34d' : '#64748b' }}>
                                  {v}
                                </button>
                              ))}
                              <input type="number" min="1" max="100" value={waxMintQty}
                                onChange={e => { setWaxMintQty(e.target.value); setWaxMintStatus('idle'); setWaxMintMsg(''); }}
                                className="w-16 px-2 py-1.5 rounded-lg text-xs text-white text-center outline-none"
                                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }} />
                            </div>
                          </div>

                          {/* Cost display */}
                          {(() => {
                            const qty = Math.max(1, parseInt(waxMintQty) || 1);
                            return (
                              <div className="flex items-center gap-3">
                                <div className="text-center">
                                  <div className="text-4xl font-black" style={{ color: '#fcd34d' }}>{qty * 10}</div>
                                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">WAX{qty > 1 && <span className="text-slate-600"> (10 × {qty})</span>}</div>
                                </div>
                                <div className="text-slate-600 font-bold text-lg">=</div>
                                <div className="text-center">
                                  <div className="text-4xl font-black" style={{ color: '#86efac' }}>{qty}</div>
                                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Farmland{qty > 1 ? 's' : ''}</div>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Description */}
                          <p className="text-slate-400 text-sm leading-relaxed">
                            Send WAX from your connected wallet to receive Vayal farmlands. The transaction is verified on-chain and your farmlands are queued immediately.
                          </p>

                          {/* Wallet status */}
                          {!waxActor ? (
                            <div className="rounded-xl px-4 py-3 text-sm text-center"
                              style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#fca5a5' }}>
                              No WAX wallet connected — visit Atavi Forest to connect your wallet first
                            </div>
                          ) : (
                            <div className="rounded-xl px-4 py-3 flex items-center gap-3"
                              style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)' }}>
                              <span className="text-green-400 text-sm">✓</span>
                              <div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-500">Connected wallet</div>
                                <div className="text-green-300 text-sm font-bold font-mono">{waxActor}</div>
                              </div>
                            </div>
                          )}

                          {/* Status message */}
                          {waxMintMsg && (
                            <div className="rounded-xl px-4 py-3 text-sm"
                              style={{
                                background: waxMintStatus === 'error' ? 'rgba(239,68,68,0.08)' : waxMintStatus === 'done' ? 'rgba(34,197,94,0.08)' : 'rgba(251,191,36,0.08)',
                                border: `1px solid ${waxMintStatus === 'error' ? 'rgba(239,68,68,0.2)' : waxMintStatus === 'done' ? 'rgba(34,197,94,0.2)' : 'rgba(251,191,36,0.2)'}`,
                                color: waxMintStatus === 'error' ? '#fca5a5' : waxMintStatus === 'done' ? '#86efac' : '#fcd34d',
                              }}>
                              {waxMintMsg}
                            </div>
                          )}

                          {/* Action buttons */}
                          {waxMintStatus === 'done' ? (
                            <button
                              onClick={() => { setShowWaxMintModal(false); setWaxMintStatus('idle'); setWaxMintMsg(''); setWaxMintQty('1'); }}
                              className="w-full py-3 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90"
                              style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)' }}>
                              Done
                            </button>
                          ) : (
                            <button
                              onClick={handleBuyVayalWax}
                              disabled={!waxActor || waxMintStatus === 'sending' || waxMintStatus === 'verifying'}
                              className="w-full py-3 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                              style={{ background: 'linear-gradient(135deg, #b45309, #92400e)' }}>
                              {waxMintStatus === 'sending' ? 'Sending transaction…' : waxMintStatus === 'verifying' ? 'Verifying on-chain…' : `Send ${Math.max(1, parseInt(waxMintQty)||1) * 10} WAX`}
                            </button>
                          )}

                        </div>
                      </div>
                    </div>
                  )}

                  {/* VAYAL Withdrawal Panel */}
                  {(data?.vayal_balance ?? 0) > 0 && (
                    <div className="rounded-2xl border px-6 py-5 flex flex-col gap-4"
                      style={{ background: 'rgba(15,23,42,0.85)', borderColor: 'rgba(34,197,94,0.2)' }}>
                      <div>
                        <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#4ade80' }}>💸 Withdraw VAYAL to WAX Chain</div>
                        <div className="text-slate-400 text-sm">Send your in-game VAYAL to your WAX wallet. 1% fee — you receive 99%.</div>
                      </div>
                      <div className="flex gap-3 items-end">
                        <div className="flex-1">
                          <div className="text-[10px] font-bold uppercase tracking-widest mb-1.5 text-slate-500">Amount to withdraw</div>
                          <div className="relative">
                            <input
                              type="number"
                              min="1"
                              max={data?.vayal_balance ?? 0}
                              step="1"
                              value={vayalWithdrawAmount}
                              onChange={e => { setVayalWithdrawAmount(e.target.value); setVayalWithdrawStatus('idle'); setVayalWithdrawMsg(''); }}
                              placeholder={`Max ${(data?.vayal_balance ?? 0).toFixed(2)}`}
                              className="w-full px-3 py-2 rounded-xl text-sm font-bold text-white outline-none"
                              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(34,197,94,0.2)' }}
                            />
                          </div>
                          {vayalWithdrawAmount && !isNaN(parseFloat(vayalWithdrawAmount)) && parseFloat(vayalWithdrawAmount) >= 1 && (
                            <div className="text-[11px] mt-1.5" style={{ color: '#86efac' }}>
                              You receive: <strong>{(parseFloat(vayalWithdrawAmount) * 0.99).toFixed(8)} VAYAL</strong> · Fee: {(parseFloat(vayalWithdrawAmount) * 0.01).toFixed(8)} VAYAL
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => setVayalWithdrawAmount((data?.vayal_balance ?? 0).toFixed(2))}
                          className="px-3 py-2 rounded-xl text-xs font-bold transition-colors"
                          style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', color: '#4ade80' }}>
                          Max
                        </button>
                        <button
                          onClick={handleVayalWithdraw}
                          disabled={vayalWithdrawStatus === 'sending'}
                          className="px-5 py-2 rounded-xl text-sm font-bold transition-all"
                          style={{ background: vayalWithdrawStatus === 'sending' ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)', color: '#4ade80', opacity: vayalWithdrawStatus === 'sending' ? 0.6 : 1 }}>
                          {vayalWithdrawStatus === 'sending' ? 'Submitting…' : 'Withdraw'}
                        </button>
                      </div>
                      {vayalWithdrawMsg && (
                        <div className="text-sm px-4 py-2.5 rounded-xl font-medium"
                          style={{
                            background: vayalWithdrawStatus === 'error' ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.08)',
                            border: `1px solid ${vayalWithdrawStatus === 'error' ? 'rgba(239,68,68,0.2)' : 'rgba(34,197,94,0.2)'}`,
                            color: vayalWithdrawStatus === 'error' ? '#fca5a5' : '#86efac',
                          }}>
                          {vayalWithdrawMsg}
                        </div>
                      )}
                      {/* Withdrawal history */}
                      {vayalWithdrawHistory.length > 0 && (
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-widest mb-2 text-slate-500">Recent Withdrawals</div>
                          <div className="flex flex-col gap-2">
                            {vayalWithdrawHistory.slice(0, 5).map((row: any) => (
                              <div key={row.id} className="flex items-center justify-between text-xs px-3 py-2 rounded-xl"
                                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                                <span className="text-slate-400">{new Date(row.created_at).toLocaleDateString()}</span>
                                <span className="font-bold" style={{ color: '#4ade80' }}>{parseFloat(row.payout_vayal).toFixed(4)} VAYAL</span>
                                <span className={`font-bold uppercase tracking-wide ${row.status === 'completed' ? 'text-green-400' : row.status === 'failed' ? 'text-red-400' : 'text-yellow-400'}`}>
                                  {row.status}
                                </span>
                                {row.tx_id && (
                                  <a href={`https://waxblock.io/transaction/${row.tx_id}`} target="_blank" rel="noreferrer"
                                    className="text-blue-400 hover:text-blue-300 underline">tx</a>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                </div>
              );
            })()}

            {/* ── ATAVI TAB ─────────────────────────────────────────── */}
            {tab === 'atavi' && myAcct && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                {/* ── New City notice ── */}
                <div style={{ background: 'linear-gradient(90deg, rgba(52,211,153,0.08), rgba(52,211,153,0.03))', border: '1px solid rgba(52,211,153,0.25)', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>🏙️</span>
                  <div>
                    <div style={{ fontWeight: 800, color: '#34d399', fontSize: 13, marginBottom: 4 }}>New City is now opening — Season 1 begins</div>
                    <div style={{ color: '#94a3b8', fontSize: 12, lineHeight: 1.6 }}>
                      Plant your Atavi Forest, earn food tokens, and contribute to the colony. New territories and council systems are now live across the city map.
                    </div>
                  </div>
                </div>

                {/* ── Go to Map button ── */}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={onOpenAtaviForest}
                    style={{ padding: '7px 18px', borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: 'pointer', background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.35)', color: '#fbbf24', transition: 'all 0.15s' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(251,191,36,0.2)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(251,191,36,0.1)'; }}
                  >⬡ Go to Map →</button>
                </div>

                {/* ── Stats grid ── */}
                {data && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                    {/* Row 1: Atavi Plots | Bhoomi | Seeds */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
                      <div style={{ background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '14px 16px' }}>
                        <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6 }}>🗺️ Atavi Plots</div>
                        <div style={{ fontSize: 26, color: 'white', fontWeight: 900, fontFamily: 'monospace' }}>{typeof (ataviStats?.forest_plots ?? '…') === 'number' ? (ataviStats!.forest_plots).toLocaleString() : (ataviStats?.forest_plots ?? '…')}</div>
                      </div>
                      <div style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 12, padding: '14px 16px' }}>
                        <div style={{ fontSize: 11, color: '#a78bfa', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6 }}>🪙 Bhoomi</div>
                        <div style={{ fontSize: 26, color: '#c4b5fd', fontWeight: 900, fontFamily: 'monospace' }}>{data.bhoomi_score.toFixed(1)}</div>
                        <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 4 }}>+{data.bhoomi_rate.toFixed(2)} / hr</div>
                      </div>
                      <div style={{ background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '14px 16px' }}>
                        <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6 }}>🌱 Seeds</div>
                        <div style={{ fontSize: 26, color: 'white', fontWeight: 900, fontFamily: 'monospace' }}>{ataviStats ? ataviStats.total_seeds_bought.toLocaleString() : '…'}</div>
                        {ataviStats && <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 6, fontFamily: 'monospace' }}>{ataviStats.total_seeds_bought} total · {ataviStats.seeds_sown} sown · {ataviStats.seeds_in_wallet} wallet</div>}
                      </div>
                    </div>

                    {/* Row 2: Logs Inventory (collapsible) */}
                    <div style={{ background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(234,179,8,0.15)', borderRadius: 12, overflow: 'hidden' }}>
                      <div onClick={() => setLogsOpen(o => !o)} style={{ padding: '10px 16px', borderBottom: logsOpen ? '1px solid rgba(234,179,8,0.1)' : 'none', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <span>🪵</span>
                        <span style={{ fontSize: 10, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', color: '#ca8a04' }}>Logs Inventory</span>
                        <span style={{ marginLeft: 'auto', fontSize: 10, color: '#475569' }}>{Object.values(logs).reduce((a, b) => a + b, 0)} total</span>
                        <span style={{ fontSize: 10, color: '#475569', marginLeft: 8 }}>{logsOpen ? '▲' : '▼'}</span>
                      </div>
                      {logsOpen && (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                              <th style={{ padding: '8px 16px', textAlign: 'left', fontSize: 9, color: '#475569', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase' }}>Species</th>
                              <th style={{ padding: '8px 16px', textAlign: 'right', fontSize: 9, color: '#475569', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase' }}>Logs</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(logs).filter(([, qty]) => qty > 0).map(([si, qty]) => {
                              const info = TREE_HARVEST_INFO[parseInt(si)];
                              if (!info) return null;
                              return (
                                <tr key={si} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                  <td style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{ fontSize: 18 }}>{info.emoji}</span>
                                    <span style={{ fontSize: 12, color: 'white', fontWeight: 600 }}>{info.name}</span>
                                  </td>
                                  <td style={{ padding: '10px 16px', textAlign: 'right', fontSize: 16, color: '#fbbf24', fontWeight: 900, fontFamily: 'monospace' }}>{qty}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>

                    {/* Row 3: Planted Trees | Rental Trees | Food */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
                      <div style={{ background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '14px 16px' }}>
                        <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6 }}>🌳 Planted Trees (owned-bhoomi)</div>
                        <div style={{ fontSize: 26, color: 'white', fontWeight: 900, fontFamily: 'monospace' }}>{ataviStats ? (ataviStats.owned_land_trees + ataviStats.trees_on_rental_land).toLocaleString() : '…'}</div>
                        {ataviStats && <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 6, fontFamily: 'monospace' }}>{ataviStats.owned_land_trees} owned land · {ataviStats.trees_on_rental_land} rental land</div>}
                      </div>
                      <div style={{ background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '14px 16px' }}>
                        <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6 }}>🌰 Rental Trees on My Land</div>
                        <div style={{ fontSize: 26, color: 'white', fontWeight: 900, fontFamily: 'monospace' }}>{ataviStats ? ataviStats.rental_on_land.toLocaleString() : '…'}</div>
                      </div>
                      <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 12, padding: '14px 16px' }}>
                        <div style={{ fontSize: 11, color: '#6ee7b7', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6 }}>🍃 Food</div>
                        <div style={{ fontSize: 26, color: '#34d399', fontWeight: 900, fontFamily: 'monospace' }}>{data.food_score.toFixed(1)}</div>
                        <div style={{ fontSize: 11, color: '#059669', marginTop: 4, fontWeight: 700 }}>+{data.food_rate.toFixed(2)} / hr total</div>
                        {ataviStats && ataviStats.food_rate_total > 0 && (
                          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {[
                              { label: '🪓 Own land (bhoomi)',       rate: ataviStats.food_rate_own,            pct: '100%', color: '#34d399' },
                              { label: '🌿 Rental land (bhoomi)',    rate: ataviStats.food_rate_rental_bhoomi,  pct:  '80%', color: '#a78bfa' },
                              { label: '🌱 Rental land (seeds)',     rate: ataviStats.food_rate_rental_seed,    pct:  '80%', color: '#6ee7b7' },
                              { label: '🏠 Rent income',             rate: ataviStats.food_rate_rent_income,    pct:  '20%', color: '#fbbf24' },
                            ].map(s => (
                              <div key={s.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                <span style={{ fontSize: 10, color: '#64748b' }}>{s.label}</span>
                                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                  <span style={{ fontSize: 9, color: '#334155', fontFamily: 'monospace' }}>{s.pct}</span>
                                  <span style={{ fontSize: 11, color: s.rate > 0 ? s.color : '#334155', fontWeight: 800, fontFamily: 'monospace' }}>+{s.rate.toFixed(2)}</span>
                                </div>
                              </div>
                            ))}
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
                              <span style={{ fontSize: 10, color: '#475569', fontWeight: 700 }}>Total (forest)</span>
                              <span style={{ fontSize: 11, color: '#34d399', fontWeight: 900, fontFamily: 'monospace' }}>+{ataviStats.food_rate_total.toFixed(2)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ── Plant Trees by Bhoomi — combined panel ── */}
                    <div style={{ background: 'rgba(15,23,42,0.9)', border: '1px solid rgba(217,119,6,0.2)', borderRadius: 12, padding: '18px 20px' }}>
                      {/* Header */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                        <div style={{ fontSize: 10, color: '#fbbf24', fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase' }}>🪙 Plant Trees by Bhoomi</div>
                        {data && <div style={{ fontSize: 11, color: '#d97706', fontWeight: 700 }}>Bhoomi: <span style={{ color: '#fbbf24', fontFamily: 'monospace' }}>{data.bhoomi_score.toFixed(1)}</span></div>}
                      </div>

                      {/* Shared species picker */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                        {[
                          { id: 'amaranth', emoji: '🌱', name: 'Amaranth', cost: 10  },
                          { id: 'vetiver',  emoji: '🌾', name: 'Vetiver',  cost: 20  },
                          { id: 'opuntia',  emoji: '🌵', name: 'Opuntia',  cost: 35  },
                          { id: 'moringa',  emoji: '🌿', name: 'Moringa',  cost: 50  },
                          { id: 'jujube',   emoji: '🫐', name: 'Jujube',   cost: 70  },
                          { id: 'acacia',   emoji: '🌳', name: 'Acacia',   cost: 90  },
                          { id: 'mesquite', emoji: '🌲', name: 'Mesquite', cost: 120 },
                          { id: 'neem',     emoji: '🌴', name: 'Neem',     cost: 160 },
                        ].map(sp => {
                          const active = bhoomiBulkSpecies === sp.id;
                          const canAfford = data ? data.bhoomi_score >= sp.cost : false;
                          return (
                            <button key={sp.id} onClick={() => { setBhoomiBulkSpecies(sp.id); setBhoomiBulkQty(1); setRentalSpecies(sp.id); setRentalQty(1); }}
                              style={{ padding: '6px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s', opacity: canAfford ? 1 : 0.4,
                                background: active ? 'rgba(217,119,6,0.2)' : 'rgba(255,255,255,0.04)',
                                border: `1px solid ${active ? 'rgba(245,158,11,0.6)' : 'rgba(255,255,255,0.08)'}`,
                                color: active ? '#fbbf24' : '#64748b' }}>
                              {sp.emoji} {sp.name} <span style={{ fontSize: 9, opacity: 0.7 }}>🪙{sp.cost}</span>
                            </button>
                          );
                        })}
                      </div>

                      {/* Shared qty + cost */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                        <input type="number" min={1}
                          value={bhoomiBulkQty}
                          onChange={e => { const v = Math.max(1, Math.floor(Number(e.target.value) || 1)); setBhoomiBulkQty(v); setRentalQty(v); }}
                          style={{ width: 80, padding: '8px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'white', fontWeight: 900, fontSize: 14, fontFamily: 'monospace', textAlign: 'center', outline: 'none' }}
                        />
                        <button onClick={() => {
                          const max = data ? Math.max(1, Math.floor(data.bhoomi_score / (BHOOMI_SEED_COST[bhoomiBulkSpecies] ?? 10))) : 1;
                          setBhoomiBulkQty(max); setRentalQty(max);
                        }} style={{ padding: '8px 12px', borderRadius: 8, fontSize: 11, fontWeight: 800, cursor: 'pointer', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', color: '#fbbf24' }}>
                          Max
                        </button>
                        <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace', flex: 1 }}>
                          = 🪙 {(bhoomiBulkQty * (BHOOMI_SEED_COST[bhoomiBulkSpecies] ?? 0)).toLocaleString()}
                        </span>
                      </div>

                      {/* Two buttons side by side */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                        <button
                          disabled={plantOwnedBusy || !data || data.bhoomi_score < (BHOOMI_SEED_COST[bhoomiBulkSpecies] ?? 0)}
                          onClick={async () => {
                            if (!myAcct || plantOwnedBusy) return;
                            setPlantOwnedBusy(true); setPlantOwnedMsg('');
                            try {
                              const r = await fetch('/samatvam/api/samatvam/atavi-forest/plant-owned', {
                                method: 'POST', headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ wax_account: myAcct, species: bhoomiBulkSpecies, quantity: bhoomiBulkQty }),
                              });
                              const d = await r.json();
                              if (d.success) {
                                setPlantOwnedMsg(`✅ Planted ${d.planted} · 🪙 ${d.bhoomi_spent} spent${d.all_full ? ' · All slots full' : ` · ${d.remaining_owned_slots} left`}`);
                                if (myAcct) fetchData(myAcct);
                              } else { setPlantOwnedMsg(`❌ ${d.error}`); }
                            } catch { setPlantOwnedMsg('❌ Network error'); }
                            finally { setPlantOwnedBusy(false); }
                          }}
                          style={{ padding: '10px 12px', borderRadius: 8, fontSize: 12, fontWeight: 800,
                            cursor: (plantOwnedBusy || !data || data.bhoomi_score < (BHOOMI_SEED_COST[bhoomiBulkSpecies] ?? 0)) ? 'default' : 'pointer',
                            background: plantOwnedBusy ? 'rgba(217,119,6,0.06)' : 'rgba(217,119,6,0.18)',
                            border: `1px solid rgba(217,119,6,${plantOwnedBusy ? 0.1 : 0.4})`,
                            color: plantOwnedBusy ? 'rgba(251,191,36,0.3)' : '#fbbf24', transition: 'all 0.2s' }}>
                          {plantOwnedBusy ? '⏳ Planting…' : `🌳 Plant ${bhoomiBulkQty} on Owned Plots`}
                        </button>
                        <button
                          disabled={rentalSubmitting || !data || data.bhoomi_score < (BHOOMI_SEED_COST[bhoomiBulkSpecies] ?? 0)}
                          onClick={async () => {
                            if (!myAcct || rentalSubmitting) return;
                            setRentalSubmitting(true); setRentalMsg('');
                            try {
                              const r = await fetch('/samatvam/api/samatvam/atavi-forest/queue-plant-rental-bhoomi', {
                                method: 'POST', headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ wax_account: myAcct, species: bhoomiBulkSpecies, quantity: bhoomiBulkQty }),
                              });
                              const d = await r.json();
                              if (d.success) {
                                setRentalMsg(`✅ Queued ${d.queued} · 🪙 ${d.bhoomi_spent} spent`);
                                setRentalJob({ id: d.job_id, species: bhoomiBulkSpecies, quantity: bhoomiBulkQty, planted: 0, status: 'pending', error: null });
                                if (myAcct) fetchData(myAcct);
                              } else { setRentalMsg(`❌ ${d.error}`); }
                            } catch { setRentalMsg('❌ Network error'); }
                            finally { setRentalSubmitting(false); }
                          }}
                          style={{ padding: '10px 12px', borderRadius: 8, fontSize: 12, fontWeight: 800,
                            cursor: (rentalSubmitting || !data || data.bhoomi_score < (BHOOMI_SEED_COST[bhoomiBulkSpecies] ?? 0)) ? 'default' : 'pointer',
                            background: rentalSubmitting ? 'rgba(139,92,246,0.06)' : 'rgba(139,92,246,0.18)',
                            border: `1px solid rgba(139,92,246,${rentalSubmitting ? 0.1 : 0.4})`,
                            color: rentalSubmitting ? 'rgba(167,139,250,0.3)' : '#a78bfa', transition: 'all 0.2s' }}>
                          {rentalSubmitting ? '⏳ Queuing…' : `🌿 Plant ${bhoomiBulkQty} on Rental Plots`}
                        </button>
                      </div>

                      {/* Result messages */}
                      {plantOwnedMsg && <p style={{ fontSize: 11, marginTop: 4, fontWeight: 700, color: plantOwnedMsg.startsWith('✅') ? '#6ee7b7' : '#f87171' }}>{plantOwnedMsg}</p>}
                      {rentalMsg && <p style={{ fontSize: 11, marginTop: 4, fontWeight: 700, color: rentalMsg.startsWith('✅') ? '#6ee7b7' : '#f87171' }}>{rentalMsg}</p>}

                      {/* Rental job progress */}
                      {rentalJob && (
                        <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(139,92,246,0.15)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>Rental Job #{rentalJob.id} · {rentalJob.species}</span>
                            <span style={{ fontSize: 11, fontWeight: 900, color: rentalJob.status === 'done' ? '#34d399' : rentalJob.status === 'processing' ? '#a78bfa' : '#94a3b8' }}>
                              {rentalJob.status === 'done' ? '✅ Done' : rentalJob.status === 'processing' ? '⏳ Planting…' : '🕐 Pending'}
                            </span>
                          </div>
                          <div style={{ height: 5, background: 'rgba(255,255,255,0.06)', borderRadius: 999, overflow: 'hidden', marginBottom: 4 }}>
                            <div style={{ height: '100%', borderRadius: 999, transition: 'width 0.5s ease',
                              width: `${Math.min(100, (rentalJob.planted / rentalJob.quantity) * 100)}%`,
                              background: rentalJob.status === 'done' ? '#34d399' : 'linear-gradient(90deg,#7c3aed,#a78bfa)' }} />
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>
                            {rentalJob.planted} / {rentalJob.quantity} planted
                            {rentalJob.error && <span style={{ color: '#f87171', marginLeft: 8 }}>· {rentalJob.error}</span>}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* ── Plant Trees by Seeds ── */}
                    <div style={{ background: 'rgba(15,23,42,0.9)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: 12, padding: '18px 20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                        <div style={{ fontSize: 10, color: '#34d399', fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase' }}>🌱 Plant Trees by Seeds</div>
                        {ataviStats && <div style={{ fontSize: 11, color: '#059669', fontWeight: 700 }}>Seeds in wallet: <span style={{ color: '#34d399', fontFamily: 'monospace' }}>{ataviStats.seeds_in_wallet}</span></div>}
                      </div>

                      {/* Species picker */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                        {[
                          { id: 'amaranth', emoji: '🌱', name: 'Amaranth' },
                          { id: 'vetiver',  emoji: '🌾', name: 'Vetiver'  },
                          { id: 'opuntia',  emoji: '🌵', name: 'Opuntia'  },
                          { id: 'moringa',  emoji: '🌿', name: 'Moringa'  },
                          { id: 'jujube',   emoji: '🫐', name: 'Jujube'   },
                          { id: 'acacia',   emoji: '🌳', name: 'Acacia'   },
                          { id: 'mesquite', emoji: '🌲', name: 'Mesquite' },
                          { id: 'neem',     emoji: '🌴', name: 'Neem'     },
                        ].map(sp => {
                          const active = seedPlantSpecies === sp.id;
                          return (
                            <button key={sp.id} onClick={() => { setSeedPlantSpecies(sp.id); setSeedPlantQty(1); }}
                              style={{ padding: '6px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s',
                                background: active ? 'rgba(52,211,153,0.15)' : 'rgba(255,255,255,0.04)',
                                border: `1px solid ${active ? 'rgba(52,211,153,0.5)' : 'rgba(255,255,255,0.08)'}`,
                                color: active ? '#34d399' : '#64748b' }}>
                              {sp.emoji} {sp.name}
                            </button>
                          );
                        })}
                      </div>

                      {/* Qty + max */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                        <input type="number" min={1}
                          value={seedPlantQty}
                          onChange={e => setSeedPlantQty(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
                          style={{ width: 80, padding: '8px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'white', fontWeight: 900, fontSize: 14, fontFamily: 'monospace', textAlign: 'center', outline: 'none' }}
                        />
                        <button onClick={() => setSeedPlantQty(ataviStats?.seeds_in_wallet ?? 1)}
                          style={{ padding: '8px 12px', borderRadius: 8, fontSize: 11, fontWeight: 800, cursor: 'pointer', background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)', color: '#34d399' }}>
                          Max
                        </button>
                        <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace', flex: 1 }}>
                          = {seedPlantQty} seed{seedPlantQty !== 1 ? 's' : ''}
                        </span>
                      </div>

                      <button
                        disabled={seedPlantSubmitting || !ataviStats || ataviStats.seeds_in_wallet < 1}
                        onClick={async () => {
                          if (!myAcct || seedPlantSubmitting) return;
                          setSeedPlantSubmitting(true); setSeedPlantMsg('');
                          try {
                            const r = await fetch('/samatvam/api/samatvam/atavi-forest/queue-plant', {
                              method: 'POST', headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ wax_account: myAcct, species: seedPlantSpecies, quantity: seedPlantQty }),
                            });
                            const d = await r.json();
                            if (d.success) {
                              setSeedPlantMsg(`✅ Queued ${seedPlantQty} ${seedPlantSpecies} · ${d.seeds_in_wallet} seeds remaining`);
                              setSeedPlantJob({ id: d.job_id, species: seedPlantSpecies, quantity: seedPlantQty, planted: 0, status: 'pending', error: null });
                              if (myAcct) fetchData(myAcct);
                            } else { setSeedPlantMsg(`❌ ${d.error}`); }
                          } catch { setSeedPlantMsg('❌ Network error'); }
                          finally { setSeedPlantSubmitting(false); }
                        }}
                        style={{ width: '100%', padding: '10px 16px', borderRadius: 8, fontSize: 12, fontWeight: 800,
                          cursor: (seedPlantSubmitting || !ataviStats || ataviStats.seeds_in_wallet < 1) ? 'default' : 'pointer',
                          background: seedPlantSubmitting ? 'rgba(52,211,153,0.04)' : 'rgba(52,211,153,0.14)',
                          border: `1px solid rgba(52,211,153,${seedPlantSubmitting ? 0.1 : 0.4})`,
                          color: seedPlantSubmitting ? 'rgba(52,211,153,0.3)' : '#34d399', transition: 'all 0.2s' }}>
                        {seedPlantSubmitting ? '⏳ Queuing…' : `🌱 Plant ${seedPlantQty} ${seedPlantSpecies} on Rental Plots`}
                      </button>

                      {seedPlantMsg && <p style={{ fontSize: 11, marginTop: 10, fontWeight: 700, color: seedPlantMsg.startsWith('✅') ? '#6ee7b7' : '#f87171' }}>{seedPlantMsg}</p>}

                      {/* Job progress */}
                      {seedPlantJob && (
                        <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(52,211,153,0.12)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>Job #{seedPlantJob.id} · {seedPlantJob.species}</span>
                            <span style={{ fontSize: 11, fontWeight: 900, color: seedPlantJob.status === 'done' ? '#34d399' : seedPlantJob.status === 'processing' ? '#fbbf24' : '#94a3b8' }}>
                              {seedPlantJob.status === 'done' ? '✅ Done' : seedPlantJob.status === 'processing' ? '⏳ Planting…' : '🕐 Pending'}
                            </span>
                          </div>
                          <div style={{ height: 5, background: 'rgba(255,255,255,0.06)', borderRadius: 999, overflow: 'hidden', marginBottom: 4 }}>
                            <div style={{ height: '100%', borderRadius: 999, transition: 'width 0.5s ease',
                              width: `${Math.min(100, (seedPlantJob.planted / seedPlantJob.quantity) * 100)}%`,
                              background: seedPlantJob.status === 'done' ? '#34d399' : 'linear-gradient(90deg,#059669,#34d399)' }} />
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>
                            {seedPlantJob.planted} / {seedPlantJob.quantity} planted
                            {seedPlantJob.error && <span style={{ color: '#f87171', marginLeft: 8 }}>· {seedPlantJob.error}</span>}
                          </div>
                        </div>
                      )}
                    </div>

                  </div>
                )}

                {/* ── Harvest Trees Panel ── */}
                {(() => {
                  const SPECIES = [
                    { id: 'amaranth', emoji: '🌱', label: 'Amaranth' },
                    { id: 'vetiver',  emoji: '🌾', label: 'Vetiver'  },
                    { id: 'opuntia',  emoji: '🌵', label: 'Opuntia'  },
                    { id: 'moringa',  emoji: '🌿', label: 'Moringa'  },
                    { id: 'jujube',   emoji: '🫐', label: 'Jujube'   },
                    { id: 'acacia',   emoji: '🌳', label: 'Acacia'   },
                    { id: 'mesquite', emoji: '🌲', label: 'Mesquite' },
                    { id: 'neem',     emoji: '🌴', label: 'Neem'     },
                  ];
                  const sel = matureCounts[harvestSpecies] ?? { own: 0, rental_bhoomi: 0, rental_seed: 0 };
                  const anyBusy = harvestOwnBusy || harvestBhoomiBusy || harvestSeedBusy;
                  return (
                    <div style={{ background: 'rgba(15,23,42,0.9)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 12, padding: '18px 20px', marginBottom: 14 }}>
                      <div style={{ fontSize: 10, color: '#fbbf24', fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 14 }}>🪓 Harvest Trees</div>

                      {/* Species picker */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                        {SPECIES.map(sp => {
                          const c = matureCounts[sp.id] ?? { own: 0, rental_bhoomi: 0, rental_seed: 0 };
                          const total = c.own + c.rental_bhoomi + c.rental_seed;
                          const active = harvestSpecies === sp.id;
                          return (
                            <button key={sp.id} onClick={() => setHarvestSpecies(sp.id)}
                              style={{ padding: '7px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s', textAlign: 'left',
                                background: active ? 'rgba(251,191,36,0.15)' : 'rgba(255,255,255,0.04)',
                                border: `1px solid ${active ? 'rgba(251,191,36,0.5)' : 'rgba(255,255,255,0.08)'}`,
                                color: active ? '#fbbf24' : '#64748b' }}>
                              <div>{sp.emoji} {sp.label}</div>
                              {matureCountsLoading ? <div style={{ fontSize: 9, marginTop: 2, color: '#475569' }}>…</div> : total > 0 ? (
                                <div style={{ fontSize: 9, marginTop: 2, color: '#64748b', fontFamily: 'monospace' }}>
                                  {c.own > 0 && <span style={{ color: '#34d399' }}>{c.own}own </span>}
                                  {c.rental_bhoomi > 0 && <span style={{ color: '#a78bfa' }}>{c.rental_bhoomi}bh </span>}
                                  {c.rental_seed > 0 && <span style={{ color: '#6ee7b7' }}>{c.rental_seed}sd</span>}
                                </div>
                              ) : <div style={{ fontSize: 9, marginTop: 2, color: '#334155' }}>0 mature</div>}
                            </button>
                          );
                        })}
                      </div>

                      {/* Qty row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                        <span style={{ fontSize: 11, color: '#64748b' }}>Harvest qty:</span>
                        <input type="number" min={0} value={harvestQty}
                          onChange={e => setHarvestQty(Math.max(0, parseInt(e.target.value) || 0))}
                          placeholder="0 = all"
                          style={{ width: 80, padding: '7px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'white', fontWeight: 900, fontSize: 13, fontFamily: 'monospace', textAlign: 'center', outline: 'none' }}
                        />
                        <span style={{ fontSize: 10, color: '#475569' }}>trees · 0 = all mature</span>
                        <button onClick={fetchMatureCounts} disabled={matureCountsLoading}
                          style={{ marginLeft: 'auto', fontSize: 10, color: '#475569', background: 'none', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}>
                          {matureCountsLoading ? '…' : '↺ refresh'}
                        </button>
                      </div>

                      {/* 3 buttons */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>

                        {/* Own land — bhoomi owner trees */}
                        <div>
                          <button onClick={handleHarvestOwn} disabled={harvestOwnBusy || anyBusy || sel.own === 0}
                            style={{ width: '100%', padding: '10px 8px', borderRadius: 8, fontSize: 11, fontWeight: 800, cursor: (harvestOwnBusy || anyBusy || sel.own === 0) ? 'default' : 'pointer', transition: 'all 0.2s', textAlign: 'center',
                              background: sel.own > 0 && !anyBusy ? 'rgba(52,211,153,0.14)' : 'rgba(52,211,153,0.04)',
                              border: `1px solid rgba(52,211,153,${sel.own > 0 && !anyBusy ? 0.4 : 0.1})`,
                              color: sel.own > 0 && !anyBusy ? '#34d399' : 'rgba(52,211,153,0.3)' }}>
                            {harvestOwnBusy ? '⏳…' : `🪓 Own Land\n(Bhoomi)\n${sel.own} mature`}

                          </button>
                          {harvestOwnMsg && <p style={{ fontSize: 10, marginTop: 6, fontWeight: 700, color: harvestOwnMsg.startsWith('✅') ? '#6ee7b7' : harvestOwnMsg.startsWith('⏳') ? '#fbbf24' : '#f87171' }}>{harvestOwnMsg}</p>}
                        </div>

                        {/* Others' land — bhoomi rental trees */}
                        <div>
                          <button onClick={handleHarvestBhoomiRental} disabled={harvestBhoomiBusy || anyBusy || sel.rental_bhoomi === 0}
                            style={{ width: '100%', padding: '10px 8px', borderRadius: 8, fontSize: 11, fontWeight: 800, cursor: (harvestBhoomiBusy || anyBusy || sel.rental_bhoomi === 0) ? 'default' : 'pointer', transition: 'all 0.2s', textAlign: 'center',
                              background: sel.rental_bhoomi > 0 && !anyBusy ? 'rgba(167,139,250,0.14)' : 'rgba(167,139,250,0.04)',
                              border: `1px solid rgba(167,139,250,${sel.rental_bhoomi > 0 && !anyBusy ? 0.4 : 0.1})`,
                              color: sel.rental_bhoomi > 0 && !anyBusy ? '#c4b5fd' : 'rgba(167,139,250,0.3)' }}>
                            {harvestBhoomiBusy ? '⏳…' : `🌿 Rental Land\n(Bhoomi)\n${sel.rental_bhoomi} mature`}
                          </button>
                          {harvestBhoomiMsg && <p style={{ fontSize: 10, marginTop: 6, fontWeight: 700, color: harvestBhoomiMsg.startsWith('✅') ? '#6ee7b7' : harvestBhoomiMsg.startsWith('⏳') ? '#fbbf24' : '#f87171' }}>{harvestBhoomiMsg}</p>}
                        </div>

                        {/* Others' land — seed rental trees → seeds back */}
                        <div>
                          <button onClick={handleHarvestSeedRental} disabled={harvestSeedBusy || anyBusy || sel.rental_seed === 0}
                            style={{ width: '100%', padding: '10px 8px', borderRadius: 8, fontSize: 11, fontWeight: 800, cursor: (harvestSeedBusy || anyBusy || sel.rental_seed === 0) ? 'default' : 'pointer', transition: 'all 0.2s', textAlign: 'center',
                              background: sel.rental_seed > 0 && !anyBusy ? 'rgba(16,185,129,0.14)' : 'rgba(16,185,129,0.04)',
                              border: `1px solid rgba(16,185,129,${sel.rental_seed > 0 && !anyBusy ? 0.4 : 0.1})`,
                              color: sel.rental_seed > 0 && !anyBusy ? '#6ee7b7' : 'rgba(16,185,129,0.3)' }}>
                            {harvestSeedBusy ? '⏳…' : `🌱 Rental Land\n(Seeds)\n${sel.rental_seed} mature`}
                          </button>
                          {harvestSeedMsg && <p style={{ fontSize: 10, marginTop: 6, fontWeight: 700, color: harvestSeedMsg.startsWith('✅') ? '#6ee7b7' : harvestSeedMsg.startsWith('⏳') ? '#fbbf24' : '#f87171' }}>{harvestSeedMsg}</p>}
                        </div>

                      </div>
                    </div>
                  );
                })()}

                {/* ── Growing Trees Panel ── */}
                {(() => {
                  const SPECIES = [
                    { id: 'amaranth', emoji: '🌱', label: 'Amaranth' },
                    { id: 'vetiver',  emoji: '🌾', label: 'Vetiver'  },
                    { id: 'opuntia',  emoji: '🌵', label: 'Opuntia'  },
                    { id: 'moringa',  emoji: '🌿', label: 'Moringa'  },
                    { id: 'jujube',   emoji: '🫐', label: 'Jujube'   },
                    { id: 'acacia',   emoji: '🌳', label: 'Acacia'   },
                    { id: 'mesquite', emoji: '🌲', label: 'Mesquite' },
                    { id: 'neem',     emoji: '🌴', label: 'Neem'     },
                  ];
                  const getFiltered = (c: { own: number; rental_bhoomi: number; rental_seed: number }) => {
                    if (growingFilter === 'seeds')  return c.rental_seed;
                    if (growingFilter === 'bhoomi') return c.own + c.rental_bhoomi;
                    return c.own + c.rental_bhoomi + c.rental_seed;
                  };
                  const totalGrowing = SPECIES.reduce((sum, sp) => {
                    return sum + getFiltered(growingCounts[sp.id] ?? { own: 0, rental_bhoomi: 0, rental_seed: 0 });
                  }, 0);
                  const FILTERS: { key: 'all' | 'seeds' | 'bhoomi'; label: string; color: string }[] = [
                    { key: 'all',    label: 'All',    color: '#34d399' },
                    { key: 'seeds',  label: '🌾 Seeds',  color: '#6ee7b7' },
                    { key: 'bhoomi', label: '🟣 Bhoomi', color: '#a78bfa' },
                  ];
                  return (
                    <div style={{ background: 'rgba(15,23,42,0.9)', border: '1px solid rgba(34,211,153,0.2)', borderRadius: 12, padding: '18px 20px', marginBottom: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <div style={{ fontSize: 10, color: '#34d399', fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase' }}>🌱 Growing Trees</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {totalGrowing > 0 && <span style={{ fontSize: 10, color: '#64748b', fontFamily: 'monospace' }}>{totalGrowing} total</span>}
                          <button onClick={fetchGrowingCounts} disabled={growingCountsLoading}
                            style={{ fontSize: 10, color: '#475569', background: 'none', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}>
                            {growingCountsLoading ? '…' : '↺'}
                          </button>
                        </div>
                      </div>
                      {/* Toggle */}
                      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
                        {FILTERS.map(f => (
                          <button key={f.key} onClick={() => setGrowingFilter(f.key)}
                            style={{ padding: '5px 10px', borderRadius: 6, fontSize: 10, fontWeight: 800, cursor: 'pointer', transition: 'all 0.15s',
                              background: growingFilter === f.key ? `rgba(${f.key === 'all' ? '52,211,153' : f.key === 'seeds' ? '110,231,183' : '167,139,250'},0.18)` : 'rgba(255,255,255,0.04)',
                              border: `1px solid ${growingFilter === f.key ? f.color + '88' : 'rgba(255,255,255,0.08)'}`,
                              color: growingFilter === f.key ? f.color : '#475569' }}>
                            {f.label}
                          </button>
                        ))}
                      </div>
                      {/* Species tiles */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {SPECIES.map(sp => {
                          const c = growingCounts[sp.id] ?? { own: 0, rental_bhoomi: 0, rental_seed: 0 };
                          const total = getFiltered(c);
                          return (
                            <div key={sp.id}
                              style={{ padding: '7px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, textAlign: 'left',
                                background: total > 0 ? 'rgba(34,211,153,0.08)' : 'rgba(255,255,255,0.04)',
                                border: `1px solid ${total > 0 ? 'rgba(34,211,153,0.3)' : 'rgba(255,255,255,0.08)'}`,
                                color: total > 0 ? '#34d399' : '#334155' }}>
                              <div>{sp.emoji} {sp.label}</div>
                              {growingCountsLoading
                                ? <div style={{ fontSize: 9, marginTop: 2, color: '#475569' }}>…</div>
                                : total > 0
                                  ? <div style={{ fontSize: 9, marginTop: 2, fontFamily: 'monospace' }}>
                                      {growingFilter !== 'seeds'  && c.own > 0          && <span style={{ color: '#34d399' }}>{c.own}own </span>}
                                      {growingFilter !== 'seeds'  && c.rental_bhoomi > 0 && <span style={{ color: '#a78bfa' }}>{c.rental_bhoomi}bh </span>}
                                      {growingFilter !== 'bhoomi' && c.rental_seed > 0   && <span style={{ color: '#6ee7b7' }}>{c.rental_seed}sd</span>}
                                    </div>
                                  : <div style={{ fontSize: 9, marginTop: 2, color: '#334155' }}>0 growing</div>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* ── Plant modal inline ── */}
                <AtaviForestPlantModal waxAccount={myAcct} onClose={() => setTab('overview')} inline />

              </div>
            )}

            {/* ── MY TEAM TAB ── */}
            {tab === 'team' && (
              <div className="flex flex-col gap-6">

                {teamLoading && (
                  <div className="flex items-center justify-center py-16">
                    <div className="w-7 h-7 rounded-full border-2 border-orange-500 border-t-transparent animate-spin" />
                  </div>
                )}

                {!teamLoading && (
                  <>
                    {/* Summary bar */}
                    <div className="grid grid-cols-3 gap-4">
                      {[
                        { label: 'In Training', value: teamData?.training_workers.length ?? 0, color: '#a78bfa', emoji: '🎓' },
                        { label: 'Operatives', value: teamData?.operatives.length ?? 0, color: '#fb923c', emoji: '🚌' },
                        { label: 'Idle Workers', value: teamData?.idle_workers.length ?? 0, color: '#22d3ee', emoji: '🧑' },
                      ].map(c => (
                        <div key={c.label} className="rounded-2xl p-5 text-center" style={{ background: 'rgba(15,23,42,0.85)', border: `1px solid ${c.color}20` }}>
                          <div className="text-3xl mb-2">{c.emoji}</div>
                          <div className="font-black text-3xl" style={{ color: c.color }}>{c.value}</div>
                          <div className="text-slate-500 text-xs font-bold uppercase tracking-widest mt-1">{c.label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Transport Operatives */}
                    <div className="rounded-2xl border border-orange-500/20 overflow-hidden" style={{ background: 'rgba(15,23,42,0.85)' }}>
                      <div className="px-5 py-3 border-b border-slate-800/50 flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-widest text-orange-400/70">🚌 Transport Operatives</span>
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(251,146,60,0.1)', color: '#fb923c', border: '1px solid rgba(251,146,60,0.25)' }}>{teamData?.operatives.length ?? 0} NFTs</span>
                      </div>
                      {(teamData?.operatives.length ?? 0) === 0 ? (
                        <div className="px-6 py-10 text-center">
                          <div className="text-4xl mb-3">🚌</div>
                          <div className="text-white font-bold mb-1">No operatives yet</div>
                          <div className="text-slate-500 text-sm">Send a worker to Training Academy in the University to earn your first Transport Operative NFT.</div>
                        </div>
                      ) : (
                        <div className="divide-y divide-slate-800/50">
                          {teamData!.operatives.map(op => (
                            <div key={op.operative_index} className="flex items-center gap-4 px-5 py-4">
                              <div className="shrink-0 rounded-xl overflow-hidden" style={{ width: 52, height: 52, border: '2px solid rgba(251,146,60,0.3)' }}>
                                <img src={`${process.env.PUBLIC_URL}/transport-operative.png`} alt="Operative" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-white font-black text-sm">Operative #{String(op.operative_index).padStart(3, '0')}</span>
                                  <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: 'rgba(52,211,153,0.1)', color: '#34d399', border: '1px solid rgba(52,211,153,0.25)' }}>Active</span>
                                </div>
                                <div className="text-slate-400 text-xs mt-0.5">{op.track.charAt(0).toUpperCase() + op.track.slice(1)} track · Certified {new Date(op.minted_at).toLocaleDateString()}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* In Training */}
                    {(teamData?.training_workers.length ?? 0) > 0 && (
                      <div className="rounded-2xl border border-violet-500/20 overflow-hidden" style={{ background: 'rgba(15,23,42,0.85)' }}>
                        <div className="px-5 py-3 border-b border-slate-800/50">
                          <span className="text-xs font-bold uppercase tracking-widest text-violet-400/70">🎓 In Training</span>
                        </div>
                        <div className="divide-y divide-slate-800/50">
                          {teamData!.training_workers.map(w => {
                            const pct = w.progress_pct ?? 0;
                            const trackLabel = w.track.charAt(0).toUpperCase() + w.track.slice(1);
                            return (
                              <div key={w.id} className="px-5 py-4">
                                <div className="flex items-center gap-3 mb-3">
                                  <div className="shrink-0 rounded-xl overflow-hidden" style={{ width: 40, height: 40, border: '1px solid rgba(167,139,250,0.3)' }}>
                                    <img src={`${process.env.PUBLIC_URL}/transport-operative.png`} alt="Operative" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-white font-bold text-sm">{w.worker_name}</span>
                                      {w.operative_index != null && (
                                        <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: 'rgba(167,139,250,0.1)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.25)' }}>#{String(w.operative_index).padStart(3,'0')} · Reserved</span>
                                      )}
                                    </div>
                                    <div className="text-slate-500 text-xs">{trackLabel} track · {w.is_ready ? <span className="text-green-400 font-bold">Ready to collect!</span> : `${Math.floor(w.seconds_remaining/3600)}h ${Math.floor((w.seconds_remaining%3600)/60)}m remaining`}</div>
                                  </div>
                                </div>
                                <div className="flex justify-between text-xs text-slate-600 mb-1">
                                  <span>{trackLabel}</span><span>{pct}%</span>
                                </div>
                                <div className="rounded-full overflow-hidden" style={{ height: 6, background: 'rgba(255,255,255,0.06)' }}>
                                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: w.is_ready ? 'linear-gradient(90deg,#059669,#34d399)' : 'linear-gradient(90deg,#7c3aed,#a78bfa)' }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

          </>
        )}
      </div>

      {showAtaviForestPlant && (waxActor || myAcct) && (
        <AtaviForestPlantModal
          waxAccount={waxActor || myAcct}
          onClose={() => setShowAtaviForestPlant(false)}
        />
      )}
    </div>
  );
}

// ─── University Page ──────────────────────────────────────────────────────────

type UniView = 'list' | 'detail';
type QuizPhase = 'idle' | 'starting' | 'active' | 'submitting' | 'result';
type UniItemSummary = { slug: string; title: string; type: string; emoji: string; description: string; wax_cost: number; owned: boolean };
type UniItemDetail = { slug: string; title: string; type: string; emoji: string; description: string; wax_cost: number; content_points: { heading: string; body: string }[] };
type QuizQ = { index: number; question: string; options: string[] };
type QuizResult = { passed: boolean; correct_count: number; wrong_count: number; locked_until: string | null };
type AcademyWorker = { id: number; worker_name: string };
type AcademyTrainingWorker = { id: number; worker_name: string; track: string; training_started_at: string; ready_at: string; seconds_remaining: number; is_ready: boolean; duration_sec: number; progress_pct: number; operative_index?: number };
type AcademyOperative = { operative_index: number; track: string; wax_paid: number; minted_at: string; status: string };
type AcademyData = { idle_workers: AcademyWorker[]; training_workers: AcademyTrainingWorker[]; operatives: AcademyOperative[] };

const ACADEMY_TRACK_CFG: Record<string, { label: string; emoji: string; duration: string; wax_cost: number; tagline: string; bullets: string[]; color: string; border: string }> = {
  standard: { label: 'Standard', emoji: '🐢', duration: '3 Days', wax_cost: 0, tagline: '0 WAX — free', color: 'rgba(100,116,139,0.12)', border: 'rgba(100,116,139,0.35)', bullets: ['Full training programme', 'No acceleration', 'Best for patient players'] },
  boost:    { label: 'Boost',    emoji: '⚡', duration: '1 Day',  wax_cost: 3, tagline: '3 WAX',        color: 'rgba(99,102,241,0.12)',  border: 'rgba(99,102,241,0.35)',  bullets: ['Accelerated programme', '3× faster than standard', 'Pay once at enrolment'] },
  express:  { label: 'Express',  emoji: '🚀', duration: '4 Hours',wax_cost: 7, tagline: '7 WAX',        color: 'rgba(251,191,36,0.10)',  border: 'rgba(251,191,36,0.35)',  bullets: ['Crash course', '18× faster than standard', 'Ready same session'] },
};

function UniversityPage({ onBack, onOpenCityMap }: { onBack: () => void; onOpenCityMap: () => void }) {
  const [myAcct, setMyAcct] = React.useState('');
  const [view, setView] = React.useState<UniView>('list');
  const [items, setItems] = React.useState<UniItemSummary[]>([]);
  const [loadingList, setLoadingList] = React.useState(true);
  const [detail, setDetail] = React.useState<UniItemDetail | null>(null);
  const [quizStatus, setQuizStatus] = React.useState('not_started');
  const [lockedUntil, setLockedUntil] = React.useState<string | null>(null);
  const [loadingDetail, setLoadingDetail] = React.useState(false);
  const [quizPhase, setQuizPhase] = React.useState<QuizPhase>('idle');
  const [quizQuestions, setQuizQuestions] = React.useState<QuizQ[]>([]);
  const [answers, setAnswers] = React.useState<Record<number, number>>({});
  const [quizResult, setQuizResult] = React.useState<QuizResult | null>(null);
  const [buyPhase, setBuyPhase] = React.useState<'idle'|'sending'|'verifying'|'done'|'error'>('idle');
  const [buyMsg, setBuyMsg] = React.useState('');
  const [pendingTxId, setPendingTxId] = React.useState('');
  // WAX wallet — from global context
  const { waxSession, waxActor, waxConnecting, connect: rawWaxConnect, disconnect: rawWaxDisconnect } = useWax();

  const handleWaxConnect = async () => {
    try {
      await rawWaxConnect();
    } catch { /* cancelled */ }
  };

  const handleWaxDisconnect = async () => {
    await rawWaxDisconnect();
  };
  // Academy state
  const [academyData, setAcademyData] = React.useState<AcademyData | null>(null);
  const [academyLoading, setAcademyLoading] = React.useState(false);
  const [selectedWorkerId, setSelectedWorkerId] = React.useState<number | ''>('');
  const [selectedTrack, setSelectedTrack] = React.useState<'standard'|'boost'|'express'|null>(null);
  const [enrolPhase, setEnrolPhase] = React.useState<'idle'|'sending'|'verifying'|'done'|'error'>('idle');
  const [enrolMsg, setEnrolMsg] = React.useState('');
  const [collectingId, setCollectingId] = React.useState<number | null>(null);
  const [collectMsg, setCollectMsg] = React.useState<Record<number, string>>({});

  React.useEffect(() => {
    const acct = localStorage.getItem('samatvam_active_account') || '';
    setMyAcct(acct);
  }, []);

  const fetchItems = React.useCallback((acct: string) => {
    setLoadingList(true);
    const url = acct ? `/samatvam/api/samatvam/university/items?account=${encodeURIComponent(acct)}` : '/samatvam/api/samatvam/university/items';
    fetch(url).then(r => r.json()).then(d => { if (d.success) setItems(d.items); }).catch(() => {}).finally(() => setLoadingList(false));
  }, []);

  React.useEffect(() => { fetchItems(myAcct); }, [myAcct, fetchItems]);

  const openDetail = async (slug: string) => {
    setView('detail'); setLoadingDetail(true); setDetail(null);
    setQuizPhase('idle'); setQuizResult(null); setBuyPhase('idle'); setBuyMsg('');
    setEnrolPhase('idle'); setEnrolMsg(''); setSelectedWorkerId(''); setSelectedTrack(null); setCollectMsg({});
    try {
      const url = myAcct ? `/samatvam/api/samatvam/university/items/${slug}?account=${encodeURIComponent(myAcct)}` : `/samatvam/api/samatvam/university/items/${slug}`;
      const r = await fetch(url); const d = await r.json();
      if (d.success) { setDetail(d.item); setQuizStatus(d.quiz_status); setLockedUntil(d.locked_until); }
    } catch {} finally { setLoadingDetail(false); }
    if (slug === 'transport-operative-cert' && myAcct) fetchAcademyData(myAcct);
  };

  const startQuiz = async () => {
    if (!myAcct || !detail) return;
    setQuizPhase('starting');
    try {
      const r = await fetch('/samatvam/api/samatvam/university/quiz/start', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wax_account: myAcct, item_slug: detail.slug }) });
      const d = await r.json();
      if (d.already_passed) { setQuizStatus('passed'); setQuizPhase('idle'); return; }
      if (d.already_owned) { setQuizStatus('owned'); setQuizPhase('idle'); return; }
      if (!r.ok) { setQuizPhase('idle'); return; }
      setQuizQuestions(d.questions); setAnswers({}); setQuizPhase('active');
    } catch { setQuizPhase('idle'); }
  };

  const submitQuiz = async () => {
    if (!myAcct || !detail) return;
    const answerList = quizQuestions.map(q => ({ question_index: q.index, selected_pos: answers[q.index] ?? -1 }));
    if (answerList.some(a => a.selected_pos === -1)) { alert('Please answer all 5 questions before submitting.'); return; }
    setQuizPhase('submitting');
    try {
      const r = await fetch('/samatvam/api/samatvam/university/quiz/submit', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wax_account: myAcct, item_slug: detail.slug, answers: answerList }) });
      const d = await r.json();
      setQuizResult({ passed: d.passed, correct_count: d.correct_count, wrong_count: d.wrong_count, locked_until: d.locked_until });
      if (d.passed) { setQuizStatus('passed'); } else { setLockedUntil(d.locked_until); setQuizStatus('cooldown'); }
      setQuizPhase('result');
    } catch { setQuizPhase('idle'); }
  };

  const verifyPendingTx = async (txId: string) => {
    if (!waxActor || !detail) return;
    setBuyPhase('verifying'); setBuyMsg('Transaction sent — checking blockchain…');
    // Poll every 5s for up to 60s
    const deadline = Date.now() + 60_000;
    let done = false;
    while (Date.now() < deadline && !done) {
      await new Promise(r => setTimeout(r, 5000));
      try {
        const reg = await fetch('/samatvam/api/samatvam/university/buy', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wax_account: waxActor, item_slug: detail.slug, tx_id: txId }) });
        const regD = await reg.json();
        if (regD.success) {
          setBuyPhase('done'); setBuyMsg(`${detail.title} added to your Samrajya!`); setQuizStatus('owned'); fetchItems(myAcct);
          done = true;
        } else if (regD.error?.includes('already owned') || regD.error?.includes('already purchased')) {
          setBuyPhase('done'); setBuyMsg('Already owned!'); setQuizStatus('owned');
          done = true;
        }
        // any other error — keep polling until deadline
      } catch { /* network hiccup — keep trying */ }
      if (!done) {
        const secsLeft = Math.round((deadline - Date.now()) / 1000);
        setBuyMsg(`Waiting for blockchain confirmation… (${secsLeft}s)`);
      }
    }
    if (!done) {
      setBuyPhase('error');
      setBuyMsg(`Could not confirm after 60s. Save your tx ID and contact support: ${txId}`);
    }
  };

  const handleBuy = async () => {
    if (!waxSession || !waxActor || !detail) { setBuyMsg('Connect your WAX wallet first (open Atavi Forest and connect).'); return; }
    if (!confirmWaxSpend(`${detail.wax_cost} WAX`)) return;
    setBuyPhase('sending'); setBuyMsg(''); setPendingTxId('');
    try {
      const result = await waxSession.transact({ actions: [{ account: 'eosio.token', name: 'transfer', authorization: [{ actor: waxSession.actor, permission: 'active' }], data: { from: waxSession.actor, to: 'samatvam.gm', quantity: `${detail.wax_cost}.00000000 WAX`, memo: `university:${detail.slug}` } }] });
      const txId = result?.resolved?.transaction?.id || (result as any)?.transaction_id || '';
      setPendingTxId(txId);
      await verifyPendingTx(txId);
    } catch (err: any) { setBuyPhase('error'); setBuyMsg(err.message || 'Transaction failed'); }
  };

  const fmtLocked = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const fetchAcademyData = React.useCallback((acct: string) => {
    if (!acct) return;
    setAcademyLoading(true);
    fetch(`/samatvam/api/samatvam/academy/status/${encodeURIComponent(acct)}`)
      .then(r => r.json()).then(d => { if (d.success) setAcademyData(d); }).catch(() => {})
      .finally(() => setAcademyLoading(false));
  }, []);

  const fmtDuration = (secs: number) => {
    if (secs <= 0) return 'Ready!';
    const h = Math.floor(secs / 3600); const m = Math.floor((secs % 3600) / 60); const s = secs % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const handleEnrol = async () => {
    if (!selectedWorkerId || !selectedTrack) { setEnrolMsg('Select a worker and a track first.'); return; }
    const trackCfg = ACADEMY_TRACK_CFG[selectedTrack];
    setEnrolPhase('sending'); setEnrolMsg('');
    try {
      if (trackCfg.wax_cost > 0) {
        if (!waxSession) { setEnrolPhase('error'); setEnrolMsg('Connect your WAX wallet first.'); return; }
        if (!confirmWaxSpend(`${trackCfg.wax_cost} WAX`)) return;
        const result = await waxSession.transact({ actions: [{ account: 'eosio.token', name: 'transfer', authorization: [{ actor: waxSession.actor, permission: 'active' }], data: { from: waxSession.actor, to: 'samatvam.gm', quantity: `${trackCfg.wax_cost}.00000000 WAX`, memo: `academy:${selectedTrack}:${selectedWorkerId}` } }] });
        const txId = result?.resolved?.transaction?.id || (result as any)?.transaction_id || '';
        setEnrolPhase('verifying'); setEnrolMsg('WAX sent — verifying…');
        const r = await fetch('/samatvam/api/samatvam/academy/enrol', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wax_account: myAcct, worker_id: selectedWorkerId, track: selectedTrack, tx_id: txId }) });
        const d = await r.json();
        if (!r.ok) { setEnrolPhase('error'); setEnrolMsg(d.error || 'Enrolment failed'); return; }
      } else {
        const r = await fetch('/samatvam/api/samatvam/academy/enrol', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wax_account: myAcct, worker_id: selectedWorkerId, track: selectedTrack }) });
        const d = await r.json();
        if (!r.ok) { setEnrolPhase('error'); setEnrolMsg(d.error || 'Enrolment failed'); return; }
      }
      setEnrolPhase('done'); setEnrolMsg(`Worker enrolled on ${trackCfg.label} track!`);
      setSelectedWorkerId(''); setSelectedTrack(null);
      fetchAcademyData(myAcct);
    } catch (err: any) { setEnrolPhase('error'); setEnrolMsg(err.message || 'Transaction failed'); }
  };

  const handleCollect = async (workerId: number) => {
    setCollectingId(workerId);
    try {
      const r = await fetch(`/samatvam/api/samatvam/academy/collect/${workerId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wax_account: myAcct }) });
      const d = await r.json();
      if (!r.ok) { setCollectMsg(prev => ({ ...prev, [workerId]: d.error || 'Failed' })); }
      else { setCollectMsg(prev => ({ ...prev, [workerId]: `✅ Operative #${d.operative_index} minted!` })); fetchAcademyData(myAcct); }
    } catch { setCollectMsg(prev => ({ ...prev, [workerId]: 'Network error' })); }
    finally { setCollectingId(null); }
  };

  const BackBtn = ({ onClick }: { onClick: () => void }) => (
    <button onClick={onClick} className="text-slate-400 hover:text-white transition-colors p-1">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
    </button>
  );

  if (view === 'list') return (
    <div className="min-h-screen bg-slate-900" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div className="sticky top-0 z-40 backdrop-blur-md border-b border-slate-800/60" style={{ background: 'rgba(2,6,23,0.92)' }}>
        <div className="w-[90%] mx-auto py-4 flex items-center gap-3">
          <div><h1 className="text-white font-black text-xl leading-tight">University</h1><p className="text-slate-500 text-sm">Knowledge · Recipes · Handbooks</p></div>
          <div className="ml-auto flex gap-2 items-center">
            {waxActor ? (
              <button onClick={handleWaxDisconnect} className="text-xs font-bold px-3 py-2 rounded-lg border transition-colors" style={{ borderColor: 'rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.08)', color: '#fcd34d' }}>
                ⬡ {waxActor}
              </button>
            ) : (
              <button onClick={handleWaxConnect} disabled={waxConnecting} className="text-xs font-bold px-3 py-2 rounded-lg border transition-colors disabled:opacity-50" style={{ borderColor: 'rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.1)', color: '#fcd34d' }}>
                {waxConnecting ? '…' : '⬡ Connect WAX'}
              </button>
            )}
            <button onClick={onBack} className="text-sm font-bold px-4 py-2 rounded-lg border border-slate-700/60 text-slate-400 hover:text-white hover:border-slate-500 transition-colors">← Samrajya</button>
            <button onClick={onOpenCityMap} className="text-sm font-bold px-4 py-2 rounded-lg border border-slate-700/60 text-slate-400 hover:text-white hover:border-slate-500 transition-colors">← City Map</button>
          </div>
        </div>
      </div>
      <div className="w-[90%] mx-auto py-6">
        {loadingList && <div className="flex items-center justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin"/></div>}
        {!loadingList && items.map(item => (
          <button key={item.slug} onClick={() => openDetail(item.slug)} className="w-full text-left mb-4 rounded-2xl border overflow-hidden transition-all"
            style={{ borderColor: item.owned ? 'rgba(34,197,94,0.4)' : 'rgba(255,255,255,0.08)', background: item.owned ? 'rgba(5,46,22,0.3)' : 'rgba(15,23,42,0.8)' }}>
            <div className="px-6 py-5 flex items-center gap-5">
              <span className="text-4xl">{item.emoji}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-white font-bold text-lg">{item.title}</span>
                  <span className="text-xs font-black uppercase tracking-wider px-2 py-0.5 rounded-full"
                    style={{ background: item.type === 'handbook' ? 'rgba(99,102,241,0.15)' : item.type === 'certification' ? 'rgba(251,191,36,0.15)' : 'rgba(245,158,11,0.15)', color: item.type === 'handbook' ? '#a5b4fc' : item.type === 'certification' ? '#fbbf24' : '#fcd34d', border: `1px solid ${item.type === 'handbook' ? 'rgba(99,102,241,0.3)' : item.type === 'certification' ? 'rgba(251,191,36,0.3)' : 'rgba(245,158,11,0.3)'}` }}>
                    {item.type}
                  </span>
                </div>
                <p className="text-slate-400 text-base truncate">{item.description}</p>
                <p className="text-slate-600 text-sm mt-0.5">{item.type === 'certification' ? 'Free quiz · 3 training tracks (0–7 WAX) · 20 questions' : `${item.wax_cost} WAX · 20 points · 20 questions`}</p>
              </div>
              {item.owned ? <span className="text-green-400 text-base font-bold shrink-0">✓ Owned</span> : <span className="text-slate-600 text-base shrink-0">→</span>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  // Detail view
  return (
    <div className="min-h-screen bg-slate-900" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div className="sticky top-0 z-40 backdrop-blur-md border-b border-slate-800/60" style={{ background: 'rgba(2,6,23,0.92)' }}>
        <div className="w-[90%] mx-auto py-4 flex items-center gap-3">
          <button onClick={() => { setView('list'); fetchItems(myAcct); }} className="text-slate-400 hover:text-white transition-colors p-1 shrink-0">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
          </button>
          <div className="flex-1 min-w-0"><h1 className="text-white font-black text-lg leading-tight truncate">{detail?.title ?? '…'}</h1><p className="text-slate-500 text-sm">{detail?.type === 'certification' ? 'University · Training Academy' : `University · ${detail?.type ?? ''}`}</p></div>
          {quizStatus === 'owned' && <div className="px-3 py-1 rounded-full text-sm font-bold border border-green-500/30 bg-green-900/30 text-green-300 shrink-0">✓ Owned</div>}
          <div className="flex gap-2 shrink-0 items-center">
            {waxActor ? (
              <button onClick={handleWaxDisconnect} className="text-xs font-bold px-3 py-2 rounded-lg border transition-colors" style={{ borderColor: 'rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.08)', color: '#fcd34d' }}>
                ⬡ {waxActor}
              </button>
            ) : (
              <button onClick={handleWaxConnect} disabled={waxConnecting} className="text-xs font-bold px-3 py-2 rounded-lg border transition-colors disabled:opacity-50" style={{ borderColor: 'rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.1)', color: '#fcd34d' }}>
                {waxConnecting ? '…' : '⬡ Connect WAX'}
              </button>
            )}
            <button onClick={onBack} className="text-sm font-bold px-4 py-2 rounded-lg border border-slate-700/60 text-slate-400 hover:text-white hover:border-slate-500 transition-colors">← Samrajya</button>
            <button onClick={onOpenCityMap} className="text-sm font-bold px-4 py-2 rounded-lg border border-slate-700/60 text-slate-400 hover:text-white hover:border-slate-500 transition-colors">← City Map</button>
          </div>
        </div>
      </div>
      <div className="w-[90%] mx-auto py-6">
        {loadingDetail && <div className="flex items-center justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin"/></div>}
        {!loadingDetail && detail && (<>
          {/* Header */}
          <div className="rounded-2xl border border-slate-700/40 px-6 py-5 mb-5" style={{ background: 'rgba(15,23,42,0.8)' }}>
            <div className="flex items-center gap-4 mb-2"><span className="text-4xl">{detail.emoji}</span><div><div className="text-white font-bold text-lg">{detail.title}</div><div className="text-slate-400 text-base mt-0.5">{detail.description}</div></div></div>
            <div className="text-sm text-indigo-400/70 mt-2">{detail.type === 'certification' ? '10 knowledge points · Quiz of 5 random questions · Free certification · 3 training tracks (0–7 WAX)' : `20 knowledge points · Quiz of 5 random questions · ${detail.wax_cost} WAX to own`}</div>

            {/* ── Allocate Worker — compact form in header (cert only) ── */}
            {detail.type === 'certification' && myAcct && (
              <div className="mt-4 pt-4 border-t border-slate-700/40">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Allocate Worker to Academy</div>
                {academyLoading && <div className="text-slate-500 text-xs mb-2">Loading workers…</div>}
                {!academyLoading && academyData && academyData.idle_workers.length === 0 && (
                  <div className="text-slate-500 text-xs mb-2">No idle workers available — all are busy or in training.</div>
                )}
                <div className="flex gap-2 flex-wrap items-center">
                  <select value={selectedWorkerId} onChange={e => setSelectedWorkerId(Number(e.target.value) || '')}
                    className="flex-1 min-w-0 rounded-lg px-3 py-2 text-sm font-semibold"
                    style={{ background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(255,255,255,0.1)', color: '#e2e8f0', outline: 'none' }}>
                    <option value="">— Select worker —</option>
                    {(academyData?.idle_workers ?? []).map(w => <option key={w.id} value={w.id}>Worker #{w.id} · {w.worker_name}</option>)}
                  </select>
                  <select value={selectedTrack ?? ''} onChange={e => setSelectedTrack(e.target.value as any || null)}
                    className="rounded-lg px-3 py-2 text-sm font-semibold"
                    style={{ background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(255,255,255,0.1)', color: '#e2e8f0', outline: 'none' }}>
                    <option value="">— Track —</option>
                    <option value="standard">🐢 Standard · Free</option>
                    <option value="boost">⚡ Boost · 3 WAX</option>
                    <option value="express">🚀 Express · 7 WAX</option>
                  </select>
                  <button onClick={handleEnrol} disabled={!selectedWorkerId || !selectedTrack || enrolPhase === 'sending' || enrolPhase === 'verifying'}
                    className="px-4 py-2 rounded-lg font-bold text-sm text-white disabled:opacity-40 transition-all hover:opacity-90"
                    style={{ background: 'linear-gradient(135deg, #d97706, #b45309)', whiteSpace: 'nowrap' }}>
                    {enrolPhase === 'sending' ? 'Sending…' : enrolPhase === 'verifying' ? 'Verifying…' : 'Allocate →'}
                  </button>
                </div>
                {enrolMsg && <div className={`text-xs mt-2 ${enrolPhase === 'error' ? 'text-red-400' : 'text-green-400'}`}>{enrolMsg}</div>}
              </div>
            )}
          </div>

          {/* ── Training Academy — in-training workers + collect (certification only, always visible) ── */}
          {detail.slug === 'transport-operative-cert' && myAcct && (
            <div className="mb-6">
              <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">🎓 Training Status</p>
              <div className="rounded-2xl border border-amber-500/20 overflow-hidden" style={{ background: 'rgba(15,23,42,0.9)' }}>
                {academyLoading && <div className="px-6 py-5 text-slate-500 text-sm">Loading…</div>}

                {/* In-training workers */}
                {!academyLoading && academyData && academyData.training_workers.length > 0 && (
                  <div className="px-6 py-5 border-b border-slate-800/60">
                    <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Workers In Training</div>
                    <div className="flex flex-col gap-3">
                      {academyData.training_workers.map(w => {
                        const cfg = ACADEMY_TRACK_CFG[w.track] ?? ACADEMY_TRACK_CFG.standard;
                        const quizBlocked = w.is_ready && quizStatus !== 'passed';
                        const pct = w.progress_pct ?? 0;
                        return (
                          <div key={w.id} className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                            {/* Top: big image left, details right */}
                            <div className="flex">
                              {/* NFT image — 30vw wide */}
                              <div className="shrink-0 overflow-hidden" style={{ width: '30vw', maxWidth: 380, minWidth: 160, background: 'rgba(245,158,11,0.06)', borderRight: '1px solid rgba(245,158,11,0.15)' }}>
                                <img src={`${process.env.PUBLIC_URL}/transport-operative.png`} alt="Transport Operative" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                              </div>
                              {/* Info */}
                              <div className="flex-1 min-w-0 p-5 flex flex-col justify-between">
                                <div>
                                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Transport Operative</div>
                                  <div className="text-white font-bold text-base mb-1">{w.worker_name}</div>
                                  {w.operative_index != null && (
                                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-3" style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)' }}>
                                      <span className="text-amber-400 font-black text-sm">#{String(w.operative_index).padStart(3, '0')}</span>
                                      <span className="text-amber-600 text-xs font-semibold">· Reserved</span>
                                    </div>
                                  )}
                                  <div className="text-sm text-slate-400 mb-3">Worker enrolled on <span className="text-amber-400 font-bold">{cfg.label}</span> track!</div>
                                  <div className="text-sm font-bold mb-4" style={{ color: w.is_ready ? '#34d399' : '#94a3b8' }}>
                                    {w.is_ready ? '✅ Ready to collect!' : `⏱ ${fmtDuration(w.seconds_remaining)} remaining`}
                                  </div>
                                  {quizBlocked && <div className="text-amber-400 text-xs mb-3">Pass the Knowledge Check below to collect</div>}
                                </div>
                                {/* Collect button */}
                                {w.is_ready && !quizBlocked && (
                                  <button onClick={() => handleCollect(w.id)} disabled={collectingId === w.id}
                                    className="px-5 py-2.5 rounded-xl font-bold text-sm text-white disabled:opacity-50 self-start"
                                    style={{ background: 'linear-gradient(135deg, #059669, #047857)' }}>
                                    {collectingId === w.id ? 'Collecting…' : 'Collect NFT'}
                                  </button>
                                )}
                              </div>
                            </div>
                            {/* Progress bar */}
                            <div className="px-5 py-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                              <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                                <span>{cfg.emoji} {cfg.label} track</span>
                                <span className="font-bold">{pct}%</span>
                              </div>
                              <div className="rounded-full overflow-hidden" style={{ height: 10, background: 'rgba(255,255,255,0.06)' }}>
                                <div className="h-full rounded-full transition-all duration-500"
                                  style={{ width: `${pct}%`, background: w.is_ready ? 'linear-gradient(90deg, #059669, #34d399)' : 'linear-gradient(90deg, #d97706, #f59e0b)' }} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {Object.entries(collectMsg).map(([id, msg]) => <div key={id} className="text-sm text-center text-green-400">{msg}</div>)}
                    </div>
                  </div>
                )}

                {/* Minted operatives */}
                {!academyLoading && academyData && academyData.operatives.length > 0 && (
                  <div className="px-6 py-5">
                    <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Your Transport Operatives</div>
                    <div className="flex flex-col gap-2">
                      {academyData.operatives.map(op => {
                        const cfg = ACADEMY_TRACK_CFG[op.track] ?? ACADEMY_TRACK_CFG.standard;
                        return (
                          <div key={op.operative_index} className="rounded-xl px-4 py-3 flex items-center gap-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                            <div className="text-xl font-black text-amber-400 shrink-0 w-8 text-center">#{op.operative_index}</div>
                            <div className="flex-1">
                              <div className="text-white font-bold text-sm">Operative #{op.operative_index}</div>
                              <div className="text-slate-400 text-xs">{cfg.emoji} {cfg.label} track · Certified {new Date(op.minted_at).toLocaleDateString()}</div>
                            </div>
                            <div className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: 'rgba(52,211,153,0.1)', color: '#34d399', border: '1px solid rgba(52,211,153,0.25)' }}>{op.status}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {!academyLoading && academyData && academyData.operatives.length === 0 && academyData.training_workers.length === 0 && (
                  <div className="px-6 py-5 text-center text-slate-500 text-sm">No workers in training yet. Use the Allocate Worker form above to get started.</div>
                )}
              </div>
            </div>
          )}

          {/* Content points */}
          <div className="mb-5">
            <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">Knowledge Points</p>
            <div className="rounded-2xl border border-slate-700/40 overflow-hidden" style={{ background: 'rgba(15,23,42,0.8)' }}>
              {detail.content_points.map((pt, i) => (
                <div key={i} className={`px-6 py-4 ${i < detail.content_points.length - 1 ? 'border-b border-slate-800/60' : ''}`}>
                  <div className="flex gap-4">
                    <span className="text-indigo-400/40 font-black text-sm pt-0.5 shrink-0 w-6 text-right">{i + 1}</span>
                    <div><div className="text-white text-base font-bold mb-1">{pt.heading}</div><div className="text-slate-400 text-base leading-relaxed">{pt.body}</div></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Knowledge Check */}
          <div className="mb-6">
            <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">{detail.slug === 'transport-operative-cert' ? '📋 Knowledge Check — Required to Collect' : 'Knowledge Check'}</p>

            {quizStatus === 'owned' && detail.type !== 'certification' && (
              <div className="rounded-2xl border border-green-500/30 px-6 py-7 text-center" style={{ background: 'rgba(5,46,22,0.4)' }}>
                <div className="text-4xl mb-2">✅</div>
                <div className="text-green-400 font-bold text-base">You own this {detail.type}</div>
                <div className="text-slate-500 text-sm mt-1">Active in your Samrajya · Qualifies for city tasks requiring it.</div>
              </div>
            )}

            {quizStatus === 'passed' && buyPhase !== 'done' && detail.type !== 'certification' && (
              <div className="rounded-2xl border border-indigo-500/30 px-6 py-6" style={{ background: 'rgba(30,27,75,0.4)' }}>
                <div className="text-center mb-5"><div className="text-3xl mb-2">🎓</div><div className="text-white font-bold text-base">Quiz Passed!</div><div className="text-slate-400 text-sm mt-1">You can now purchase this {detail.type} for {detail.wax_cost} WAX</div></div>
                {buyPhase === 'error' && pendingTxId ? (
                  <div className="flex flex-col gap-3">
                    <button onClick={() => verifyPendingTx(pendingTxId)}
                      className="w-full py-4 rounded-xl font-bold text-base text-white transition-all hover:opacity-90"
                      style={{ background: 'linear-gradient(135deg, #0f766e, #0d9488)' }}>
                      Re-check Transaction
                    </button>
                    <button onClick={handleBuy}
                      className="w-full py-3 rounded-xl font-bold text-sm transition-all hover:opacity-80"
                      style={{ background: 'rgba(99,102,241,0.12)', color: '#a5b4fc', border: '1px solid rgba(99,102,241,0.3)' }}>
                      Send New Transaction · {detail.wax_cost} WAX
                    </button>
                  </div>
                ) : (
                  <button onClick={handleBuy} disabled={buyPhase === 'sending' || buyPhase === 'verifying'}
                    className="w-full py-4 rounded-xl font-bold text-base text-white transition-all hover:opacity-90 disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
                    {buyPhase === 'sending' ? 'Sending…' : buyPhase === 'verifying' ? 'Verifying on-chain…' : `Buy · ${detail.wax_cost} WAX`}
                  </button>
                )}
                {buyMsg && <div className={`text-sm mt-3 text-center ${buyPhase === 'error' ? 'text-red-400' : 'text-indigo-300'}`}>{buyMsg}</div>}
              </div>
            )}

            {buyPhase === 'done' && (
              <div className="rounded-2xl border border-green-500/30 px-6 py-7 text-center" style={{ background: 'rgba(5,46,22,0.4)' }}>
                <div className="text-4xl mb-2">🎓</div><div className="text-green-400 font-bold text-base">{buyMsg}</div>
              </div>
            )}

            {quizStatus === 'passed' && detail.type === 'certification' && (
              <div className="rounded-2xl border border-amber-500/30 px-6 py-5 text-center" style={{ background: 'rgba(30,20,0,0.4)' }}>
                <div className="text-3xl mb-2">🎓</div>
                <div className="text-amber-400 font-bold text-base">Certification Quiz Passed!</div>
                <div className="text-slate-400 text-sm mt-1">Scroll down to the Training Academy to enrol a worker.</div>
              </div>
            )}

            {quizStatus === 'cooldown' && (
              <div className="rounded-2xl border border-orange-500/20 px-6 py-6 text-center" style={{ background: 'rgba(60,20,0,0.3)' }}>
                <div className="text-3xl mb-2">⏳</div>
                <div className="text-orange-300 font-bold text-base">Quiz locked</div>
                <div className="text-slate-400 text-sm mt-1">{lockedUntil ? `Try again after ${fmtLocked(lockedUntil)}` : 'Try again in 1 hour'}</div>
                {quizResult && <div className="text-slate-500 text-sm mt-2">{quizResult.correct_count}/5 correct on last attempt</div>}
              </div>
            )}

            {(quizStatus === 'not_started' || quizStatus === 'can_retry') && quizPhase === 'idle' && (
              <div className="rounded-2xl border border-slate-700/40 px-6 py-6" style={{ background: 'rgba(15,23,42,0.8)' }}>
                <div className="text-base text-slate-300 mb-1 font-semibold">{quizStatus === 'can_retry' ? '↺ Retry Quiz' : 'Take the Knowledge Check'}</div>
                <div className="text-sm text-slate-500 mb-5">5 random questions from a pool of 20 · All must be correct · 1 hour cooldown on failure</div>
                {!myAcct && <div className="text-sm text-amber-400 mb-3">Connect your account in Atavi Forest first</div>}
                <button onClick={startQuiz} disabled={!myAcct}
                  className="w-full py-4 rounded-xl font-bold text-base text-white transition-all hover:opacity-90 disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #1d4ed8, #4338ca)' }}>
                  {quizStatus === 'can_retry' ? 'Retry Quiz' : 'Start Quiz'}
                </button>
              </div>
            )}

            {quizPhase === 'active' && (
              <div className="rounded-2xl border border-indigo-500/20 overflow-hidden" style={{ background: 'rgba(15,23,42,0.9)' }}>
                <div className="px-6 py-4 border-b border-slate-800/60"><span className="text-sm font-bold text-indigo-400">Answer all 5 · Select one per question</span></div>
                {quizQuestions.map((q, qi) => (
                  <div key={q.index} className={`px-6 py-5 ${qi < quizQuestions.length - 1 ? 'border-b border-slate-800/60' : ''}`}>
                    <div className="flex gap-3 mb-4">
                      <span className="text-indigo-400/60 font-black text-base pt-0.5 shrink-0">{qi + 1}.</span>
                      <div className="text-white text-base font-semibold leading-relaxed">{q.question}</div>
                    </div>
                    <div className="flex flex-col gap-3">
                      {q.options.map((opt, pos) => {
                        const sel = answers[q.index] === pos;
                        return (
                          <button key={pos} onClick={() => setAnswers(prev => ({ ...prev, [q.index]: pos }))}
                            className="w-full text-left px-5 py-3.5 rounded-xl text-base transition-all"
                            style={{ border: sel ? '1px solid rgba(99,102,241,0.6)' : '1px solid rgba(255,255,255,0.06)', background: sel ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.03)', color: sel ? '#a5b4fc' : '#94a3b8', fontWeight: sel ? 700 : 400 }}>
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <div className="px-6 py-5 border-t border-slate-800/60">
                  <button onClick={submitQuiz} disabled={Object.keys(answers).length < 5}
                    className="w-full py-4 rounded-xl font-bold text-base text-white transition-all hover:opacity-90 disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, #1d4ed8, #4338ca)' }}>
                    Submit Answers
                  </button>
                </div>
              </div>
            )}

            {quizPhase === 'result' && quizResult && (
              <div className="mt-4 rounded-2xl border px-6 py-5 text-center"
                style={{ borderColor: quizResult.passed ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.3)', background: quizResult.passed ? 'rgba(5,46,22,0.4)' : 'rgba(60,0,0,0.3)' }}>
                <div className="text-3xl mb-2">{quizResult.passed ? '🎉' : '❌'}</div>
                <div className={`font-bold text-base ${quizResult.passed ? 'text-green-400' : 'text-red-400'}`}>
                  {quizResult.passed ? 'All correct!' : `${quizResult.correct_count} / 5 correct`}
                </div>
                {!quizResult.passed && lockedUntil && <div className="text-slate-400 text-sm mt-1">Try again after {fmtLocked(lockedUntil)}</div>}
              </div>
            )}
          </div>


        </>)}
      </div>
    </div>
  );
}


// ─── Packets Leaderboard (public) ────────────────────────────────────────────

function PacketsLeaderboard({ highlight }: { highlight: string }) {
  const [rows, setRows] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    fetch('/samatvam/api/samatvam/warehouse/packets-all')
      .then(r => r.json())
      .then(d => { if (d.success) setRows(d.rows); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const total = rows.reduce((s, r) => s + parseInt(r.total_packets), 0);

  return (
    <div className="bg-slate-800/60 border border-white/10 rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
          style={{ background: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.25)' }}>
          📦
        </div>
        <div>
          <p className="text-white font-black text-base">Food Packet Holdings</p>
          <p className="text-slate-500 text-xs">Public · all players · available packets only</p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-9 bg-white/5 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-6 text-slate-500 text-sm">No packets crafted yet</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-2 px-2 text-slate-400 font-semibold text-xs uppercase tracking-wide">#</th>
                <th className="text-left py-2 px-2 text-slate-400 font-semibold text-xs uppercase tracking-wide">Player</th>
                <th className="text-right py-2 px-2 text-slate-400 font-semibold text-xs uppercase tracking-wide">Total Packets</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const isMe = highlight && row.wax_account === highlight;
                return (
                  <tr key={row.wax_account}
                    className={`border-b border-white/5 transition-colors ${isMe ? 'bg-purple-500/10' : 'hover:bg-white/5'}`}>
                    <td className="py-2.5 px-2 text-slate-500 text-xs">{i + 1}</td>
                    <td className="py-2.5 px-2 font-mono text-xs">
                      <span className={isMe ? 'text-purple-300 font-bold' : 'text-teal-400'}>
                        {row.wax_account}
                      </span>
                      {isMe && <span className="ml-2 text-[10px] bg-purple-500/20 text-purple-300 border border-purple-500/30 px-1.5 py-0.5 rounded-full font-bold">you</span>}
                    </td>
                    <td className="py-2.5 px-2 text-right">
                      <span className="font-black text-purple-400">{row.total_packets}</span>
                      <span className="text-slate-500 text-xs ml-1">📦</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-white/10">
                <td colSpan={2} className="py-2.5 px-2 text-xs font-bold text-slate-400 uppercase tracking-wide">Total in circulation</td>
                <td className="py-2.5 px-2 text-right font-black text-white">{total} <span className="text-slate-500 text-xs">📦</span></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Warehouse Page ───────────────────────────────────────────────────────────

const CRAFT_COST = 10.1;
const SUGAR_CRAFT_COST = 50;

function WarehousePage({ onBack }: { onBack: () => void }) {
  const [waxActor, setWaxActor]   = React.useState('');
  const [waxSession, setWaxSession] = React.useState<any>(null);
  const [waxConnecting, setWaxConnecting] = React.useState(false);
  const [foodScore, setFoodScore] = React.useState(0);
  const [foodRate, setFoodRate]   = React.useState(0);
  const [packets, setPackets]     = React.useState(0);
  const [sugarPackets, setSugarPackets] = React.useState(0);
  const [qty, setQty]             = React.useState(1);
  const [sugarQty, setSugarQty]   = React.useState(1);
  const [crafting, setCrafting]   = React.useState(false);
  const [craftingSugar, setCraftingSugar] = React.useState(false);
  const [toast, setToast]         = React.useState('');
  const [loading, setLoading]     = React.useState(true);

  const handleWaxConnect = async () => {
    setWaxConnecting(true);
    try {
      const { session: s } = await getSessionKit().login();
      const acct = String(s.actor);
      setWaxSession(s);
      setWaxActor(acct);
      localStorage.setItem('samatvam_active_account', acct);
    } catch { /* cancelled */ }
    setWaxConnecting(false);
  };

  const handleWaxDisconnect = async () => {
    if (waxSession) await getSessionKit().logout(waxSession);
    setWaxSession(null);
    setWaxActor('');
    setFoodScore(0); setFoodRate(0); setPackets(0); setSugarPackets(0);
  };

  // Restore session or fall back to localStorage
  React.useEffect(() => {
    getSessionKit().restore().then((s: any) => {
      if (s) {
        setWaxSession(s);
        setWaxActor(String(s.actor));
      } else {
        const acct = localStorage.getItem('samatvam_active_account') || '';
        if (acct) setWaxActor(acct);
      }
    }).catch(() => {
      const acct = localStorage.getItem('samatvam_active_account') || '';
      if (acct) setWaxActor(acct);
    });
  }, []);

  const fetchWarehouse = React.useCallback((acct: string) => {
    if (!acct) return;
    setLoading(true);
    fetch(`/samatvam/api/samatvam/warehouse/${acct}`)
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setFoodScore(d.food_score);
          setFoodRate(d.food_rate);
          setPackets(d.packets);
          setSugarPackets(d.sugar_packets ?? 0);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    if (waxActor) fetchWarehouse(waxActor);
  }, [waxActor, fetchWarehouse]);

  const maxCraft = Math.floor(foodScore / CRAFT_COST);
  const totalCost = Math.round(CRAFT_COST * qty * 10000) / 10000;
  const canCraft = waxActor && foodScore >= totalCost;

  const maxSugarCraft = Math.floor(foodScore / SUGAR_CRAFT_COST);
  const totalSugarCost = SUGAR_CRAFT_COST * sugarQty;
  const canCraftSugar = waxActor && foodScore >= totalSugarCost;

  const craft = async () => {
    if (!canCraft || crafting) return;
    setCrafting(true); setToast('');
    try {
      const r = await fetch('/samatvam/api/samatvam/warehouse/craft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxActor, quantity: qty }),
      });
      const d = await r.json();
      if (d.success) {
        setToast(`✅ Crafted ${d.crafted} packet${d.crafted > 1 ? 's' : ''} · spent ${d.food_spent.toFixed(2)} Food`);
        setFoodScore(d.new_food);
        setPackets(p => p + d.crafted);
        setQty(1);
      } else {
        setToast(`❌ ${d.error}`);
      }
    } catch { setToast('❌ Network error'); }
    setCrafting(false);
  };

  const craftSugar = async () => {
    if (!canCraftSugar || craftingSugar) return;
    setCraftingSugar(true); setToast('');
    try {
      const r = await fetch('/samatvam/api/samatvam/warehouse/craft-sugar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxActor, quantity: sugarQty }),
      });
      const d = await r.json();
      if (d.success) {
        setToast(`✅ Crafted ${d.crafted} sugar packet${d.crafted > 1 ? 's' : ''} · spent ${d.food_spent} Food`);
        setFoodScore(d.new_food);
        setSugarPackets(d.new_sugar_packets);
        setSugarQty(1);
      } else {
        setToast(`❌ ${d.error}`);
      }
    } catch { setToast('❌ Network error'); }
    setCraftingSugar(false);
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className="bg-slate-800/80 border-b border-white/8 px-6 py-4 flex items-center gap-4">
        <button onClick={onBack} className="text-slate-400 hover:text-white transition-colors text-sm font-bold flex items-center gap-2">
          ← Back
        </button>
        <div className="w-px h-5 bg-white/10" />
        <span className="text-xl">🏭</span>
        <div className="flex-1">
          <h1 className="text-white font-black text-base leading-tight">Warehouse</h1>
          <p className="text-slate-500 text-xs">Craft Food Packets · 10 Food + 1% fee per packet</p>
        </div>
        {waxActor ? (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-bold"
              style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', color: '#fcd34d' }}>
              <span>⬡</span><span className="font-mono">{waxActor}</span>
            </div>
            <button onClick={handleWaxDisconnect}
              className="text-xs text-slate-500 hover:text-slate-300 transition-colors font-bold">
              Disconnect
            </button>
          </div>
        ) : (
          <button onClick={handleWaxConnect} disabled={waxConnecting}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all disabled:opacity-50"
            style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', color: '#fcd34d' }}>
            <span>⬡</span>{waxConnecting ? 'Connecting…' : 'Connect WAX Wallet'}
          </button>
        )}
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8 flex flex-col gap-6">

        {/* Not connected */}
        {!waxActor && (
          <div className="rounded-2xl border border-slate-700/40 bg-slate-800/40 p-14 flex flex-col items-center gap-5 text-center">
            <span className="text-5xl">🏭</span>
            <div>
              <p className="text-white font-black text-xl mb-2">Connect your WAX wallet</p>
              <p className="text-slate-400 text-sm">Sign in to craft Food Packets and Sugar Packets from your Food balance.</p>
            </div>
            <button onClick={handleWaxConnect} disabled={waxConnecting}
              className="flex items-center gap-3 px-8 py-3 rounded-xl font-bold text-sm disabled:opacity-50 transition-all"
              style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)', color: '#fcd34d' }}>
              <span style={{ fontSize: 20 }}>⬡</span>
              {waxConnecting ? 'Connecting…' : 'Connect WAX Wallet'}
            </button>
          </div>
        )}

        {waxActor && (
        <>

        {/* Balances */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-slate-800/60 border border-amber-500/20 rounded-2xl p-5">
            <p className="text-xs uppercase tracking-widest text-amber-500/60 font-bold mb-1">Food Balance</p>
            <p className="text-3xl font-black text-amber-400">{loading ? '…' : foodScore.toFixed(2)}</p>
            <p className="text-xs text-slate-500 mt-1">+{foodRate.toFixed(1)} /hr from trees</p>
          </div>
          <div className="bg-slate-800/60 border border-purple-500/20 rounded-2xl p-5">
            <p className="text-xs uppercase tracking-widest text-purple-400/60 font-bold mb-1">Food Packets</p>
            <p className="text-3xl font-black text-purple-400">{loading ? '…' : packets}</p>
            <p className="text-xs text-slate-500 mt-1">ready for marketplace</p>
          </div>
          <div className="bg-slate-800/60 border border-yellow-500/20 rounded-2xl p-5">
            <p className="text-xs uppercase tracking-widest text-yellow-500/60 font-bold mb-1">Sugar Packets</p>
            <p className="text-3xl font-black text-yellow-400">{loading ? '…' : sugarPackets}</p>
            <p className="text-xs text-slate-500 mt-1">🍬 in warehouse</p>
          </div>
        </div>

        {/* Crafting card */}
        <div className="bg-slate-800/60 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
              style={{ background: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.25)' }}>
              📦
            </div>
            <div>
              <p className="text-white font-black text-base">Craft Food Packet</p>
              <p className="text-slate-400 text-xs">10 Food + 1% fee = <span className="text-amber-400 font-bold">10.1 Food</span> per packet</p>
            </div>
          </div>

          {/* Quantity selector */}
          <div className="flex items-center gap-3 mb-4">
            <p className="text-xs text-slate-400 uppercase tracking-widest font-bold w-20">Quantity</p>
            <button onClick={() => setQty(q => Math.max(1, q - 1))}
              className="w-8 h-8 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-black text-lg flex items-center justify-center transition-colors">−</button>
            <input
              type="number"
              min={1}
              max={maxCraft || 1}
              value={qty}
              onChange={e => {
                const v = parseInt(e.target.value);
                if (!isNaN(v)) setQty(Math.min(Math.max(1, v), maxCraft || 1));
              }}
              className="w-16 h-8 rounded-lg bg-slate-700 border border-slate-600 text-white font-black text-base text-center focus:outline-none focus:border-purple-400 transition-colors"
            />
            <button onClick={() => setQty(q => Math.min(maxCraft || 1, q + 1))}
              className="w-8 h-8 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-black text-lg flex items-center justify-center transition-colors">+</button>
            {maxCraft > 1 && (
              <button onClick={() => setQty(maxCraft)}
                className="ml-2 text-xs text-slate-500 hover:text-slate-300 transition-colors">max ({maxCraft})</button>
            )}
          </div>

          {/* Cost summary */}
          <div className="bg-slate-900/60 rounded-xl p-4 mb-5 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 mb-0.5">Total cost</p>
              <p className="text-amber-400 font-black text-lg">{totalCost.toFixed(2)} Food</p>
            </div>
            <div className="text-slate-500 text-xl font-black">→</div>
            <div>
              <p className="text-xs text-slate-500 mb-0.5">You receive</p>
              <p className="text-purple-400 font-black text-lg">{qty} Packet{qty > 1 ? 's' : ''} 📦</p>
            </div>
          </div>

          <button onClick={craft} disabled={!canCraft || crafting}
            className="w-full py-3 rounded-xl font-black text-sm transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
            style={canCraft && !crafting ? {
              background: 'linear-gradient(135deg, rgba(168,85,247,0.4), rgba(139,92,246,0.5))',
              border: '1px solid rgba(168,85,247,0.5)',
              color: '#e9d5ff',
            } : {
              background: 'rgba(30,41,59,0.6)',
              border: '1px solid rgba(255,255,255,0.06)',
              color: '#475569',
            }}>
            {crafting ? 'Crafting…' : !canCraft ? `Need ${totalCost.toFixed(2)} Food` : `Craft ${qty} Packet${qty > 1 ? 's' : ''} →`}
          </button>

          {toast && <p className="text-xs mt-3 text-center text-slate-300">{toast}</p>}
        </div>

        {/* Sugar Packet crafting card */}
        <div className="bg-slate-800/60 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
              style={{ background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.25)' }}>
              🍬
            </div>
            <div>
              <p className="text-white font-black text-base">Craft Sugar Packet</p>
              <p className="text-slate-400 text-xs">50 Food = <span className="text-yellow-400 font-bold">1 Sugar Packet</span></p>
            </div>
          </div>

          {/* Quantity selector */}
          <div className="flex items-center gap-3 mb-4">
            <p className="text-xs text-slate-400 uppercase tracking-widest font-bold w-20">Quantity</p>
            <button onClick={() => setSugarQty(q => Math.max(1, q - 1))}
              className="w-8 h-8 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-black text-lg flex items-center justify-center transition-colors">−</button>
            <input
              type="number"
              min={1}
              max={maxSugarCraft || 1}
              value={sugarQty}
              onChange={e => {
                const v = parseInt(e.target.value);
                if (!isNaN(v)) setSugarQty(Math.min(Math.max(1, v), maxSugarCraft || 1));
              }}
              className="w-16 h-8 rounded-lg bg-slate-700 border border-slate-600 text-white font-black text-base text-center focus:outline-none focus:border-yellow-400 transition-colors"
            />
            <button onClick={() => setSugarQty(q => Math.min(maxSugarCraft || 1, q + 1))}
              className="w-8 h-8 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-black text-lg flex items-center justify-center transition-colors">+</button>
            {maxSugarCraft > 1 && (
              <button onClick={() => setSugarQty(maxSugarCraft)}
                className="ml-2 text-xs text-slate-500 hover:text-slate-300 transition-colors">max ({maxSugarCraft})</button>
            )}
          </div>

          {/* Cost summary */}
          <div className="bg-slate-900/60 rounded-xl p-4 mb-5 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 mb-0.5">Total cost</p>
              <p className="text-amber-400 font-black text-lg">{totalSugarCost} Food</p>
            </div>
            <div className="text-slate-500 text-xl font-black">→</div>
            <div>
              <p className="text-xs text-slate-500 mb-0.5">You receive</p>
              <p className="text-yellow-400 font-black text-lg">{sugarQty} Sugar Packet{sugarQty > 1 ? 's' : ''} 🍬</p>
            </div>
          </div>

          <button onClick={craftSugar} disabled={!canCraftSugar || craftingSugar}
            className="w-full py-3 rounded-xl font-black text-sm transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
            style={canCraftSugar && !craftingSugar ? {
              background: 'linear-gradient(135deg, rgba(234,179,8,0.4), rgba(202,138,4,0.5))',
              border: '1px solid rgba(234,179,8,0.5)',
              color: '#fef08a',
            } : {
              background: 'rgba(30,41,59,0.6)',
              border: '1px solid rgba(255,255,255,0.06)',
              color: '#475569',
            }}>
            {craftingSugar ? 'Crafting…' : !canCraftSugar ? `Need ${totalSugarCost} Food` : `Craft ${sugarQty} Sugar Packet${sugarQty > 1 ? 's' : ''} →`}
          </button>
        </div>

        {/* Info */}
        <div className="text-xs text-slate-600 leading-relaxed text-center">
          Food Packets are stored in your warehouse. Marketplace listing coming in Phase 2.
        </div>

        {/* Public packets leaderboard */}
        <PacketsLeaderboard highlight={waxActor} />

        </>
        )}

      </div>
    </div>
  );
}


// ─── City Map Creator (Editor) ────────────────────────────────────────────────

const CELL_COLORS: Record<number, string> = {
  0:  '#0F172A',  // empty
  1:  '#14532D',  // forest
  2:  '#1D4ED8',  // river (deep)
  3:  '#0EA5E9',  // water / lake
  4:  '#78350F',  // river bank / sand
  5:  '#475569',  // road main
  6:  '#334155',  // road minor
  7:  '#1A2A1A',  // vacant land
  8:  '#FBBF24',  // x.1my.c.wam
  9:  '#818CF8',  // w4dba.wam
  10: '#34D399',  // rudrikwax.gm
  11: '#F87171',  // ahantravh.gm
  12: '#38BDF8',  // lk5g.wam
  13: '#C084FC',  // ahn.gm
  14: '#FB923C',  // akshatvyu.gm
  15: '#A3E635',  // rudra.gm
  16: '#F472B6',  // 42.am.c.wam
  17: '#2DD4BF',  // dlpqa.c.wam
  18: '#F59E0B',  // city centre
  19: '#22D3EE',  // amerharki123
};

const MAP_ZONES = [
  { id: 0, name: 'Erase' },
  { id: 1, name: 'Forest' },
  { id: 2, name: 'River' },
  { id: 3, name: 'Lake / Water' },
  { id: 4, name: 'River Bank' },
  { id: 5, name: 'Road (main)' },
  { id: 6, name: 'Road (minor)' },
  { id: 7, name: 'Vacant Land' },
  { id: 18, name: 'City Centre' },
];

const MAP_PLAYERS = [
  { id: 8,  account: 'x.1my.c.wam',  plots: 350 },
  { id: 9,  account: 'w4dba.wam',    plots: 169 },
  { id: 10, account: 'rudrikwax.gm', plots: 126 },
  { id: 11, account: 'ahantravh.gm', plots:  99 },
  { id: 12, account: 'lk5g.wam',     plots:  80 },
  { id: 13, account: 'ahn.gm',       plots:  77 },
  { id: 14, account: 'akshatvyu.gm', plots:  70 },
  { id: 15, account: 'rudra.gm',     plots:  13 },
  { id: 16, account: '42.am.c.wam',  plots:  12 },
  { id: 17, account: 'dlpqa.c.wam',  plots:   4 },
  { id: 19, account: 'amerharki123', plots:  50 },
];

function buildBaseGrid(): Uint8Array {
  const g = new Uint8Array(100 * 100);
  const fill = (r0: number, r1: number, c0: number, c1: number, col: number) => {
    for (let r = r0; r <= r1; r++)
      for (let c = c0; c <= c1; c++)
        if (r >= 0 && r < 100 && c >= 0 && c < 100) g[r * 100 + c] = col;
  };

  // 1. Forest base
  g.fill(1);

  // 2. Water channels (8 wide, N/S/E/W)
  fill(0,  41, 47, 54, 2);   // North
  fill(59, 99, 47, 54, 2);   // South
  fill(47, 54, 59, 99, 2);   // East
  fill(47, 54,  0, 41, 2);   // West

  // 3. Roads beside each water channel (2 cells wide on both sides)
  fill(0,  41, 45, 46, 5);   // North — left road
  fill(0,  41, 55, 56, 5);   // North — right road
  fill(59, 99, 45, 46, 5);   // South — left road
  fill(59, 99, 55, 56, 5);   // South — right road
  fill(45, 46, 59, 99, 5);   // East  — top road
  fill(55, 56, 59, 99, 5);   // East  — bottom road
  fill(45, 46,  0, 41, 5);   // West  — top road
  fill(55, 56,  0, 41, 5);   // West  — bottom road

  // 4. Top-4 player zones — solid blocks, each touching both adjacent channel roads
  fill(15, 39, 31, 44,  8);  // x.1my.c.wam  350 = 25×14  NW (col44→road, no green gap)
  fill(32, 44, 59, 69,  9);  // w4dba.wam    NE  13×11=143
  fill(32, 41, 57, 58,  9);  //                  10×2 =20
  fill(32, 37, 70, 70,  9);  //                  6×1  =6   → 169
  fill(57, 58, 59, 73, 10);  // rudrikwax.gm SE  2×15 =30
  fill(59, 66, 57, 68, 10);  //                  8×12 =96  → 126
  fill(57, 58, 34, 41, 11);  // ahantravh.gm SW  2×8  =16
  fill(59, 65, 34, 44, 11);  //                  7×11 =77
  fill(66, 67, 42, 44, 11);  //                  2×3  =6   → 99

  // 5. Outer player zones — exact counts, each adjacent to a channel road
  fill( 8, 14, 34, 44, 13);  // ahn.gm       77 = 7×11  NW (above x.1my, no gap)
  fill(20, 27, 57, 66, 12);  // lk5g.wam     80 = 8×10  NE (moved closer to centre)
  fill( 0,  6, 57, 66, 14);  // akshatvyu.gm 70 = 7×10  NE outer
  fill(67, 69, 57, 60, 15);  // rudra.gm     12 = 3×4 \  compact square
  fill(67, 67, 61, 61, 15);  //               1 = 1×1 /  → 13
  fill(57, 58, 74, 79, 16);  // 42.am.c.wam  12 = 2×6
  fill(68, 69, 43, 44, 17);  // dlpqa.c.wam   4 = 2×2
  fill(38, 42,  0,  9, 19);  // amerharki123 50 = 5×10  W (adjacent to west channel)

  // 5. Ring road (4 wide around city centre 46–54 × 46–54)
  fill(42, 45, 42, 58, 5);   // north band
  fill(55, 58, 42, 58, 5);   // south band
  fill(46, 54, 42, 45, 5);   // west band
  fill(46, 54, 55, 58, 5);   // east band

  // 6. City centre (9×9)
  fill(46, 54, 46, 54, 18);

  return g;
}

function CityMapCreator({ onBack }: { onBack: () => void }) {
  const canvasRef  = React.useRef<HTMLCanvasElement>(null);
  const wrapRef    = React.useRef<HTMLDivElement>(null);
  const cellsRef   = React.useRef<Uint8Array>(new Uint8Array(100 * 100));
  const zoomRef    = React.useRef(8);
  const panRef     = React.useRef({ x: 0, y: 0 });
  const hovRef     = React.useRef<{ x: number; y: number } | null>(null);
  const isPainting = React.useRef(false);
  const isPanning  = React.useRef(false);
  const dragFrom   = React.useRef({ mx: 0, my: 0, px: 0, py: 0 });
  const hqImgRef   = React.useRef<HTMLImageElement | null>(null);

  const [zoom,     setZoom]     = React.useState(8);
  const [hov,      setHov]      = React.useState<{ x: number; y: number } | null>(null);
  const [selColor, setSelColor] = React.useState(1);
  const [tool,     setTool]     = React.useState<'paint'|'pan'|'move'>('paint');
  const [counts,   setCounts]   = React.useState<Record<number, number>>({});
  const [saveMsg,  setSaveMsg]  = React.useState('');
  const [saveLoading, setSaveLoading] = React.useState(false);
  const [loadLoading, setLoadLoading] = React.useState(false);
  const [selCell,  setSelCell]  = React.useState<{ x: number; y: number; type: number } | null>(null);
  const selColorRef = React.useRef(1);
  const toolRef     = React.useRef<'paint'|'pan'|'move'>('paint');
  const selCellRef  = React.useRef<{ x: number; y: number; type: number } | null>(null);

  // ── Draw ─────────────────────────────────────────────────────────────────────
  const draw = React.useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || canvas.width === 0) return;
    const ctx = canvas.getContext('2d')!;
    const cs = zoomRef.current;
    const { x: ox, y: oy } = panRef.current;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (let cy = 0; cy < 100; cy++) {
      for (let cx = 0; cx < 100; cx++) {
        ctx.fillStyle = CELL_COLORS[cellsRef.current[cy * 100 + cx]] ?? '#0F172A';
        ctx.fillRect(ox + cx * cs, oy + cy * cs, cs, cs);
      }
    }
    // Faint outer boundary
    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.lineWidth = 1;
    ctx.strokeRect(ox + 0.5, oy + 0.5, 100 * cs - 1, 100 * cs - 1);
    // Headquarters image on city centre (rows 46–54, cols 46–54)
    const hqImg = hqImgRef.current;
    if (hqImg && hqImg.complete && hqImg.naturalWidth > 0) {
      ctx.drawImage(hqImg, ox + 46 * cs, oy + 46 * cs, 9 * cs, 9 * cs);
    }
    // Hover highlight — only the hovered cell, no fill
    const h = hovRef.current;
    if (h && (toolRef.current === 'paint' || toolRef.current === 'move')) {
      const lw = Math.max(1, Math.round(cs * 0.1));
      ctx.strokeStyle = 'rgba(255,255,255,0.7)';
      ctx.lineWidth = lw;
      ctx.strokeRect(ox + h.x * cs + lw / 2, oy + h.y * cs + lw / 2, cs - lw, cs - lw);
    }
    // Selected cell highlight for move tool (yellow outline)
    const sc = selCellRef.current;
    if (sc && toolRef.current === 'move') {
      const lw = Math.max(2, Math.round(cs * 0.2));
      ctx.strokeStyle = 'rgba(250,204,21,1)';
      ctx.lineWidth = lw;
      ctx.strokeRect(ox + sc.x * cs + lw / 2, oy + sc.y * cs + lw / 2, cs - lw, cs - lw);
    }
  }, []);

  // ── Init ─────────────────────────────────────────────────────────────────────
  React.useLayoutEffect(() => {
    const canvas = canvasRef.current, wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    canvas.width  = wrap.clientWidth;
    canvas.height = wrap.clientHeight;
    panRef.current = {
      x: Math.round((wrap.clientWidth  - 100 * 8) / 2),
      y: Math.round((wrap.clientHeight - 100 * 8) / 2),
    };
    cellsRef.current = buildBaseGrid();
    const img = new Image();
    img.src = '/samatvam/headquarters.jpg';
    img.onload = () => { hqImgRef.current = img; draw(); };
    draw();
  }, [draw]);

  React.useEffect(() => { draw(); }, [draw, zoom, hov]);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const getCell = (e: React.MouseEvent) => {
    const r = canvasRef.current!.getBoundingClientRect();
    const cx = Math.floor((e.clientX - r.left - panRef.current.x) / zoomRef.current);
    const cy = Math.floor((e.clientY - r.top  - panRef.current.y) / zoomRef.current);
    return cx >= 0 && cx < 100 && cy >= 0 && cy < 100 ? { x: cx, y: cy } : null;
  };

  const recount = () => {
    const c: Record<number, number> = {};
    cellsRef.current.forEach(id => { c[id] = (c[id] ?? 0) + 1; });
    setCounts(c);
  };

  const applyZoom = (newZoom: number, originX: number, originY: number) => {
    // Zoom toward the given screen point (keeps that point fixed under cursor)
    const oldZoom = zoomRef.current;
    const clipped = Math.max(2, Math.min(40, newZoom));
    const wx = (originX - panRef.current.x) / oldZoom;
    const wy = (originY - panRef.current.y) / oldZoom;
    panRef.current = { x: originX - wx * clipped, y: originY - wy * clipped };
    zoomRef.current = clipped;
    setZoom(clipped);
    draw();
  };

  const fitMap = () => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const fz = Math.min(Math.floor(wrap.clientWidth / 100), Math.floor(wrap.clientHeight / 100));
    const cz = Math.max(2, fz);
    panRef.current = {
      x: Math.round((wrap.clientWidth  - 100 * cz) / 2),
      y: Math.round((wrap.clientHeight - 100 * cz) / 2),
    };
    zoomRef.current = cz;
    setZoom(cz);
    draw();
  };

  const resetLayout = () => { handleLoad(); };

  const handleSave = async () => {
    setSaveLoading(true); setSaveMsg('');
    try {
      const token = localStorage.getItem('token') || '';
      const grid_data = Array.from(cellsRef.current);
      const r = await fetch('/samatvam/api/samatvam/admin/citymap/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ grid_data }),
      });
      const d = await r.json();
      setSaveMsg(d.success ? '✅ Saved' : `❌ ${d.error}`);
    } catch { setSaveMsg('❌ Network error'); }
    setSaveLoading(false);
    setTimeout(() => setSaveMsg(''), 3000);
  };

  const handleLoad = async () => {
    setLoadLoading(true); setSaveMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/citymap/load');
      const d = await r.json();
      if (d.success && Array.isArray(d.grid_data)) {
        cellsRef.current = new Uint8Array(d.grid_data);
        recount(); draw();
        setSaveMsg(`✅ Loaded (saved ${new Date(d.saved_at).toLocaleString()})`);
      } else {
        setSaveMsg(d.message || '❌ No saved map');
      }
    } catch { setSaveMsg('❌ Network error'); }
    setLoadLoading(false);
    setTimeout(() => setSaveMsg(''), 4000);
  };

  // Auto-load saved map on mount (after building base grid)
  React.useEffect(() => {
    (async () => {
      try {
        const r = await fetch('/samatvam/api/samatvam/citymap/load');
        const d = await r.json();
        if (d.success && Array.isArray(d.grid_data)) {
          cellsRef.current = new Uint8Array(d.grid_data);
          recount(); draw();
          console.log('[CityMapCreator] Auto-loaded saved map');
        }
      } catch {}
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Mouse ────────────────────────────────────────────────────────────────────
  const onMD = (e: React.MouseEvent) => {
    if (e.button === 1 || e.button === 2 || toolRef.current === 'pan') {
      // Pan: middle button, right button, or pan tool active
      isPanning.current = true;
      dragFrom.current = { mx: e.clientX, my: e.clientY, px: panRef.current.x, py: panRef.current.y };
      return;
    }
    // Move tool: first click selects, second click places
    if (toolRef.current === 'move') {
      const cell = getCell(e);
      if (!cell) return;
      const existing = selCellRef.current;
      if (!existing) {
        const type = cellsRef.current[cell.y * 100 + cell.x];
        selCellRef.current = { x: cell.x, y: cell.y, type };
        setSelCell({ x: cell.x, y: cell.y, type });
      } else {
        cellsRef.current[cell.y * 100 + cell.x] = existing.type;
        cellsRef.current[existing.y * 100 + existing.x] = 1; // revert source to forest
        selCellRef.current = null;
        setSelCell(null);
        recount();
      }
      draw();
      return;
    }
    // Paint
    isPainting.current = true;
    const cell = getCell(e);
    if (cell) { cellsRef.current[cell.y * 100 + cell.x] = selColorRef.current; draw(); }
  };

  const onMM = (e: React.MouseEvent) => {
    if (isPanning.current) {
      panRef.current = {
        x: dragFrom.current.px + e.clientX - dragFrom.current.mx,
        y: dragFrom.current.py + e.clientY - dragFrom.current.my,
      };
      draw();
      return;
    }
    const cell = getCell(e);
    hovRef.current = cell;
    setHov(cell);
    if (isPainting.current && cell) {
      cellsRef.current[cell.y * 100 + cell.x] = selColorRef.current;
      draw();
    }
  };

  const onMU = () => {
    if (isPainting.current) recount();
    isPainting.current = false;
    isPanning.current  = false;
  };

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const r = canvasRef.current!.getBoundingClientRect();
    applyZoom(zoomRef.current * (e.deltaY < 0 ? 1.15 : 0.87), e.clientX - r.left, e.clientY - r.top);
  };

  const selectColor = (id: number) => { selColorRef.current = id; setSelColor(id); };
  const switchTool = (t: 'paint'|'pan'|'move') => {
    toolRef.current = t;
    setTool(t);
    // Clear move selection when switching away from move tool
    if (t !== 'move') { selCellRef.current = null; setSelCell(null); draw(); }
  };

  const cursorClass = tool === 'pan'
    ? (isPanning.current ? 'cursor-grabbing' : 'cursor-grab')
    : tool === 'move'
    ? (selCell ? 'cursor-cell' : 'cursor-pointer')
    : 'cursor-crosshair';

  return (
    <div className="h-screen bg-slate-950 text-white flex flex-col overflow-hidden" onContextMenu={e => e.preventDefault()}>

      {/* Header */}
      <div className="flex-shrink-0 bg-slate-900/95 backdrop-blur-sm border-b border-white/10">
        <div className="px-4 py-2 flex items-center gap-3">
          <button onClick={onBack} className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors flex-shrink-0">
            <ArrowDownLeft className="w-4 h-4 rotate-45" /><span className="text-sm">Dashboard</span>
          </button>

          <span className="text-slate-700">|</span>
          <span className="text-sm font-black text-white">🗺️ City Map Editor — 100×100</span>

          <span className="text-slate-700">|</span>

          {/* Tool toggle */}
          <div className="flex items-center gap-1 bg-slate-800 rounded-lg p-0.5">
            <button onClick={() => switchTool('paint')}
              className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${tool==='paint' ? 'bg-white/15 text-white' : 'text-slate-500 hover:text-slate-300'}`}>
              ✏️ Paint
            </button>
            <button onClick={() => switchTool('move')}
              className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${tool==='move' ? 'bg-amber-500/25 text-amber-300' : 'text-slate-500 hover:text-slate-300'}`}
              title="Click a cell to select it (yellow border), then click destination to move it">
              ⊞ Move
            </button>
            <button onClick={() => switchTool('pan')}
              className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${tool==='pan' ? 'bg-white/15 text-white' : 'text-slate-500 hover:text-slate-300'}`}>
              ✋ Pan
            </button>
          </div>

          <span className="text-slate-700">|</span>

          {/* Zoom controls */}
          <div className="flex items-center gap-1">
            <button onClick={() => { const r = canvasRef.current!.getBoundingClientRect(); applyZoom(zoomRef.current * 1.25, r.width/2, r.height/2); }}
              className="w-7 h-7 bg-slate-800 hover:bg-slate-700 rounded-lg text-white font-bold text-base flex items-center justify-center transition-colors">+</button>
            <span className="text-xs text-slate-400 w-12 text-center tabular-nums">{Math.round(zoom)}px</span>
            <button onClick={() => { const r = canvasRef.current!.getBoundingClientRect(); applyZoom(zoomRef.current * 0.8, r.width/2, r.height/2); }}
              className="w-7 h-7 bg-slate-800 hover:bg-slate-700 rounded-lg text-white font-bold text-base flex items-center justify-center transition-colors">−</button>
            <button onClick={fitMap}
              className="px-2 h-7 bg-slate-800 hover:bg-slate-700 rounded-lg text-xs text-slate-300 transition-colors ml-1">Fit</button>
            <button onClick={resetLayout}
              className="px-2 h-7 bg-blue-950/60 hover:bg-blue-900/60 border border-blue-700/30 rounded-lg text-xs text-blue-400 transition-colors ml-2">↺ Reload</button>
          </div>

          <span className="text-slate-700 hidden lg:block">|</span>

          {/* Save / Load */}
          <div className="flex items-center gap-1 hidden lg:flex">
            <button onClick={handleSave} disabled={saveLoading}
              className="px-3 h-7 bg-green-950/60 hover:bg-green-900/60 border border-green-700/30 rounded-lg text-xs text-green-400 transition-colors disabled:opacity-50">
              {saveLoading ? 'Saving…' : '💾 Save'}
            </button>
            <button onClick={handleLoad} disabled={loadLoading}
              className="px-3 h-7 bg-blue-950/60 hover:bg-blue-900/60 border border-blue-700/30 rounded-lg text-xs text-blue-400 transition-colors disabled:opacity-50">
              {loadLoading ? 'Loading…' : '📂 Load'}
            </button>
          </div>

          <div className="flex-1" />
          {selCell && tool === 'move' && (
            <span className="text-xs font-mono text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-md">
              Selected ({selCell.x},{selCell.y}) → click destination
            </span>
          )}
          {saveMsg && <span className="text-xs text-slate-300">{saveMsg}</span>}
          {hov && (() => {
            const t = cellsRef.current[hov.y * 100 + hov.x];
            const owner = MAP_ZONES.find(z => z.id === t)?.name || MAP_PLAYERS.find(p => p.id === t)?.account || '?';
            return (
              <span className="text-xs font-mono text-slate-400">
                ({hov.x},{hov.y}) · <span className="text-slate-200 font-semibold">{owner}</span>
              </span>
            );
          })()}
          <span className="text-xs text-slate-600 hidden lg:block">Right-click = pan · Scroll = zoom</span>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">

        {/* Palette */}
        <div className="w-44 flex-shrink-0 bg-slate-900/80 border-r border-white/10 overflow-y-auto p-3 flex flex-col gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Zones</p>
            <div className="space-y-0.5">
              {MAP_ZONES.map(z => (
                <button key={z.id} onClick={() => selectColor(z.id)}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-all ${selColor===z.id ? 'bg-white/15 ring-1 ring-white/25' : 'hover:bg-white/5'}`}>
                  <div className="w-3.5 h-3.5 rounded-sm flex-shrink-0"
                    style={{ background: CELL_COLORS[z.id], border: z.id===0 ? '1px solid #334155' : 'none' }} />
                  <span className="text-xs text-slate-300">{z.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Players</p>
            <div className="space-y-0.5">
              {MAP_PLAYERS.map(p => {
                const painted = counts[p.id] ?? 0;
                const pct = Math.min(1, painted / p.plots);
                return (
                  <button key={p.id} onClick={() => selectColor(p.id)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-all ${selColor===p.id ? 'bg-white/15 ring-1 ring-white/25' : 'hover:bg-white/5'}`}>
                    <div className="w-3.5 h-3.5 rounded-sm flex-shrink-0" style={{ background: CELL_COLORS[p.id] }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-mono text-slate-300 truncate">{p.account}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <div className="flex-1 h-1 bg-slate-700 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct*100}%`, background: CELL_COLORS[p.id] }} />
                        </div>
                        <span className={`text-[9px] flex-shrink-0 ${painted>=p.plots?'text-green-400':painted>0?'text-amber-400':'text-slate-600'}`}>
                          {painted}/{p.plots}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-auto pt-3 border-t border-white/10">
            <p className="text-[10px] text-slate-600 mb-1">Selected</p>
            <div className="w-full h-7 rounded-lg border border-white/10" style={{ background: CELL_COLORS[selColor] }} />
          </div>
        </div>

        {/* Canvas */}
        <div
          ref={wrapRef}
          className={`flex-1 overflow-hidden bg-[#070d14] ${cursorClass}`}
          onMouseDown={onMD} onMouseMove={onMM} onMouseUp={onMU} onMouseLeave={onMU}
          onWheel={onWheel}
        >
          <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%' }} />
        </div>
      </div>
    </div>
  );
}

// ─── City Map Page (public — map image) ──────────────────────────────────────

const BMAP_TILE = 42;
const BMAP_GAP  = 2;
const BMAP_PAD  = 3;

const BMAP_KEYFRAMES = `
  @keyframes bmap-aurora {
    0%   { background-position: 0%   50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0%   50%; }
  }
  @keyframes bmap-amber {
    0%, 100% { box-shadow: 0 0 8px 2px #f59e0b44; }
    50%       { box-shadow: 0 0 20px 7px #f59e0b88; }
  }
  @keyframes bmap-green {
    0%, 100% { box-shadow: 0 0 8px 2px #22c55e44; }
    50%       { box-shadow: 0 0 20px 7px #22c55e88; }
  }
  @keyframes bmap-blue {
    0%, 100% { box-shadow: 0 0 10px 3px #06b6d455; }
    50%       { box-shadow: 0 0 24px 9px #06b6d4aa; }
  }
  @keyframes bmap-shimmer {
    0%   { left: -80%; }
    100% { left: 180%; }
  }
  @keyframes bmap-star {
    0%, 100% { opacity: 0.15; }
    50%       { opacity: 0.85; }
  }
  @keyframes bmap-float {
    0%   { transform: translateY(0) scale(1);    opacity: 0.7; }
    100% { transform: translateY(-60px) scale(0); opacity: 0; }
  }
  @keyframes bmap-beacon {
    0%, 100% { transform: translateZ(36px) scale(1);    opacity: 0.7; }
    50%       { transform: translateZ(36px) scale(1.18); opacity: 1;   }
  }
  @keyframes bmap-water-ring {
    0%   { transform: translateZ(35px) scale(0.85); opacity: 0.9; }
    50%  { transform: translateZ(35px) scale(1.05); opacity: 1; }
    100% { transform: translateZ(35px) scale(0.85); opacity: 0.9; }
  }
`;

interface BmapPlotRow {
  plot_index: number;
  coord_x: number;
  coord_y: number;
  state: string;
  wax_account: string | null;
  species_index: number | null;
  infrastructure_type: string | null;
}

type BmapKind = 'empty' | 'bare' | 'sapling' | 'tree' | 'orchard' | 'farm' | 'well';

interface BmapBuildingDef {
  label: string;
  coin: string;
  layer: number;
  topGrad: string;
  glowColor: string;
  southColor: string;
  eastColor: string;
  height: number;
  anim: string;
  animDurBase: number;
}

const BMAP_DEF: Record<BmapKind, BmapBuildingDef> = {
  empty:   { label: '',        coin: '',        layer: 0, topGrad: '#0a0703',                                                          glowColor: 'transparent', southColor: '#060502',  eastColor: '#050401',  height: 2,  anim: 'none',       animDurBase: 3   },
  bare:    { label: 'Land',    coin: 'BHOOMI',  layer: 1, topGrad: 'linear-gradient(145deg,#fde68a 0%,#f59e0b 50%,#b45309 100%)',     glowColor: '#f59e0b',     southColor: '#92400e',  eastColor: '#78350f',  height: 10, anim: 'bmap-amber', animDurBase: 2.4 },
  sapling: { label: 'Sapling', coin: 'ANNA',    layer: 2, topGrad: 'linear-gradient(145deg,#bbf7d0 0%,#4ade80 40%,#16a34a 100%)',     glowColor: '#22c55e',     southColor: '#15803d',  eastColor: '#166534',  height: 16, anim: 'bmap-green', animDurBase: 2.6 },
  tree:    { label: 'Tree',    coin: 'ANNA',    layer: 2, topGrad: 'linear-gradient(145deg,#86efac 0%,#22c55e 40%,#15803d 100%)',     glowColor: '#22c55e',     southColor: '#166534',  eastColor: '#14532d',  height: 24, anim: 'bmap-green', animDurBase: 2.8 },
  orchard: { label: 'Orchard', coin: 'ANNA',    layer: 2, topGrad: 'linear-gradient(145deg,#6ee7b7 0%,#10b981 40%,#047857 100%)',     glowColor: '#10b981',     southColor: '#065f46',  eastColor: '#064e3b',  height: 20, anim: 'bmap-green', animDurBase: 3.0 },
  farm:    { label: 'Farm',    coin: 'ANNA',    layer: 2, topGrad: 'linear-gradient(145deg,#34d399 0%,#059669 40%,#065f46 100%)',     glowColor: '#10b981',     southColor: '#064e3b',  eastColor: '#052e1c',  height: 28, anim: 'bmap-green', animDurBase: 2.2 },
  well:    { label: 'Well',    coin: 'WARI',    layer: 3, topGrad: 'linear-gradient(145deg,#a5f3fc 0%,#22d3ee 35%,#0e7490 100%)',     glowColor: '#06b6d4',     southColor: '#0c6282',  eastColor: '#0a4f6a',  height: 34, anim: 'bmap-blue',  animDurBase: 2.0 },
};

function bmapGetKind(plot: BmapPlotRow | null | undefined): BmapKind {
  if (!plot || plot.state !== 'owned') return 'empty';
  if (plot.infrastructure_type === 'water_harvester') return 'well';
  if (!plot.species_index) return 'bare';
  if (plot.species_index <= 2) return 'sapling';
  if (plot.species_index <= 4) return 'tree';
  if (plot.species_index <= 6) return 'orchard';
  return 'farm';
}

function BmapTile({ plot, isOrigin, dimmed, onClick }: {
  plot: BmapPlotRow | null;
  isOrigin: boolean;
  dimmed: boolean;
  onClick: () => void;
}) {
  const kind = bmapGetKind(plot);
  const b    = BMAP_DEF[kind];
  const h    = b.height;
  const owned = kind !== 'empty';

  const pidx       = plot?.plot_index ?? 0;
  const animDur    = `${b.animDurBase + (pidx % 6) * 0.25}s`;
  const animDelay  = `${(pidx * 0.19) % 2.8}s`;
  const shimmerDur = `${2.0 + (pidx % 5) * 0.3}s`;
  const shimmerDly = `${(pidx * 0.41) % 3}s`;

  return (
    <div style={{
      width: BMAP_TILE, height: BMAP_TILE,
      position: 'relative', transformStyle: 'preserve-3d',
      cursor: owned ? 'pointer' : 'default',
      opacity: dimmed ? 0.15 : 1,
      transition: 'opacity 0.25s',
      pointerEvents: 'none',
    }}>
      {/* Click zone */}
      <div
        style={{ position: 'absolute', inset: 0, zIndex: 10, transform: `translateZ(${h + 1}px)`, pointerEvents: 'auto' }}
        onClick={(e) => { e.stopPropagation(); onClick(); }}
      />

      {/* ── Top face ── */}
      <div style={{
        position: 'absolute', inset: 0,
        background: owned
          ? b.topGrad
          : (isOrigin ? 'linear-gradient(145deg,#2a1e04,#1a1204)' : b.topGrad),
        border: `1px solid ${owned ? b.glowColor + '55' : (isOrigin ? '#f59e0b22' : '#ffffff08')}`,
        borderRadius: 2,
        transform: `translateZ(${h}px)`,
        overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 1,
        animation: (owned && b.anim !== 'none') ? `${b.anim} ${animDur} ease-in-out infinite` : 'none',
        animationDelay: animDelay,
        pointerEvents: 'none',
      }}>
        {/* Shimmer sweep on non-bare tiles */}
        {owned && kind !== 'bare' && (
          <div style={{
            position: 'absolute', top: 0, bottom: 0, width: '55%',
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
            animation: `bmap-shimmer ${shimmerDur} ease-in-out infinite`,
            animationDelay: shimmerDly,
            pointerEvents: 'none',
          }}/>
        )}

        {/* Label */}
        {owned && (
          <span style={{
            color: '#ffffffdd', fontSize: kind === 'bare' ? 6 : 6.5,
            fontFamily: 'monospace', fontWeight: 900,
            letterSpacing: 0.2, textAlign: 'center', zIndex: 1,
            textShadow: `0 0 5px ${b.glowColor}`,
            lineHeight: 1.2,
          }}>
            {kind === 'bare'
              ? (plot?.wax_account?.slice(0, 7) ?? '')
              : b.label
            }
          </span>
        )}

        {/* Origin marker when empty */}
        {isOrigin && !owned && (
          <span style={{ color: '#f59e0b33', fontSize: 6, fontWeight: 900 }}>⊕</span>
        )}

        {/* Well ripple dots */}
        {kind === 'well' && (
          <>
            <div style={{ position: 'absolute', inset: 3, border: '1px solid #06b6d455', borderRadius: '50%', animation: `bmap-water-ring 2.4s ease-in-out infinite`, animationDelay: '0s', pointerEvents: 'none' }}/>
            <div style={{ position: 'absolute', inset: 8, border: '1px solid #06b6d488', borderRadius: '50%', animation: `bmap-water-ring 2.4s ease-in-out infinite`, animationDelay: '0.6s', pointerEvents: 'none' }}/>
          </>
        )}

        {/* Food sparkle dots */}
        {(kind === 'tree' || kind === 'farm') && (
          <>
            {[{l:'18%',t:'22%'},{l:'68%',t:'38%'},{l:'38%',t:'70%'}].map((w, wi) => (
              <div key={wi} style={{
                position: 'absolute', left: w.l, top: w.t,
                width: 2, height: 2, borderRadius: '50%',
                background: b.glowColor,
                boxShadow: `0 0 4px 2px ${b.glowColor}99`,
                animation: `bmap-star ${2 + wi * 0.5}s ease-in-out infinite`,
                animationDelay: `${wi * 0.4}s`,
                pointerEvents: 'none',
              }}/>
            ))}
          </>
        )}
      </div>

      {/* ── South face ── */}
      <div style={{
        position: 'absolute', left: 0, top: BMAP_TILE,
        width: BMAP_TILE, height: Math.max(h, 1),
        background: `linear-gradient(180deg, ${b.southColor} 0%, ${b.eastColor} 100%)`,
        border: `1px solid ${owned ? b.glowColor + '22' : '#ffffff05'}`,
        borderTop: 'none', borderRadius: '0 0 2px 2px',
        transformOrigin: 'top center', transform: 'rotateX(-90deg)',
        pointerEvents: 'none',
      }}/>

      {/* ── East face ── */}
      <div style={{
        position: 'absolute', left: BMAP_TILE, top: 0,
        width: Math.max(h, 1), height: BMAP_TILE,
        background: `linear-gradient(90deg, ${b.southColor} 0%, ${b.eastColor} 100%)`,
        border: `1px solid ${owned ? b.glowColor + '18' : '#ffffff04'}`,
        borderLeft: 'none', borderRadius: '0 2px 2px 0',
        transformOrigin: 'left center', transform: 'rotateY(90deg)',
        pointerEvents: 'none',
      }}/>

      {/* Origin beacon ring */}
      {isOrigin && owned && (
        <div style={{
          position: 'absolute', inset: -4,
          border: '2px solid #f59e0b88',
          borderRadius: 5,
          transform: `translateZ(${h}px)`,
          animation: 'bmap-beacon 2.6s ease-in-out infinite',
          boxShadow: '0 0 18px 6px #f59e0b55, inset 0 0 8px 2px #f59e0b22',
          pointerEvents: 'none',
        }}/>
      )}
    </div>
  );
}

const BMAP_LAYERS = [
  { layer: 1,  coin: 'BHOOMI',  label: 'Land',      color: '#f59e0b', kinds: ['bare']                             as BmapKind[] },
  { layer: 2,  coin: 'ANNA',    label: 'Food',       color: '#22c55e', kinds: ['sapling','tree','orchard','farm']  as BmapKind[] },
  { layer: 3,  coin: 'WARI',    label: 'Water',      color: '#06b6d4', kinds: ['well']                             as BmapKind[] },
  { layer: 4,  coin: 'GRIHA',   label: 'Shelter',    color: '#a78bfa', kinds: []                                   as BmapKind[] },
  { layer: 5,  coin: 'URJA',    label: 'Energy',     color: '#fbbf24', kinds: []                                   as BmapKind[] },
  { layer: 6,  coin: 'AROGYA',  label: 'Health',     color: '#f472b6', kinds: []                                   as BmapKind[] },
  { layer: 7,  coin: 'VIDYA',   label: 'Knowledge',  color: '#818cf8', kinds: []                                   as BmapKind[] },
  { layer: 8,  coin: 'VANIJYA', label: 'Commerce',   color: '#fb923c', kinds: []                                   as BmapKind[] },
  { layer: 9,  coin: 'YANTRA',  label: 'Industry',   color: '#94a3b8', kinds: []                                   as BmapKind[] },
  { layer: 10, coin: 'KALA',    label: 'Arts',       color: '#e879f9', kinds: []                                   as BmapKind[] },
];

// ─── Biorefinery Batch Modal ─────────────────────────────────────────────────

const BIO_YIELD_MAP: Record<number, number> = { 1: 3, 2: 6, 3: 10 };

function BiorefineryBatchModal({ onClose, waxAcct }: { onClose: () => void; waxAcct: string }) {
  type BatchItem = { id: number; started_at: string; completes_at: string; status: string; ethanol_litres: number; firewood_used: number };
  const [inv, setInv] = React.useState<Record<string, number>>({});
  const [batches, setBatches] = React.useState<BatchItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [fwQty, setFwQty] = React.useState(1);
  const [starting, setStarting] = React.useState(false);
  const [collectingId, setCollectingId] = React.useState<number | null>(null);
  const [msg, setMsg] = React.useState('');
  const [now, setNow] = React.useState(Date.now());

  const fetchBatches = React.useCallback(() => {
    if (!waxAcct) { setLoading(false); return; }
    fetch(`/samatvam/api/samatvam/biorefinery/batch/${waxAcct}`)
      .then(r => r.json())
      .then(d => { if (d.success) { setBatches(d.batches); setInv(d.inventory); } })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [waxAcct]);

  React.useEffect(() => { fetchBatches(); }, [fetchBatches]);
  React.useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  const sugar   = inv['sugar_packet']  ?? 0;
  const fw      = inv['firewood']      ?? 0;
  const yc      = inv['yeast_culture'] ?? 0;
  const ethanol = inv['ethanol']       ?? 0;
  const meetsSugar = sugar >= 20;
  const meetsFw    = fw >= fwQty;
  const meetsYc    = yc >= fwQty;
  const meetsAll   = meetsSugar && meetsFw && meetsYc;
  const yieldL     = BIO_YIELD_MAP[fwQty];

  const fmtTime = (s: number) => { const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const sec = s % 60; return `${h}h ${String(m).padStart(2,'0')}m ${String(sec).padStart(2,'0')}s`; };

  const req = (met: boolean, label: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderRadius: 10, background: met ? 'rgba(34,197,94,0.07)' : 'rgba(255,255,255,0.03)', border: `1px solid ${met ? 'rgba(34,197,94,0.25)' : 'rgba(255,255,255,0.08)'}` }}>
      <span style={{ fontSize: 16 }}>{met ? '✅' : '❌'}</span>
      <span style={{ color: met ? '#86efac' : '#94a3b8', fontSize: 13, fontWeight: met ? 700 : 400 }}>{label}</span>
    </div>
  );

  const handleStart = async () => {
    if (!waxAcct) { setMsg('Connect WAX wallet first'); return; }
    setStarting(true); setMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/biorefinery/start-batch', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxAcct, firewood_qty: fwQty }),
      });
      const d = await r.json();
      if (d.success) { fetchBatches(); setMsg(`✅ Batch started — ${yieldL}L ethanol in 24h`); }
      else { setMsg(`❌ ${d.error}`); }
    } catch { setMsg('❌ Network error'); }
    setStarting(false);
  };

  const handleCollect = async (batchId: number, litres: number) => {
    setCollectingId(batchId); setMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/biorefinery/collect', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxAcct, batch_id: batchId }),
      });
      const d = await r.json();
      if (d.success) { setMsg(`✅ Collected ${litres}L ethanol! Total: ${d.total_ethanol}L`); fetchBatches(); }
      else { setMsg(`❌ ${d.error}`); }
    } catch { setMsg('❌ Network error'); }
    setCollectingId(null);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(5,4,2,0.88)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: 'rgba(10,7,2,0.98)', border: '2px solid rgba(34,197,94,0.45)', borderRadius: 20, padding: '28px 32px', width: 480, maxWidth: '92vw', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 0 80px rgba(34,197,94,0.18), 0 24px 80px rgba(0,0,0,0.85)' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, rgba(34,197,94,0.25), rgba(16,185,129,0.25))', border: '2px solid rgba(34,197,94,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>⚗️</div>
            <div>
              <p style={{ color: '#86efac', fontSize: 17, fontWeight: 900, margin: 0, letterSpacing: -0.3 }}>Sugar Pre-Processor</p>
              <p style={{ color: '#22c55e60', fontSize: 9, margin: 0, fontWeight: 700, letterSpacing: 2.5, textTransform: 'uppercase' }}>Biorefinery · Batch Run · 24 Hours</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#ffffff60', fontSize: 20, width: 34, height: 34, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>
        </div>

        {/* Ethanol balance */}
        {ethanol > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderRadius: 10, background: 'rgba(34,211,238,0.06)', border: '1px solid rgba(34,211,238,0.2)', marginBottom: 16 }}>
            <span>🧪</span>
            <span style={{ color: '#22d3ee', fontSize: 12, fontWeight: 700 }}>Ethanol in store: <strong>{ethanol}L</strong></span>
          </div>
        )}

        {/* Active batches list */}
        {batches.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <p style={{ color: '#22c55e80', fontSize: 9, fontWeight: 900, letterSpacing: 2.5, textTransform: 'uppercase', margin: '0 0 10px' }}>Active Batches ({batches.length})</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {batches.map(b => {
                const secsLeft = b.status === 'running' ? Math.max(0, Math.floor((new Date(b.completes_at).getTime() - now) / 1000)) : 0;
                const isReady = b.status === 'ready';
                return (
                  <div key={b.id} style={{ borderRadius: 12, padding: '12px 16px', background: isReady ? 'rgba(74,222,128,0.08)' : 'rgba(34,197,94,0.05)', border: `1px solid ${isReady ? 'rgba(74,222,128,0.4)' : 'rgba(34,197,94,0.2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div>
                      <div style={{ color: isReady ? '#4ade80' : '#86efac', fontSize: 12, fontWeight: 900 }}>
                        {isReady ? '✅ Ready' : fmtTime(secsLeft)}
                      </div>
                      <div style={{ color: '#ffffff40', fontSize: 10, marginTop: 2 }}>
                        {b.ethanol_litres}L · {['🌲','🌲🌲','🌲🌲🌲'][b.firewood_used - 1]} {b.firewood_used}fw · #{b.id}
                      </div>
                    </div>
                    {isReady && (
                      <button onClick={() => handleCollect(b.id, b.ethanol_litres)} disabled={collectingId === b.id}
                        style={{ padding: '8px 16px', borderRadius: 10, fontWeight: 900, fontSize: 12, cursor: collectingId === b.id ? 'not-allowed' : 'pointer', border: 'none', background: collectingId === b.id ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg, #0891b2, #0d9488)', color: collectingId === b.id ? '#ffffff30' : 'white', flexShrink: 0 }}>
                        {collectingId === b.id ? '⏳' : `Collect ${b.ethanol_litres}L`}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Start new batch */}
        <p style={{ color: '#22c55e', fontSize: 9, fontWeight: 900, letterSpacing: 2.5, textTransform: 'uppercase', margin: '0 0 8px' }}>Firewood & Yeast (sets yield)</p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {[1, 2, 3].map(n => {
            const selected = fwQty === n;
            const canAfford = fw >= n && yc >= n;
            return (
              <button key={n} onClick={() => setFwQty(n)}
                style={{ flex: 1, padding: '10px 6px', borderRadius: 10, border: `2px solid ${selected ? '#22c55e' : 'rgba(255,255,255,0.08)'}`, background: selected ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.03)', color: selected ? '#86efac' : canAfford ? '#94a3b8' : '#475569', cursor: 'pointer', fontWeight: 900, fontSize: 12, transition: 'all 0.15s' }}>
                <div style={{ fontSize: 16, marginBottom: 3 }}>{['🌲','🌲🌲','🌲🌲🌲'][n-1]}</div>
                <div>{n} · {BIO_YIELD_MAP[n]}L</div>
                <div style={{ fontSize: 9, opacity: 0.6, marginTop: 2 }}>{[30,65,100][n-1]}% yield</div>
              </button>
            );
          })}
        </div>

        <p style={{ color: '#22c55e', fontSize: 9, fontWeight: 900, letterSpacing: 2.5, textTransform: 'uppercase', margin: '0 0 10px' }}>Materials Required</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {loading ? (
            <p style={{ color: '#64748b', fontSize: 12, textAlign: 'center', padding: 12 }}>Checking your inventory…</p>
          ) : !waxAcct ? (
            <p style={{ color: '#64748b', fontSize: 12, textAlign: 'center', padding: 12 }}>Connect WAX wallet to check materials</p>
          ) : (
            <>
              {req(meetsSugar, `20 Sugar Packets  (you have ${sugar})`)}
              {req(meetsFw,    `${fwQty} Firewood  (you have ${fw})`)}
              {req(meetsYc,    `${fwQty} Yeast Culture  (you have ${yc})`)}
            </>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 12, background: 'rgba(34,211,238,0.06)', border: '1px solid rgba(34,211,238,0.2)', marginBottom: 18 }}>
          <span style={{ fontSize: 22 }}>🧪</span>
          <div>
            <p style={{ color: '#22d3ee', fontSize: 14, fontWeight: 900, margin: 0 }}>{yieldL} Litres Ethanol</p>
            <p style={{ color: '#22d3ee60', fontSize: 10, margin: 0, fontWeight: 700 }}>Output after 24-hour fermentation · {[30,65,100][fwQty-1]}% yield</p>
          </div>
        </div>

        {msg && <p style={{ color: msg.startsWith('✅') ? '#4ade80' : '#f87171', fontSize: 12, margin: '0 0 12px', padding: '10px 14px', background: 'rgba(0,0,0,0.2)', borderRadius: 8 }}>{msg}</p>}

        <button onClick={handleStart} disabled={!meetsAll || starting || loading}
          style={{ width: '100%', padding: '13px', borderRadius: 12, fontWeight: 900, fontSize: 14, cursor: (!meetsAll || starting) ? 'not-allowed' : 'pointer', border: 'none', background: (!meetsAll || starting) ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg, #16a34a, #0d9488)', color: (!meetsAll || starting) ? '#ffffff30' : 'white', transition: 'all 0.2s', boxShadow: meetsAll ? '0 0 24px rgba(22,163,74,0.35)' : 'none' }}>
          {starting ? '⏳ Starting batch…' : meetsAll ? `⚗️ Start Batch — ${yieldL}L Ethanol in 24h` : '🔒 Materials not met'}
        </button>
      </div>
    </div>
  );
}

// ─── Biorefinery Page ────────────────────────────────────────────────────────

const BIO_W = 1600;
const BIO_H = 912;

type BioLabel = {
  id: string;
  label: string;
  cx: number; // pixels from left
  cy: number; // pixels from bottom (flipped: svgY = BIO_H - cy)
  icon: string;
  action?: string;
};

// Placeholder labels — user will add more later
const BIO_LABELS_DEFAULT: BioLabel[] = [
  { id: 'fermenters', label: 'Fermentation Tanks',  cx: 520,  cy: 480, icon: '🧫' },
  { id: 'bioreactor',  label: 'Bioreactor Core',    cx: 960,  cy: 420, icon: '⚗️' },
  { id: 'control',     label: 'Control Panel',      cx: 1340, cy: 540, icon: '🖥️' },
  { id: 'pipeline',    label: 'Biomass Pipeline',   cx: 200,  cy: 360, icon: '🔩' },
];

function BiorefineryPage({ onBack }: { onBack: () => void }) {
  const svgRef = React.useRef<SVGSVGElement>(null);
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth < 768);
  const [hoveredPin, setHoveredPin] = React.useState<string | null>(null);
  const [coords, setCoords] = React.useState<{ x: number; y: number } | null>(null);
  const [batchModalOpen, setBatchModalOpen] = React.useState(false);
  const [waxAcct, setWaxAcct] = React.useState('');
  const [waxSession, setWaxSession] = React.useState<any>(null);
  const [waxConnecting, setWaxConnecting] = React.useState(false);

  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  React.useEffect(() => {
    getSessionKit().restore().then((s: any) => {
      if (s) {
        setWaxSession(s);
        setWaxAcct(String(s.actor));
      } else {
        setWaxAcct(localStorage.getItem('samatvam_active_account') || '');
      }
    }).catch(() => {
      setWaxAcct(localStorage.getItem('samatvam_active_account') || '');
    });
  }, []);

  const handleWaxConnect = async () => {
    setWaxConnecting(true);
    try {
      const { session: s } = await getSessionKit().login();
      const acct = String(s.actor);
      setWaxSession(s);
      setWaxAcct(acct);
      localStorage.setItem('samatvam_active_account', acct);
    } catch { /* cancelled */ }
    setWaxConnecting(false);
  };

  const handleWaxDisconnect = async () => {
    if (waxSession) await getSessionKit().logout(waxSession);
    setWaxSession(null);
    setWaxAcct('');
  };

  const handleMouseMove = React.useCallback((e: React.MouseEvent) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * BIO_W;
    const svgY = ((e.clientY - rect.top) / rect.height) * BIO_H;
    if (svgX >= 0 && svgY >= 0 && svgX <= BIO_W && svgY <= BIO_H) {
      setCoords({ x: Math.round(svgX), y: Math.round(BIO_H - svgY) });
    } else {
      setCoords(null);
    }
  }, []);

  const handleMouseLeave = React.useCallback(() => setCoords(null), []);

  const labels = BIO_LABELS_DEFAULT;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#050e05', color: '#e2e8f0', fontFamily: 'system-ui,sans-serif', overflow: 'hidden' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 20px', background: 'rgba(3,10,3,0.95)', borderBottom: '1px solid rgba(34,197,94,0.15)', flexShrink: 0 }}>
        <button onClick={onBack} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#9ca3af', fontSize: 18, width: 34, height: 34, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s' }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.12)'; (e.currentTarget as HTMLButtonElement).style.color = '#fff'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.06)'; (e.currentTarget as HTMLButtonElement).style.color = '#9ca3af'; }}
        >←</button>
        <span style={{ fontSize: 22 }}>🏭</span>
        <div>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 900, color: '#86efac', letterSpacing: -0.3 }}>BIOREFINERY</p>
          <p style={{ margin: 0, fontSize: 9, fontWeight: 700, color: '#22c55e40', letterSpacing: 2.5, textTransform: 'uppercase' }}>Level 1 · Task 4 Complete</p>
        </div>
        <div style={{ flex: 1 }} />
        {waxAcct ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ color: '#fcd34d', fontSize: 11 }}>⬡</span>
              <span style={{ color: '#fcd34d', fontSize: 10, fontWeight: 700, fontFamily: 'monospace' }}>{waxAcct}</span>
            </div>
            <button onClick={handleWaxDisconnect} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 10, fontWeight: 700, cursor: 'pointer', padding: '2px 4px' }}>Disconnect</button>
          </div>
        ) : (
          <button onClick={handleWaxConnect} disabled={waxConnecting} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: '5px 12px', color: '#fcd34d', fontSize: 11, fontWeight: 700, cursor: waxConnecting ? 'not-allowed' : 'pointer', opacity: waxConnecting ? 0.6 : 1 }}>
            <span>⬡</span>{waxConnecting ? '…' : 'Connect WAX'}
          </button>
        )}
        <div style={{ background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.2)', borderRadius: 8, padding: '4px 12px' }}>
          <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: '#4ade80' }}>🟢 Operational</p>
        </div>
      </div>

      {/* ── Map area ── */}
      <div style={{ flex: 1, overflow: isMobile ? 'auto' : 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '0' : '20px', background: 'radial-gradient(ellipse 80% 70% at 50% 55%, #0a1a0a 0%, #060e06 60%, #030803 100%)' }}>
        <div style={{ position: 'relative', lineHeight: 0, boxShadow: '0 0 60px 16px rgba(34,197,94,0.10), 0 0 0 1px rgba(34,197,94,0.2), 0 8px 60px rgba(0,0,0,0.8)', borderRadius: 6, width: isMobile ? '100%' : undefined }}>

          {/* Background image */}
          <img
            src={`${process.env.PUBLIC_URL}/images/biorefinery-interior.jpg`}
            alt="Biorefinery Interior"
            style={{ display: 'block', width: '100%', height: 'auto', maxHeight: isMobile ? undefined : 'calc(100vh - 130px)', borderRadius: 6, userSelect: 'none', objectFit: 'contain' }}
          />

          {/* SVG overlay — same coordinate system as city map */}
          <svg
            ref={svgRef}
            viewBox={`0 0 ${BIO_W} ${BIO_H}`}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', borderRadius: 6, userSelect: 'none', overflow: 'visible' }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            {labels.map(lm => {
              const sx = lm.cx;
              const sy = BIO_H - lm.cy;
              const isHov = hoveredPin === lm.id;
              const lblW = lm.label.length * 6.5 + 28;
              const lblX = Math.min(Math.max(sx - lblW / 2, 4), BIO_W - lblW - 4);
              const lblY = sy - 26;
              return (
                <g key={lm.id}
                  onMouseEnter={() => setHoveredPin(lm.id)}
                  onMouseLeave={() => setHoveredPin(null)}
                  style={{ cursor: lm.action ? 'pointer' : 'default' }}
                >
                  <circle cx={sx} cy={sy} r={16} fill="transparent" />
                  <circle cx={sx} cy={sy} r={isHov ? 6 : 4}
                    fill={isHov ? '#86efac' : 'rgba(74,222,128,0.6)'}
                    stroke={isHov ? '#22c55e' : 'rgba(34,197,94,0.35)'}
                    strokeWidth={1.5}
                    style={{ transition: 'r 0.15s, fill 0.15s' }}
                  />
                  <text x={sx} y={sy - 7} textAnchor="middle" fontSize={14} style={{ userSelect: 'none' }}>{lm.icon}</text>
                  {isHov && (
                    <g pointerEvents="none">
                      <rect x={lblX} y={lblY - 14} width={lblW} height={18} rx={5}
                        fill="rgba(3,10,3,0.92)" stroke="rgba(34,197,94,0.45)" strokeWidth={1}
                      />
                      <text x={lblX + 14} y={lblY - 2} fontFamily="system-ui,sans-serif" fontSize={10} fontWeight={800} fill="#86efac">
                        {lm.icon} {lm.label}
                      </text>
                    </g>
                  )}
                </g>
              );
            })}

            {/* ── Chamber headings ── */}
            <g pointerEvents="none">
              {/* Sugar Pre-Processor — Chamber 1 */}
              <text x={20} y={322} fontFamily="system-ui,sans-serif" fontSize={28} fontWeight={900}
                fill="#0f172a" stroke="#0f172a" strokeWidth={6} strokeLinejoin="round"
                style={{ letterSpacing: 1.5 }}
              >SUGAR PRE-PROCESSOR</text>
              <text x={20} y={322} fontFamily="system-ui,sans-serif" fontSize={28} fontWeight={900}
                fill="#1e3a8a"
                style={{ letterSpacing: 1.5 }}
              >SUGAR PRE-PROCESSOR</text>
              <text x={20} y={348} fontFamily="system-ui,sans-serif" fontSize={11} fontWeight={700}
                fill="#1e3a8a" stroke="#0f172a" strokeWidth={3} strokeLinejoin="round"
                style={{ letterSpacing: 3, textTransform: 'uppercase' }}>Chamber 1</text>
              <text x={20} y={348} fontFamily="system-ui,sans-serif" fontSize={11} fontWeight={700}
                fill="#1d4ed8"
                style={{ letterSpacing: 3, textTransform: 'uppercase' }}>Chamber 1</text>
            </g>

            {/* Glow filter */}
            <defs>
            </defs>

            {/* ── Sugar Pre-Processor Machine — fills x:103–351, cy:163–463 (svgY 449–749) ── */}
            {(() => {
              const X0 = 103; const X1 = 351;
              const Y0 = 449; const Y1 = 749;
              const W  = X1 - X0;
              const H  = Y1 - Y0;
              const MX = (X0 + X1) / 2;
              const inRowY   = Y0 + 52;
              const pipeTopY = Y0 + 70;
              const bodyY0   = Y0 + 90;
              const bodyH    = 108;
              const pipeOutY = bodyY0 + bodyH;
              const outPillY = pipeOutY + 44;
              return (
                <g>
                  <text x={MX} y={Y0 + 22} textAnchor="middle" fontFamily="system-ui,sans-serif" fontSize={15} fontWeight={900} fill="#64748b" style={{ letterSpacing: 2 }}>INPUTS</text>

                  {(() => {
                    const pillW = 72; const pillH = 42;
                    const c1 = X0 + 41; const c2 = MX; const c3 = X1 - 41;
                    const inputs = [
                      { cx: c1, label: '🍬 Sugar', sub: 'PACKETS', stroke: '#fbbf24', fill: 'rgba(251,191,36,0.18)' },
                      { cx: c2, label: '🌲 Fire',  sub: 'WOOD',    stroke: '#b45309', fill: 'rgba(180,83,9,0.18)'   },
                      { cx: c3, label: '🧫 Yeast', sub: 'CULTURE', stroke: '#a78bfa', fill: 'rgba(167,139,250,0.18)'},
                    ];
                    return (
                      <g>
                        {inputs.map(inp => (
                          <g key={inp.cx}>
                            <rect x={inp.cx - pillW/2} y={inRowY - 28} width={pillW} height={pillH} rx={7} fill={inp.fill} stroke={inp.stroke} strokeWidth={1.5}/>
                            <text x={inp.cx} y={inRowY - 11} textAnchor="middle" fontFamily="system-ui,sans-serif" fontSize={13} fontWeight={900} fill={inp.stroke}>{inp.label}</text>
                            <text x={inp.cx} y={inRowY + 5}  textAnchor="middle" fontFamily="system-ui,sans-serif" fontSize={9}  fontWeight={700} fill={inp.stroke} style={{ letterSpacing: 1 }}>{inp.sub}</text>
                            <line x1={inp.cx} y1={inRowY + 14} x2={inp.cx} y2={pipeTopY + 8} stroke={inp.stroke} strokeWidth={2.5} strokeDasharray="5,4"/>
                            <polygon points={`${inp.cx},${pipeTopY+16} ${inp.cx-6},${pipeTopY+6} ${inp.cx+6},${pipeTopY+6}`} fill={inp.stroke}/>
                          </g>
                        ))}
                      </g>
                    );
                  })()}

                  {/* Machine body — clickable */}
                  <g style={{ cursor: 'pointer' }} onClick={() => setBatchModalOpen(true)}>
                  <rect x={X0 + 4} y={bodyY0} width={W - 8} height={bodyH} rx={10} fill="rgba(15,23,42,0.90)" stroke="#475569" strokeWidth={2.5}/>
                  <rect x={X0 + 4} y={bodyY0} width={W - 8} height={14} rx={10} fill="rgba(71,85,105,0.5)"/>
                  <rect x={X0 + 4} y={bodyY0 + 7} width={W - 8} height={7} fill="rgba(71,85,105,0.5)"/>
                  <text x={MX - 36} y={bodyY0 + 56} textAnchor="middle" fontSize={36} style={{ userSelect: 'none' }}>⚙️</text>
                  <text x={MX + 28} y={bodyY0 + 42} textAnchor="middle" fontFamily="system-ui,sans-serif" fontSize={17} fontWeight={900} fill="#94a3b8" style={{ letterSpacing: 1 }}>PROCESSOR</text>
                  <text x={MX + 28} y={bodyY0 + 62} textAnchor="middle" fontFamily="system-ui,sans-serif" fontSize={12} fontWeight={700} fill="#475569" style={{ letterSpacing: 2 }}>SUGAR PRE-PROC</text>
                  <circle cx={X0 + W - 18} cy={bodyY0 + 18} r={6} fill="#22c55e">
                    <animate attributeName="opacity" values="1;0.2;1" dur="1.8s" repeatCount="indefinite"/>
                  </circle>
                  </g>

                  {/* Output pipe */}
                  <line x1={MX} y1={pipeOutY} x2={MX} y2={outPillY - 18} stroke="#22d3ee" strokeWidth={3.5} strokeDasharray="5,4"/>
                  <polygon points={`${MX},${outPillY-10} ${MX-7},${outPillY-20} ${MX+7},${outPillY-20}`} fill="#22d3ee"/>
                  <rect x={MX - 68} y={outPillY - 8} width={136} height={34} rx={8} fill="rgba(34,211,238,0.14)" stroke="#22d3ee" strokeWidth={1.5}/>
                  <text x={MX} y={outPillY + 2} textAnchor="middle" fontFamily="system-ui,sans-serif" fontSize={10} fontWeight={700} fill="#22d3ee80" style={{ letterSpacing: 2 }}>OUTPUT</text>
                  <text x={MX} y={outPillY + 18} textAnchor="middle" fontFamily="system-ui,sans-serif" fontSize={15} fontWeight={900} fill="#22d3ee">SUGAR MASH</text>
                </g>
              );
            })()}

            {/* ── Coordinate readout (bottom-left, same style as city map) ── */}
            {coords && (
              <g pointerEvents="none">
                <rect x={8} y={BIO_H - 28} width={100} height={20} rx={4} fill="rgba(3,10,3,0.85)" stroke="rgba(34,197,94,0.35)" strokeWidth={1} />
                <text x={58} y={BIO_H - 14} fontFamily="monospace" fontSize={11} fontWeight={700} fill="#86efac" textAnchor="middle">
                  {coords.x}, {coords.y}
                </text>
              </g>
            )}
          </svg>
        </div>
      </div>

      {batchModalOpen && (
        <BiorefineryBatchModal onClose={() => setBatchModalOpen(false)} waxAcct={waxAcct} />
      )}
    </div>
  );
}

// Original image dimensions
const MAP_W = 1179;
const MAP_H = 896;

// Zone definitions — bottom-left origin (x right, y up)
// svgY is computed as MAP_H - cy when rendering
const CITY_ZONES = [
  {
    id: 'food-drop',
    cx: 835, cy: 783,  // bottom-left origin
    w: 60,  h: 60,
    label: 'Food Drop Zone',
    note: 'Place food packets here to unlock the map',
    counterTarget: 10000,
    counterUnit: 'packets',
  },
];

interface MapZone {
  id: number;
  zone_id: string;
  label: string;
  cx: number;
  cy: number;
  w: number;
  h: number;
  note: string;
  counter_target: number;
  counter_unit: string;
  task_number: number | null;
  is_active: boolean;
}

// ─── Council Worker types + animated SVG component ───────────────────────────
interface CouncilWorker {
  id: number;
  worker_name: string;
  wax_account: string;
  state: 0 | 1 | 2 | 3;
  pos_x: number; pos_y: number;
  dest_x: number | null; dest_y: number | null;
  move_start_at: string | null;
  move_duration_sec: number;
  label: string;
}

const MAP_W_CW = 1179;
const MAP_H_CW = 896;

function WorkerIcon({ w, tick }: { w: CouncilWorker; tick: number }) {
  // Compute position — lerp for state 1
  let cx = w.pos_x;
  let cy = MAP_H_CW - w.pos_y; // SVG top-left origin
  let t = 1;

  if (w.state === 1 && w.dest_x != null && w.dest_y != null && w.move_start_at) {
    const elapsed = (Date.now() - new Date(w.move_start_at).getTime()) / 1000;
    t = Math.min(1, Math.max(0, elapsed / w.move_duration_sec));
    cx = w.pos_x + (w.dest_x - w.pos_x) * t;
    cy = (MAP_H_CW - w.pos_y) + ((MAP_H_CW - w.dest_y) - (MAP_H_CW - w.pos_y)) * t;
  }

  // If the worker has arrived (t === 1) but the DB hasn't updated state yet, show as working
  const displayState: 0 | 1 | 2 | 3 = (w.state === 1 && t >= 1) ? 2 : w.state;

  // Animation values keyed to tick (updates every 200ms from parent)
  const breathe  = 1 + 0.018 * Math.sin((tick * 0.2 * 2 * Math.PI) / 5);   // state 0
  const legSwing = 8  * Math.sin(tick * 0.2 * 2 * Math.PI);                 // state 1
  const armChop  = 14 * Math.abs(Math.sin(tick * 0.2 * 2 * Math.PI * 1.5)); // state 2

  const stateColors: Record<number, string> = { 0: '#f59e0b', 1: '#22d3ee', 2: '#4ade80' };
  const bodyColor = stateColors[displayState] ?? '#f59e0b';

  const tooltip = w.label || (w.wax_account ? `Works for: ${w.wax_account}` : w.worker_name);

  return (
    <g style={{ cursor: 'pointer' }}>
      <title>{tooltip}</title>

      {/* Shadow */}
      <ellipse cx={cx} cy={cy + 16} rx={6} ry={2.5} fill="rgba(0,0,0,0.35)" />

      {/* Body group — breathing scale for state 0 */}
      <g transform={`translate(${cx},${cy}) scale(${displayState === 0 ? breathe : 1}) translate(${-cx},${-cy})`}>

        {/* Legs — swing for state 1 */}
        <line
          x1={cx - 2} y1={cy + 8}
          x2={cx - 4 + (displayState === 1 ? legSwing * 0.4 : 0)} y2={cy + 16}
          stroke="#fde68a" strokeWidth={2.2} strokeLinecap="round"
        />
        <line
          x1={cx + 2} y1={cy + 8}
          x2={cx + 4 - (displayState === 1 ? legSwing * 0.4 : 0)} y2={cy + 16}
          stroke="#fde68a" strokeWidth={2.2} strokeLinecap="round"
        />

        {/* Body */}
        <rect x={cx - 5} y={cy} width={10} height={9} rx={2.5} fill={bodyColor} />

        {/* Arms — swing for state 1, chop for state 2 */}
        <line
          x1={cx - 5} y1={cy + 2}
          x2={cx - 10 + (displayState === 1 ? legSwing * 0.3 : 0)} y2={cy + 5 + (displayState === 2 ? armChop : 0)}
          stroke="#fde68a" strokeWidth={2} strokeLinecap="round"
        />
        <line
          x1={cx + 5} y1={cy + 2}
          x2={cx + 10 - (displayState === 1 ? legSwing * 0.3 : 0)} y2={cy + 5 + (displayState === 2 ? armChop : 0)}
          stroke="#fde68a" strokeWidth={2} strokeLinecap="round"
        />

        {/* Head */}
        <circle cx={cx} cy={cy - 5} r={5} fill="#fde68a" />

        {/* Hard hat */}
        <path d={`M${cx - 6},${cy - 5} Q${cx},${cy - 13} ${cx + 6},${cy - 5}`}
          fill="#ef4444" stroke="#dc2626" strokeWidth={0.8} />
        <rect x={cx - 7} y={cy - 5.5} width={14} height={2} rx={1} fill="#ef4444" />

        {/* Tool for state 2 */}
        {displayState === 2 && (
          <line
            x1={cx + 10} y1={cy + 5 + armChop}
            x2={cx + 14} y2={cy + 9 + armChop}
            stroke="#94a3b8" strokeWidth={2.5} strokeLinecap="round"
          />
        )}
      </g>

      {/* State indicator dot */}
      <circle cx={cx + 7} cy={cy - 10} r={3}
        fill={displayState === 0 ? '#64748b' : displayState === 1 ? '#22d3ee' : '#4ade80'}
        stroke="rgba(0,0,0,0.5)" strokeWidth={0.8}
      />

      {/* Name label */}
      <rect x={cx - 14} y={cy + 18} width={28} height={10} rx={3} fill="rgba(10,8,3,0.85)" />
      <text x={cx} y={cy + 26} fontFamily="monospace" fontSize={7} fontWeight={800}
        fill={bodyColor} textAnchor="middle">
        {w.worker_name.length > 7 ? w.worker_name.slice(0, 6) + '…' : w.worker_name}
      </text>
    </g>
  );
}

// ─── Biorefinery Modal ───────────────────────────────────────────────────────
function BiorefineryModal({ onClose, onEnter }: { onClose: () => void; onEnter?: () => void }) {
  const [bigblvdMats, setBigblvdMats] = React.useState<{ material_id: string; quantity: number }[]>([]);
  const [nextRewardIn, setNextRewardIn] = React.useState(0);
  const [jivurjaMap, setJivurjaMap] = React.useState<Record<string, number>>({});
  React.useEffect(() => {
    fetch('/samatvam/api/samatvam/materials/bigblvd.gm')
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setBigblvdMats(d.materials);
          setNextRewardIn(d.next_reward_in_seconds);
        }
      }).catch(() => {});
    fetch('/samatvam/api/samatvam/biorefinery/jivurja-leaderboard')
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          const m: Record<string, number> = {};
          for (const row of d.data) m[row.wax_account] = row.jivurja_balance;
          setJivurjaMap(m);
        }
      }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (nextRewardIn <= 0) return;
    const t = setInterval(() => setNextRewardIn(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [nextRewardIn]);
  const fmtCountdown = (s: number) => {
    const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const sec = s % 60;
    return `${h}h ${String(m).padStart(2,'0')}m ${String(sec).padStart(2,'0')}s`;
  };
  const getMat = (id: string) => bigblvdMats.find(m => m.material_id === id)?.quantity ?? 0;
  const foodBoard = [
    { rank: 1,  wax_account: 'prosciutto15', food: 29720 },
    { rank: 2,  wax_account: 'amerharki123', food: 25838 },
    { rank: 3,  wax_account: 'martin111112', food: 16213 },
    { rank: 4,  wax_account: 'lonestar1234', food: 13277 },
    { rank: 5,  wax_account: 'w4dba.wam',    food:  4500 },
    { rank: 6,  wax_account: 'x.1my.c.wam',  food:  3535 },
    { rank: 7,  wax_account: 'bigblvd.gm',   food:  3000 },
    { rank: 8,  wax_account: 'patesz111111', food:  2162 },
    { rank: 9,  wax_account: 'lk5g.wam',     food:   925 },
    { rank: 10, wax_account: 'dreamingpal1', food:   500 },
    { rank: 11, wax_account: 'rudrikwax.gm', food:   330 },
  ];
  const total = foodBoard.reduce((s, r) => s + r.food, 0);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(2,5,2,0.85)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: 'rgba(3,10,3,0.98)', border: '1px solid rgba(34,197,94,0.22)', borderRadius: 18, padding: '26px 30px', width: '80vw', maxWidth: '560px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 12px 80px rgba(0,0,0,0.8)' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 26 }}>🏭</span>
            <div>
              <p style={{ color: '#86efac', fontSize: 17, fontWeight: 900, margin: 0, letterSpacing: -0.3 }}>Biorefinery</p>
              <p style={{ color: '#22c55e50', fontSize: 9, margin: 0, fontWeight: 700, letterSpacing: 2.5, textTransform: 'uppercase' }}>Task 4 Complete · Start Biorefinery</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#ffffff60', fontSize: 18, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        {/* Task completion badge */}
        <div style={{ background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.2)', borderRadius: 12, padding: '12px 16px', marginBottom: 20, display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ fontSize: 20 }}>🏆</span>
          <div>
            <p style={{ color: '#4ade80', fontSize: 12, fontWeight: 900, margin: 0 }}>Task 4 Complete!</p>
            <p style={{ color: '#ffffff40', fontSize: 10, margin: 0, marginTop: 2 }}>100,000 food packets · 30 Mesquite logs · 20 Moringa bundles · Biofuel Synthesis Recipe</p>
          </div>
        </div>

        {/* Log contributors */}
        <div style={{ marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{ color: '#22c55e80', fontSize: 9, fontWeight: 900, letterSpacing: 2.5, textTransform: 'uppercase', margin: '0 0 6px' }}>Log Contributors</p>
          <div style={{ background: 'rgba(34,197,94,0.04)', border: '1px solid rgba(34,197,94,0.15)', borderRadius: 10, padding: '10px 14px' }}>
            <p style={{ color: '#86efac', fontSize: 11, fontWeight: 700, margin: 0 }}>🌲 Mesquite logs (30/30) — delivered by <span style={{ color: '#fde68a', fontFamily: 'monospace' }}>bigblvd.gm</span></p>
          </div>
          <div style={{ background: 'rgba(34,197,94,0.04)', border: '1px solid rgba(34,197,94,0.15)', borderRadius: 10, padding: '10px 14px' }}>
            <p style={{ color: '#86efac', fontSize: 11, fontWeight: 700, margin: 0 }}>🌿 Moringa bundles (20/20) — delivered by <span style={{ color: '#fde68a', fontFamily: 'monospace' }}>bigblvd.gm</span></p>
          </div>
        </div>

        {/* ── Daily Materials Machine ── */}
        <div style={{ marginBottom: 20, background: 'rgba(15,23,42,0.7)', border: '1px solid rgba(71,85,105,0.4)', borderRadius: 12, padding: '14px 16px' }}>
          <p style={{ color: '#64748b', fontSize: 9, fontWeight: 900, letterSpacing: 2.5, textTransform: 'uppercase', margin: '0 0 12px' }}>⚙️ Sugar Pre-Processor · Daily Reward Machine</p>

          {/* Inputs */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
            <div style={{ flex: 1, background: 'rgba(180,83,9,0.15)', border: '1px solid #b4530960', borderRadius: 8, padding: '8px 10px' }}>
              <p style={{ margin: 0, fontSize: 9, fontWeight: 700, color: '#92400e', letterSpacing: 1.5, textTransform: 'uppercase' }}>INPUT</p>
              <p style={{ margin: '3px 0 0', fontSize: 13, fontWeight: 900, color: '#d97706' }}>🪵 Firewood</p>
              <p style={{ margin: '2px 0 0', fontSize: 9, color: '#92400e' }}>1 per day</p>
            </div>
            <div style={{ flex: 1, background: 'rgba(21,128,61,0.15)', border: '1px solid #15803d60', borderRadius: 8, padding: '8px 10px' }}>
              <p style={{ margin: 0, fontSize: 9, fontWeight: 700, color: '#166534', letterSpacing: 1.5, textTransform: 'uppercase' }}>INPUT</p>
              <p style={{ margin: '3px 0 0', fontSize: 13, fontWeight: 900, color: '#4ade80' }}>🧫 Yeast Culture</p>
              <p style={{ margin: '2px 0 0', fontSize: 9, color: '#166534' }}>1 per day</p>
            </div>
          </div>

          {/* Contributor inventory */}
          <div style={{ background: 'rgba(34,211,238,0.06)', border: '1px solid rgba(34,211,238,0.18)', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: '#22d3ee80', letterSpacing: 1 }}>bigblvd.gm — Material Inventory</p>
              <span style={{ fontSize: 9, color: '#ffffff20', fontFamily: 'monospace' }}>Next: {nextRewardIn > 0 ? fmtCountdown(nextRewardIn) : '—'}</span>
            </div>
            <div style={{ display: 'flex', gap: 16 }}>
              <div>
                <p style={{ margin: 0, fontSize: 20 }}>🌲</p>
                <p style={{ margin: '2px 0 0', fontSize: 18, fontWeight: 900, color: '#d97706', fontFamily: 'monospace' }}>{getMat('firewood')}</p>
                <p style={{ margin: 0, fontSize: 8, color: '#92400e' }}>firewood</p>
              </div>
              <div>
                <p style={{ margin: 0, fontSize: 20 }}>🌿</p>
                <p style={{ margin: '2px 0 0', fontSize: 18, fontWeight: 900, color: '#4ade80', fontFamily: 'monospace' }}>{getMat('yeast_culture')}</p>
                <p style={{ margin: 0, fontSize: 8, color: '#166534' }}>yeast culture</p>
              </div>
            </div>
          </div>
        </div>

        {/* Food leaderboard */}
        <p style={{ color: '#22c55e80', fontSize: 9, fontWeight: 900, letterSpacing: 2.5, textTransform: 'uppercase', margin: '0 0 10px' }}>Task 4 Food Leaderboard</p>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {[['#', false], ['Player', true], ['Food', false], ['Share', false], ['JIVURJA', false]].map(([h, left]) => (
                <th key={h as string} style={{ textAlign: (left as boolean) ? 'left' : 'right', fontSize: 9, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', color: h === 'JIVURJA' ? '#a78bfa60' : '#22c55e40', paddingBottom: 9, borderBottom: '1px solid rgba(34,197,94,0.1)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {foodBoard.map(r => (
              <tr key={r.wax_account} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={{ padding: '10px 0', color: '#ffffff25', fontSize: 15, width: 32 }}>{r.rank}</td>
                <td style={{ padding: '10px 0', color: '#86efac', fontSize: 14, fontFamily: 'monospace', fontWeight: 700 }}>{r.wax_account}</td>
                <td style={{ padding: '10px 0', textAlign: 'right', color: '#22d3ee', fontSize: 14, fontWeight: 900 }}>{r.food.toLocaleString()}</td>
                <td style={{ padding: '10px 0', textAlign: 'right', color: '#ffffff50', fontSize: 12 }}>{((r.food / total) * 100).toFixed(1)}%</td>
                <td style={{ padding: '10px 0', textAlign: 'right', color: '#c4b5fd', fontSize: 12, fontWeight: 700, fontFamily: 'monospace' }}>{(jivurjaMap[r.wax_account] ?? 0).toFixed(4)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 10, background: 'rgba(255,255,255,0.05)', borderRadius: 4, height: 4, overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 4, background: 'linear-gradient(90deg, #22c55e, #22d3ee)', width: '100%' }} />
        </div>
        <p style={{ marginTop: 8, color: '#ffffff15', fontSize: 10, textAlign: 'right' }}>{total.toLocaleString()} total food packets</p>

        {/* Enter Biorefinery button */}
        <div style={{ marginTop: 22, borderTop: '1px solid rgba(34,197,94,0.12)', paddingTop: 18 }}>
          <button
            onClick={() => { onClose(); onEnter?.(); }}
            style={{ width: '100%', background: 'linear-gradient(135deg, #16a34a, #15803d)', border: '1px solid rgba(74,222,128,0.4)', borderRadius: 10, color: '#d1fae5', fontSize: 13, fontWeight: 900, letterSpacing: 0.5, padding: '12px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all 0.2s', boxShadow: '0 4px 20px rgba(22,163,74,0.25)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'linear-gradient(135deg, #15803d, #166534)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 28px rgba(22,163,74,0.4)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'linear-gradient(135deg, #16a34a, #15803d)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 20px rgba(22,163,74,0.25)'; }}
          >
            <span style={{ fontSize: 16 }}>🏭</span>
            Enter Biorefinery →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Power House Modal ───────────────────────────────────────────────────────
function PowerHouseModal({ onClose }: { onClose: () => void }) {
  const task3Board = [
    { rank: 1, wax_account: 'amerharki123', food: 16326 },
    { rank: 2, wax_account: 'lonestar1234', food: 12837 },
    { rank: 3, wax_account: 'x.1my.c.wam',  food: 11451 },
    { rank: 4, wax_account: 'dreamingpal1', food:  3500 },
    { rank: 5, wax_account: 'w4dba.wam',    food:  3076 },
    { rank: 6, wax_account: 'lk5g.wam',     food:  1700 },
    { rank: 7, wax_account: 'patesz111111', food:  1000 },
    { rank: 8, wax_account: 'bigblvd.gm',   food:   100 },
    { rank: 9, wax_account: 'rudrikwax.gm', food:    25 },
  ];
  const total = task3Board.reduce((s, r) => s + r.food, 0);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(5,4,2,0.85)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: 'rgba(12,9,3,0.98)', border: '1px solid rgba(245,158,11,0.22)', borderRadius: 18, padding: '26px 30px', width: '80vw', maxWidth: '560px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 12px 80px rgba(0,0,0,0.8)' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 26 }}>⚡</span>
            <div>
              <p style={{ color: '#fde68a', fontSize: 17, fontWeight: 900, margin: 0, letterSpacing: -0.3 }}>Power House</p>
              <p style={{ color: '#f59e0b50', fontSize: 9, margin: 0, fontWeight: 700, letterSpacing: 2.5, textTransform: 'uppercase' }}>Task 3 Complete · Connect Power Supply</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#ffffff60', fontSize: 18, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        {/* Task completion badge */}
        <div style={{ background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.2)', borderRadius: 12, padding: '12px 16px', marginBottom: 20, display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ fontSize: 20 }}>🏆</span>
          <div>
            <p style={{ color: '#4ade80', fontSize: 12, fontWeight: 900, margin: 0 }}>Task 3 Complete!</p>
            <p style={{ color: '#ffffff40', fontSize: 10, margin: 0, marginTop: 2 }}>50,000 food packets · 15 acacia logs · 2 workers · Electrician Guide Book</p>
          </div>
        </div>

        {/* Acacia note */}
        <div style={{ background: 'rgba(34,197,94,0.04)', border: '1px solid rgba(34,197,94,0.15)', borderRadius: 10, padding: '10px 14px', marginBottom: 20 }}>
          <p style={{ color: '#86efac', fontSize: 11, fontWeight: 700, margin: 0 }}>🌳 Acacia logs (15/15) — delivered by <span style={{ color: '#fde68a', fontFamily: 'monospace' }}>x.1my.c.wam</span></p>
        </div>

        {/* Leaderboard */}
        <p style={{ color: '#f59e0b80', fontSize: 9, fontWeight: 900, letterSpacing: 2.5, textTransform: 'uppercase', margin: '0 0 10px' }}>Task 3 Food Leaderboard</p>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {[['#', false], ['Player', true], ['Food', false], ['Share', false]].map(([h, left]) => (
                <th key={h as string} style={{ textAlign: (left as boolean) ? 'left' : 'right', fontSize: 9, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', color: '#f59e0b40', paddingBottom: 9, borderBottom: '1px solid rgba(245,158,11,0.1)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {task3Board.map(r => (
              <tr key={r.wax_account} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={{ padding: '10px 0', color: '#ffffff25', fontSize: 15, width: 32 }}>{r.rank}</td>
                <td style={{ padding: '10px 0', color: '#fde68a', fontSize: 14, fontFamily: 'monospace', fontWeight: 700 }}>{r.wax_account}</td>
                <td style={{ padding: '10px 0', textAlign: 'right', color: '#22d3ee', fontSize: 14, fontWeight: 900 }}>{r.food.toLocaleString()}</td>
                <td style={{ padding: '10px 0', textAlign: 'right', color: '#ffffff50', fontSize: 12 }}>{((r.food / total) * 100).toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 10, background: 'rgba(255,255,255,0.05)', borderRadius: 4, height: 4, overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 4, background: 'linear-gradient(90deg, #f59e0b, #22d3ee)', width: '100%' }} />
        </div>
        <p style={{ marginTop: 8, color: '#ffffff15', fontSize: 10, textAlign: 'right' }}>{total.toLocaleString()} total food packets</p>
      </div>
    </div>
  );
}

// ─── Bus Stop Modal ───────────────────────────────────────────────────────────
function BusStopModal({ onClose, waxAcct }: { onClose: () => void; waxAcct?: string }) {
  const TRACK_COLOR: Record<string, string> = { express: '#f59e0b', boost: '#22d3ee', standard: '#94a3b8' };
  const TRACK_ICON:  Record<string, string> = { express: '⚡', boost: '🚀', standard: '🚌' };

  type ContribRow = { wax_account: string; amount: number };
  type Operative  = { id: number; operative_index: number; track: string; location: string; rest_until: string | null; in_queue: boolean; carry_wax?: number };
  type SeatRow    = { seat: number; empty?: boolean; operative_index?: number; wax_account?: string; track?: string; seeds_loaded?: number; wax_loaded?: number; status?: string };

  const [logs,          setLogs]          = React.useState<ContribRow[]>([]);
  const [ethanol,       setEthanol]       = React.useState<ContribRow[]>([]);
  const [vayal,         setVayal]         = React.useState<ContribRow[]>([]);
  const [food,          setFood]          = React.useState<ContribRow[]>([]);
  const [seats,         setSeats]         = React.useState<SeatRow[]>([]);
  const [operatives,    setOperatives]    = React.useState<Operative[]>([]);
  const [seeds,         setSeeds]         = React.useState(0);
  const [selOp,         setSelOp]         = React.useState<number | ''>('');
  const [qSeeds,        setQSeeds]        = React.useState(0);
  const [qWax,          setQWax]          = React.useState(0);
  const [queueMsg,      setQueueMsg]      = React.useState('');
  const [queueing,      setQueueing]      = React.useState(false);
  const [recalling,     setRecalling]     = React.useState<number | null>(null);
  const [recallMsg,     setRecallMsg]     = React.useState('');
  const [secondsTo,     setSecondsTo]     = React.useState(0);
  const [nextDep,       setNextDep]       = React.useState('');
  const [remnantSeeds,  setRemnantSeeds]  = React.useState(0);
  const [remnantOps,    setRemnantOps]    = React.useState(0);
  const [remnantPulling, setRemnantPulling] = React.useState(false);
  const [remnantMsg,    setRemnantMsg]    = React.useState('');
  const [transportSel,  setTransportSel]  = React.useState<Set<number>>(new Set());
  const [transportDest, setTransportDest] = React.useState<'edinburgh_birch' | 'glasgow' | ''>('');
  const [transporting,  setTransporting]  = React.useState(false);
  const [transportMsg,  setTransportMsg]  = React.useState('');

  const loadSeats = React.useCallback(() => {
    fetch('/samatvam/api/samatvam/bus/seats')
      .then(r => r.json()).then(d => { if (d.success) setSeats(d.seats); }).catch(() => {});
  }, []);

  const loadModalData = React.useCallback(() => {
    if (!waxAcct) return;
    fetch(`/samatvam/api/samatvam/bus/modal-data/${waxAcct}`)
      .then(r => r.json()).then(d => {
        if (d.success) {
          setOperatives(d.operatives);
          setSeeds(d.balances.seeds);
        }
      }).catch(() => {});
    fetch(`/samatvam/api/samatvam/bus/remnant-seeds/${waxAcct}`)
      .then(r => r.json()).then(d => {
        if (d.success) { setRemnantSeeds(d.total_seeds); setRemnantOps(d.ops.length); }
      }).catch(() => {});
  }, [waxAcct]);

  // Countdown timer
  React.useEffect(() => {
    const fetchNext = () => {
      fetch('/samatvam/api/samatvam/bus/next-departure')
        .then(r => r.json()).then(d => {
          if (d.success) { setSecondsTo(d.seconds_to); setNextDep(d.next_departure); }
        }).catch(() => {});
    };
    fetchNext();
    const iv = setInterval(() => {
      setSecondsTo(s => {
        if (s <= 1) { fetchNext(); loadSeats(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [loadSeats]);

  React.useEffect(() => {
    fetch('/samatvam/api/samatvam/map/bus-stop-contributions')
      .then(r => r.json()).then(d => {
        if (d.success) { setLogs(d.logs); setEthanol(d.ethanol); setVayal(d.vayal); setFood(d.food); }
      }).catch(() => {});
    loadSeats();
    loadModalData();
  }, [loadSeats, loadModalData]);

  const fmtCountdown = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${String(m).padStart(2,'0')}m`;
    if (m > 0) return `${m}m ${String(sec).padStart(2,'0')}s`;
    return `${sec}s`;
  };

  const fmtTime = (iso: string) => {
    try { return new Date(iso).toUTCString().slice(17, 22) + ' UTC'; } catch { return ''; }
  };

  const handleQueue = async () => {
    if (!waxAcct || selOp === '') return;
    setQueueing(true); setQueueMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/bus/queue', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxAcct, operative_index: selOp, seeds: qSeeds, wax: qWax }),
      });
      const d = await r.json();
      if (d.success) {
        setQueueMsg('Operative queued!');
        setSelOp(''); setQSeeds(0); setQWax(0);
        loadModalData(); loadSeats();
      } else { setQueueMsg(d.error || 'Failed.'); }
    } catch { setQueueMsg('Network error.'); }
    setQueueing(false);
  };

  const handleRecall = async (operative_index: number) => {
    if (!waxAcct) return;
    setRecalling(operative_index); setRecallMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/bus/recall', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxAcct, operative_index }),
      });
      const d = await r.json();
      if (d.success) { setRecallMsg('Operative will board next bus home.'); loadModalData(); }
      else { setRecallMsg(d.error || 'Failed.'); }
    } catch { setRecallMsg('Network error.'); }
    setRecalling(null);
  };

  const handleTransport = async () => {
    if (!waxAcct || !transportDest || !transportSel.size) return;
    setTransporting(true); setTransportMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/bus/transport', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxAcct, operative_indices: Array.from(transportSel), to: transportDest }),
      });
      const d = await r.json();
      if (d.success) {
        setTransportMsg(`${d.moved} operative(s) sent to ${transportDest === 'edinburgh_birch' ? 'Birch Forest' : 'Glasgow'}.`);
        setTransportSel(new Set()); setTransportDest('');
        loadModalData();
      } else { setTransportMsg(d.error || 'Failed.'); }
    } catch { setTransportMsg('Network error.'); }
    setTransporting(false);
  };

  const handlePullRemnant = async () => {
    if (!waxAcct || remnantPulling) return;
    setRemnantPulling(true); setRemnantMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/bus/pull-remnant-seeds', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxAcct }),
      });
      const d = await r.json();
      if (d.success) { setRemnantMsg(d.message); loadModalData(); }
      else { setRemnantMsg(d.error || 'Failed.'); }
    } catch { setRemnantMsg('Network error.'); }
    setRemnantPulling(false);
  };

  const filledSeats = seats.filter(s => !s.empty).length;

  const renderBusSeats = () => (
    <div style={{ marginBottom: 24 }}>
      {/* Countdown bar */}
      <div style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 10, padding: '10px 16px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <p style={{ color: '#f59e0b', fontSize: 11, fontWeight: 900, margin: 0, letterSpacing: 1 }}>NEXT DEPARTURE</p>
          <p style={{ color: '#ffffff50', fontSize: 9, margin: '2px 0 0' }}>{nextDep ? fmtTime(nextDep) : '...'} · Every 2 hours (GMT)</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ color: secondsTo < 120 ? '#4ade80' : '#fde68a', fontSize: 22, fontWeight: 900, margin: 0, fontFamily: 'monospace' }}>
            {secondsTo > 0 ? fmtCountdown(secondsTo) : 'DEPARTING'}
          </p>
          <p style={{ color: '#ffffff30', fontSize: 9, margin: '2px 0 0' }}>{filledSeats}/20 seats filled</p>
        </div>
      </div>

      {/* Bus top-view */}
      <p style={{ color: '#f59e0b50', fontSize: 9, fontWeight: 900, letterSpacing: 2.5, textTransform: 'uppercase', margin: '0 0 10px' }}>🚌 Glasgow → Edinburgh · Passengers</p>
      <div style={{ background: 'rgba(8,6,2,0.97)', border: '2px solid rgba(245,158,11,0.25)', borderRadius: 16, padding: '14px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid rgba(245,158,11,0.12)' }}>
          <span style={{ color: '#f59e0b30', fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase' }}>FRONT — DRIVER</span>
          <span style={{ color: '#f59e0b30', fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase' }}>Glasgow</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 8px 1fr', gap: '5px 0' }}>
          {Array.from({ length: 10 }, (_, row) => {
            const leftSeat  = seats[row * 2]     ?? { seat: row * 2 + 1,     empty: true };
            const rightSeat = seats[row * 2 + 1] ?? { seat: row * 2 + 2,     empty: true };
            return (
              <React.Fragment key={row}>
                {/* Left seat */}
                {(() => {
                  const s = leftSeat;
                  const occ = !s.empty;
                  const tc  = occ ? (TRACK_COLOR[s.track ?? ''] ?? '#94a3b8') : 'transparent';
                  return (
                    <div style={{ background: occ ? `${tc}14` : 'rgba(255,255,255,0.025)', border: `1px solid ${occ ? tc+'44' : 'rgba(255,255,255,0.06)'}`, borderRadius: 7, padding: '7px 9px', minHeight: 48, position: 'relative', marginRight: 3 }}>
                      <span style={{ position: 'absolute', top: 4, left: 6, color: '#ffffff15', fontSize: 8 }}>{s.seat}</span>
                      {occ ? (
                        <>
                          <p style={{ color: tc, fontSize: 10, fontWeight: 900, margin: '8px 0 2px', lineHeight: 1.2 }}>{TRACK_ICON[s.track ?? ''] ?? '🚌'} {(s.wax_account ?? '').slice(0, 9)}</p>
                          <p style={{ color: tc+'80', fontSize: 8, margin: 0 }}>#{s.operative_index}</p>
                          {(s.seeds_loaded! > 0 || s.wax_loaded! > 0) && (
                            <p style={{ color: '#ffffff25', fontSize: 7, margin: '2px 0 0' }}>
                              {s.seeds_loaded! > 0 && `${s.seeds_loaded}🌱`} {s.wax_loaded! > 0 && `${s.wax_loaded}WAX`}
                            </p>
                          )}
                        </>
                      ) : <div style={{ height: 32 }} />}
                    </div>
                  );
                })()}
                {/* Aisle */}
                <div style={{ gridColumn: 2 }} />
                {/* Right seat */}
                {(() => {
                  const s = rightSeat;
                  const occ = !s.empty;
                  const tc  = occ ? (TRACK_COLOR[s.track ?? ''] ?? '#94a3b8') : 'transparent';
                  return (
                    <div style={{ background: occ ? `${tc}14` : 'rgba(255,255,255,0.025)', border: `1px solid ${occ ? tc+'44' : 'rgba(255,255,255,0.06)'}`, borderRadius: 7, padding: '7px 9px', minHeight: 48, position: 'relative', marginLeft: 3 }}>
                      <span style={{ position: 'absolute', top: 4, right: 6, color: '#ffffff15', fontSize: 8 }}>{s.seat}</span>
                      {occ ? (
                        <>
                          <p style={{ color: tc, fontSize: 10, fontWeight: 900, margin: '8px 0 2px', lineHeight: 1.2 }}>{TRACK_ICON[s.track ?? ''] ?? '🚌'} {(s.wax_account ?? '').slice(0, 9)}</p>
                          <p style={{ color: tc+'80', fontSize: 8, margin: 0 }}>#{s.operative_index}</p>
                          {(s.seeds_loaded! > 0 || s.wax_loaded! > 0) && (
                            <p style={{ color: '#ffffff25', fontSize: 7, margin: '2px 0 0' }}>
                              {s.seeds_loaded! > 0 && `${s.seeds_loaded}🌱`} {s.wax_loaded! > 0 && `${s.wax_loaded}WAX`}
                            </p>
                          )}
                        </>
                      ) : <div style={{ height: 32 }} />}
                    </div>
                  );
                })()}
              </React.Fragment>
            );
          })}
        </div>
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid rgba(245,158,11,0.1)', textAlign: 'center' }}>
          <span style={{ color: '#f59e0b20', fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase' }}>REAR</span>
        </div>
      </div>
    </div>
  );

  const makeTable = (rows: ContribRow[], title: string, icon: string, color: string, unit: string, req: number) => {
    const total = rows.reduce((s, r) => s + r.amount, 0);
    return (
      <div style={{ marginBottom: 24 }}>
        <p style={{ color: color + '99', fontSize: 9, fontWeight: 900, letterSpacing: 2.5, textTransform: 'uppercase', margin: '0 0 10px' }}>{icon} {title}</p>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            {[['#', false], ['Player', true], [unit, false], ['Share', false]].map(([h, left]) => (
              <th key={h as string} style={{ textAlign: (left as boolean) ? 'left' : 'right', fontSize: 9, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', color: color + '50', paddingBottom: 9, borderBottom: `1px solid ${color}20` }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.wax_account} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={{ padding: '10px 0', color: '#ffffff25', fontSize: 15, width: 32 }}>{i + 1}</td>
                <td style={{ padding: '10px 0', color: '#fde68a', fontSize: 14, fontFamily: 'monospace', fontWeight: 700 }}>{r.wax_account}</td>
                <td style={{ padding: '10px 0', textAlign: 'right', color, fontSize: 14, fontWeight: 900 }}>{r.amount.toLocaleString()}</td>
                <td style={{ padding: '10px 0', textAlign: 'right', color: '#ffffff50', fontSize: 12 }}>{((r.amount / req) * 100).toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 8, background: 'rgba(255,255,255,0.05)', borderRadius: 4, height: 4, overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 4, background: color, width: '100%' }} />
        </div>
        <p style={{ marginTop: 6, color: '#ffffff15', fontSize: 10, textAlign: 'right' }}>{total.toLocaleString()} total</p>
      </div>
    );
  };

  const glasgowOps  = operatives.filter(o => o.location === 'glasgow' && !o.in_queue);
  const edinburghOps = operatives.filter(o => o.location === 'edinburgh');
  const busyOps      = operatives.filter(o => !['glasgow','edinburgh'].includes(o.location) || o.in_queue);
  const selOpData    = operatives.find(o => o.operative_index === selOp);
  const isResting    = selOpData?.rest_until ? new Date(selOpData.rest_until) > new Date() : false;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(5,4,2,0.88)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: 'rgba(10,7,2,0.99)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 18, padding: '24px 28px', width: '88vw', maxWidth: '620px', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 16px 80px rgba(0,0,0,0.85)' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 26 }}>🚌</span>
            <div>
              <p style={{ color: '#fde68a', fontSize: 17, fontWeight: 900, margin: 0 }}>Bus Stop — Glasgow</p>
              <p style={{ color: '#f59e0b40', fontSize: 9, margin: 0, fontWeight: 700, letterSpacing: 2.5, textTransform: 'uppercase' }}>Task 6 Complete · Service Active</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#ffffff60', fontSize: 18, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        {renderBusSeats()}

        {/* Edinburgh section */}
        {edinburghOps.length > 0 && (
          <div style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 12, padding: '14px 18px', marginBottom: 16 }}>
            <p style={{ color: '#93c5fd', fontSize: 10, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 12px' }}>🏴󠁧󠁢󠁳󠁣󠁴󠁿 In Edinburgh — Waiting</p>
            {edinburghOps.map(op => (
              <div key={op.operative_index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <div>
                  <p style={{ color: TRACK_COLOR[op.track] ?? '#94a3b8', fontSize: 12, fontWeight: 900, margin: 0 }}>
                    {TRACK_ICON[op.track] ?? '🚌'} Op #{op.operative_index} · {op.track}
                  </p>
                  <p style={{ color: '#ffffff30', fontSize: 9, margin: '2px 0 0' }}>Waiting at Edinburgh Bus Stop</p>
                </div>
              </div>
            ))}
            {recallMsg && <p style={{ color: '#4ade80', fontSize: 11, margin: '10px 0 0', textAlign: 'center' }}>{recallMsg}</p>}
          </div>
        )}

        {/* Transport Panel — move Edinburgh operatives */}
        {(() => {
          const edinOps = operatives.filter(o => o.location === 'edinburgh');
          if (!edinOps.length) return null;
          const freeOps = edinOps; // all at general stop are free to move
          const allSelected = edinOps.every(o => transportSel.has(o.operative_index));
          const toggleOp = (idx: number) => {
            setTransportSel(prev => {
              const next = new Set(prev);
              next.has(idx) ? next.delete(idx) : next.add(idx);
              return next;
            });
          };
          const selectAll  = () => setTransportSel(new Set(edinOps.map(o => o.operative_index)));
          const selectFree = () => setTransportSel(new Set(freeOps.map(o => o.operative_index)));
          const clearSel   = () => setTransportSel(new Set());
          return (
            <div style={{ background: 'rgba(99,102,241,0.07)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
              <p style={{ color: '#a5b4fc', fontSize: 10, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 10px' }}>
                🚉 Transport — Edinburgh General Stop · {edinOps.length} operative{edinOps.length !== 1 ? 's' : ''}
              </p>

              {/* Destination */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <button onClick={() => setTransportDest(transportDest === 'edinburgh_birch' ? '' : 'edinburgh_birch')}
                  style={{ flex: 1, padding: '8px 6px', borderRadius: 8, border: `1px solid ${transportDest === 'edinburgh_birch' ? '#a3e635' : 'rgba(255,255,255,0.1)'}`, background: transportDest === 'edinburgh_birch' ? 'rgba(163,230,53,0.12)' : 'rgba(255,255,255,0.04)', color: transportDest === 'edinburgh_birch' ? '#a3e635' : '#64748b', fontSize: 11, fontWeight: 900, cursor: 'pointer' }}>
                  🌳 → Birch Forest
                </button>
                <button onClick={() => setTransportDest(transportDest === 'glasgow' ? '' : 'glasgow')}
                  style={{ flex: 1, padding: '8px 6px', borderRadius: 8, border: `1px solid ${transportDest === 'glasgow' ? '#f87171' : 'rgba(255,255,255,0.1)'}`, background: transportDest === 'glasgow' ? 'rgba(248,113,113,0.12)' : 'rgba(255,255,255,0.04)', color: transportDest === 'glasgow' ? '#f87171' : '#64748b', fontSize: 11, fontWeight: 900, cursor: 'pointer' }}>
                  ← Glasgow
                </button>
              </div>

              {/* Quick selects */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                <button onClick={allSelected ? clearSel : selectAll}
                  style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: '#94a3b8', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                  {allSelected ? 'Clear All' : 'Select All'}
                </button>
                <button onClick={selectFree}
                  style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: '#94a3b8', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                  Select Free
                </button>
              </div>

              {/* Scrollable operative list */}
              <div style={{ maxHeight: 160, overflowY: 'auto', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)', marginBottom: 10 }}>
                {edinOps.map(op => {
                  const checked = transportSel.has(op.operative_index);
                  const carry = op.carry_wax && op.carry_wax > 0 ? `${op.carry_wax} WAX` : 'free';
                  return (
                    <div key={op.operative_index}
                      onClick={() => toggleOp(op.operative_index)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderBottom: '1px solid rgba(255,255,255,0.04)', cursor: 'pointer', background: checked ? 'rgba(99,102,241,0.12)' : 'transparent' }}>
                      <div style={{ width: 14, height: 14, borderRadius: 3, border: `1.5px solid ${checked ? '#818cf8' : 'rgba(255,255,255,0.2)'}`, background: checked ? '#818cf8' : 'transparent', flexShrink: 0 }} />
                      <span style={{ fontSize: 13 }}>{TRACK_ICON[op.track] ?? '🚌'}</span>
                      <span style={{ color: '#cbd5e1', fontSize: 11, fontWeight: 700, flex: 1 }}>Op #{op.operative_index}</span>
                      <span style={{ color: '#475569', fontSize: 10 }}>{carry}</span>
                    </div>
                  );
                })}
              </div>

              {transportMsg && <p style={{ color: transportMsg.includes('sent') ? '#4ade80' : '#f87171', fontSize: 11, margin: '0 0 8px', textAlign: 'center' }}>{transportMsg}</p>}

              <button
                onClick={handleTransport}
                disabled={transporting || !transportSel.size || !transportDest}
                style={{ width: '100%', padding: '9px', borderRadius: 8, border: 'none', background: !transportSel.size || !transportDest ? 'rgba(255,255,255,0.06)' : transportDest === 'edinburgh_birch' ? 'linear-gradient(135deg,#65a30d,#a3e635)' : 'linear-gradient(135deg,#dc2626,#f87171)', color: !transportSel.size || !transportDest ? '#334155' : '#000', fontSize: 12, fontWeight: 900, cursor: !transportSel.size || !transportDest ? 'not-allowed' : 'pointer' }}>
                {transporting ? 'Sending...' : transportSel.size ? `Send ${transportSel.size} operative(s) ${transportDest === 'edinburgh_birch' ? '→ Birch' : transportDest === 'glasgow' ? '← Glasgow' : ''}` : 'Select operatives and destination'}
              </button>
            </div>
          );
        })()}

        {/* Go to Edinburgh plots */}
        <a href="/edinburgh-smt/" target="_blank" rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg, rgba(22,101,52,0.35) 0%, rgba(6,95,70,0.25) 100%)', border: '1px solid rgba(52,211,153,0.4)', borderRadius: 12, padding: '14px 18px', marginBottom: 20, textDecoration: 'none', cursor: 'pointer', transition: 'border-color 0.2s' }}
          onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(52,211,153,0.8)')}
          onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(52,211,153,0.4)')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 22 }}>🌲</span>
            <div>
              <p style={{ color: '#34d399', fontSize: 13, fontWeight: 900, margin: 0 }}>Edinburgh Forest</p>
              <p style={{ color: '#34d39960', fontSize: 9, margin: '2px 0 0', letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700 }}>Scots Pine · Plant · Grow · Harvest</p>
            </div>
          </div>
          <span style={{ color: '#34d399', fontSize: 15, fontWeight: 900 }}>Go to your plots →</span>
        </a>

        {/* Travel to Birch Forest */}
        <div
          onClick={() => { onClose(); (window as any).__openBirchBusStop?.(); }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg, rgba(101,57,8,0.35) 0%, rgba(52,83,6,0.25) 100%)', border: '1px solid rgba(163,230,53,0.4)', borderRadius: 12, padding: '14px 18px', marginBottom: 20, cursor: 'pointer', transition: 'border-color 0.2s' }}
          onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(163,230,53,0.8)')}
          onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(163,230,53,0.4)')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 22 }}>🌳</span>
            <div>
              <p style={{ color: '#a3e635', fontSize: 13, fontWeight: 900, margin: 0 }}>Silver Birch Forest</p>
              <p style={{ color: '#a3e63560', fontSize: 9, margin: '2px 0 0', letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700 }}>Forest 2 · Now Open · Claim Plots</p>
            </div>
          </div>
          <span style={{ color: '#a3e635', fontSize: 15, fontWeight: 900 }}>Travel to Birch →</span>
        </div>

        {/* Remnant Seeds */}
        {waxAcct && (
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 16, marginBottom: 20 }}>
            <p style={{ color: '#86efac50', fontSize: 9, fontWeight: 900, letterSpacing: 2.5, textTransform: 'uppercase', margin: '0 0 10px' }}>🌱 Remnant Seeds</p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: remnantSeeds > 0 ? 'rgba(134,239,172,0.06)' : 'rgba(255,255,255,0.02)', border: `1px solid ${remnantSeeds > 0 ? 'rgba(134,239,172,0.25)' : 'rgba(255,255,255,0.07)'}`, borderRadius: 10, padding: '12px 16px' }}>
              <div>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: remnantSeeds > 0 ? '#86efac' : '#ffffff25' }}>
                  {remnantSeeds > 0 ? `${remnantSeeds} seed${remnantSeeds > 1 ? 's' : ''} stranded on ${remnantOps} operative${remnantOps > 1 ? 's' : ''}` : 'No stranded seeds'}
                </p>
                <p style={{ margin: '3px 0 0', fontSize: 9, color: '#ffffff20' }}>Seeds left over after planting (not divisible by 3)</p>
              </div>
              <button
                onClick={handlePullRemnant}
                disabled={remnantSeeds === 0 || remnantPulling}
                style={{
                  marginLeft: 14, flexShrink: 0,
                  padding: '8px 14px', borderRadius: 8, fontSize: 11, fontWeight: 900, cursor: remnantSeeds === 0 ? 'not-allowed' : 'pointer',
                  background: remnantSeeds > 0 ? 'rgba(134,239,172,0.15)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${remnantSeeds > 0 ? 'rgba(134,239,172,0.4)' : 'rgba(255,255,255,0.08)'}`,
                  color: remnantSeeds > 0 ? '#86efac' : '#ffffff20',
                  opacity: remnantPulling ? 0.6 : 1,
                }}>
                {remnantPulling ? 'Pulling…' : 'Pull to Wallet'}
              </button>
            </div>
            {remnantMsg && <p style={{ margin: '8px 0 0', fontSize: 11, color: remnantMsg.includes('returned') ? '#4ade80' : '#f87171', textAlign: 'center' }}>{remnantMsg}</p>}
          </div>
        )}

        {/* Queue section */}
        {waxAcct && (
          <div style={{ borderTop: '1px solid rgba(245,158,11,0.12)', paddingTop: 20, marginBottom: 24 }}>
            <p style={{ color: '#f59e0b80', fontSize: 9, fontWeight: 900, letterSpacing: 2.5, textTransform: 'uppercase', margin: '0 0 14px' }}>🚌 Glasgow → Edinburgh · Send Operative</p>

            {operatives.length === 0 ? (
              <p style={{ color: '#ffffff30', fontSize: 12, textAlign: 'center', padding: '12px 0' }}>No active transport operatives. Train one at the University.</p>
            ) : glasgowOps.length === 0 && busyOps.length > 0 ? (
              <p style={{ color: '#ffffff30', fontSize: 12, textAlign: 'center', padding: '12px 0' }}>All operatives are currently on a trip or resting.</p>
            ) : (
              <>
                {/* Operative selector — Glasgow only */}
                {glasgowOps.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <p style={{ color: '#ffffff40', fontSize: 9, marginBottom: 8, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase' }}>Available in Glasgow</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                      {glasgowOps.map(op => {
                        const resting = op.rest_until ? new Date(op.rest_until) > new Date() : false;
                        const restMins = resting ? Math.ceil((new Date(op.rest_until!).getTime() - Date.now()) / 60000) : 0;
                        return (
                          <button key={op.operative_index}
                            onClick={() => !resting && setSelOp(selOp === op.operative_index ? '' : op.operative_index)}
                            style={{
                              background: resting ? 'rgba(255,255,255,0.03)' : selOp === op.operative_index ? `${TRACK_COLOR[op.track] ?? '#f59e0b'}22` : 'rgba(255,255,255,0.05)',
                              border: `1px solid ${resting ? 'rgba(255,255,255,0.06)' : selOp === op.operative_index ? (TRACK_COLOR[op.track] ?? '#f59e0b') : 'rgba(255,255,255,0.1)'}`,
                              borderRadius: 8, padding: '7px 13px', cursor: resting ? 'not-allowed' : 'pointer',
                              color: resting ? '#ffffff20' : selOp === op.operative_index ? (TRACK_COLOR[op.track] ?? '#fde68a') : '#ffffff60',
                              fontSize: 11, fontWeight: 700,
                            }}>
                            {TRACK_ICON[op.track] ?? '🚌'} #{op.operative_index}
                            {resting && <span style={{ fontSize: 9, marginLeft: 5, color: '#ffffff20' }}>resting {restMins}m</span>}
                            {!resting && (op.carry_wax ?? 0) > 0 && <span style={{ fontSize: 9, marginLeft: 5, color: '#34d399', fontWeight: 900 }}>+{op.carry_wax}WAX carried</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Busy operatives info */}
                {busyOps.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <p style={{ color: '#ffffff25', fontSize: 9, marginBottom: 6, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase' }}>On Trip / In Queue</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {busyOps.map(op => (
                        <div key={op.operative_index} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 7, padding: '5px 10px', color: '#ffffff25', fontSize: 10 }}>
                          #{op.operative_index} · {op.in_queue ? 'queued' : op.location.replace('_', ' ')}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Load boxes — only show when operative selected and not resting */}
                {selOp !== '' && !isResting && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                    {[
                      { label: 'Seeds', icon: '🌱', val: qSeeds, max: Math.min(100, seeds), set: setQSeeds, color: '#86efac', hint: `${seeds} avail · max 100` },
                      { label: 'WAX',   icon: '⬡',  val: qWax,   max: 100,                  set: setQWax,   color: '#fde68a', hint: '1 WAX = 1 Edinburgh plot' },
                    ].map(({ label, icon, val, max, set, color, hint }) => (
                      <div key={label} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '12px 14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                          <p style={{ color, fontSize: 11, fontWeight: 900, letterSpacing: 1, margin: 0 }}>{icon} {label}</p>
                          <p style={{ color: '#ffffff25', fontSize: 9, margin: 0 }}>{hint}</p>
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <input type="number" min={0} max={max} step={1} value={val}
                            onChange={e => set(Math.min(max, Math.max(0, Math.floor(Number(e.target.value)))))}
                            style={{ flex: 1, background: 'rgba(0,0,0,0.5)', border: `1px solid ${color}30`, borderRadius: 7, padding: '6px 10px', color, fontSize: 16, fontWeight: 900, width: '100%', textAlign: 'center' }} />
                          <button onClick={() => set(max)}
                            style={{ background: `${color}15`, border: `1px solid ${color}30`, borderRadius: 7, color, fontSize: 9, padding: '6px 8px', cursor: 'pointer', fontWeight: 900 }}>
                            MAX
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Submit */}
                {glasgowOps.length > 0 && (
                  <button onClick={handleQueue} disabled={queueing || selOp === '' || isResting}
                    style={{
                      width: '100%', padding: '12px 0', borderRadius: 10, fontWeight: 900, fontSize: 13,
                      cursor: (selOp === '' || isResting) ? 'not-allowed' : 'pointer',
                      background: (selOp === '' || isResting) ? 'rgba(255,255,255,0.04)' : 'linear-gradient(135deg, rgba(245,158,11,0.25), rgba(245,158,11,0.12))',
                      border: `1px solid ${(selOp === '' || isResting) ? 'rgba(255,255,255,0.08)' : 'rgba(245,158,11,0.4)'}`,
                      color: (selOp === '' || isResting) ? '#ffffff25' : '#fde68a',
                    }}>
                    {queueing ? 'Queuing...' : selOp === '' ? 'Select an operative' : `Board Bus → Edinburgh${qSeeds > 0 || qWax > 0 ? ` (${qSeeds > 0 ? qSeeds+'🌱 ' : ''}${qWax > 0 ? qWax+' WAX' : ''})` : ''}`}
                  </button>
                )}

                {queueMsg && (
                  <p style={{ textAlign: 'center', marginTop: 10, fontSize: 12, color: queueMsg.includes('queued') || queueMsg.includes('!') ? '#4ade80' : '#f87171' }}>{queueMsg}</p>
                )}
              </>
            )}
          </div>
        )}

        {/* Contribution leaderboards */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 20 }}>
          <p style={{ color: '#ffffff20', fontSize: 9, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 16px' }}>Task 6 Build Contributions</p>
          {makeTable(logs,    'Opuntia Logs',  '🌵', '#f59e0b', 'Logs',    7000)}
          {makeTable(ethanol, 'Ethanol',       '⚗️',  '#22d3ee', 'Ethanol', 500)}
          {makeTable(vayal,   'Vayal',         '🌾', '#34d399', 'Vayal',   15000)}
          {makeTable(food,    'Food Packets',  '📦', '#f472b6', 'Food',    1000000)}
        </div>

      </div>
    </div>
  );
}

// ─── Water Harvester Modal ────────────────────────────────────────────────────
function WaterHarvesterModal({ onClose, waxAcct }: { onClose: () => void; waxAcct: string }) {
  const [shareholders, setShareholders] = React.useState<any[]>([]);
  const [wariRows, setWariRows] = React.useState<any[]>([]);
  const [waterBarrels,    setWaterBarrels]    = React.useState<number>(0);
  const [royalty,         setRoyalty]         = React.useState<{ eligible: boolean; accrued: number; claimable: number; total_claimed: number; share_pct: number } | null>(null);
  const [playerEthanol,   setPlayerEthanol]   = React.useState<number>(0);
  const [craftingBarrel,  setCraftingBarrel]  = React.useState(false);
  const [claimingRoyalty, setClaimingRoyalty] = React.useState(false);
  const [barrelMsg,       setBarrelMsg]       = React.useState('');
  const [royaltyMsg,      setRoyaltyMsg]      = React.useState('');
  const [craftQty,        setCraftQty]        = React.useState<number>(1);

  const loadPlayerData = React.useCallback(() => {
    if (!waxAcct) return;
    fetch(`/samatvam/api/samatvam/water/barrel-count/${waxAcct}`)
      .then(r => r.json()).then(d => { if (d.success) setWaterBarrels(d.barrels); }).catch(() => {});
    fetch(`/samatvam/api/samatvam/water/royalty/${waxAcct}`)
      .then(r => r.json()).then(d => { if (d.success) setRoyalty(d); }).catch(() => {});
    fetch(`/samatvam/api/samatvam/materials/${waxAcct}`)
      .then(r => r.json()).then(d => { if (d.success && d.materials) { const e = d.materials.find((m: any) => m.material_id === 'ethanol'); setPlayerEthanol(e ? parseInt(e.quantity) : 0); } }).catch(() => {});
  }, [waxAcct]);

  React.useEffect(() => {
    fetch('/samatvam/api/samatvam/task-shareholders/water-harvesting-zone')
      .then(r => r.json())
      .then(d => { if (d.success) setShareholders(d.shareholders); })
      .catch(() => {});
    fetch('/samatvam/api/samatvam/wari/all')
      .then(r => r.json())
      .then(d => { if (d.success) setWariRows(d.rows); })
      .catch(() => {});
    loadPlayerData();
  }, [loadPlayerData]);

  const handleCraftBarrel = async () => {
    if (!waxAcct || craftingBarrel || craftQty < 1) return;
    setCraftingBarrel(true); setBarrelMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/water/craft-barrel', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wax_account: waxAcct, quantity: craftQty }) });
      const d = await r.json();
      if (d.success) {
        setBarrelMsg(`✅ Crafted ${d.quantity_crafted} water barrel${d.quantity_crafted > 1 ? 's' : ''}! You now have ${d.barrels}.`);
        setWaterBarrels(d.barrels);
        setPlayerEthanol(d.ethanol_left);
      } else { setBarrelMsg(`❌ ${d.error}`); }
    } catch { setBarrelMsg('❌ Network error'); }
    setCraftingBarrel(false);
  };

  const handleClaimRoyalty = async () => {
    if (!waxAcct || claimingRoyalty) return;
    setClaimingRoyalty(true); setRoyaltyMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/water/claim-royalty', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wax_account: waxAcct }) });
      const d = await r.json();
      if (d.success) {
        setRoyaltyMsg(`✅ Claimed ${d.claimed} water barrel${d.claimed > 1 ? 's' : ''}! You now have ${d.barrels}.`);
        setWaterBarrels(d.barrels);
        setRoyalty(prev => prev ? { ...prev, accrued: d.accrued_remaining, claimable: 0 } : prev);
      } else { setRoyaltyMsg(`❌ ${d.error}`); }
    } catch { setRoyaltyMsg('❌ Network error'); }
    setClaimingRoyalty(false);
  };

  const wariMap: Record<string, { wari_balance: string; wari_rate: string }> = {};
  wariRows.forEach(r => { wariMap[r.wax_account] = r; });

  const medals = ['🥇','🥈','🥉'];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'linear-gradient(160deg,rgba(2,15,35,0.98),rgba(1,8,23,0.99))', overflowY: 'auto' }}>

      {/* ── Header bar ── */}
      <div style={{ position: 'sticky', top: 0, zIndex: 10, background: 'rgba(2,10,28,0.95)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(56,189,248,0.15)', padding: '16px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 32 }}>💧</span>
          <div>
            <p style={{ color: '#7dd3fc', fontSize: 20, fontWeight: 900, margin: 0 }}>Water Harvester</p>
            <p style={{ color: 'rgba(56,189,248,0.5)', fontSize: 10, fontWeight: 700, letterSpacing: 2.5, textTransform: 'uppercase', margin: 0 }}>Task 2 Complete · City Infrastructure</p>
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#64748b', fontSize: 18, cursor: 'pointer', padding: '6px 16px', fontWeight: 700 }}>✕ Close</button>
      </div>

      <div style={{ padding: '32px 32px 60px' }}>

        {/* ── Status + description ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 32 }}>
          <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 14, padding: '20px 24px' }}>
            <p style={{ color: '#6ee7b7', fontSize: 11, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 8px' }}>✅ Status</p>
            <p style={{ color: '#fff', fontSize: 16, fontWeight: 900, margin: '0 0 4px' }}>Operational</p>
            <p style={{ color: 'rgba(110,231,183,0.6)', fontSize: 12, margin: 0 }}>20,000 food packets delivered · Task 2 closed</p>
          </div>
          <div style={{ background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.15)', borderRadius: 14, padding: '20px 24px' }}>
            <p style={{ color: '#7dd3fc', fontSize: 11, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 8px' }}>💧 WARI Token</p>
            <p style={{ color: '#fff', fontSize: 16, fontWeight: 900, margin: '0 0 4px' }}>3 WARI / day</p>
            <p style={{ color: 'rgba(125,211,252,0.6)', fontSize: 12, margin: 0 }}>Distributed every hour · proportional to contribution share</p>
          </div>
        </div>

        {/* ── Water Barrel Crafting ── */}
        {waxAcct && (
          <div style={{ marginBottom: 36 }}>
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, fontWeight: 900, letterSpacing: 2.5, textTransform: 'uppercase', margin: '0 0 14px' }}>💧 Craft Water Barrels</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {/* Craft panel */}
              <div style={{ background: 'rgba(56,189,248,0.05)', border: '1px solid rgba(56,189,248,0.2)', borderRadius: 14, padding: '20px 24px' }}>
                <p style={{ color: '#38bdf8', fontSize: 11, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 14px' }}>🛢 Craft Water Barrels</p>
                <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                  <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '8px 14px' }}>
                    <p style={{ color: '#ffffff40', fontSize: 9, fontWeight: 700, margin: '0 0 3px', letterSpacing: 1 }}>COST PER BARREL</p>
                    <p style={{ color: '#fde68a', fontSize: 13, fontWeight: 900, margin: 0, fontFamily: 'monospace' }}>200 food packets + 1L ethanol</p>
                  </div>
                  <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '8px 14px' }}>
                    <p style={{ color: '#ffffff40', fontSize: 9, fontWeight: 700, margin: '0 0 3px', letterSpacing: 1 }}>YOUR BARRELS</p>
                    <p style={{ color: '#38bdf8', fontSize: 13, fontWeight: 900, margin: 0, fontFamily: 'monospace' }}>💧 {waterBarrels}</p>
                  </div>
                  <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '8px 14px' }}>
                    <p style={{ color: '#ffffff40', fontSize: 9, fontWeight: 700, margin: '0 0 3px', letterSpacing: 1 }}>ETHANOL</p>
                    <p style={{ color: playerEthanol >= craftQty ? '#6ee7b7' : '#f87171', fontSize: 13, fontWeight: 900, margin: 0, fontFamily: 'monospace' }}>⚗️ {playerEthanol}L</p>
                  </div>
                </div>
                {/* Quantity input */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ color: '#ffffff40', fontSize: 9, fontWeight: 700, margin: '0 0 5px', letterSpacing: 1 }}>QUANTITY</p>
                    <input
                      type="number" min={1} max={playerEthanol || 1} value={craftQty}
                      onChange={e => setCraftQty(Math.max(1, parseInt(e.target.value) || 1))}
                      style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(56,189,248,0.3)', borderRadius: 8, padding: '8px 12px', color: '#fff', fontSize: 15, fontWeight: 900, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '8px 14px', marginTop: 16 }}>
                    <p style={{ color: '#ffffff40', fontSize: 9, fontWeight: 700, margin: '0 0 3px', letterSpacing: 1 }}>TOTAL COST</p>
                    <p style={{ color: '#fde68a', fontSize: 12, fontWeight: 900, margin: 0, fontFamily: 'monospace' }}>{(craftQty * 200).toLocaleString()} food packets + {craftQty}L eth</p>
                  </div>
                </div>
                <button onClick={handleCraftBarrel} disabled={craftingBarrel || playerEthanol < craftQty || craftQty < 1}
                  style={{ width: '100%', padding: '11px', borderRadius: 10, fontSize: 13, fontWeight: 800, background: (craftingBarrel || playerEthanol < craftQty) ? 'rgba(255,255,255,0.03)' : 'rgba(56,189,248,0.1)', border: `1px solid ${(craftingBarrel || playerEthanol < craftQty) ? 'rgba(255,255,255,0.06)' : 'rgba(56,189,248,0.4)'}`, color: (craftingBarrel || playerEthanol < craftQty) ? '#ffffff20' : '#38bdf8', cursor: (craftingBarrel || playerEthanol < craftQty) ? 'default' : 'pointer', transition: 'all 0.2s' }}>
                  {craftingBarrel ? 'Crafting…' : `💧 Craft ${craftQty} Water Barrel${craftQty > 1 ? 's' : ''}`}
                </button>
                {barrelMsg && <p style={{ color: barrelMsg.startsWith('✅') ? '#6ee7b7' : '#f87171', fontSize: 11, margin: '8px 0 0', fontWeight: 700 }}>{barrelMsg}</p>}
              </div>
              {/* Royalty panel — only for task-2 contributors */}
              {royalty?.eligible ? (
                <div style={{ background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.18)', borderRadius: 14, padding: '20px 24px' }}>
                  <p style={{ color: '#10b981', fontSize: 11, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 14px' }}>🏗 Royalty — Task 2 Share</p>
                  <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                    <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '8px 14px' }}>
                      <p style={{ color: '#ffffff40', fontSize: 9, fontWeight: 700, margin: '0 0 3px', letterSpacing: 1 }}>YOUR SHARE</p>
                      <p style={{ color: '#6ee7b7', fontSize: 13, fontWeight: 900, margin: 0, fontFamily: 'monospace' }}>{(royalty.share_pct * 100).toFixed(2)}%</p>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '8px 14px' }}>
                      <p style={{ color: '#ffffff40', fontSize: 9, fontWeight: 700, margin: '0 0 3px', letterSpacing: 1 }}>ACCRUED</p>
                      <p style={{ color: '#6ee7b7', fontSize: 13, fontWeight: 900, margin: 0, fontFamily: 'monospace' }}>💧 {royalty.accrued.toFixed(4)}</p>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '8px 14px' }}>
                      <p style={{ color: '#ffffff40', fontSize: 9, fontWeight: 700, margin: '0 0 3px', letterSpacing: 1 }}>CLAIMABLE</p>
                      <p style={{ color: royalty.claimable >= 1 ? '#6ee7b7' : '#ffffff30', fontSize: 13, fontWeight: 900, margin: 0, fontFamily: 'monospace' }}>💧 {royalty.claimable}</p>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '8px 14px' }}>
                      <p style={{ color: '#ffffff40', fontSize: 9, fontWeight: 700, margin: '0 0 3px', letterSpacing: 1 }}>TOTAL CLAIMED</p>
                      <p style={{ color: '#ffffff50', fontSize: 13, fontWeight: 900, margin: 0, fontFamily: 'monospace' }}>💧 {royalty.total_claimed}</p>
                    </div>
                  </div>
                  <button onClick={handleClaimRoyalty} disabled={claimingRoyalty || royalty.claimable < 1}
                    style={{ width: '100%', padding: '11px', borderRadius: 10, fontSize: 13, fontWeight: 800, background: (claimingRoyalty || royalty.claimable < 1) ? 'rgba(255,255,255,0.03)' : 'rgba(16,185,129,0.12)', border: `1px solid ${(claimingRoyalty || royalty.claimable < 1) ? 'rgba(255,255,255,0.06)' : 'rgba(16,185,129,0.4)'}`, color: (claimingRoyalty || royalty.claimable < 1) ? '#ffffff20' : '#6ee7b7', cursor: (claimingRoyalty || royalty.claimable < 1) ? 'default' : 'pointer', transition: 'all 0.2s' }}>
                    {claimingRoyalty ? 'Claiming…' : royalty.claimable >= 1 ? `💧 Claim ${royalty.claimable} Barrel${royalty.claimable > 1 ? 's' : ''}` : `Accruing… (${royalty.accrued.toFixed(4)} / 1.0 needed)`}
                  </button>
                  {royaltyMsg && <p style={{ color: royaltyMsg.startsWith('✅') ? '#6ee7b7' : '#f87171', fontSize: 11, margin: '8px 0 0', fontWeight: 700 }}>{royaltyMsg}</p>}
                </div>
              ) : (
                <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <p style={{ color: '#ffffff20', fontSize: 12, fontWeight: 700, textAlign: 'center', margin: 0 }}>Royalties flow to the 7 players<br/>who built this facility</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Shareholders table ── */}
        <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, fontWeight: 900, letterSpacing: 2.5, textTransform: 'uppercase', margin: '0 0 14px' }}>Shareholders & WARI Accrual</p>
        <div style={{ borderRadius: 14, border: '1px solid rgba(56,189,248,0.12)', overflow: 'hidden' }}>

          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: '36px 1fr 140px 100px 120px 130px', alignItems: 'center', gap: 12, padding: '10px 20px', background: 'rgba(56,189,248,0.06)', borderBottom: '1px solid rgba(56,189,248,0.1)' }}>
            <span style={{ color: '#475569', fontSize: 10, fontWeight: 900 }}>#</span>
            <span style={{ color: '#475569', fontSize: 10, fontWeight: 900, letterSpacing: 1, textTransform: 'uppercase' }}>Player</span>
            <span style={{ color: '#475569', fontSize: 10, fontWeight: 900, letterSpacing: 1, textTransform: 'uppercase' }}>Food Contributed</span>
            <span style={{ color: '#475569', fontSize: 10, fontWeight: 900, letterSpacing: 1, textTransform: 'uppercase' }}>Share</span>
            <span style={{ color: '#475569', fontSize: 10, fontWeight: 900, letterSpacing: 1, textTransform: 'uppercase' }}>WARI Balance</span>
            <span style={{ color: '#475569', fontSize: 10, fontWeight: 900, letterSpacing: 1, textTransform: 'uppercase' }}>WARI Rate/hr</span>
          </div>

          {shareholders.map((s, i) => {
            const w = wariMap[s.wax_account];
            const medalColors = ['#fcd34d','#94a3b8','#cd7c2a'];
            const nameColor = i < 3 ? medalColors[i] : '#cbd5e1';
            return (
              <div key={s.wax_account} style={{ display: 'grid', gridTemplateColumns: '36px 1fr 140px 100px 120px 130px', alignItems: 'center', gap: 12, padding: '14px 20px', borderBottom: i < shareholders.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none', background: i === 0 ? 'rgba(252,211,77,0.02)' : 'transparent' }}>
                <span style={{ fontSize: 16 }}>{i < 3 ? medals[i] : <span style={{ color: '#334155', fontSize: 11, fontWeight: 900 }}>{s.rank}</span>}</span>
                <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: nameColor }}>{s.wax_account}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#7dd3fc' }}>{Number(s.food_contributed).toLocaleString()} <span style={{ color: '#334155', fontSize: 10 }}>pkts</span></span>
                <span style={{ fontSize: 13, fontWeight: 900, color: '#6ee7b7' }}>{s.share_pct}%</span>
                <span style={{ fontSize: 13, fontWeight: 900, color: '#38bdf8', fontFamily: 'monospace' }}>{w ? Number(w.wari_balance).toFixed(4) : '—'}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'rgba(56,189,248,0.6)', fontFamily: 'monospace' }}>{w ? `+${Number(w.wari_rate).toFixed(5)}/hr` : '—'}</span>
              </div>
            );
          })}
        </div>

        <p style={{ color: '#1e3a5f', fontSize: 11, marginTop: 20, lineHeight: 1.7 }}>
          WARI accrues every hour. Shareholder positions are permanent — based on food packets contributed before Task 2 closed.
        </p>

      </div>
    </div>
  );
}

// ─── Council Workers Salary Modal ─────────────────────────────────────────────

const HOME_X = 1015;
const HOME_Y = 765;

function calcDuration(x1: number, y1: number, x2: number, y2: number): number {
  const dist = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
  // ~20 px/sec walking speed, min 10s, max 120s
  return Math.min(120, Math.max(10, Math.round(dist / 20)));
}

function CouncilHouseModal({ onClose, waxAcct, workers, zones }: {
  onClose: () => void;
  waxAcct: string;
  workers: CouncilWorker[];
  zones: MapZone[];
}) {
  const [shramRows, setShramRows] = React.useState<{ wax_account: string; worker_count: number; shram_balance: string; transport_operative_count: number; training_count: number }[]>([]);
  const [history, setHistory] = React.useState<any[]>([]);
  const [allocation, setAllocation] = React.useState<{ wax_account: string; task1_packets: number; score_workers: number; invite_workers: number; should_have: number; current_workers: number; training_workers: number; operatives: number; on_wax: number; total_held: number; still_owed: number }[]>([]);
  const [loading, setLoading] = React.useState(true);

  // Worker control state: per-worker selected zone id
  const [workerDest, setWorkerDest] = React.useState<Record<number, string>>({});
  const [sending, setSending] = React.useState<Record<number, boolean>>({});
  const [msgs, setMsgs] = React.useState<Record<number, string>>({});
  const [batchZone, setBatchZone] = React.useState('');
  const [batchSending, setBatchSending] = React.useState(false);
  const [batchMsg, setBatchMsg] = React.useState('');

  const myWorkers = workers.filter(w => w.wax_account === waxAcct);

  React.useEffect(() => {
    Promise.all([
      fetch('/samatvam/api/samatvam/shram/all').then(r => r.json()),
      fetch('/samatvam/api/samatvam/workers/allocation').then(r => r.json()),
    ]).then(([shram, alloc]) => {
      if (shram.success) setShramRows(shram.rows);
      if (alloc.success) setAllocation(alloc.rows);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const sendWorker = async (workerId: number, destX: number, destY: number, fromX: number, fromY: number) => {
    setSending(s => ({ ...s, [workerId]: true }));
    setMsgs(m => ({ ...m, [workerId]: '' }));
    try {
      const r = await fetch(`/samatvam/api/samatvam/workers/${workerId}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wax_account: waxAcct,
          dest_x: destX, dest_y: destY,
          move_duration_sec: calcDuration(fromX, fromY, destX, destY),
        }),
      });
      const d = await r.json();
      setMsgs(m => ({ ...m, [workerId]: d.success ? '✅ On the way' : `❌ ${d.error}` }));
    } catch { setMsgs(m => ({ ...m, [workerId]: '❌ Network error' })); }
    setSending(s => ({ ...s, [workerId]: false }));
  };

  const handleSendOne = async (w: CouncilWorker) => {
    const zoneId = workerDest[w.id];
    if (!zoneId) { setMsgs(m => ({ ...m, [w.id]: '❌ Pick a zone first' })); return; }
    if (zoneId === 'home') {
      await sendWorker(w.id, HOME_X, HOME_Y, w.pos_x, w.pos_y);
    } else {
      const zone = zones.find(z => z.zone_id === zoneId);
      if (!zone) return;
      await sendWorker(w.id, zone.cx, zone.cy, w.pos_x, w.pos_y);
    }
  };

  const handleCallBack = async (w: CouncilWorker) => {
    if (w.state === 3) return;
    await sendWorker(w.id, HOME_X, HOME_Y, w.pos_x, w.pos_y);
  };

  const handleSendAll = async () => {
    if (!batchZone) { setBatchMsg('❌ Pick a zone first'); return; }
    setBatchSending(true); setBatchMsg('');
    const dest = batchZone === 'home'
      ? { cx: HOME_X, cy: HOME_Y }
      : zones.find(z => z.zone_id === batchZone);
    if (!dest) { setBatchSending(false); return; }
    const movableWorkers = myWorkers.filter(w => w.state !== 3);
    for (const w of movableWorkers) {
      await sendWorker(w.id, dest.cx, dest.cy, w.pos_x, w.pos_y);
    }
    const trainingCount = myWorkers.length - movableWorkers.length;
    setBatchMsg(`✅ ${movableWorkers.length} worker${movableWorkers.length !== 1 ? 's' : ''} dispatched${trainingCount > 0 ? ` · ${trainingCount} in training (skipped)` : ''}`);
    setBatchSending(false);
  };

  const handleCallAll = async () => {
    setBatchSending(true); setBatchMsg('');
    const movableWorkers = myWorkers.filter(w => w.state !== 3);
    for (const w of movableWorkers) {
      await sendWorker(w.id, HOME_X, HOME_Y, w.pos_x, w.pos_y);
    }
    const trainingCount = myWorkers.length - movableWorkers.length;
    setBatchMsg(`✅ ${movableWorkers.length} worker${movableWorkers.length !== 1 ? 's' : ''} called back${trainingCount > 0 ? ` · ${trainingCount} in training (skipped)` : ''}`);
    setBatchSending(false);
  };

  const stateLabel = (w: CouncilWorker) => {
    const elapsed = w.move_start_at ? (Date.now() - new Date(w.move_start_at).getTime()) / 1000 : 0;
    const t = w.move_duration_sec > 0 ? Math.min(1, elapsed / w.move_duration_sec) : 1;
    if (w.state === 1 && t >= 1) return { label: 'Working', color: '#4ade80' };
    if (w.state === 1) return { label: `Moving (${Math.round(t * 100)}%)`, color: '#22d3ee' };
    if (w.state === 2) return { label: 'Working', color: '#4ade80' };
    return { label: 'Idle', color: '#64748b' };
  };

  const zoneOptions = [
    { value: 'home', label: '🏠 Home (Council House)' },
    ...zones.map(z => ({ value: z.zone_id, label: z.label })),
  ];

  const selStyle: React.CSSProperties = {
    flex: 1, padding: '6px 8px', borderRadius: 7, fontSize: 11,
    background: '#1e1a2e', border: '1px solid rgba(255,255,255,0.18)',
    color: '#e2d9f3', outline: 'none', colorScheme: 'dark',
  };
  const btnStyle = (col: string, disabled: boolean): React.CSSProperties => ({
    padding: '6px 12px', borderRadius: 7, fontSize: 11, fontWeight: 800, cursor: disabled ? 'default' : 'pointer',
    background: disabled ? 'rgba(255,255,255,0.04)' : `rgba(${col},0.15)`,
    border: `1px solid ${disabled ? 'rgba(255,255,255,0.08)' : `rgba(${col},0.4)`}`,
    color: disabled ? '#ffffff30' : `rgb(${col})`,
    transition: 'all 0.15s', whiteSpace: 'nowrap' as const,
  });

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(5,4,2,0.85)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'rgba(12,9,3,0.98)', border: '1px solid rgba(245,158,11,0.22)',
          borderRadius: 18, padding: '26px 30px', width: '80vw', maxWidth: '80vw',
          maxHeight: '85vh', overflowY: 'auto',
          boxShadow: '0 12px 80px rgba(0,0,0,0.8)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 26 }}>🏛️</span>
            <div>
              <p style={{ color: '#fde68a', fontSize: 17, fontWeight: 900, margin: 0, letterSpacing: -0.3 }}>Council House</p>
              <p style={{ color: '#f59e0b50', fontSize: 9, margin: 0, fontWeight: 700, letterSpacing: 2.5, textTransform: 'uppercase' }}>Worker Command · Shram</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#ffffff60', fontSize: 18, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>
        </div>

        {/* ── Worker Command Centre (only if player has workers) ── */}
        {myWorkers.length > 0 && (
          <div style={{ marginBottom: 24, background: 'rgba(34,211,238,0.04)', border: '1px solid rgba(34,211,238,0.15)', borderRadius: 14, padding: '16px 18px' }}>
            <p style={{ color: '#22d3ee', fontSize: 9, fontWeight: 900, letterSpacing: 2.5, textTransform: 'uppercase', margin: '0 0 14px' }}>
              🧑 Your Workers — Command Centre
            </p>

            {/* Batch actions */}
            <div style={{ background: 'rgba(34,211,238,0.06)', border: '1px solid rgba(34,211,238,0.12)', borderRadius: 10, padding: '10px 12px', marginBottom: 14 }}>
              <p style={{ color: '#22d3ee80', fontSize: 8.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 8px' }}>Batch Commands ({myWorkers.length} worker{myWorkers.length > 1 ? 's' : ''})</p>
              <div style={{ display: 'flex', gap: 7, alignItems: 'center', flexWrap: 'wrap' }}>
                <select value={batchZone} onChange={e => { setBatchZone(e.target.value); setBatchMsg(''); }} style={{ ...selStyle, flex: '1 1 160px' }}>
                  <option value="" style={{ background: '#1e1a2e', color: '#e2d9f3' }}>— Send all to —</option>
                  {zoneOptions.map(o => <option key={o.value} value={o.value} style={{ background: '#1e1a2e', color: '#e2d9f3' }}>{o.label}</option>)}
                </select>
                <button onClick={handleSendAll} disabled={batchSending || !batchZone} style={btnStyle('34,211,238', batchSending || !batchZone)}>
                  {batchSending ? 'Sending…' : '🧑 Send All'}
                </button>
                <button onClick={handleCallAll} disabled={batchSending} style={btnStyle('251,191,36', batchSending)}>
                  {batchSending ? '…' : '🏠 Call All Back'}
                </button>
              </div>
              {batchMsg && <p style={{ color: batchMsg.startsWith('✅') ? '#6ee7b7' : '#f87171', fontSize: 10, margin: '7px 0 0', fontWeight: 700 }}>{batchMsg}</p>}
            </div>

            {/* Individual worker cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {myWorkers.map(w => {
                const st = stateLabel(w);
                const isSending = !!sending[w.id];
                const isMoving = w.state === 1;
                const destZone = zones.find(z => z.cx === w.dest_x && z.cy === w.dest_y);
                return (
                  <div key={w.id} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '10px 12px' }}>
                    {/* Worker info row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 16 }}>🧑</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ color: '#ffffffcc', fontSize: 12, fontWeight: 800, margin: 0 }}>{w.worker_name || `Worker ${w.id}`}</p>
                        <p style={{ color: '#ffffff40', fontSize: 9.5, margin: 0, fontFamily: 'monospace' }}>
                          ({w.pos_x}, {w.pos_y})
                          {w.dest_x != null && <> → ({w.dest_x}, {w.dest_y}){destZone ? ` · ${destZone.label}` : ''}</>}
                        </p>
                      </div>
                      <span style={{ fontSize: 9.5, fontWeight: 800, color: st.color, background: `${st.color}18`, border: `1px solid ${st.color}40`, borderRadius: 5, padding: '2px 7px', whiteSpace: 'nowrap' }}>{st.label}</span>
                    </div>
                    {/* Controls */}
                    {w.state === 3 ? (
                      <div style={{ fontSize: 10, color: '#a78bfa', background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 7, padding: '6px 10px', fontWeight: 700 }}>
                        🎓 In Training Academy — immobilised
                      </div>
                    ) : (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <select
                        value={workerDest[w.id] || ''}
                        onChange={e => { setWorkerDest(d => ({ ...d, [w.id]: e.target.value })); setMsgs(m => ({ ...m, [w.id]: '' })); }}
                        style={{ ...selStyle, flex: '1 1 140px' }}
                      >
                        <option value="" style={{ background: '#1e1a2e', color: '#e2d9f3' }}>— Choose destination —</option>
                        {zoneOptions.map(o => <option key={o.value} value={o.value} style={{ background: '#1e1a2e', color: '#e2d9f3' }}>{o.label}</option>)}
                      </select>
                      <button
                        onClick={() => handleSendOne(w)}
                        disabled={isSending || !workerDest[w.id]}
                        style={btnStyle('34,211,238', isSending || !workerDest[w.id])}
                      >
                        {isSending ? '…' : isMoving ? '↪ Redirect' : 'Send'}
                      </button>
                      <button
                        onClick={() => handleCallBack(w)}
                        disabled={isSending || (w.dest_x === HOME_X && w.dest_y === HOME_Y && w.state === 1)}
                        style={btnStyle('251,191,36', isSending || (w.dest_x === HOME_X && w.dest_y === HOME_Y && w.state === 1))}
                      >
                        🏠
                      </button>
                    </div>
                    )}
                    {msgs[w.id] && <p style={{ color: msgs[w.id].startsWith('✅') ? '#6ee7b7' : '#f87171', fontSize: 10, margin: '6px 0 0', fontWeight: 700 }}>{msgs[w.id]}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: '#f59e0b80', fontSize: 13 }}>Loading…</div>
        ) : (
          <>
            {/* City Workers + Shram + Allocation — merged table */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <p style={{ color: '#f59e0b80', fontSize: 9, fontWeight: 900, letterSpacing: 2.5, textTransform: 'uppercase', margin: 0 }}>City Workers &amp; Allocation</p>
                <span style={{ fontSize: 10, fontWeight: 800, color: '#fde68a', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 6, padding: '2px 8px' }}>
                  {shramRows.reduce((s, r) => s + (r.worker_count || 0), 0)} / 30 assigned
                </span>
              </div>
              {allocation.length === 0 ? (
                <p style={{ color: '#ffffff25', fontSize: 12 }}>No data yet.</p>
              ) : (() => {
                // merge allocation with shramRows for shram balance
                const shramMap: Record<string, string> = {};
                for (const r of shramRows) shramMap[r.wax_account] = r.shram_balance;
                return (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr>
                        {[
                          ['#', false],
                          ['Player', true],
                          ['Task 1', false],
                          ['Packets 🧑', false],
                          ['Invite 🧑', false],
                          ['Should', false],
                          ['Active 🧑', false],
                          ['Training 🎓', false],
                          ['Operative 🚌', false],
                          ['On WAX ⬡', false],
                          ['Total Held', false],
                          ['Owed', false],
                          ['Shram', false],
                        ].map(([h, left]) => (
                          <th key={h as string} style={{ textAlign: (left as boolean) ? 'left' : 'right', fontSize: 9, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', color: '#f59e0b40', paddingBottom: 9, borderBottom: '1px solid rgba(245,158,11,0.1)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {allocation.map((r, i) => {
                        const shram = shramMap[r.wax_account];
                        const isOwed = r.still_owed > 0;
                        const packetWorkers = r.should_have - (r.invite_workers || 0);
                        return (
                          <tr key={r.wax_account} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: isOwed ? 'rgba(251,191,36,0.03)' : 'transparent' }}>
                            <td style={{ padding: '8px 0', color: '#ffffff25', fontSize: 12, width: 24 }}>{i + 1}</td>
                            <td style={{ padding: '8px 0', color: '#fde68a', fontFamily: 'monospace', fontWeight: 700 }}>{r.wax_account}</td>
                            <td style={{ padding: '8px 0', textAlign: 'right', color: '#ffffff50' }}>{r.task1_packets.toLocaleString()}</td>
                            <td style={{ padding: '8px 0', textAlign: 'right', color: '#94a3b8', fontWeight: 700 }}>{packetWorkers}</td>
                            <td style={{ padding: '8px 0', textAlign: 'right' }}>
                              {r.invite_workers > 0
                                ? <span style={{ color: '#a78bfa', fontWeight: 900 }}>+{r.invite_workers}</span>
                                : <span style={{ color: '#ffffff20' }}>—</span>
                              }
                            </td>
                            <td style={{ padding: '8px 0', textAlign: 'right', color: '#a3e635', fontWeight: 800 }}>{r.should_have}</td>
                            <td style={{ padding: '8px 0', textAlign: 'right' }}>
                              <span style={{ color: '#22d3ee', fontWeight: 900 }}>{r.current_workers}</span>
                            </td>
                            <td style={{ padding: '8px 0', textAlign: 'right' }}>
                              {r.training_workers > 0
                                ? <span style={{ color: '#a78bfa', fontWeight: 900, background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.3)', borderRadius: 5, padding: '1px 6px' }}>{r.training_workers}</span>
                                : <span style={{ color: '#ffffff20' }}>—</span>
                              }
                            </td>
                            <td style={{ padding: '8px 0', textAlign: 'right' }}>
                              {r.operatives > 0
                                ? <span style={{ color: '#fb923c', fontWeight: 900, background: 'rgba(251,146,60,0.12)', border: '1px solid rgba(251,146,60,0.3)', borderRadius: 5, padding: '1px 6px' }}>{r.operatives}</span>
                                : <span style={{ color: '#ffffff20' }}>—</span>
                              }
                            </td>
                            <td style={{ padding: '8px 0', textAlign: 'right' }}>
                              {r.on_wax > 0
                                ? <span style={{ color: '#facc15', fontWeight: 900, background: 'rgba(250,204,21,0.1)', border: '1px solid rgba(250,204,21,0.3)', borderRadius: 5, padding: '1px 6px' }}>⬡ {r.on_wax}</span>
                                : <span style={{ color: '#ffffff20' }}>—</span>
                              }
                            </td>
                            <td style={{ padding: '8px 0', textAlign: 'right' }}>
                              <span style={{ color: '#f1f5f9', fontWeight: 900 }}>{r.total_held}</span>
                            </td>
                            <td style={{ padding: '8px 0', textAlign: 'right' }}>
                              {r.still_owed > 0
                                ? <span style={{ color: '#fbbf24', fontWeight: 900, background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 5, padding: '1px 6px' }}>+{r.still_owed}</span>
                                : <span style={{ color: '#4ade80', fontWeight: 900 }}>✓</span>
                              }
                            </td>
                            <td style={{ padding: '8px 0', textAlign: 'right', color: '#a3e63580' }}>{shram ? parseFloat(shram).toFixed(1) : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })()}
              {/* Progress bar */}
              <div style={{ marginTop: 10, background: 'rgba(255,255,255,0.05)', borderRadius: 4, height: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', borderRadius: 4, background: 'linear-gradient(90deg, #f59e0b, #22d3ee)', width: `${Math.min(100, (shramRows.reduce((s, r) => s + (r.worker_count || 0), 0) / 30) * 100)}%`, transition: 'width 0.5s' }} />
              </div>
            </div>

            {/* Settlement History */}
            {history.length > 0 && (
            <></>
            )}

            <p style={{ marginTop: 8, color: '#ffffff15', fontSize: 10, textAlign: 'center' }}>1 shram per worker per day</p>
          </>
        )}
      </div>
    </div>
  );
}


// ─── Secure Land for Farming — City Task Modal ───────────────────────────────

interface TaskCompletion { completed_by: string; tx_id: string; completed_at: string; }

function SecureLandTaskModal({ onClose, waxAcct, onCompleted, activeTask }: {
  onClose: () => void;
  waxAcct: string;
  onCompleted: (info: TaskCompletion) => void;
  activeTask: { task_id: string; label: string; food_req: number; cart_req: boolean; machine_req: boolean } | null;
}) {
  const [status, setStatus] = React.useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [msg, setMsg] = React.useState('');
  const [samrajya, setSamrajya] = React.useState<{ food_packets: number; machine_owned: boolean; machine_placed: boolean; handcart_owned: boolean; handcart_placed: boolean } | null>(null);
  const [loadingReqs, setLoadingReqs] = React.useState(true);

  React.useEffect(() => {
    if (!waxAcct) { setLoadingReqs(false); return; }
    fetch(`/samatvam/api/samatvam/samrajya/${waxAcct}`)
      .then(r => r.json())
      .then(d => { if (d.success) setSamrajya({ food_packets: d.food_packets, machine_owned: d.machine_owned, machine_placed: d.machine_placed, handcart_owned: d.handcart_owned, handcart_placed: d.handcart_placed }); })
      .catch(() => {})
      .finally(() => setLoadingReqs(false));
  }, [waxAcct]);

  const meetsFood = !!samrajya && !!activeTask && samrajya.food_packets >= activeTask.food_req;
  const meetsCart = !activeTask?.cart_req || !!samrajya?.handcart_owned;
  const meetsMachine = !activeTask?.machine_req || !!samrajya?.machine_owned;
  const meetsAll = !loadingReqs && !!samrajya && !!activeTask && meetsFood && meetsCart && meetsMachine;

  const handleComplete = async () => {
    if (!waxAcct || !activeTask) { setMsg('Connect your WAX wallet first.'); return; }
    setStatus('sending'); setMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/task/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxAcct, task_id: activeTask.task_id }),
      });
      const d = await r.json();
      if (d.success) {
        setStatus('done');
        onCompleted({ completed_by: waxAcct, tx_id: d.tx_id, completed_at: new Date().toISOString() });
      } else {
        setStatus('error'); setMsg(d.error || 'Failed to complete task');
      }
    } catch { setStatus('error'); setMsg('Network error'); }
  };

  const req = (met: boolean, label: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderRadius: 10, background: met ? 'rgba(34,197,94,0.07)' : 'rgba(255,255,255,0.03)', border: `1px solid ${met ? 'rgba(34,197,94,0.25)' : 'rgba(255,255,255,0.08)'}` }}>
      <span style={{ fontSize: 16 }}>{met ? '✅' : '❌'}</span>
      <span style={{ color: met ? '#86efac' : '#94a3b8', fontSize: 13, fontWeight: met ? 700 : 400 }}>{label}</span>
    </div>
  );

  if (!activeTask) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(5,4,2,0.88)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div style={{ background: 'rgba(10,7,2,0.98)', border: '2px solid rgba(251,191,36,0.45)', borderRadius: 20, padding: '28px 32px', width: 460, maxWidth: '92vw', boxShadow: '0 0 80px rgba(251,191,36,0.18), 0 24px 80px rgba(0,0,0,0.85)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, rgba(251,191,36,0.25), rgba(234,88,12,0.25))', border: '2px solid rgba(251,191,36,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>⚔️</div>
            <div>
              <p style={{ color: '#fde68a', fontSize: 17, fontWeight: 900, margin: 0, letterSpacing: -0.3 }}>{activeTask.label}</p>
              <p style={{ color: '#f59e0b60', fontSize: 9, margin: 0, fontWeight: 700, letterSpacing: 2.5, textTransform: 'uppercase' }}>City Task · One-Time · Season Zero</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#ffffff60', fontSize: 20, width: 34, height: 34, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>
        </div>

        {/* Requirements */}
        <p style={{ color: '#f59e0b', fontSize: 9, fontWeight: 900, letterSpacing: 2.5, textTransform: 'uppercase', margin: '0 0 10px' }}>Requirements</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
          {loadingReqs ? (
            <p style={{ color: '#64748b', fontSize: 12, textAlign: 'center', padding: 12 }}>Checking your inventory…</p>
          ) : !waxAcct ? (
            <p style={{ color: '#64748b', fontSize: 12, textAlign: 'center', padding: 12 }}>Connect WAX wallet to check requirements</p>
          ) : (
            <>
              {req(meetsFood, `${activeTask.food_req} Food Packets  (you have ${samrajya?.food_packets ?? 0})`)}
              {activeTask.cart_req && req(meetsCart, 'Hand Cart')}
              {activeTask.machine_req && req(meetsMachine, 'Water Harvesting Machine')}
            </>
          )}
        </div>

        {/* Reward */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 12, background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.22)', marginBottom: 22 }}>
          <span style={{ fontSize: 22 }}>🏆</span>
          <div>
            <p style={{ color: '#fde68a', fontSize: 14, fontWeight: 900, margin: 0 }}>Season Zero Champion</p>
            <p style={{ color: '#f59e0b60', fontSize: 10, margin: 0, fontWeight: 700 }}>First to complete this task earns season recognition</p>
          </div>
        </div>

        {/* Done state */}
        {status === 'done' ? (
          <div style={{ padding: '16px', borderRadius: 12, background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)', textAlign: 'center' }}>
            <p style={{ color: '#4ade80', fontSize: 18, fontWeight: 900, margin: '0 0 6px' }}>✅ Task Completed!</p>
            <p style={{ color: '#86efac', fontSize: 12, margin: '0 0 4px' }}>Task completed by <strong>{waxAcct}</strong></p>
          </div>
        ) : (
          <>
            {msg && status === 'error' && (
              <p style={{ color: '#f87171', fontSize: 12, margin: '0 0 12px', padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8 }}>{msg}</p>
            )}
            <button
              onClick={handleComplete}
              disabled={!meetsAll || status === 'sending'}
              style={{ width: '100%', padding: '13px', borderRadius: 12, fontWeight: 900, fontSize: 14, cursor: (!meetsAll || status === 'sending') ? 'not-allowed' : 'pointer', border: 'none', background: (!meetsAll || status === 'sending') ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg, #f59e0b, #ea580c)', color: (!meetsAll || status === 'sending') ? '#ffffff30' : 'white', transition: 'all 0.2s', boxShadow: meetsAll ? '0 0 24px rgba(245,158,11,0.35)' : 'none' }}
            >
              {status === 'sending' ? '⏳ Completing task…' : meetsAll ? '⚔️ Complete Task' : '🔒 Requirements not met'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Birch Bus Stop Modal ───────────────────────────────────────────────────────
function BirchBusStopModal({ onClose, waxAcct }: { onClose: () => void; waxAcct?: string }) {
  const [forestStats, setForestStats]   = React.useState<any>(null);
  const [sessions,    setSessions]      = React.useState<Record<number, any>>({});
  const [operatives,  setOperatives]    = React.useState<any[]>([]);
  const [startingIdx, setStartingIdx]   = React.useState<number | null>(null);
  const [startMsg,    setStartMsg]      = React.useState('');
  const [plots,       setPlots]         = React.useState<any[]>([]);
  const [loading,     setLoading]       = React.useState(true);

  const API = '/samatvam/api/samatvam';

  const loadAll = React.useCallback(async () => {
    if (!waxAcct) return;
    setLoading(true);
    try {
      const [statsR, sessR, plotsR, opR] = await Promise.all([
        fetch(`${API}/birch-bus/forest-stats`).then(r => r.json()),
        fetch(`${API}/birch-bus/claim-status/${waxAcct}`).then(r => r.json()),
        fetch(`${API}/birch-bus/plots/${waxAcct}`).then(r => r.json()),
        fetch(`${API}/bus/modal-data/${waxAcct}`).then(r => r.json()),
      ]);
      if (statsR.success) setForestStats(statsR);
      if (sessR.success)  setSessions(sessR.sessions ?? {});
      if (plotsR.success) setPlots(plotsR.plots ?? []);
      if (opR.success)    setOperatives((opR.operatives ?? []).filter((o: any) => o.location === 'edinburgh' || o.location === 'edinburgh_birch'));
    } finally { setLoading(false); }
  }, [waxAcct]);

  React.useEffect(() => { loadAll(); }, [loadAll]);

  const handleStartClaim = async (operative_index: number) => {
    if (!waxAcct) return;
    setStartingIdx(operative_index); setStartMsg('');
    try {
      const res = await fetch(`${API}/birch-bus/start-claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxAcct, operative_index }),
      });
      const d = await res.json();
      if (res.ok) { setStartMsg('Birch claim session started!'); loadAll(); }
      else setStartMsg(d.error || 'Failed to start');
    } catch (e: any) { setStartMsg(e.message); }
    finally { setStartingIdx(null); }
  };

  const pct = forestStats ? Math.min(100, Math.round((forestStats.total_plots / forestStats.max_plots) * 100)) : 0;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#0a1628', border: '1px solid rgba(163,230,53,0.3)', borderRadius: 20, width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto', padding: 28, position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'transparent', border: 'none', color: '#64748b', fontSize: 20, cursor: 'pointer' }}>✕</button>

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <p style={{ color: '#a3e63560', fontSize: 9, fontWeight: 900, letterSpacing: 3, textTransform: 'uppercase', margin: '0 0 6px' }}>🌳 Silver Birch Forest · Bus Stop</p>
          <p style={{ color: '#a3e635', fontSize: 22, fontWeight: 900, margin: 0 }}>Forest 2 — Claim Plots</p>
          <p style={{ color: '#64748b', fontSize: 12, margin: '4px 0 0' }}>1 WAX per plot · 5 min per plot · 10,000 plot cap</p>
        </div>

        {/* Forest fill bar */}
        {forestStats && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#64748b', marginBottom: 6 }}>
              <span>{forestStats.total_plots.toLocaleString()} plots claimed</span>
              <span>{forestStats.available.toLocaleString()} remaining · {pct}% full</span>
            </div>
            <div style={{ height: 8, background: '#1e3a1e', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, #4ade80, #a3e635)', borderRadius: 6, transition: 'width 0.5s' }} />
            </div>
          </div>
        )}

        {/* Link to Edinburgh SMT Forest 2 */}
        <a href="/edinburgh-smt/" target="_blank" rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(163,230,53,0.06)', border: '1px solid rgba(163,230,53,0.3)', borderRadius: 12, padding: '14px 18px', marginBottom: 20, textDecoration: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>🌳</span>
            <div>
              <p style={{ color: '#a3e635', fontSize: 13, fontWeight: 900, margin: 0 }}>Silver Birch Forest</p>
              <p style={{ color: '#a3e63560', fontSize: 9, margin: '2px 0 0', letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700 }}>Plant · Grow · Harvest</p>
            </div>
          </div>
          <span style={{ color: '#a3e635', fontSize: 13, fontWeight: 900 }}>Go to Forest →</span>
        </a>

        {/* My plots count */}
        {plots.length > 0 && (
          <div style={{ background: 'rgba(163,230,53,0.05)', border: '1px solid rgba(163,230,53,0.2)', borderRadius: 12, padding: '12px 18px', marginBottom: 20 }}>
            <p style={{ color: '#a3e635', fontSize: 13, fontWeight: 900, margin: 0 }}>You own {plots.length.toLocaleString()} Silver Birch plots</p>
          </div>
        )}

        {/* Operatives in Edinburgh with claim status */}
        {loading ? (
          <p style={{ color: '#64748b', textAlign: 'center', fontSize: 13 }}>Loading…</p>
        ) : operatives.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ color: '#64748b', fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 4px' }}>Operatives in Edinburgh</p>
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '16px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ color: '#475569', fontSize: 14, fontWeight: 800, margin: 0 }}>No operative in Edinburgh Birch</p>
                  <p style={{ color: '#334155', fontSize: 11, margin: '3px 0 0' }}>Send an operative via the Glasgow Bus Stop</p>
                </div>
                <button
                  onClick={() => setStartMsg('No operative in Edinburgh Birch')}
                  style={{ background: 'rgba(163,230,53,0.15)', border: '1px solid rgba(163,230,53,0.2)', borderRadius: 10, padding: '9px 18px', fontSize: 12, fontWeight: 900, color: '#4b5563', cursor: 'pointer' }}>
                  🌳 Start Claiming
                </button>
              </div>
              {startMsg && (
                <p style={{ fontSize: 12, color: '#f87171', margin: '10px 0 0' }}>{startMsg}</p>
              )}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ color: '#64748b', fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 4px' }}>Operatives in Edinburgh</p>
            {operatives.map(op => {
              const sess = sessions[op.operative_index];
              const hasActive = sess?.status === 'active';
              return (
                <div key={op.operative_index} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '16px 18px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: hasActive ? 10 : 0 }}>
                    <div>
                      <p style={{ color: '#e2e8f0', fontSize: 14, fontWeight: 800, margin: 0 }}>Operative #{op.operative_index}</p>
                      <p style={{ color: '#64748b', fontSize: 11, margin: '3px 0 0' }}>{op.track} track</p>
                    </div>
                    {!hasActive && (
                      <button
                        onClick={() => handleStartClaim(op.operative_index)}
                        disabled={startingIdx === op.operative_index}
                        style={{ background: 'linear-gradient(135deg, #4ade80, #a3e635)', border: 'none', borderRadius: 10, padding: '9px 18px', fontSize: 12, fontWeight: 900, color: '#0a1a00', cursor: 'pointer', opacity: startingIdx === op.operative_index ? 0.6 : 1 }}>
                        {startingIdx === op.operative_index ? 'Starting…' : '🌳 Start Claiming'}
                      </button>
                    )}
                  </div>
                  {hasActive && (
                    <div style={{ background: 'rgba(163,230,53,0.07)', border: '1px solid rgba(163,230,53,0.2)', borderRadius: 8, padding: '10px 14px' }}>
                      <p style={{ color: '#a3e635', fontSize: 12, fontWeight: 900, margin: '0 0 4px' }}>
                        🌳 Claiming birch plots — {sess.plots_claimed} / {sess.plots_to_claim} done
                      </p>
                      <p style={{ color: '#64748b', fontSize: 11, margin: 0 }}>
                        Next plot: {new Date(sess.next_claim_at) > new Date() ? `in ${Math.round((new Date(sess.next_claim_at).getTime() - Date.now()) / 60000)} min` : 'processing…'}
                      </p>
                    </div>
                  )}
                  {sess?.status === 'completed' && (
                    <p style={{ color: '#4ade80', fontSize: 11, fontWeight: 700, margin: '8px 0 0' }}>✓ All {sess.plots_to_claim} plots claimed</p>
                  )}
                </div>
              );
            })}
            {startMsg && (
              <p style={{ fontSize: 12, color: startMsg.includes('started') ? '#4ade80' : '#f87171', textAlign: 'center', margin: '4px 0 0' }}>{startMsg}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CityMapPage({ onBack, onOpenUniversity, onOpenWarehouse, onEnterBiorefinery }: { onBack: () => void; onOpenUniversity?: () => void; onOpenWarehouse?: () => void; onEnterBiorefinery?: () => void }) {
  const svgRef = React.useRef<SVGSVGElement>(null);
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth < 768);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // ── Mobile pinch-zoom + pan state ──
  const [mapScale,  setMapScale]  = React.useState(1);
  const [mapTx,     setMapTx]     = React.useState(0);
  const [mapTy,     setMapTy]     = React.useState(0);
  const pinchRef = React.useRef<{ lastDist: number; lastMidX: number; lastMidY: number; startTx: number; startTy: number; startScale: number } | null>(null);
  const panRef   = React.useRef<{ startX: number; startY: number; startTx: number; startTy: number } | null>(null);
  const lastTapRef = React.useRef<number>(0);

  const clampTranslate = (tx: number, ty: number, scale: number, elW: number, elH: number) => {
    const maxX = elW  * (scale - 1) / 2;
    const maxY = elH  * (scale - 1) / 2;
    return { tx: Math.max(-maxX, Math.min(maxX, tx)), ty: Math.max(-maxY, Math.min(maxY, ty)) };
  };

  const handleMapTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile) return;
    if (e.touches.length === 2) {
      const t0 = e.touches[0], t1 = e.touches[1];
      const dist = Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
      pinchRef.current = { lastDist: dist, lastMidX: (t0.clientX + t1.clientX) / 2, lastMidY: (t0.clientY + t1.clientY) / 2, startTx: mapTx, startTy: mapTy, startScale: mapScale };
      panRef.current = null;
    } else if (e.touches.length === 1) {
      // double-tap to reset
      const now = Date.now();
      if (now - lastTapRef.current < 300) {
        setMapScale(1); setMapTx(0); setMapTy(0);
        lastTapRef.current = 0;
        return;
      }
      lastTapRef.current = now;
      panRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY, startTx: mapTx, startTy: mapTy };
      pinchRef.current = null;
    }
  };

  const handleMapTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile) return;
    const el = e.currentTarget;
    const elW = el.offsetWidth, elH = el.offsetHeight;
    if (e.touches.length === 2 && pinchRef.current) {
      e.preventDefault();
      const t0 = e.touches[0], t1 = e.touches[1];
      const dist = Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
      const rawScale = Math.max(1, Math.min(4, pinchRef.current.startScale * (dist / pinchRef.current.lastDist)));
      const clamped = clampTranslate(pinchRef.current.startTx, pinchRef.current.startTy, rawScale, elW, elH);
      setMapScale(rawScale);
      setMapTx(clamped.tx);
      setMapTy(clamped.ty);
    } else if (e.touches.length === 1 && panRef.current && mapScale > 1) {
      e.preventDefault();
      const dx = e.touches[0].clientX - panRef.current.startX;
      const dy = e.touches[0].clientY - panRef.current.startY;
      const clamped = clampTranslate(panRef.current.startTx + dx, panRef.current.startTy + dy, mapScale, elW, elH);
      setMapTx(clamped.tx);
      setMapTy(clamped.ty);
    }
  };

  const handleMapTouchEnd = () => {
    pinchRef.current = null;
    panRef.current = null;
  };

  // Zones loaded from API
  const [zones, setZones] = React.useState<MapZone[]>([]);

  // Council workers — polled every 5s
  const [workers, setWorkers] = React.useState<CouncilWorker[]>([]);
  const [workerTick, setWorkerTick] = React.useState(0); // drives animation frames

  // Coordinate tracker (bottom-left origin, for display)
  const [coords,     setCoords]     = React.useState<{ x: number; y: number } | null>(null);
  const [activeZone, setActiveZone] = React.useState<MapZone | null>(null);
  const [hoverZone,      setHoverZone]      = React.useState<string | null>(null);
  const [hoveredLandmark, setHoveredLandmark] = React.useState<string | null>(null);
  const [councilModalOpen, setCouncilModalOpen] = React.useState(false);
  const [biorefineryModalOpen, setBiorefineryModalOpen] = React.useState(false);
  const [powerHouseModalOpen, setPowerHouseModalOpen] = React.useState(false);
  const [busStopModalOpen, setBusStopModalOpen] = React.useState(false);
  const [birchBusModalOpen, setBirchBusModalOpen] = React.useState(false);
  React.useEffect(() => {
    (window as any).__openBirchBusStop = () => setBirchBusModalOpen(true);
    return () => { delete (window as any).__openBirchBusStop; };
  }, []);
  const [waterHarvesterModalOpen, setWaterHarvesterModalOpen] = React.useState(false);
  const [taskModalOpen, setTaskModalOpen] = React.useState(false);
  const [taskCompletion, setTaskCompletion] = React.useState<TaskCompletion | null>(null);
  const [activeTask, setActiveTask] = React.useState<{ task_id: string; label: string; food_req: number; cart_req: boolean; machine_req: boolean; map_x: number; map_y: number } | null>(null);
  const [dbLabels, setDbLabels] = React.useState<{ id: number; label_id: string; label: string; cx: number; cy: number; icon: string; action: string | null }[]>([]);

  // WAX wallet
  const [waxSession,    setWaxSession]    = React.useState<any>(null);
  const [waxAcct,       setWaxAcct]       = React.useState<string>('');
  const [waxConnecting, setWaxConnecting] = React.useState(false);

  // Player inventory
  const [packets,       setPackets]       = React.useState<number>(0);
  const [handcartOwned, setHandcartOwned] = React.useState<boolean>(false);
  const [playerLogs,    setPlayerLogs]    = React.useState<Record<number, number>>({});

  // Log delivery state (Task 4)
  const [logSpecies,   setLogSpecies]   = React.useState<number>(3); // default Moringa
  const [logQty,       setLogQty]       = React.useState<number>(1);
  const [logSending,   setLogSending]   = React.useState(false);
  const [logMsg,       setLogMsg]       = React.useState('');

  // Chemical Factory (Task 7) state
  const [chemProgress,      setChemProgress]      = React.useState<{ neem: number; jujube: number; ethanol: number; vayal: number; shram: number; barrels: number; food: number; food_req: number; complete: boolean; completed_by?: string } | null>(null);
  const [myChemContrib,     setMyChemContrib]     = React.useState<{ neem: number; jujube: number; ethanol: number; vayal: number; shram: number; barrels: number; food: number } | null>(null);
  const [playerNeem,        setPlayerNeem]        = React.useState<number>(0);
  const [playerJujube,      setPlayerJujube]      = React.useState<number>(0);
  const [playerShram,       setPlayerShram]       = React.useState<number>(0);
  const [playerBarrels,     setPlayerBarrels]     = React.useState<number>(0);
  const [chemNeemQty,       setChemNeemQty]       = React.useState<number>(1);
  const [chemJujubeQty,     setChemJujubeQty]     = React.useState<number>(1);
  const [chemEthQty,        setChemEthQty]        = React.useState<number>(1);
  const [chemVayalQty,      setChemVayalQty]      = React.useState<number>(100);
  const [chemShramQty,      setChemShramQty]      = React.useState<number>(1);
  const [chemBarrelQty,     setChemBarrelQty]     = React.useState<number>(1);
  const [chemSendingNeem,   setChemSendingNeem]   = React.useState(false);
  const [chemSendingJujube, setChemSendingJujube] = React.useState(false);
  const [chemSendingEth,    setChemSendingEth]    = React.useState(false);
  const [chemSendingVayal,  setChemSendingVayal]  = React.useState(false);
  const [chemSendingShram,  setChemSendingShram]  = React.useState(false);
  const [chemSendingBarrel, setChemSendingBarrel] = React.useState(false);
  const [chemNeemMsg,       setChemNeemMsg]       = React.useState('');
  const [chemJujubeMsg,     setChemJujubeMsg]     = React.useState('');
  const [chemEthMsg,        setChemEthMsg]        = React.useState('');
  const [chemVayalMsg,      setChemVayalMsg]      = React.useState('');
  const [chemShramMsg,      setChemShramMsg]      = React.useState('');
  const [chemBarrelMsg,     setChemBarrelMsg]     = React.useState('');
  const [chemFoodQty,       setChemFoodQty]       = React.useState<number>(1000);
  const [chemSendingFood,   setChemSendingFood]   = React.useState(false);
  const [chemFoodMsg,       setChemFoodMsg]       = React.useState('');

  // Iron Mining Team (Task 8) state
  const [ironProgress,      setIronProgress]      = React.useState<{ opuntia: number; neem: number; jujube: number; ethanol: number; vayal: number; shram: number; barrels: number; food: number; complete: boolean; completed_by?: string } | null>(null);
  const [myIronContrib,     setMyIronContrib]     = React.useState<{ opuntia: number; neem: number; jujube: number; ethanol: number; vayal: number; shram: number; barrels: number; food: number } | null>(null);
  const [ironOpuntiaQty,    setIronOpuntiaQty]    = React.useState<number>(100);
  const [ironNeemQty,       setIronNeemQty]       = React.useState<number>(100);
  const [ironJujubeQty,     setIronJujubeQty]     = React.useState<number>(100);
  const [ironEthQty,        setIronEthQty]        = React.useState<number>(10);
  const [ironVayalQty,      setIronVayalQty]      = React.useState<number>(1000);
  const [ironShramQty,      setIronShramQty]      = React.useState<number>(1);
  const [ironBarrelQty,     setIronBarrelQty]     = React.useState<number>(1);
  const [ironFoodQty,       setIronFoodQty]       = React.useState<number>(10000);
  const [ironSendingOpuntia, setIronSendingOpuntia] = React.useState(false);
  const [ironSendingNeem,   setIronSendingNeem]   = React.useState(false);
  const [ironSendingJujube, setIronSendingJujube] = React.useState(false);
  const [ironSendingEth,    setIronSendingEth]    = React.useState(false);
  const [ironSendingVayal,  setIronSendingVayal]  = React.useState(false);
  const [ironSendingShram,  setIronSendingShram]  = React.useState(false);
  const [ironSendingBarrel, setIronSendingBarrel] = React.useState(false);
  const [ironSendingFood,   setIronSendingFood]   = React.useState(false);
  const [ironOpuntiaMsg,    setIronOpuntiaMsg]    = React.useState('');
  const [ironNeemMsg,       setIronNeemMsg]       = React.useState('');
  const [ironJujubeMsg,     setIronJujubeMsg]     = React.useState('');
  const [ironEthMsg,        setIronEthMsg]        = React.useState('');
  const [ironVayalMsg,      setIronVayalMsg]      = React.useState('');
  const [ironShramMsg,      setIronShramMsg]      = React.useState('');
  const [ironBarrelMsg,     setIronBarrelMsg]     = React.useState('');
  const [ironFoodMsg,       setIronFoodMsg]       = React.useState('');

  // Bioplastics Manufacturing (Task 9) state
  const [bioProgress,       setBioProgress]       = React.useState<{ mesquite: number; acacia: number; moringa: number; amaranth: number; vetiver: number; ethanol: number; barrels: number; vayal: number; shram: number; wari: number; food: number; complete: boolean; completed_by?: string } | null>(null);
  const [myBioContrib,      setMyBioContrib]      = React.useState<{ mesquite: number; acacia: number; moringa: number; amaranth: number; vetiver: number; ethanol: number; barrels: number; vayal: number; shram: number; wari: number; food: number } | null>(null);
  const [bioMesquiteQty,    setBioMesquiteQty]    = React.useState<number>(100);
  const [bioAcaciaQty,      setBioAcaciaQty]      = React.useState<number>(100);
  const [bioMoringaQty,     setBioMoringaQty]     = React.useState<number>(100);
  const [bioAmaranthQty,    setBioAmaranthQty]    = React.useState<number>(1000);
  const [bioVetiverQty,     setBioVetiverQty]     = React.useState<number>(1000);
  const [bioEthQty,         setBioEthQty]         = React.useState<number>(1);
  const [bioBarrelQty,      setBioBarrelQty]      = React.useState<number>(1);
  const [bioVayalQty,       setBioVayalQty]       = React.useState<number>(100);
  const [bioShramQty,       setBioShramQty]       = React.useState<number>(1);
  const [bioWariQty,        setBioWariQty]        = React.useState<number>(1);
  const [bioFoodQty,        setBioFoodQty]        = React.useState<number>(1000);
  const [bioSendingMesquite, setBioSendingMesquite] = React.useState(false);
  const [bioSendingAcacia,   setBioSendingAcacia]   = React.useState(false);
  const [bioSendingMoringa,  setBioSendingMoringa]  = React.useState(false);
  const [bioSendingAmaranth, setBioSendingAmaranth] = React.useState(false);
  const [bioSendingVetiver,  setBioSendingVetiver]  = React.useState(false);
  const [bioSendingEth,      setBioSendingEth]      = React.useState(false);
  const [bioSendingBarrel,   setBioSendingBarrel]   = React.useState(false);
  const [bioSendingVayal,    setBioSendingVayal]    = React.useState(false);
  const [bioSendingShram,    setBioSendingShram]    = React.useState(false);
  const [bioSendingWari,     setBioSendingWari]     = React.useState(false);
  const [bioSendingFood,     setBioSendingFood]     = React.useState(false);
  const [bioMesquiteMsg,     setBioMesquiteMsg]     = React.useState('');
  const [bioAcaciaMsg,       setBioAcaciaMsg]       = React.useState('');
  const [bioMoringaMsg,      setBioMoringaMsg]      = React.useState('');
  const [bioAmaranthMsg,     setBioAmaranthMsg]     = React.useState('');
  const [bioVetiverMsg,      setBioVetiverMsg]      = React.useState('');
  const [bioEthMsg,          setBioEthMsg]          = React.useState('');
  const [bioBarrelMsg,       setBioBarrelMsg]       = React.useState('');
  const [bioVayalMsg,        setBioVayalMsg]        = React.useState('');
  const [bioShramMsg,        setBioShramMsg]        = React.useState('');
  const [bioWariMsg,         setBioWariMsg]         = React.useState('');
  const [bioFoodMsg,         setBioFoodMsg]         = React.useState('');
  const [playerWari,         setPlayerWari]         = React.useState<number>(0);

  // Construct Boat Pier (Task 10) state
  const [pierProgress,       setPierProgress]       = React.useState<{ opuntia: number; neem: number; jujube: number; acacia: number; mesquite: number; moringa: number; amaranth: number; vetiver: number; ethanol: number; barrels: number; vayal: number; shram: number; wari: number; food: number; complete: boolean; completed_by?: string } | null>(null);
  const [pierOpuntiaQty,     setPierOpuntiaQty]     = React.useState<number>(1000);
  const [pierNeemQty,        setPierNeemQty]        = React.useState<number>(1000);
  const [pierJujubeQty,      setPierJujubeQty]      = React.useState<number>(1000);
  const [pierAcaciaQty,      setPierAcaciaQty]      = React.useState<number>(1000);
  const [pierMesquiteQty,    setPierMesquiteQty]    = React.useState<number>(1000);
  const [pierMoringaQty,     setPierMoringaQty]     = React.useState<number>(1000);
  const [pierAmaranthQty,    setPierAmaranthQty]    = React.useState<number>(5000);
  const [pierVetiverQty,     setPierVetiverQty]     = React.useState<number>(5000);
  const [pierEthQty,         setPierEthQty]         = React.useState<number>(10);
  const [pierBarrelQty,      setPierBarrelQty]      = React.useState<number>(10);
  const [pierVayalQty,       setPierVayalQty]       = React.useState<number>(1000);
  const [pierShramQty,       setPierShramQty]       = React.useState<number>(1);
  const [pierWariQty,        setPierWariQty]        = React.useState<number>(1);
  const [pierFoodQty,        setPierFoodQty]        = React.useState<number>(10000);
  const [pierSendingOpuntia,  setPierSendingOpuntia]  = React.useState(false);
  const [pierSendingNeem,     setPierSendingNeem]     = React.useState(false);
  const [pierSendingJujube,   setPierSendingJujube]   = React.useState(false);
  const [pierSendingAcacia,   setPierSendingAcacia]   = React.useState(false);
  const [pierSendingMesquite, setPierSendingMesquite] = React.useState(false);
  const [pierSendingMoringa,  setPierSendingMoringa]  = React.useState(false);
  const [pierSendingAmaranth, setPierSendingAmaranth] = React.useState(false);
  const [pierSendingVetiver,  setPierSendingVetiver]  = React.useState(false);
  const [pierSendingEth,      setPierSendingEth]      = React.useState(false);
  const [pierSendingBarrel,   setPierSendingBarrel]   = React.useState(false);
  const [pierSendingVayal,    setPierSendingVayal]    = React.useState(false);
  const [pierSendingShram,    setPierSendingShram]    = React.useState(false);
  const [pierSendingWari,     setPierSendingWari]     = React.useState(false);
  const [pierSendingFood,     setPierSendingFood]     = React.useState(false);
  const [pierOpuntiaMsg,      setPierOpuntiaMsg]      = React.useState('');
  const [pierNeemMsg,         setPierNeemMsg]         = React.useState('');
  const [pierJujubeMsg,       setPierJujubeMsg]       = React.useState('');
  const [pierAcaciaMsg,       setPierAcaciaMsg]       = React.useState('');
  const [pierMesquiteMsg,     setPierMesquiteMsg]     = React.useState('');
  const [pierMoringaMsg,      setPierMoringaMsg]      = React.useState('');
  const [pierAmaranthMsg,     setPierAmaranthMsg]     = React.useState('');
  const [pierVetiverMsg,      setPierVetiverMsg]      = React.useState('');
  const [pierEthMsg,          setPierEthMsg]          = React.useState('');
  const [pierBarrelMsg,       setPierBarrelMsg]       = React.useState('');
  const [pierVayalMsg,        setPierVayalMsg]        = React.useState('');
  const [pierShramMsg,        setPierShramMsg]        = React.useState('');
  const [pierWariMsg,         setPierWariMsg]         = React.useState('');
  const [pierFoodMsg,         setPierFoodMsg]         = React.useState('');

  // Bus Stop (Task 6) contribution state
  const [playerEthanol,   setPlayerEthanol]   = React.useState<number>(0);
  const [playerVayal,     setPlayerVayal]     = React.useState<number>(0);
  const [playerFoodScore, setPlayerFoodScore] = React.useState<number>(0);
  const [busProgress,     setBusProgress]     = React.useState<{ logs: number; ethanol: number; vayal: number; food: number; food_req: number; complete: boolean; completed_by?: string } | null>(null);
  const [busLogQty,       setBusLogQty]       = React.useState<number>(1);
  const [busEthQty,       setBusEthQty]       = React.useState<number>(1);
  const [busVayalQty,     setBusVayalQty]     = React.useState<number>(100);
  const [busFoodQty,      setBusFoodQty]      = React.useState<number>(1000);
  const [busSendingLogs,  setBusSendingLogs]  = React.useState(false);
  const [busSendingEth,   setBusSendingEth]   = React.useState(false);
  const [busSendingVayal, setBusSendingVayal] = React.useState(false);
  const [busSendingFood,  setBusSendingFood]  = React.useState(false);
  const [busLogMsg,       setBusLogMsg]       = React.useState('');
  const [busEthMsg,       setBusEthMsg]       = React.useState('');
  const [busVayalMsg,     setBusVayalMsg]     = React.useState('');
  const [busFoodMsg,      setBusFoodMsg]      = React.useState('');

  // Server-side zone progress (workers + infrastructure check from DB)
  const [zoneProgress, setZoneProgress] = React.useState<{ totals: { workers_at_zone: number; handcart_count: number; machine_count: number; food_packets: number; electrician_handbook_count: number; biofuel_recipe_count: number; log_totals?: Record<string, number> }; players: any[] } | null>(null);
  const [placeQty, setPlaceQty] = React.useState<number>(1);
  const [placing,  setPlacing]  = React.useState(false);
  const [placeMsg, setPlaceMsg] = React.useState<string>('');

  // Per-zone leaderboard (keyed by zone_id)
  const [lbTotal, setLbTotal] = React.useState(0);
  const [lbRows,  setLbRows]  = React.useState<{ wax_account: string; placed: number }[]>([]);
  const [lbZoneId, setLbZoneId] = React.useState<string>('task1-food-drop');
  // Task 1 total + rows — never overwritten by zone switches
  const [task1ZoneTotal, setTask1ZoneTotal] = React.useState(0);
  const [task1LbRows,   setTask1LbRows]    = React.useState<{ wax_account: string; placed: number }[]>([]);
  // Map image swap — true when harvest-water task is complete
  const [harvestWaterDone, setHarvestWaterDone] = React.useState(false);
  // Map image swap — true when build-bus-stop task is complete
  const [busStopDone, setBusStopDone] = React.useState(false);

  // Fetch zones + DB labels + city task status once
  React.useEffect(() => {
    fetch('/samatvam/api/samatvam/map/zones')
      .then(r => r.json())
      .then(d => { if (d.success) setZones(d.zones); })
      .catch(() => {});
    fetch('/samatvam/api/samatvam/map/labels')
      .then(r => r.json())
      .then(d => { if (d.success) setDbLabels(d.labels); })
      .catch(() => {});
    fetch('/samatvam/api/samatvam/map/zone-leaderboard/harvest-water')
      .then(r => r.json())
      .then(d => { if (d.success && d.total >= 20000) setHarvestWaterDone(true); })
      .catch(() => {});
    fetch('/samatvam/api/samatvam/map/bus-stop-progress')
      .then(r => r.json())
      .then(d => { if (d.success && d.complete) setBusStopDone(true); })
      .catch(() => {});
    fetch('/samatvam/api/samatvam/tasks/active')
      .then(r => r.json())
      .then(d => {
        if (d.success && d.task) {
          setActiveTask({
            task_id: d.task.task_id,
            label: d.task.label,
            food_req: d.task.food_req,
            cart_req: d.task.cart_req,
            machine_req: d.task.machine_req,
            map_x: d.task.map_x,
            map_y: d.task.map_y,
          });
          if (d.task.completed) setTaskCompletion({ completed_by: d.task.completed_by, tx_id: d.task.tx_id, completed_at: d.task.completed_at });
        }
      })
      .catch(() => {});
  }, []);

  // Fetch bus stop progress when task 6 is active
  React.useEffect(() => {
    if (!activeZone || activeZone.task_number !== 6) { setBusProgress(null); return; }
    const load = () => {
      fetch('/samatvam/api/samatvam/map/bus-stop-progress')
        .then(r => r.json())
        .then(d => { if (d.success) setBusProgress(d); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [activeZone]);

  // Fetch chem factory progress + player balances when task 7 is active
  React.useEffect(() => {
    if (!activeZone || activeZone.task_number !== 7) { setChemProgress(null); return; }
    const load = () => {
      fetch('/samatvam/api/samatvam/map/chem-factory-progress')
        .then(r => r.json()).then(d => { if (d.success) setChemProgress(d); }).catch(() => {});
    };
    load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [activeZone]);

  React.useEffect(() => {
    if (!activeZone || activeZone.task_number !== 7 || !waxAcct) return;
    const load = () => {
      fetch(`/samatvam/api/samatvam/logs/${waxAcct}`)
        .then(r => r.json()).then(d => { if (d.success) { setPlayerNeem(d.logs?.[6] ?? 0); setPlayerJujube(d.logs?.[8] ?? 0); } }).catch(() => {});
      fetch(`/samatvam/api/samatvam/samrajya/${waxAcct}`)
        .then(r => r.json()).then(d => { if (d.success) { setPlayerShram(parseFloat(d.shram_balance ?? 0)); setPlayerVayal(parseFloat(d.vayal_balance ?? 0)); setPlayerFoodScore(parseInt(d.food_packets ?? 0)); } }).catch(() => {});
      fetch(`/samatvam/api/samatvam/water/barrel-count/${waxAcct}`)
        .then(r => r.json()).then(d => { if (d.success) setPlayerBarrels(d.barrels); }).catch(() => {});
      fetch(`/samatvam/api/samatvam/materials/${waxAcct}`)
        .then(r => r.json()).then(d => { if (d.success && d.materials) { const e = d.materials.find((m: any) => m.material_id === 'ethanol'); setPlayerEthanol(e ? parseInt(e.quantity) : 0); } }).catch(() => {});
      fetch(`/samatvam/api/samatvam/map/chem-my-contribution/${waxAcct}`)
        .then(r => r.json()).then(d => { if (d.success) setMyChemContrib(d); }).catch(() => {});
    };
    load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [activeZone, waxAcct]);

  // Fetch iron mining progress when task 8 is active
  React.useEffect(() => {
    if (!activeZone || activeZone.task_number !== 8) { setIronProgress(null); return; }
    const load = () => {
      fetch('/samatvam/api/samatvam/map/iron-mining-progress')
        .then(r => r.json()).then(d => { if (d.success) setIronProgress(d); }).catch(() => {});
    };
    load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [activeZone]);

  React.useEffect(() => {
    if (!activeZone || activeZone.task_number !== 8 || !waxAcct) return;
    const load = () => {
      fetch(`/samatvam/api/samatvam/logs/${waxAcct}`)
        .then(r => r.json()).then(d => { if (d.success) { setPlayerNeem(d.logs?.[6] ?? 0); setPlayerJujube(d.logs?.[8] ?? 0); } }).catch(() => {});
      fetch(`/samatvam/api/samatvam/samrajya/${waxAcct}`)
        .then(r => r.json()).then(d => { if (d.success) { setPlayerShram(parseFloat(d.shram_balance ?? 0)); setPlayerVayal(parseFloat(d.vayal_balance ?? 0)); setPlayerFoodScore(parseInt(d.food_packets ?? 0)); } }).catch(() => {});
      fetch(`/samatvam/api/samatvam/materials/${waxAcct}`)
        .then(r => r.json()).then(d => {
          if (d.success && d.materials) {
            const e = d.materials.find((m: any) => m.material_id === 'ethanol');
            setPlayerEthanol(e ? parseInt(e.quantity) : 0);
            const b = d.materials.find((m: any) => m.material_id === 'water_barrel');
            setPlayerBarrels(b ? parseInt(b.quantity) : 0);
          }
        }).catch(() => {});
      fetch(`/samatvam/api/samatvam/map/iron-my-contribution/${waxAcct}`)
        .then(r => r.json()).then(d => { if (d.success) setMyIronContrib(d); }).catch(() => {});
    };
    load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [activeZone, waxAcct]);

  // Task 9 — Bioplastics progress polling
  React.useEffect(() => {
    if (!activeZone || activeZone.task_number !== 9) { setBioProgress(null); return; }
    const load = () => {
      fetch('/samatvam/api/samatvam/map/bioplastics-progress')
        .then(r => r.json()).then(d => { if (d.success) setBioProgress(d); }).catch(() => {});
    };
    load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [activeZone]);

  // Task 10 — Pier progress polling
  React.useEffect(() => {
    if (!activeZone || activeZone.task_number !== 10) { setPierProgress(null); return; }
    const load = () => {
      fetch('/samatvam/api/samatvam/map/pier-progress')
        .then(r => r.json()).then(d => { if (d.success) setPierProgress(d); }).catch(() => {});
    };
    load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [activeZone]);

  // Task 10 — Pier player inventory polling
  React.useEffect(() => {
    if (!activeZone || activeZone.task_number !== 10 || !waxAcct) return;
    const load = () => {
      fetch(`/samatvam/api/samatvam/logs/${waxAcct}`)
        .then(r => r.json()).then(d => { if (d.success && d.logs) setPlayerLogs(d.logs); }).catch(() => {});
      fetch(`/samatvam/api/samatvam/samrajya/${waxAcct}`)
        .then(r => r.json()).then(d => {
          if (d.success) {
            setPlayerShram(parseFloat(d.shram_balance ?? 0));
            setPlayerVayal(parseFloat(d.vayal_balance ?? 0));
            setPlayerFoodScore(parseInt(d.food_packets ?? 0));
            setPlayerWari(parseFloat(d.wari_balance ?? 0));
          }
        }).catch(() => {});
      fetch(`/samatvam/api/samatvam/materials/${waxAcct}`)
        .then(r => r.json()).then(d => {
          if (d.success && d.materials) {
            const e = d.materials.find((m: any) => m.material_id === 'ethanol');
            setPlayerEthanol(e ? parseInt(e.quantity) : 0);
            const b = d.materials.find((m: any) => m.material_id === 'water_barrel');
            setPlayerBarrels(b ? parseInt(b.quantity) : 0);
          }
        }).catch(() => {});
    };
    load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [activeZone, waxAcct]);

  React.useEffect(() => {
    if (!activeZone || activeZone.task_number !== 9 || !waxAcct) return;
    const load = () => {
      fetch(`/samatvam/api/samatvam/logs/${waxAcct}`)
        .then(r => r.json()).then(d => { if (d.success && d.logs) setPlayerLogs(d.logs); }).catch(() => {});
      fetch(`/samatvam/api/samatvam/samrajya/${waxAcct}`)
        .then(r => r.json()).then(d => {
          if (d.success) {
            setPlayerShram(parseFloat(d.shram_balance ?? 0));
            setPlayerVayal(parseFloat(d.vayal_balance ?? 0));
            setPlayerFoodScore(parseInt(d.food_packets ?? 0));
            setPlayerWari(parseFloat(d.wari_balance ?? 0));
          }
        }).catch(() => {});
      fetch(`/samatvam/api/samatvam/materials/${waxAcct}`)
        .then(r => r.json()).then(d => {
          if (d.success && d.materials) {
            const e = d.materials.find((m: any) => m.material_id === 'ethanol');
            setPlayerEthanol(e ? parseInt(e.quantity) : 0);
            const b = d.materials.find((m: any) => m.material_id === 'water_barrel');
            setPlayerBarrels(b ? parseInt(b.quantity) : 0);
          }
        }).catch(() => {});
      fetch(`/samatvam/api/samatvam/map/bioplastics-my-contribution/${waxAcct}`)
        .then(r => r.json()).then(d => { if (d.success) setMyBioContrib(d); }).catch(() => {});
    };
    load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [activeZone, waxAcct]);

  // Fetch zone progress from server whenever active zone changes
  React.useEffect(() => {
    if (!activeZone || activeZone.task_number === 1) { setZoneProgress(null); return; }
    const load = () => {
      fetch(`/samatvam/api/samatvam/map/zone-progress/${activeZone.zone_id}`)
        .then(r => r.json())
        .then(d => { if (d.success) setZoneProgress(d); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [activeZone]);

  // Poll workers every 5s
  const fetchWorkers = React.useCallback(() => {
    fetch('/samatvam/api/samatvam/workers')
      .then(r => r.json())
      .then(d => { if (d.success) setWorkers(d.workers); })
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    fetchWorkers();
    const poll = setInterval(fetchWorkers, 5000);
    return () => clearInterval(poll);
  }, [fetchWorkers]);

  // Animation tick — every 200ms, smooth animation without re-fetching
  React.useEffect(() => {
    const anim = setInterval(() => setWorkerTick(t => t + 1), 200);
    return () => clearInterval(anim);
  }, []);

  const fetchLeaderboard = React.useCallback((zoneId: string = 'task1-food-drop') => {
    fetch(`/samatvam/api/samatvam/map/zone-leaderboard/${encodeURIComponent(zoneId)}`)
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setLbTotal(d.total);
          setLbRows(d.rows);
          setLbZoneId(zoneId);
          if (zoneId === 'task1-food-drop') { setTask1ZoneTotal(d.total); setTask1LbRows(d.rows); }
        }
      })
      .catch(() => {});
  }, []);

  const fetchPackets = React.useCallback((acct: string) => {
    fetch(`/samatvam/api/samatvam/warehouse/${acct}`)
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setPackets(d.packets);
          if (typeof d.handcart_owned === 'boolean') setHandcartOwned(d.handcart_owned);
        }
      })
      .catch(() => {});
    fetch(`/samatvam/api/samatvam/infrastructure/${acct}`)
      .then(r => r.json())
      .then(d => { if (d.success && d.logs) setPlayerLogs(d.logs); })
      .catch(() => {});
    fetch(`/samatvam/api/samatvam/materials/${acct}`)
      .then(r => r.json())
      .then(d => { if (d.success && d.materials) { const eth = d.materials.find((m: any) => m.material_id === 'ethanol'); setPlayerEthanol(eth ? parseInt(eth.quantity) : 0); } })
      .catch(() => {});
    fetch(`/samatvam/api/samatvam/samrajya/${acct}`)
      .then(r => r.json())
      .then(d => { if (d.success) { setPlayerVayal(parseFloat(d.vayal_balance ?? 0)); setPlayerFoodScore(parseInt(d.food_packets ?? 0)); } })
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    fetchLeaderboard('task1-food-drop');
    getSessionKit().restore().then((s: any) => {
      if (s) {
        const acct = String(s.actor);
        setWaxSession(s);
        setWaxAcct(acct);
        fetchPackets(acct);
      }
    }).catch(() => {});
  }, [fetchLeaderboard, fetchPackets]);

  const handleWaxConnect = async () => {
    setWaxConnecting(true);
    try {
      const { session: s } = await getSessionKit().login();
      const acct = String(s.actor);
      setWaxSession(s);
      setWaxAcct(acct);
      fetchPackets(acct);
    } catch { /* cancelled */ }
    setWaxConnecting(false);
  };

  const handleWaxDisconnect = async () => {
    if (waxSession) await getSessionKit().logout(waxSession);
    setWaxSession(null);
    setWaxAcct('');
    setPackets(0);
  };

  const [workerSending, setWorkerSending] = React.useState(false);
  const [workerMsg, setWorkerMsg] = React.useState('');

  // Zone contribution (tasks 2–4, worker-gated)
  const [contribQty,    setContribQty]    = React.useState<number>(1);
  const [contributing,  setContributing]  = React.useState(false);
  const [contribMsg,    setContribMsg]    = React.useState<string>('');

  const handleSendWorker = async (zone: MapZone) => {
    if (!waxAcct) return;
    const myWorker = workers.find(w => w.wax_account === waxAcct);
    if (!myWorker) return;
    setWorkerSending(true); setWorkerMsg('');
    try {
      const r = await fetch(`/samatvam/api/samatvam/workers/${myWorker.id}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxAcct, dest_x: zone.cx, dest_y: zone.cy, move_duration_sec: 30 }),
      });
      const d = await r.json();
      if (d.success) {
        setWorkerMsg(`✅ Worker heading to ${zone.label}`);
        fetchWorkers();
      } else {
        setWorkerMsg(`❌ ${d.error}`);
      }
    } catch { setWorkerMsg('❌ Network error'); }
    setWorkerSending(false);
  };

  const handleContrib = async (zoneId: string) => {
    if (!waxAcct || contributing || contribQty < 1 || contribQty > packets) return;
    setContributing(true);
    setContribMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/map/zone-contribute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxAcct, zone_id: zoneId, quantity: contribQty }),
      });
      const d = await r.json();
      if (d.success) {
        setContribMsg(`✅ Placed ${contribQty} packet${contribQty > 1 ? 's' : ''}!`);
        setPackets(d.packets_left);
        setContribQty(1);
        fetchLeaderboard(zoneId);
      } else {
        setContribMsg(`❌ ${d.error}`);
      }
    } catch {
      setContribMsg('❌ Network error');
    } finally {
      setContributing(false);
    }
  };

  const handleLogDeliver = async (zoneId: string) => {
    const available = playerLogs[logSpecies] ?? 0;
    if (!waxAcct || logSending || logQty < 1 || logQty > available) return;
    setLogSending(true);
    setLogMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/map/contribute-logs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxAcct, zone_id: zoneId, species_index: logSpecies, quantity: logQty }),
      });
      const d = await r.json();
      if (d.success) {
        const speciesName = ({ 1:'Mesquite', 2:'Acacia', 3:'Moringa', 4:'Prickly Pear', 5:'Amaranth', 6:'Neem', 7:'Vetiver', 8:'Jujube' } as Record<number, string>)[logSpecies] ?? 'logs';
        setLogMsg(`✅ Delivered ${logQty} ${speciesName} log${logQty > 1 ? 's' : ''}!`);
        setPlayerLogs(prev => ({ ...prev, [logSpecies]: d.logs_left }));
        setLogQty(1);
        // Refresh zone progress
        if (activeZone) {
          fetch(`/samatvam/api/samatvam/map/zone-progress/${activeZone.zone_id}`)
            .then(r2 => r2.json())
            .then(d2 => { if (d2.success) setZoneProgress(d2); })
            .catch(() => {});
        }
      } else {
        setLogMsg(`❌ ${d.error}`);
      }
    } catch {
      setLogMsg('❌ Network error');
    } finally {
      setLogSending(false);
    }
  };

  const makeChemHandler = (
    endpoint: string, qty: number, sending: boolean,
    setSending: (v: boolean) => void, setMsg: (v: string) => void,
    label: string, refreshFn?: () => void,
  ) => async () => {
    if (!waxAcct || sending) return;
    setSending(true); setMsg('');
    try {
      const r = await fetch(`/samatvam/api/samatvam/map/${endpoint}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wax_account: waxAcct, quantity: qty }) });
      const d = await r.json();
      if (d.success) {
        setMsg(d.refunded > 0 ? `✅ Contributed ${d.contributed} ${label} — ${d.refunded} refunded (quota hit)` : `✅ Contributed ${d.contributed} ${label}!`);
        fetch('/samatvam/api/samatvam/map/chem-factory-progress').then(r2 => r2.json()).then(d2 => { if (d2.success) setChemProgress(d2); }).catch(() => {});
        if (refreshFn) refreshFn();
      } else { setMsg(`❌ ${d.error}`); }
    } catch { setMsg('❌ Network error'); }
    setSending(false);
  };

  const handleChemNeem   = makeChemHandler('contribute-chem-neem',    chemNeemQty,   chemSendingNeem,   setChemSendingNeem,   setChemNeemMsg,   'Neem logs',     () => setPlayerNeem(n => Math.max(0, n - chemNeemQty)));
  const handleChemJujube = makeChemHandler('contribute-chem-jujube',  chemJujubeQty, chemSendingJujube, setChemSendingJujube, setChemJujubeMsg, 'Jujube logs',   () => setPlayerJujube(n => Math.max(0, n - chemJujubeQty)));
  const handleChemEth    = makeChemHandler('contribute-chem-ethanol', chemEthQty,    chemSendingEth,    setChemSendingEth,    setChemEthMsg,    'L ethanol',     () => setPlayerEthanol(n => Math.max(0, n - chemEthQty)));
  const handleChemVayal  = makeChemHandler('contribute-chem-vayal',   chemVayalQty,  chemSendingVayal,  setChemSendingVayal,  setChemVayalMsg,  'Vayal',         () => setPlayerVayal(n => Math.max(0, n - chemVayalQty)));
  const handleChemShram  = makeChemHandler('contribute-chem-shram',   chemShramQty,  chemSendingShram,  setChemSendingShram,  setChemShramMsg,  'SHRAM',         () => setPlayerShram(n => Math.max(0, n - chemShramQty)));
  const handleChemBarrel = makeChemHandler('contribute-chem-barrels', chemBarrelQty, chemSendingBarrel, setChemSendingBarrel, setChemBarrelMsg, 'water barrels', () => setPlayerBarrels(n => Math.max(0, n - chemBarrelQty)));
  const handleChemFood   = makeChemHandler('contribute-chem-food',    chemFoodQty,   chemSendingFood,   setChemSendingFood,   setChemFoodMsg,   'food packets',  () => setPlayerFoodScore(n => Math.max(0, n - chemFoodQty)));

  // Iron Mining (Task 8) handlers — reuse makeChemHandler pattern
  const handleIronOpuntia = makeChemHandler('contribute-iron-opuntia', ironOpuntiaQty, ironSendingOpuntia, setIronSendingOpuntia, setIronOpuntiaMsg, 'Opuntia logs',  () => { setPlayerLogs((p: any) => ({ ...p, 4: Math.max(0, (p[4] ?? 0) - ironOpuntiaQty) })); });
  const handleIronNeem    = makeChemHandler('contribute-iron-neem',    ironNeemQty,    ironSendingNeem,    setIronSendingNeem,    setIronNeemMsg,    'Neem logs',     () => setPlayerNeem(n => Math.max(0, n - ironNeemQty)));
  const handleIronJujube  = makeChemHandler('contribute-iron-jujube',  ironJujubeQty,  ironSendingJujube,  setIronSendingJujube,  setIronJujubeMsg,  'Jujube logs',   () => setPlayerJujube(n => Math.max(0, n - ironJujubeQty)));
  const handleIronEth     = makeChemHandler('contribute-iron-ethanol', ironEthQty,     ironSendingEth,     setIronSendingEth,     setIronEthMsg,     'Ethanol',       () => setPlayerEthanol(n => Math.max(0, n - ironEthQty)));
  const handleIronBarrel  = makeChemHandler('contribute-iron-barrels', ironBarrelQty,  ironSendingBarrel,  setIronSendingBarrel,  setIronBarrelMsg,  'Water Barrels', () => setPlayerBarrels(n => Math.max(0, n - ironBarrelQty)));
  const handleIronVayal = async () => {
    if (!waxAcct || ironSendingVayal || ironVayalQty <= 0 || ironVayalQty > playerVayal) return;
    setIronSendingVayal(true); setIronVayalMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/map/contribute-iron-vayal', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wax_account: waxAcct, quantity: ironVayalQty }) });
      const d = await r.json();
      if (d.success) { setIronVayalMsg(d.refunded > 0 ? `✅ Contributed ${d.contributed.toLocaleString()} Vayal — ${d.refunded} refunded` : `✅ Contributed ${d.contributed.toLocaleString()} Vayal!`); setPlayerVayal(d.vayal_left); setIronVayalQty(1000); fetch('/samatvam/api/samatvam/map/iron-mining-progress').then(r2 => r2.json()).then(d2 => { if (d2.success) setIronProgress(d2); }).catch(() => {}); }
      else { setIronVayalMsg(`❌ ${d.error}`); }
    } catch { setIronVayalMsg('❌ Network error'); }
    setIronSendingVayal(false);
  };
  const handleIronShram = async () => {
    if (!waxAcct || ironSendingShram || ironShramQty <= 0 || ironShramQty > playerShram) return;
    setIronSendingShram(true); setIronShramMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/map/contribute-iron-shram', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wax_account: waxAcct, quantity: ironShramQty }) });
      const d = await r.json();
      if (d.success) { setIronShramMsg(d.refunded > 0 ? `✅ Contributed ${d.contributed} SHRAM — ${d.refunded} refunded` : `✅ Contributed ${d.contributed} SHRAM!`); setPlayerShram(s => Math.max(0, s - ironShramQty)); setIronShramQty(1); fetch('/samatvam/api/samatvam/map/iron-mining-progress').then(r2 => r2.json()).then(d2 => { if (d2.success) setIronProgress(d2); }).catch(() => {}); }
      else { setIronShramMsg(`❌ ${d.error}`); }
    } catch { setIronShramMsg('❌ Network error'); }
    setIronSendingShram(false);
  };
  const handleIronFood = makeChemHandler('contribute-iron-food', ironFoodQty, ironSendingFood, setIronSendingFood, setIronFoodMsg, 'food packets', () => setPlayerFoodScore(n => Math.max(0, n - ironFoodQty)));

  // ── Bioplastics (Task 9) handlers ───────────────────────────────────────────
  const handleBioMesquite = makeChemHandler('contribute-bio-mesquite', bioMesquiteQty, bioSendingMesquite, setBioSendingMesquite, setBioMesquiteMsg, 'Mesquite logs', () => setPlayerLogs((p: any) => ({ ...p, 1: Math.max(0, (p[1] ?? 0) - bioMesquiteQty) })));
  const handleBioAcacia   = makeChemHandler('contribute-bio-acacia',   bioAcaciaQty,   bioSendingAcacia,   setBioSendingAcacia,   setBioAcaciaMsg,   'Acacia logs',   () => setPlayerLogs((p: any) => ({ ...p, 2: Math.max(0, (p[2] ?? 0) - bioAcaciaQty) })));
  const handleBioMoringa  = makeChemHandler('contribute-bio-moringa',  bioMoringaQty,  bioSendingMoringa,  setBioSendingMoringa,  setBioMoringaMsg,  'Moringa logs',  () => setPlayerLogs((p: any) => ({ ...p, 3: Math.max(0, (p[3] ?? 0) - bioMoringaQty) })));
  const handleBioAmaranth = makeChemHandler('contribute-bio-amaranth', bioAmaranthQty, bioSendingAmaranth, setBioSendingAmaranth, setBioAmaranthMsg, 'Amaranth logs', () => setPlayerLogs((p: any) => ({ ...p, 5: Math.max(0, (p[5] ?? 0) - bioAmaranthQty) })));
  const handleBioVetiver  = makeChemHandler('contribute-bio-vetiver',  bioVetiverQty,  bioSendingVetiver,  setBioSendingVetiver,  setBioVetiverMsg,  'Vetiver logs',  () => setPlayerLogs((p: any) => ({ ...p, 7: Math.max(0, (p[7] ?? 0) - bioVetiverQty) })));
  const handleBioEth      = makeChemHandler('contribute-bio-ethanol',  bioEthQty,      bioSendingEth,      setBioSendingEth,      setBioEthMsg,      'Ethanol',       () => setPlayerEthanol(n => Math.max(0, n - bioEthQty)));
  const handleBioBarrel   = makeChemHandler('contribute-bio-barrels',  bioBarrelQty,   bioSendingBarrel,   setBioSendingBarrel,   setBioBarrelMsg,   'Water Barrels', () => setPlayerBarrels(n => Math.max(0, n - bioBarrelQty)));
  const handleBioFood     = makeChemHandler('contribute-bio-food',     bioFoodQty,     bioSendingFood,     setBioSendingFood,     setBioFoodMsg,     'Food Packets',  () => setPlayerFoodScore(n => Math.max(0, n - bioFoodQty)));

  const handleBioVayal = async () => {
    const max = Math.floor(playerVayal);
    if (!waxAcct || bioSendingVayal || bioVayalQty < 1 || max < 1) return;
    setBioSendingVayal(true); setBioVayalMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/map/contribute-bio-vayal', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wax_account: waxAcct, quantity: bioVayalQty }) });
      const d = await r.json();
      if (d.success) { setBioVayalMsg(d.refunded > 0 ? `✅ Contributed ${d.contributed.toLocaleString()} Vayal — ${d.refunded} refunded` : `✅ Contributed ${d.contributed.toLocaleString()} Vayal!`); setPlayerVayal(d.vayal_left); setBioVayalQty(100); fetch('/samatvam/api/samatvam/map/bioplastics-progress').then(r2 => r2.json()).then(d2 => { if (d2.success) setBioProgress(d2); }).catch(() => {}); }
      else setBioVayalMsg(`❌ ${d.error}`);
    } catch { setBioVayalMsg('❌ Network error'); } finally { setBioSendingVayal(false); }
  };

  const handleBioShram = async () => {
    if (!waxAcct || bioSendingShram || bioShramQty < 0.0001 || playerShram < bioShramQty) return;
    setBioSendingShram(true); setBioShramMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/map/contribute-bio-shram', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wax_account: waxAcct, quantity: bioShramQty }) });
      const d = await r.json();
      if (d.success) { setBioShramMsg(d.refunded > 0 ? `✅ Contributed ${d.contributed} SHRAM — ${d.refunded} refunded` : `✅ Contributed ${d.contributed} SHRAM!`); setPlayerShram(s => Math.max(0, s - bioShramQty)); setBioShramQty(1); fetch('/samatvam/api/samatvam/map/bioplastics-progress').then(r2 => r2.json()).then(d2 => { if (d2.success) setBioProgress(d2); }).catch(() => {}); }
      else setBioShramMsg(`❌ ${d.error}`);
    } catch { setBioShramMsg('❌ Network error'); } finally { setBioSendingShram(false); }
  };

  const handleBioWari = async () => {
    if (!waxAcct || bioSendingWari || bioWariQty < 0.0001 || playerWari < bioWariQty) return;
    setBioSendingWari(true); setBioWariMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/map/contribute-bio-wari', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wax_account: waxAcct, quantity: bioWariQty }) });
      const d = await r.json();
      if (d.success) { setBioWariMsg(d.refunded > 0 ? `✅ Contributed ${d.contributed} WARI — ${d.refunded} refunded` : `✅ Contributed ${d.contributed} WARI!`); setPlayerWari(w => Math.max(0, w - bioWariQty)); setBioWariQty(1); fetch('/samatvam/api/samatvam/map/bioplastics-progress').then(r2 => r2.json()).then(d2 => { if (d2.success) setBioProgress(d2); }).catch(() => {}); }
      else setBioWariMsg(`❌ ${d.error}`);
    } catch { setBioWariMsg('❌ Network error'); } finally { setBioSendingWari(false); }
  };

  // ── Pier (Task 10) handlers ─────────────────────────────────────────────────
  const refreshPier = () => fetch('/samatvam/api/samatvam/map/pier-progress').then(r => r.json()).then(d => { if (d.success) setPierProgress(d); }).catch(() => {});
  const handlePierOpuntia  = makeChemHandler('contribute-pier-opuntia',  pierOpuntiaQty,  pierSendingOpuntia,  setPierSendingOpuntia,  setPierOpuntiaMsg,  'Opuntia logs',  () => { setPlayerLogs((p: any) => ({ ...p, 4: Math.max(0, (p[4] ?? 0) - pierOpuntiaQty) })); refreshPier(); });
  const handlePierNeem     = makeChemHandler('contribute-pier-neem',     pierNeemQty,     pierSendingNeem,     setPierSendingNeem,     setPierNeemMsg,     'Neem logs',     () => { setPlayerLogs((p: any) => ({ ...p, 6: Math.max(0, (p[6] ?? 0) - pierNeemQty) })); refreshPier(); });
  const handlePierJujube   = makeChemHandler('contribute-pier-jujube',   pierJujubeQty,   pierSendingJujube,   setPierSendingJujube,   setPierJujubeMsg,   'Jujube logs',   () => { setPlayerLogs((p: any) => ({ ...p, 8: Math.max(0, (p[8] ?? 0) - pierJujubeQty) })); refreshPier(); });
  const handlePierAcacia   = makeChemHandler('contribute-pier-acacia',   pierAcaciaQty,   pierSendingAcacia,   setPierSendingAcacia,   setPierAcaciaMsg,   'Acacia logs',   () => { setPlayerLogs((p: any) => ({ ...p, 2: Math.max(0, (p[2] ?? 0) - pierAcaciaQty) })); refreshPier(); });
  const handlePierMesquite = makeChemHandler('contribute-pier-mesquite', pierMesquiteQty, pierSendingMesquite, setPierSendingMesquite, setPierMesquiteMsg, 'Mesquite logs', () => { setPlayerLogs((p: any) => ({ ...p, 1: Math.max(0, (p[1] ?? 0) - pierMesquiteQty) })); refreshPier(); });
  const handlePierMoringa  = makeChemHandler('contribute-pier-moringa',  pierMoringaQty,  pierSendingMoringa,  setPierSendingMoringa,  setPierMoringaMsg,  'Moringa logs',  () => { setPlayerLogs((p: any) => ({ ...p, 3: Math.max(0, (p[3] ?? 0) - pierMoringaQty) })); refreshPier(); });
  const handlePierAmaranth = makeChemHandler('contribute-pier-amaranth', pierAmaranthQty, pierSendingAmaranth, setPierSendingAmaranth, setPierAmaranthMsg, 'Amaranth logs', () => { setPlayerLogs((p: any) => ({ ...p, 5: Math.max(0, (p[5] ?? 0) - pierAmaranthQty) })); refreshPier(); });
  const handlePierVetiver  = makeChemHandler('contribute-pier-vetiver',  pierVetiverQty,  pierSendingVetiver,  setPierSendingVetiver,  setPierVetiverMsg,  'Vetiver logs',  () => { setPlayerLogs((p: any) => ({ ...p, 7: Math.max(0, (p[7] ?? 0) - pierVetiverQty) })); refreshPier(); });
  const handlePierEth      = makeChemHandler('contribute-pier-ethanol',  pierEthQty,      pierSendingEth,      setPierSendingEth,      setPierEthMsg,      'Ethanol',       () => { setPlayerEthanol(n => Math.max(0, n - pierEthQty)); refreshPier(); });
  const handlePierBarrel   = makeChemHandler('contribute-pier-barrels',  pierBarrelQty,   pierSendingBarrel,   setPierSendingBarrel,   setPierBarrelMsg,   'Water Barrels', () => { setPlayerBarrels(n => Math.max(0, n - pierBarrelQty)); refreshPier(); });
  const handlePierFood     = makeChemHandler('contribute-pier-food',     pierFoodQty,     pierSendingFood,     setPierSendingFood,     setPierFoodMsg,     'Food Packets',  () => { setPlayerFoodScore(n => Math.max(0, n - pierFoodQty)); refreshPier(); });

  const handlePierVayal = async () => {
    const max = Math.floor(playerVayal);
    if (!waxAcct || pierSendingVayal || pierVayalQty < 1 || max < 1) return;
    setPierSendingVayal(true); setPierVayalMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/map/contribute-pier-vayal', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wax_account: waxAcct, quantity: pierVayalQty }) });
      const d = await r.json();
      if (d.success) { setPierVayalMsg(d.refunded > 0 ? `✅ Contributed ${d.contributed.toLocaleString()} Vayal — ${d.refunded} refunded` : `✅ Contributed ${d.contributed.toLocaleString()} Vayal!`); setPlayerVayal(d.vayal_left); setPierVayalQty(1000); refreshPier(); }
      else setPierVayalMsg(`❌ ${d.error}`);
    } catch { setPierVayalMsg('❌ Network error'); } finally { setPierSendingVayal(false); }
  };

  const handlePierShram = async () => {
    if (!waxAcct || pierSendingShram || pierShramQty < 0.0001 || playerShram < pierShramQty) return;
    setPierSendingShram(true); setPierShramMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/map/contribute-pier-shram', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wax_account: waxAcct, quantity: pierShramQty }) });
      const d = await r.json();
      if (d.success) { setPierShramMsg(d.refunded > 0 ? `✅ Contributed ${d.contributed} SHRAM — ${d.refunded} refunded` : `✅ Contributed ${d.contributed} SHRAM!`); setPlayerShram(s => Math.max(0, s - pierShramQty)); setPierShramQty(1); refreshPier(); }
      else setPierShramMsg(`❌ ${d.error}`);
    } catch { setPierShramMsg('❌ Network error'); } finally { setPierSendingShram(false); }
  };

  const handlePierWari = async () => {
    if (!waxAcct || pierSendingWari || pierWariQty < 0.0001 || playerWari < pierWariQty) return;
    setPierSendingWari(true); setPierWariMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/map/contribute-pier-wari', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wax_account: waxAcct, quantity: pierWariQty }) });
      const d = await r.json();
      if (d.success) { setPierWariMsg(d.refunded > 0 ? `✅ Contributed ${d.contributed} WARI — ${d.refunded} refunded` : `✅ Contributed ${d.contributed} WARI!`); setPlayerWari(w => Math.max(0, w - pierWariQty)); setPierWariQty(1); refreshPier(); }
      else setPierWariMsg(`❌ ${d.error}`);
    } catch { setPierWariMsg('❌ Network error'); } finally { setPierSendingWari(false); }
  };

  const handleBusLogs = async () => {
    const available = playerLogs[4] ?? 0;
    if (!waxAcct || busSendingLogs || busLogQty < 1 || busLogQty > available) return;
    setBusSendingLogs(true); setBusLogMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/map/contribute-bus-logs', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wax_account: waxAcct, quantity: busLogQty }) });
      const d = await r.json();
      if (d.success) {
        setBusLogMsg(d.refunded > 0 ? `✅ Contributed ${d.contributed} logs — ${d.refunded} refunded (quota hit)` : `✅ Contributed ${d.contributed} Opuntia logs!`);
        setPlayerLogs(prev => ({ ...prev, 4: d.logs_left }));
        setBusLogQty(1);
        fetch('/samatvam/api/samatvam/map/bus-stop-progress').then(r2 => r2.json()).then(d2 => { if (d2.success) setBusProgress(d2); }).catch(() => {});
      } else { setBusLogMsg(`❌ ${d.error}`); }
    } catch { setBusLogMsg('❌ Network error'); }
    setBusSendingLogs(false);
  };

  const handleBusEthanol = async () => {
    if (!waxAcct || busSendingEth || busEthQty < 1 || busEthQty > playerEthanol) return;
    setBusSendingEth(true); setBusEthMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/map/contribute-bus-ethanol', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wax_account: waxAcct, quantity: busEthQty }) });
      const d = await r.json();
      if (d.success) {
        setBusEthMsg(d.refunded > 0 ? `✅ Contributed ${d.contributed}L — ${d.refunded}L refunded (quota hit)` : `✅ Contributed ${d.contributed}L ethanol!`);
        setPlayerEthanol(d.ethanol_left);
        setBusEthQty(1);
        fetch('/samatvam/api/samatvam/map/bus-stop-progress').then(r2 => r2.json()).then(d2 => { if (d2.success) setBusProgress(d2); }).catch(() => {});
      } else { setBusEthMsg(`❌ ${d.error}`); }
    } catch { setBusEthMsg('❌ Network error'); }
    setBusSendingEth(false);
  };

  const handleBusVayal = async () => {
    if (!waxAcct || busSendingVayal || busVayalQty <= 0 || busVayalQty > playerVayal) return;
    setBusSendingVayal(true); setBusVayalMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/map/contribute-bus-vayal', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wax_account: waxAcct, quantity: busVayalQty }) });
      const d = await r.json();
      if (d.success) {
        setBusVayalMsg(d.refunded > 0 ? `✅ Contributed ${d.contributed} Vayal — ${d.refunded} refunded (quota hit)` : `✅ Contributed ${d.contributed} Vayal!`);
        setPlayerVayal(d.vayal_left);
        setBusVayalQty(100);
        fetch('/samatvam/api/samatvam/map/bus-stop-progress').then(r2 => r2.json()).then(d2 => { if (d2.success) setBusProgress(d2); }).catch(() => {});
      } else { setBusVayalMsg(`❌ ${d.error}`); }
    } catch { setBusVayalMsg('❌ Network error'); }
    setBusSendingVayal(false);
  };

  const handleBusFood = async () => {
    if (!waxAcct || busSendingFood || busFoodQty <= 0 || busFoodQty > playerFoodScore) return;
    setBusSendingFood(true); setBusFoodMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/map/contribute-bus-food', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wax_account: waxAcct, quantity: busFoodQty }) });
      const d = await r.json();
      if (d.success) {
        setBusFoodMsg(d.refunded > 0 ? `✅ Contributed ${d.contributed.toLocaleString()} food — ${d.refunded.toLocaleString()} refunded (quota hit)` : `✅ Contributed ${d.contributed.toLocaleString()} food packets!`);
        setPlayerFoodScore(d.food_left);
        setBusFoodQty(1000);
        fetch('/samatvam/api/samatvam/map/bus-stop-progress').then(r2 => r2.json()).then(d2 => { if (d2.success) setBusProgress(d2); }).catch(() => {});
      } else { setBusFoodMsg(`❌ ${d.error}`); }
    } catch { setBusFoodMsg('❌ Network error'); }
    setBusSendingFood(false);
  };

  const handlePlace = async () => {
    if (!waxAcct || placing || placeQty < 1 || placeQty > packets) return;
    setPlacing(true);
    setPlaceMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/map/place-packet', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxAcct, quantity: placeQty }),
      });
      const d = await r.json();
      if (d.success) {
        setPlaceMsg(`✅ Placed ${placeQty} packet${placeQty > 1 ? 's' : ''}!`);
        setPackets(d.packets_left);
        setPlaceQty(1);
        fetchLeaderboard();
      } else {
        setPlaceMsg(`❌ ${d.error}`);
      }
    } catch {
      setPlaceMsg('❌ Network error');
    } finally {
      setPlacing(false);
    }
  };

  // Mouse move: convert screen coords → SVG viewBox coords → bottom-left display coords
  const handleMouseMove = React.useCallback((e: React.MouseEvent) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width)  * MAP_W;
    const svgY = ((e.clientY - rect.top)  / rect.height) * MAP_H;
    if (svgX >= 0 && svgY >= 0 && svgX <= MAP_W && svgY <= MAP_H) {
      setCoords({ x: Math.round(svgX), y: Math.round(MAP_H - svgY) }); // y flipped for display
    } else {
      setCoords(null);
    }
  }, []);

  const handleMouseLeave = React.useCallback(() => setCoords(null), []);

  return (
    <div style={{
      minHeight: '100vh', background: '#1a1208',
      display: 'flex', flexDirection: 'column',
      fontFamily: 'system-ui, sans-serif', overflowY: 'auto',
    }}>
      {/* ── Header ── */}
      <div style={{
        flexShrink: 0, padding: isMobile ? '8px 12px' : '12px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'rgba(10,8,3,0.85)', backdropFilter: 'blur(16px)',
        borderBottom: '1px solid rgba(245,158,11,0.15)',
        zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12 }}>
          <div style={{
            width: isMobile ? 28 : 36, height: isMobile ? 28 : 36, borderRadius: 10,
            background: 'linear-gradient(135deg,#b45309,#f59e0b)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 16px 4px #f59e0b40', fontSize: isMobile ? 14 : 18,
          }}>🗺</div>
          <div>
            <h2 style={{ color: '#fde68a', fontWeight: 900, fontSize: isMobile ? 13 : 16, margin: 0, letterSpacing: -0.3 }}>
              {isMobile ? 'City Map' : 'Samatvam I \u00a0·\u00a0 City Map'}
            </h2>
            {!isMobile && <p style={{ color: '#f59e0b60', fontSize: 10, margin: 0, fontWeight: 700, letterSpacing: 2 }}>SEASON ZERO · PHASE 0</p>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {waxAcct ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {!isMobile && <span style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, padding: '5px 10px', fontFamily: 'monospace' }}>
                ⬡ {waxAcct}
              </span>}
              {isMobile && <span style={{ fontSize: 9, fontWeight: 700, color: '#f59e0b', fontFamily: 'monospace' }}>⬡ {waxAcct.length > 10 ? waxAcct.slice(0,10)+'…' : waxAcct}</span>}
              <button onClick={handleWaxDisconnect} style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: isMobile ? '4px 8px' : '5px 10px', color: '#f87171', cursor: 'pointer', fontSize: isMobile ? 10 : 11, fontWeight: 700 }}>
                {isMobile ? '✕' : 'Disconnect'}
              </button>
            </div>
          ) : (
            <button onClick={handleWaxConnect} disabled={waxConnecting} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: isMobile ? '5px 10px' : '7px 14px', borderRadius: 10, border: '1px solid rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.08)', color: '#fcd34d', fontSize: isMobile ? 11 : 12, fontWeight: 700, cursor: waxConnecting ? 'not-allowed' : 'pointer', opacity: waxConnecting ? 0.6 : 1 }}>
              <span style={{ fontSize: 13 }}>⬡</span>
              {waxConnecting ? '…' : isMobile ? 'WAX' : 'Connect WAX Wallet'}
            </button>
          )}
          <button onClick={onBack} style={{
            background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: 10, padding: isMobile ? '5px 10px' : '7px 18px', color: '#fde68a', cursor: 'pointer',
            fontSize: isMobile ? 11 : 12, fontWeight: 700,
          }}>← Back</button>
        </div>
      </div>

      {/* ── Atavi Reset Notice ── */}
      <div style={{ background: 'linear-gradient(90deg, rgba(245,158,11,0.1), rgba(245,158,11,0.05))', borderBottom: '1px solid rgba(245,158,11,0.2)', padding: '9px 16px', display: 'flex', alignItems: 'flex-start', gap: 10, flexShrink: 0 }}>
        <span style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}>🌿</span>
        <div>
          <span style={{ fontWeight: 800, color: '#fbbf24', fontSize: 12 }}>Atavi Forest Reset — 19 May 2026, 12:00 GMT &nbsp;</span>
          <span style={{ color: '#64748b', fontSize: 12 }}>All trees harvested automatically. Seeds &amp; Bhoomi returned in full. Planting/harvesting paused ~10–15 min. All other sections run as usual.</span>
        </div>
      </div>

      {/* ── Body: left panel + map + right panel ── */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        // Desktop: fixed height so map fills screen; Mobile: natural height, page scrolls
        height: isMobile ? undefined : 'calc(100vh - 61px)',
        overflow: isMobile ? 'visible' : 'hidden',
        flexShrink: 0,
      }}>

        {/* ── Left info panel ── */}
        <div style={{
          width: isMobile ? '100%' : 260,
          flexShrink: 0,
          order: isMobile ? 1 : 0,
          borderRight: isMobile ? 'none' : '1px solid rgba(245,158,11,0.1)',
          borderTop: isMobile ? '1px solid rgba(245,158,11,0.12)' : 'none',
          background: 'rgba(10,8,3,0.90)',
          display: 'flex', flexDirection: 'column',
          padding: isMobile ? '12px 14px' : '16px 14px',
          gap: 10,
          overflowY: isMobile ? 'visible' : 'auto',
        }}>
          {activeZone ? (
            (() => {
              const taskNum = activeZone.task_number ?? 1;
              const taskColors: Record<number, { accent: string; faint: string; border: string }> = {
                1: { accent: '#f59e0b', faint: 'rgba(245,158,11,', border: 'rgba(245,158,11,0.22)' },
                2: { accent: '#0ea5e9', faint: 'rgba(14,165,233,', border: 'rgba(14,165,233,0.22)' },
                3: { accent: '#a78bfa', faint: 'rgba(167,139,250,', border: 'rgba(167,139,250,0.22)' },
                4: { accent: '#f97316', faint: 'rgba(249,115,22,', border: 'rgba(249,115,22,0.22)' },
              };
              const tc = taskColors[taskNum] ?? taskColors[1];

              const taskReqs: Record<number, { icon: string; text: string }[]> = {
                1: [],
                2: [
                  { icon: '🧑', text: '1 council worker' },
                  { icon: '🛒', text: '1 hand cart' },
                  { icon: '📦', text: '20,000 food packets' },
                ],
                3: [
                  { icon: '🧑', text: '2 council workers' },
                  { icon: '📦', text: '50,000 food packets' },
                  { icon: '📗', text: 'Electrician Guide Book' },
                  { icon: '🌳', text: '15 Acacia logs (chopped & carted)' },
                ],
                4: [
                  { icon: '🧑', text: '5 council workers' },
                  { icon: '📦', text: '100,000 food packets' },
                  { icon: '📘', text: 'Biofuel Synthesis Recipe' },
                  { icon: '🌿', text: '20 Moringa bundles (chopped & carted)' },
                  { icon: '🌲', text: '30 Mesquite logs (chopped & carted)' },
                  { icon: '🌴', text: '10 Neem logs (chopped & carted)' },
                ],
              };
              const reqs = taskReqs[taskNum] ?? [];

              return (
                <>
                  {/* Task header */}
                  <div style={{ background: `${tc.faint}0.08)`, border: `1px solid ${tc.border}`, borderRadius: 10, padding: '10px 12px' }}>
                    <p style={{ color: tc.accent, fontSize: 8.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 4px' }}>Task {taskNum}</p>
                    <p style={{ color: '#fde68a', fontSize: 13, fontWeight: 800, margin: '0 0 6px', lineHeight: 1.3 }}>{activeZone.label}</p>
                    <p style={{ color: '#94a3b8', fontSize: 11, lineHeight: 1.6, margin: 0 }}>{activeZone.note}</p>
                  </div>

                  {/* Requirements (tasks 2-4) */}
                  {reqs.length > 0 && (
                    <div style={{ background: `${tc.faint}0.04)`, border: `1px solid ${tc.faint}0.15)`, borderRadius: 10, padding: '10px 12px' }}>
                      <p style={{ color: `${tc.faint}0.6)`, fontSize: 8.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 8px' }}>Requirements</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {reqs.map((r, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
                            <span style={{ fontSize: 13, flexShrink: 0 }}>{r.icon}</span>
                            <span style={{ color: '#ffffffaa', fontSize: 10.5, lineHeight: 1.5 }}>{r.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* City Progress — packets only for task 1; all requirements for tasks 2-4 */}
                  {taskNum === 1 ? (
                    <div style={{ background: `${tc.faint}0.05)`, border: `1px solid ${tc.faint}0.18)`, borderRadius: 10, padding: '12px' }}>
                      <p style={{ color: tc.accent, fontSize: 8.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 6px' }}>City Progress</p>
                      <p style={{ color: '#fff', fontSize: 20, fontWeight: 900, margin: '0 0 1px', fontFamily: 'monospace', letterSpacing: -0.5 }}>
                        {lbZoneId === activeZone.zone_id ? lbTotal.toLocaleString() : '…'}
                        <span style={{ fontSize: 11, color: `${tc.faint}0.5)`, fontWeight: 700 }}> / {activeZone.counter_target.toLocaleString()}</span>
                      </p>
                      <p style={{ color: `${tc.faint}0.5)`, fontSize: 9.5, margin: '0 0 8px', fontWeight: 600 }}>{activeZone.counter_unit}</p>
                      <div style={{ height: 5, background: 'rgba(255,255,255,0.07)', borderRadius: 999, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: lbZoneId === activeZone.zone_id ? `${Math.min(100, (lbTotal / activeZone.counter_target) * 100)}%` : '0%', background: `linear-gradient(90deg,${tc.accent},${tc.accent}cc)`, borderRadius: 999, transition: 'width 0.6s ease' }}/>
                      </div>
                    </div>
                  ) : (() => {
                    const zp = zoneProgress;
                    const workersHere = zp ? zp.totals.workers_at_zone : 0;
                    const handcartCount = zp ? zp.totals.handcart_count : 0;
                    const zonePkts = zp ? zp.totals.food_packets : 0;
                    const electricianCount = zp ? (zp.totals.electrician_handbook_count ?? 0) : 0;
                    const biofuelCount = zp ? (zp.totals.biofuel_recipe_count ?? 0) : 0;

                    type SRow = { icon: string; label: string; met: boolean; value: string; bar?: { val: number; max: number } };
                    let rows: SRow[] = [];

                    if (taskNum === 2) {
                      rows = [
                        { icon: '🧑', label: '1 council worker',    met: workersHere >= 1,    value: zp ? `${workersHere} / 1 at zone` : '…' },
                        { icon: '🛒', label: '1 hand cart',         met: handcartCount >= 1,   value: zp ? `${handcartCount} at zone` : '…' },
                        { icon: '📦', label: '20,000 food packets', met: zonePkts >= 20000,   value: zp ? `${zonePkts.toLocaleString()} / 20,000` : '…', bar: zp ? { val: zonePkts, max: 20000 } : undefined },
                      ];
                    } else if (taskNum === 3) {
                      const acacia = (zp?.totals?.log_totals ?? {})['log_2'] ?? 0;
                      rows = [
                        { icon: '🧑', label: '2 council workers',      met: workersHere >= 2,      value: zp ? `${workersHere} / 2 at zone` : '…' },
                        { icon: '📦', label: '50,000 food packets',    met: zonePkts >= 50000,     value: zp ? `${zonePkts.toLocaleString()} / 50,000` : '…', bar: zp ? { val: zonePkts, max: 50000 } : undefined },
                        { icon: '📗', label: 'Electrician Guide Book', met: electricianCount >= 1, value: zp ? `${electricianCount} player${electricianCount !== 1 ? 's' : ''} at zone` : '…' },
                        { icon: '🌳', label: '15 Acacia logs',         met: acacia >= 15,          value: zp ? `${acacia} / 15` : '…', bar: zp ? { val: acacia, max: 15 } : undefined },
                      ];
                    } else if (taskNum === 4) {
                      const lt = zp?.totals?.log_totals ?? {};
                      const moringa = lt['log_3'] ?? 0;
                      const mesquite = lt['log_1'] ?? 0;
                      const neem = lt['log_6'] ?? 0;
                      rows = [
                        { icon: '🧑', label: '5 council workers',        met: workersHere >= 5,    value: zp ? `${workersHere} / 5 at zone` : '…' },
                        { icon: '📦', label: '100,000 food packets',     met: zonePkts >= 100000,  value: zp ? `${zonePkts.toLocaleString()} / 100,000` : '…', bar: zp ? { val: zonePkts, max: 100000 } : undefined },
                        { icon: '📘', label: 'Biofuel Synthesis Recipe', met: biofuelCount >= 1,   value: zp ? `${biofuelCount} player${biofuelCount !== 1 ? 's' : ''} at zone` : '…' },
                        { icon: '🌿', label: '20 Moringa bundles',       met: moringa >= 20,       value: zp ? `${moringa} / 20` : '…', bar: zp ? { val: moringa, max: 20 } : undefined },
                        { icon: '🌲', label: '30 Mesquite logs',         met: mesquite >= 30,      value: zp ? `${mesquite} / 30` : '…', bar: zp ? { val: mesquite, max: 30 } : undefined },
                        { icon: '🌴', label: '10 Neem logs',             met: neem >= 10,          value: zp ? `${neem} / 10` : '…', bar: zp ? { val: neem, max: 10 } : undefined },
                      ];
                    } else if (taskNum === 6) {
                      const bp = busProgress;
                      rows = [
                        { icon: '🌵', label: '7,000 Opuntia logs',       met: (bp?.logs ?? 0) >= 7000,     value: bp ? `${(bp.logs).toLocaleString()} / 7,000`                       : '…', bar: bp ? { val: bp.logs,    max: 7000    } : undefined },
                        { icon: '⚗️',  label: '500L Ethanol',              met: (bp?.ethanol ?? 0) >= 500,   value: bp ? `${bp.ethanol}L / 500L`                                       : '…', bar: bp ? { val: bp.ethanol, max: 500     } : undefined },
                        { icon: '🌾', label: '15,000 Vayal',              met: (bp?.vayal ?? 0) >= 15000,   value: bp ? `${Math.floor(bp.vayal).toLocaleString()} / 15,000`           : '…', bar: bp ? { val: bp.vayal,   max: 15000   } : undefined },
                        { icon: '🍎', label: '1,000,000 Food Packets',   met: (bp?.food ?? 0) >= 1000000,  value: bp ? `${(bp.food ?? 0).toLocaleString()} / 1,000,000`             : '…', bar: bp ? { val: bp.food ?? 0, max: 1000000 } : undefined },
                      ];
                    } else if (taskNum === 7) {
                      const cp = chemProgress;
                      rows = [
                        { icon: '🌴', label: '12,000 Neem logs',       met: (cp?.neem    ?? 0) >= 12000,  value: cp ? `${cp.neem.toLocaleString()} / 12,000`                  : '…', bar: cp ? { val: cp.neem,    max: 12000  } : undefined },
                        { icon: '🍈', label: '5,000 Jujube logs',      met: (cp?.jujube  ?? 0) >= 5000,   value: cp ? `${cp.jujube.toLocaleString()} / 5,000`                 : '…', bar: cp ? { val: cp.jujube,  max: 5000   } : undefined },
                        { icon: '⚗️',  label: '1,250L Ethanol',          met: (cp?.ethanol ?? 0) >= 1250,   value: cp ? `${cp.ethanol}L / 1,250L`                              : '…', bar: cp ? { val: cp.ethanol, max: 1250   } : undefined },
                        { icon: '🌾', label: '37,500 Vayal',            met: (cp?.vayal   ?? 0) >= 37500,  value: cp ? `${Math.floor(cp.vayal).toLocaleString()} / 37,500`    : '…', bar: cp ? { val: cp.vayal,   max: 37500  } : undefined },
                        { icon: '⚒️',  label: '40 SHRAM',                met: (cp?.shram   ?? 0) >= 40,     value: cp ? `${parseFloat(cp.shram.toString()).toFixed(2)} / 40`   : '…', bar: cp ? { val: cp.shram,   max: 40     } : undefined },
                        { icon: '💧', label: '500 Water Barrels',       met: (cp?.barrels ?? 0) >= 500,    value: cp ? `${cp.barrels} / 500`                                  : '…', bar: cp ? { val: cp.barrels, max: 500    } : undefined },
                        { icon: '🍎', label: '1,500,000 Food Packets', met: (cp?.food    ?? 0) >= 1500000, value: cp ? `${(cp.food ?? 0).toLocaleString()} / 1,500,000`       : '…', bar: cp ? { val: cp.food ?? 0, max: 1500000 } : undefined },
                      ];
                    } else if (taskNum === 8) {
                      const ip = ironProgress;
                      rows = [
                        { icon: '🌵', label: '35,000 Opuntia Logs',   met: (ip?.opuntia ?? 0) >= 35000,   value: ip ? `${(ip.opuntia ?? 0).toLocaleString()} / 35,000`          : '…', bar: ip ? { val: ip.opuntia ?? 0,  max: 35000   } : undefined },
                        { icon: '🌴', label: '25,000 Neem Logs',      met: (ip?.neem    ?? 0) >= 25000,   value: ip ? `${(ip.neem ?? 0).toLocaleString()} / 25,000`             : '…', bar: ip ? { val: ip.neem ?? 0,     max: 25000   } : undefined },
                        { icon: '🍈', label: '12,000 Jujube Logs',    met: (ip?.jujube  ?? 0) >= 12000,   value: ip ? `${(ip.jujube ?? 0).toLocaleString()} / 12,000`           : '…', bar: ip ? { val: ip.jujube ?? 0,   max: 12000   } : undefined },
                        { icon: '⚗️',  label: '5,000L Ethanol',         met: (ip?.ethanol ?? 0) >= 5000,    value: ip ? `${(ip.ethanol ?? 0).toLocaleString()}L / 5,000L`         : '…', bar: ip ? { val: ip.ethanol ?? 0,  max: 5000    } : undefined },
                        { icon: '🌾', label: '100,000 Vayal',          met: (ip?.vayal   ?? 0) >= 100000,  value: ip ? `${Math.floor(ip.vayal ?? 0).toLocaleString()} / 100,000` : '…', bar: ip ? { val: ip.vayal ?? 0,    max: 100000  } : undefined },
                        { icon: '⚒️',  label: '120 SHRAM',              met: (ip?.shram   ?? 0) >= 120,     value: ip ? `${parseFloat((ip.shram ?? 0).toString()).toFixed(2)} / 120` : '…', bar: ip ? { val: ip.shram ?? 0, max: 120     } : undefined },
                        { icon: '💧', label: '2,000 Water Barrels',   met: (ip?.barrels ?? 0) >= 2000,    value: ip ? `${(ip.barrels ?? 0).toLocaleString()} / 2,000`           : '…', bar: ip ? { val: ip.barrels ?? 0,  max: 2000    } : undefined },
                        { icon: '🍎', label: '5,000,000 Food Packets', met: (ip?.food    ?? 0) >= 5000000, value: ip ? `${(ip.food ?? 0).toLocaleString()} / 5,000,000`         : '…', bar: ip ? { val: ip.food ?? 0,     max: 5000000 } : undefined },
                      ];
                    } else if (taskNum === 9) {
                      const bp = bioProgress;
                      rows = [
                        { icon: '🌿', label: '8,000 Mesquite Logs',     met: (bp?.mesquite ?? 0) >= 8000,     value: bp ? `${(bp.mesquite ?? 0).toLocaleString()} / 8,000`             : '…', bar: bp ? { val: bp.mesquite ?? 0,  max: 8000     } : undefined },
                        { icon: '🌳', label: '30,000 Acacia Logs',      met: (bp?.acacia   ?? 0) >= 30000,    value: bp ? `${(bp.acacia ?? 0).toLocaleString()} / 30,000`              : '…', bar: bp ? { val: bp.acacia ?? 0,    max: 30000    } : undefined },
                        { icon: '🌱', label: '30,000 Moringa Logs',     met: (bp?.moringa  ?? 0) >= 30000,    value: bp ? `${(bp.moringa ?? 0).toLocaleString()} / 30,000`             : '…', bar: bp ? { val: bp.moringa ?? 0,   max: 30000    } : undefined },
                        { icon: '🌾', label: '150,000 Amaranth Logs',   met: (bp?.amaranth ?? 0) >= 150000,   value: bp ? `${(bp.amaranth ?? 0).toLocaleString()} / 150,000`           : '…', bar: bp ? { val: bp.amaranth ?? 0,  max: 150000   } : undefined },
                        { icon: '🍃', label: '100,000 Vetiver Logs',    met: (bp?.vetiver  ?? 0) >= 100000,   value: bp ? `${(bp.vetiver ?? 0).toLocaleString()} / 100,000`            : '…', bar: bp ? { val: bp.vetiver ?? 0,   max: 100000   } : undefined },
                        { icon: '⚗️',  label: '15,000L Ethanol',          met: (bp?.ethanol  ?? 0) >= 15000,    value: bp ? `${(bp.ethanol ?? 0).toLocaleString()}L / 15,000L`           : '…', bar: bp ? { val: bp.ethanol ?? 0,   max: 15000    } : undefined },
                        { icon: '💧', label: '5,000 Water Barrels',     met: (bp?.barrels  ?? 0) >= 5000,     value: bp ? `${(bp.barrels ?? 0).toLocaleString()} / 5,000`              : '…', bar: bp ? { val: bp.barrels ?? 0,   max: 5000     } : undefined },
                        { icon: '🌊', label: '300,000 Vayal',           met: (bp?.vayal    ?? 0) >= 300000,   value: bp ? `${Math.floor(bp.vayal ?? 0).toLocaleString()} / 300,000`    : '…', bar: bp ? { val: bp.vayal ?? 0,     max: 300000   } : undefined },
                        { icon: '⚒️',  label: '300 SHRAM',               met: (bp?.shram    ?? 0) >= 300,      value: bp ? `${parseFloat((bp.shram ?? 0).toString()).toFixed(2)} / 300` : '…', bar: bp ? { val: bp.shram ?? 0,     max: 300      } : undefined },
                        { icon: '🌊', label: '50 WARI',                 met: (bp?.wari     ?? 0) >= 50,       value: bp ? `${parseFloat((bp.wari ?? 0).toString()).toFixed(4)} / 50`   : '…', bar: bp ? { val: bp.wari ?? 0,      max: 50       } : undefined },
                        { icon: '🍎', label: '15,000,000 Food Packets', met: (bp?.food     ?? 0) >= 15000000, value: bp ? `${(bp.food ?? 0).toLocaleString()} / 15,000,000`            : '…', bar: bp ? { val: bp.food ?? 0,      max: 15000000 } : undefined },
                      ];
                    } else if (taskNum === 10) {
                      const pp = pierProgress;
                      rows = [
                        { icon: '🌵', label: '100,000 Opuntia logs',     met: (pp?.opuntia  ?? 0) >= 100000,   value: pp ? `${(pp.opuntia ?? 0).toLocaleString()} / 100,000`              : '…', bar: pp ? { val: pp.opuntia ?? 0,  max: 100000   } : undefined },
                        { icon: '🌴', label: '80,000 Neem logs',         met: (pp?.neem     ?? 0) >= 80000,    value: pp ? `${(pp.neem ?? 0).toLocaleString()} / 80,000`                  : '…', bar: pp ? { val: pp.neem ?? 0,    max: 80000    } : undefined },
                        { icon: '🍈', label: '50,000 Jujube logs',       met: (pp?.jujube   ?? 0) >= 50000,    value: pp ? `${(pp.jujube ?? 0).toLocaleString()} / 50,000`                : '…', bar: pp ? { val: pp.jujube ?? 0,  max: 50000    } : undefined },
                        { icon: '🌳', label: '150,000 Acacia logs',      met: (pp?.acacia   ?? 0) >= 150000,   value: pp ? `${(pp.acacia ?? 0).toLocaleString()} / 150,000`               : '…', bar: pp ? { val: pp.acacia ?? 0,  max: 150000   } : undefined },
                        { icon: '🌲', label: '60,000 Mesquite logs',     met: (pp?.mesquite ?? 0) >= 60000,    value: pp ? `${(pp.mesquite ?? 0).toLocaleString()} / 60,000`              : '…', bar: pp ? { val: pp.mesquite ?? 0, max: 60000   } : undefined },
                        { icon: '🌱', label: '80,000 Moringa logs',      met: (pp?.moringa  ?? 0) >= 80000,    value: pp ? `${(pp.moringa ?? 0).toLocaleString()} / 80,000`               : '…', bar: pp ? { val: pp.moringa ?? 0, max: 80000    } : undefined },
                        { icon: '🌾', label: '500,000 Amaranth logs',    met: (pp?.amaranth ?? 0) >= 500000,   value: pp ? `${(pp.amaranth ?? 0).toLocaleString()} / 500,000`             : '…', bar: pp ? { val: pp.amaranth ?? 0, max: 500000  } : undefined },
                        { icon: '🍃', label: '300,000 Vetiver logs',     met: (pp?.vetiver  ?? 0) >= 300000,   value: pp ? `${(pp.vetiver ?? 0).toLocaleString()} / 300,000`              : '…', bar: pp ? { val: pp.vetiver ?? 0, max: 300000   } : undefined },
                        { icon: '⚗️',  label: '50,000L Ethanol',           met: (pp?.ethanol  ?? 0) >= 50000,    value: pp ? `${(pp.ethanol ?? 0).toLocaleString()}L / 50,000L`             : '…', bar: pp ? { val: pp.ethanol ?? 0, max: 50000    } : undefined },
                        { icon: '💧', label: '20,000 Water Barrels',     met: (pp?.barrels  ?? 0) >= 20000,    value: pp ? `${(pp.barrels ?? 0).toLocaleString()} / 20,000`               : '…', bar: pp ? { val: pp.barrels ?? 0, max: 20000    } : undefined },
                        { icon: '🌊', label: '1,000,000 Vayal',          met: (pp?.vayal    ?? 0) >= 1000000,  value: pp ? `${Math.floor(pp.vayal ?? 0).toLocaleString()} / 1,000,000`   : '…', bar: pp ? { val: pp.vayal ?? 0,   max: 1000000  } : undefined },
                        { icon: '⚒️',  label: '1,000 SHRAM',              met: (pp?.shram    ?? 0) >= 1000,     value: pp ? `${parseFloat((pp.shram ?? 0).toString()).toFixed(2)} / 1,000` : '…', bar: pp ? { val: pp.shram ?? 0,   max: 1000     } : undefined },
                        { icon: '🌊', label: '200 WARI',                 met: (pp?.wari     ?? 0) >= 200,      value: pp ? `${parseFloat((pp.wari ?? 0).toString()).toFixed(4)} / 200`    : '…', bar: pp ? { val: pp.wari ?? 0,    max: 200      } : undefined },
                        { icon: '🍎', label: '50,000,000 Food Packets',  met: (pp?.food     ?? 0) >= 50000000, value: pp ? `${(pp.food ?? 0).toLocaleString()} / 50,000,000`             : '…', bar: pp ? { val: pp.food ?? 0,    max: 50000000 } : undefined },
                      ];
                    }

                    const allMet = rows.length > 0 && rows.every(r => r.met);

                    return (
                      <div style={{ background: allMet ? 'rgba(16,185,129,0.06)' : `${tc.faint}0.05)`, border: `1px solid ${allMet ? 'rgba(16,185,129,0.35)' : tc.faint + '0.18)'}`, borderRadius: 10, padding: '12px' }}>
                        <p style={{ color: allMet ? '#10b981' : tc.accent, fontSize: 8.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 10px' }}>City Progress</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                          {rows.map((r, i) => (
                            <div key={i}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: r.bar ? 4 : 0 }}>
                                <span style={{ fontSize: 13, flexShrink: 0 }}>{r.icon}</span>
                                <span style={{ flex: 1, color: r.met ? '#6ee7b7' : '#ffffffaa', fontSize: 10, lineHeight: 1.3 }}>{r.label}</span>
                                <span style={{
                                  fontSize: 9, fontWeight: 800, fontFamily: 'monospace', whiteSpace: 'nowrap',
                                  color: r.met ? '#6ee7b7' : '#ffffff40',
                                  background: r.met ? 'rgba(16,185,129,0.12)' : 'rgba(255,255,255,0.04)',
                                  border: `1px solid ${r.met ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.07)'}`,
                                  borderRadius: 5, padding: '2px 7px',
                                }}>
                                  {r.met ? '✓' : '✗'} {r.value}
                                </span>
                              </div>
                              {r.bar && (
                                <div style={{ height: 4, background: 'rgba(255,255,255,0.07)', borderRadius: 999, overflow: 'hidden', marginLeft: 20 }}>
                                  <div style={{ height: '100%', width: zp ? `${Math.min(100, (r.bar.val / r.bar.max) * 100)}%` : '0%', background: r.met ? 'linear-gradient(90deg,#10b981,#34d399)' : `linear-gradient(90deg,${tc.accent},${tc.accent}cc)`, borderRadius: 999, transition: 'width 0.6s ease' }}/>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        {allMet && (
                          <div style={{ marginTop: 12, padding: '8px 10px', borderRadius: 8, background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.4)', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 16 }}>🏆</span>
                            <div>
                              <p style={{ color: '#6ee7b7', fontSize: 11, fontWeight: 900, margin: 0 }}>Task {taskNum} Complete!</p>
                              <p style={{ color: '#a7f3d0', fontSize: 9, margin: 0, lineHeight: 1.5 }}>All requirements met simultaneously</p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Bus Stop contributions (Task 6) — no worker required */}
                  {taskNum === 6 && (() => {
                    const bp = busProgress;
                    const done = bp?.complete ?? false;
                    if (done) return (
                      <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.35)', borderRadius: 10, padding: '12px', textAlign: 'center' }}>
                        <p style={{ color: '#6ee7b7', fontSize: 15, fontWeight: 900, margin: '0 0 4px' }}>🚌 Bus Stop Built!</p>
                        <p style={{ color: '#a7f3d0', fontSize: 10, margin: 0 }}>Completed by {bp?.completed_by}</p>
                      </div>
                    );
                    if (!waxAcct) return (
                      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '12px' }}>
                        <p style={{ color: '#ffffff35', fontSize: 10, margin: 0 }}>Connect your WAX wallet to contribute resources.</p>
                      </div>
                    );

                    const opuntiaHave = playerLogs[4] ?? 0;
                    const logsComplete    = (bp?.logs   ?? 0) >= 7000;
                    const ethanolComplete = (bp?.ethanol ?? 0) >= 500;
                    const vayalComplete   = (bp?.vayal  ?? 0) >= 15000;
                    const foodComplete    = (bp?.food   ?? 0) >= 1000000;
                    type BusRow = { icon: string; label: string; have: number; qty: number; setQty: (v: number) => void; max: number; sending: boolean; msg: string; onSend: () => void; color: string; resourceDone: boolean };
                    const busRows: BusRow[] = [
                      { icon: '🌵', label: 'Opuntia Logs',  have: opuntiaHave,              qty: busLogQty,   setQty: setBusLogQty,   max: opuntiaHave,              sending: busSendingLogs,  msg: busLogMsg,   onSend: handleBusLogs,    color: '#f59e0b', resourceDone: logsComplete    },
                      { icon: '⚗️',  label: 'Ethanol (L)',    have: playerEthanol,             qty: busEthQty,   setQty: setBusEthQty,   max: playerEthanol,             sending: busSendingEth,   msg: busEthMsg,   onSend: handleBusEthanol, color: '#22d3ee', resourceDone: ethanolComplete  },
                      { icon: '🌾', label: 'Vayal',          have: Math.floor(playerVayal),   qty: busVayalQty, setQty: setBusVayalQty, max: Math.floor(playerVayal),   sending: busSendingVayal, msg: busVayalMsg, onSend: handleBusVayal,   color: '#34d399', resourceDone: vayalComplete    },
                      { icon: '🍎', label: 'Food Packets',   have: Math.floor(playerFoodScore), qty: busFoodQty, setQty: setBusFoodQty, max: Math.floor(playerFoodScore), sending: busSendingFood,  msg: busFoodMsg,  onSend: handleBusFood,    color: '#f472b6', resourceDone: foodComplete     },
                    ];
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {busRows.map(row => row.resourceDone ? (
                          <div key={row.label} style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.35)', borderRadius: 10, padding: '11px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 18 }}>{row.icon}</span>
                            <div style={{ flex: 1 }}>
                              <p style={{ color: '#6ee7b7', fontSize: 11, fontWeight: 900, margin: 0 }}>{row.label} — Complete ✓</p>
                              <p style={{ color: '#a7f3d0', fontSize: 9, margin: 0 }}>Quota filled — no more needed</p>
                            </div>
                            <span style={{ fontSize: 18 }}>✅</span>
                          </div>
                        ) : (
                          <div key={row.label} style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid rgba(255,255,255,0.08)`, borderRadius: 10, padding: '11px 12px' }}>
                            <p style={{ color: row.color, fontSize: 8.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 6px' }}>
                              {row.icon} {row.label} · you have: <span style={{ fontFamily: 'monospace' }}>{row.have.toLocaleString()}</span>
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                              <button onClick={() => row.setQty(Math.max(1, row.qty - 1))} disabled={row.qty <= 1}
                                style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: 16, cursor: row.qty <= 1 ? 'default' : 'pointer', opacity: row.qty <= 1 ? 0.3 : 1, lineHeight: 1 }}>−</button>
                              <input type="number" min={1} max={row.max || 1} value={row.qty}
                                onChange={e => { const v = parseInt(e.target.value); if (!isNaN(v)) row.setQty(Math.min(Math.max(1, v), row.max || 1)); }}
                                style={{ width: 72, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', color: 'white', fontWeight: 900, fontSize: 14, fontFamily: 'monospace', textAlign: 'center', outline: 'none' }}
                              />
                              <button onClick={() => row.setQty(Math.min(row.max, row.qty + 1))} disabled={row.qty >= row.max}
                                style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: 16, cursor: row.qty >= row.max ? 'default' : 'pointer', opacity: row.qty >= row.max ? 0.3 : 1, lineHeight: 1 }}>+</button>
                              <button onClick={() => row.setQty(row.max)} disabled={row.max === 0}
                                style={{ padding: '4px 8px', borderRadius: 6, fontSize: 9, fontWeight: 800, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#ffffff50', cursor: row.max === 0 ? 'default' : 'pointer' }}>max</button>
                            </div>
                            <button onClick={row.onSend} disabled={row.sending || row.max === 0 || row.qty < 1}
                              style={{ width: '100%', padding: '9px', borderRadius: 8, fontSize: 12, fontWeight: 800, background: (row.sending || row.max === 0) ? 'rgba(255,255,255,0.04)' : `rgba(255,255,255,0.07)`, border: `1px solid ${(row.sending || row.max === 0) ? 'rgba(255,255,255,0.06)' : row.color + '55'}`, color: (row.sending || row.max === 0) ? '#ffffff25' : row.color, cursor: (row.sending || row.max === 0) ? 'default' : 'pointer', transition: 'all 0.2s' }}>
                              {row.sending ? 'Contributing…' : `${row.icon} Contribute ${row.qty.toLocaleString()} ${row.label}`}
                            </button>
                            {row.msg && <p style={{ color: row.msg.startsWith('✅') ? '#6ee7b7' : '#f87171', fontSize: 10, margin: '6px 0 0', fontWeight: 700 }}>{row.msg}</p>}
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* My Contributions panel (Task 7) */}
                  {taskNum === 7 && waxAcct && myChemContrib && (() => {
                    const mc = myChemContrib;
                    const rows7 = [
                      { icon: '🌴', label: 'Neem Logs',      val: mc.neem,                          req: 12000,  fmt: (v: number) => v.toLocaleString(),          color: '#a3e635' },
                      { icon: '🍈', label: 'Jujube Logs',    val: mc.jujube,                        req: 5000,   fmt: (v: number) => v.toLocaleString(),          color: '#fb923c' },
                      { icon: '⚗️',  label: 'Ethanol',        val: mc.ethanol,                       req: 1250,   fmt: (v: number) => `${v}L`,                     color: '#22d3ee' },
                      { icon: '🌾', label: 'Vayal',          val: Math.floor(mc.vayal),             req: 37500,  fmt: (v: number) => v.toLocaleString(),          color: '#34d399' },
                      { icon: '⚒️',  label: 'SHRAM',          val: parseFloat(mc.shram.toFixed(4)),  req: 40,     fmt: (v: number) => v.toFixed(4),                color: '#e879f9' },
                      { icon: '💧', label: 'Water Barrels',  val: mc.barrels,                       req: 500,    fmt: (v: number) => v.toLocaleString(),          color: '#38bdf8' },
                      { icon: '🍎', label: 'Food Packets',   val: mc.food,                          req: 1500000, fmt: (v: number) => v.toLocaleString(),         color: '#f472b6' },
                    ];
                    return (
                      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '12px' }}>
                        <p style={{ color: '#ffffff50', fontSize: 8.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 10px' }}>Your Contributions</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {rows7.map(r => (
                            <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 12, flexShrink: 0 }}>{r.icon}</span>
                              <span style={{ flex: 1, color: '#ffffffaa', fontSize: 10 }}>{r.label}</span>
                              <span style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 800, color: r.val > 0 ? r.color : '#ffffff20' }}>
                                {r.fmt(r.val)}
                              </span>
                              <span style={{ fontSize: 9, color: '#ffffff25', fontFamily: 'monospace' }}>/ {r.req.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Iron Mining Team — Your Contributions (Task 8) */}
                  {taskNum === 8 && waxAcct && myIronContrib && (() => {
                    const mc = myIronContrib;
                    const rows8 = [
                      { icon: '🌵', label: 'Opuntia Logs',  val: mc.opuntia,                        req: 35000,   fmt: (v: number) => v.toLocaleString(),    color: '#f59e0b' },
                      { icon: '🌴', label: 'Neem Logs',     val: mc.neem,                           req: 25000,   fmt: (v: number) => v.toLocaleString(),    color: '#a3e635' },
                      { icon: '🍈', label: 'Jujube Logs',   val: mc.jujube,                         req: 12000,   fmt: (v: number) => v.toLocaleString(),    color: '#fb923c' },
                      { icon: '⚗️',  label: 'Ethanol',       val: mc.ethanol,                        req: 5000,    fmt: (v: number) => `${v}L`,               color: '#22d3ee' },
                      { icon: '🌾', label: 'Vayal',         val: Math.floor(mc.vayal),              req: 100000,  fmt: (v: number) => v.toLocaleString(),    color: '#34d399' },
                      { icon: '⚒️',  label: 'SHRAM',         val: parseFloat(mc.shram.toFixed(4)),   req: 120,     fmt: (v: number) => v.toFixed(4),          color: '#e879f9' },
                      { icon: '💧', label: 'Water Barrels', val: mc.barrels,                        req: 2000,    fmt: (v: number) => v.toLocaleString(),    color: '#38bdf8' },
                      { icon: '🍎', label: 'Food Packets',  val: mc.food,                           req: 5000000, fmt: (v: number) => v.toLocaleString(),    color: '#f472b6' },
                    ];
                    return (
                      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '12px' }}>
                        <p style={{ color: '#ffffff50', fontSize: 8.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 10px' }}>Your Contributions</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {rows8.map(r => (
                            <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 12, flexShrink: 0 }}>{r.icon}</span>
                              <span style={{ flex: 1, color: '#ffffffaa', fontSize: 10 }}>{r.label}</span>
                              <span style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 800, color: r.val > 0 ? r.color : '#ffffff20' }}>
                                {r.fmt(r.val)}
                              </span>
                              <span style={{ fontSize: 9, color: '#ffffff25', fontFamily: 'monospace' }}>/ {r.req.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Bioplastics — Your Contributions (Task 9) */}
                  {taskNum === 9 && waxAcct && myBioContrib && (() => {
                    const mc = myBioContrib;
                    const rows9 = [
                      { icon: '🌿', label: 'Mesquite Logs',  val: mc.mesquite,                       req: 8000,    fmt: (v: number) => v.toLocaleString(),  color: '#d97706' },
                      { icon: '🌳', label: 'Acacia Logs',    val: mc.acacia,                         req: 30000,   fmt: (v: number) => v.toLocaleString(),  color: '#84cc16' },
                      { icon: '🌱', label: 'Moringa Logs',   val: mc.moringa,                        req: 30000,   fmt: (v: number) => v.toLocaleString(),  color: '#4ade80' },
                      { icon: '🌾', label: 'Amaranth Logs',  val: mc.amaranth,                       req: 150000,  fmt: (v: number) => v.toLocaleString(),  color: '#f59e0b' },
                      { icon: '🍃', label: 'Vetiver Logs',   val: mc.vetiver,                        req: 100000,  fmt: (v: number) => v.toLocaleString(),  color: '#2dd4bf' },
                      { icon: '⚗️',  label: 'Ethanol',        val: mc.ethanol,                        req: 15000,   fmt: (v: number) => `${v}L`,             color: '#22d3ee' },
                      { icon: '💧', label: 'Water Barrels',  val: mc.barrels,                        req: 5000,    fmt: (v: number) => v.toLocaleString(),  color: '#38bdf8' },
                      { icon: '🌊', label: 'Vayal',          val: Math.floor(mc.vayal),              req: 300000,  fmt: (v: number) => v.toLocaleString(),  color: '#34d399' },
                      { icon: '⚒️',  label: 'SHRAM',          val: parseFloat(mc.shram.toFixed(4)),   req: 300,     fmt: (v: number) => v.toFixed(4),        color: '#e879f9' },
                      { icon: '🌊', label: 'WARI',           val: parseFloat(mc.wari.toFixed(4)),    req: 50,      fmt: (v: number) => v.toFixed(4),        color: '#818cf8' },
                      { icon: '🍎', label: 'Food Packets',   val: mc.food,                           req: 15000000, fmt: (v: number) => v.toLocaleString(), color: '#f472b6' },
                    ];
                    return (
                      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '12px' }}>
                        <p style={{ color: '#ffffff50', fontSize: 8.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 10px' }}>Your Contributions</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {rows9.map(r => (
                            <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 12, flexShrink: 0 }}>{r.icon}</span>
                              <span style={{ flex: 1, color: '#ffffffaa', fontSize: 10 }}>{r.label}</span>
                              <span style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 800, color: r.val > 0 ? r.color : '#ffffff20' }}>{r.fmt(r.val)}</span>
                              <span style={{ fontSize: 9, color: '#ffffff25', fontFamily: 'monospace' }}>/ {r.req.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Chemical Factory contributions (Task 7) */}
                  {taskNum === 7 && (() => {
                    const cp = chemProgress;
                    const done = cp?.complete ?? false;
                    if (done) return (
                      <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.35)', borderRadius: 10, padding: '12px', textAlign: 'center' }}>
                        <p style={{ color: '#6ee7b7', fontSize: 15, fontWeight: 900, margin: '0 0 4px' }}>🏭 Chemical Factory Built!</p>
                        <p style={{ color: '#a7f3d0', fontSize: 10, margin: 0 }}>Completed by {cp?.completed_by}</p>
                      </div>
                    );
                    if (!waxAcct) return (
                      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '12px' }}>
                        <p style={{ color: '#ffffff35', fontSize: 10, margin: 0 }}>Connect your WAX wallet to contribute.</p>
                      </div>
                    );
                    const neemDone    = (cp?.neem    ?? 0) >= 12000;
                    const jujubeDone  = (cp?.jujube  ?? 0) >= 5000;
                    const ethanolDone = (cp?.ethanol ?? 0) >= 1250;
                    const vayalDone   = (cp?.vayal   ?? 0) >= 37500;
                    const shramDone   = (cp?.shram   ?? 0) >= 40;
                    const barrelsDone = (cp?.barrels ?? 0) >= 500;
                    const chemFoodDone = (cp?.food   ?? 0) >= 1500000;
                    type CRow = { icon: string; label: string; have: number | string; qty: number; setQty: (v: number) => void; max: number; sending: boolean; msg: string; onSend: () => void; color: string; done: boolean; isFloat?: boolean };
                    const cRows: CRow[] = [
                      { icon: '🌴', label: 'Neem Logs',      have: playerNeem,                    qty: chemNeemQty,   setQty: setChemNeemQty,   max: playerNeem,                    sending: chemSendingNeem,   msg: chemNeemMsg,   onSend: handleChemNeem,   color: '#a3e635', done: neemDone    },
                      { icon: '🍈', label: 'Jujube Logs',    have: playerJujube,                  qty: chemJujubeQty, setQty: setChemJujubeQty, max: playerJujube,                  sending: chemSendingJujube, msg: chemJujubeMsg, onSend: handleChemJujube, color: '#fb923c', done: jujubeDone  },
                      { icon: '⚗️',  label: 'Ethanol (L)',    have: playerEthanol,                 qty: chemEthQty,    setQty: setChemEthQty,    max: playerEthanol,                 sending: chemSendingEth,    msg: chemEthMsg,    onSend: handleChemEth,    color: '#22d3ee', done: ethanolDone },
                      { icon: '🌾', label: 'Vayal',          have: Math.floor(playerVayal),       qty: chemVayalQty,  setQty: setChemVayalQty,  max: Math.floor(playerVayal),       sending: chemSendingVayal,  msg: chemVayalMsg,  onSend: handleChemVayal,  color: '#34d399', done: vayalDone   },
                      { icon: '⚒️',  label: 'SHRAM',          have: playerShram.toFixed(4),        qty: chemShramQty,  setQty: setChemShramQty,  max: Math.floor(playerShram),       sending: chemSendingShram,  msg: chemShramMsg,  onSend: handleChemShram,  color: '#e879f9', done: shramDone,  isFloat: true },
                      { icon: '💧', label: 'Water Barrels',  have: playerBarrels,                 qty: chemBarrelQty, setQty: setChemBarrelQty, max: playerBarrels,                 sending: chemSendingBarrel, msg: chemBarrelMsg, onSend: handleChemBarrel, color: '#38bdf8', done: barrelsDone },
                      { icon: '🍎', label: 'Food Packets',   have: Math.floor(playerFoodScore),   qty: chemFoodQty,   setQty: setChemFoodQty,   max: Math.floor(playerFoodScore),   sending: chemSendingFood,   msg: chemFoodMsg,   onSend: handleChemFood,   color: '#f472b6', done: chemFoodDone },
                    ];
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {cRows.map(row => row.done ? (
                          <div key={row.label} style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.35)', borderRadius: 10, padding: '11px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 18 }}>{row.icon}</span>
                            <div style={{ flex: 1 }}>
                              <p style={{ color: '#6ee7b7', fontSize: 11, fontWeight: 900, margin: 0 }}>{row.label} — Complete ✓</p>
                              <p style={{ color: '#a7f3d0', fontSize: 9, margin: 0 }}>Quota filled</p>
                            </div>
                            <span style={{ fontSize: 18 }}>✅</span>
                          </div>
                        ) : (
                          <div key={row.label} style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid rgba(255,255,255,0.08)`, borderRadius: 10, padding: '11px 12px' }}>
                            <p style={{ color: row.color, fontSize: 8.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 6px' }}>
                              {row.icon} {row.label} · you have: <span style={{ fontFamily: 'monospace' }}>{typeof row.have === 'number' ? row.have.toLocaleString() : row.have}</span>
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                              <button onClick={() => row.setQty(Math.max(1, row.qty - 1))} disabled={row.qty <= 1}
                                style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: 16, cursor: row.qty <= 1 ? 'default' : 'pointer', opacity: row.qty <= 1 ? 0.3 : 1, lineHeight: 1 }}>−</button>
                              <input type="number" min={1} max={row.max || 1} value={row.qty}
                                onChange={e => { const v = row.isFloat ? parseFloat(e.target.value) : parseInt(e.target.value); if (!isNaN(v) && v > 0) row.setQty(Math.min(v, row.max || 1)); }}
                                style={{ width: 72, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', color: 'white', fontWeight: 900, fontSize: 14, fontFamily: 'monospace', textAlign: 'center', outline: 'none' }}
                              />
                              <button onClick={() => row.setQty(Math.min(row.max, row.qty + 1))} disabled={row.qty >= row.max}
                                style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: 16, cursor: row.qty >= row.max ? 'default' : 'pointer', opacity: row.qty >= row.max ? 0.3 : 1, lineHeight: 1 }}>+</button>
                              <button onClick={() => row.setQty(row.max)} disabled={row.max === 0}
                                style={{ padding: '4px 8px', borderRadius: 6, fontSize: 9, fontWeight: 800, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#ffffff50', cursor: row.max === 0 ? 'default' : 'pointer' }}>max</button>
                            </div>
                            <button onClick={row.onSend} disabled={row.sending || row.max === 0 || row.qty < 1}
                              style={{ width: '100%', padding: '9px', borderRadius: 8, fontSize: 12, fontWeight: 800, background: (row.sending || row.max === 0) ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.07)', border: `1px solid ${(row.sending || row.max === 0) ? 'rgba(255,255,255,0.06)' : row.color + '55'}`, color: (row.sending || row.max === 0) ? '#ffffff25' : row.color, cursor: (row.sending || row.max === 0) ? 'default' : 'pointer', transition: 'all 0.2s' }}>
                              {row.sending ? 'Contributing…' : `${row.icon} Contribute ${row.qty.toLocaleString()} ${row.label}`}
                            </button>
                            {row.msg && <p style={{ color: row.msg.startsWith('✅') ? '#6ee7b7' : '#f87171', fontSize: 10, margin: '6px 0 0', fontWeight: 700 }}>{row.msg}</p>}
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Iron Mining Team contributions (Task 8) */}
                  {taskNum === 8 && (() => {
                    const ip = ironProgress;
                    const done = ip?.complete ?? false;
                    if (done) return (
                      <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.35)', borderRadius: 10, padding: '12px', textAlign: 'center' }}>
                        <p style={{ color: '#6ee7b7', fontSize: 15, fontWeight: 900, margin: '0 0 4px' }}>⛏️ Iron Mining Team Assembled!</p>
                        <p style={{ color: '#a7f3d0', fontSize: 10, margin: 0 }}>Completed by {ip?.completed_by}</p>
                      </div>
                    );
                    if (!waxAcct) return (
                      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '12px' }}>
                        <p style={{ color: '#ffffff35', fontSize: 10, margin: 0 }}>Connect your WAX wallet to contribute.</p>
                      </div>
                    );
                    const opuntiaDone8 = (ip?.opuntia ?? 0) >= 35000;
                    const neemDone8    = (ip?.neem    ?? 0) >= 25000;
                    const jujubeDone8  = (ip?.jujube  ?? 0) >= 12000;
                    const ethanolDone8 = (ip?.ethanol ?? 0) >= 5000;
                    const vayalDone8   = (ip?.vayal   ?? 0) >= 100000;
                    const shramDone8   = (ip?.shram   ?? 0) >= 120;
                    const barrelsDone8 = (ip?.barrels ?? 0) >= 2000;
                    const foodDone8    = (ip?.food    ?? 0) >= 5000000;
                    type IRow = { icon: string; label: string; have: number | string; qty: number; setQty: (v: number) => void; max: number; sending: boolean; msg: string; onSend: () => void; color: string; done: boolean; isFloat?: boolean };
                    const iRows: IRow[] = [
                      { icon: '🌵', label: 'Opuntia Logs',  have: playerLogs[4] ?? 0,          qty: ironOpuntiaQty,  setQty: setIronOpuntiaQty,  max: playerLogs[4] ?? 0,          sending: ironSendingOpuntia,  msg: ironOpuntiaMsg,  onSend: handleIronOpuntia,  color: '#f59e0b', done: opuntiaDone8 },
                      { icon: '🌴', label: 'Neem Logs',     have: playerLogs[6] ?? 0,          qty: ironNeemQty,     setQty: setIronNeemQty,     max: playerLogs[6] ?? 0,          sending: ironSendingNeem,     msg: ironNeemMsg,     onSend: handleIronNeem,     color: '#a3e635', done: neemDone8    },
                      { icon: '🍈', label: 'Jujube Logs',   have: playerLogs[8] ?? 0,          qty: ironJujubeQty,   setQty: setIronJujubeQty,   max: playerLogs[8] ?? 0,          sending: ironSendingJujube,   msg: ironJujubeMsg,   onSend: handleIronJujube,   color: '#fb923c', done: jujubeDone8  },
                      { icon: '⚗️',  label: 'Ethanol (L)',   have: playerEthanol,               qty: ironEthQty,      setQty: setIronEthQty,      max: playerEthanol,               sending: ironSendingEth,      msg: ironEthMsg,      onSend: handleIronEth,      color: '#22d3ee', done: ethanolDone8 },
                      { icon: '🌾', label: 'Vayal',         have: Math.floor(playerVayal),     qty: ironVayalQty,    setQty: setIronVayalQty,    max: Math.floor(playerVayal),     sending: ironSendingVayal,    msg: ironVayalMsg,    onSend: handleIronVayal,    color: '#34d399', done: vayalDone8   },
                      { icon: '⚒️',  label: 'SHRAM',         have: playerShram.toFixed(4),      qty: ironShramQty,    setQty: setIronShramQty,    max: Math.floor(playerShram),     sending: ironSendingShram,    msg: ironShramMsg,    onSend: handleIronShram,    color: '#e879f9', done: shramDone8,  isFloat: true },
                      { icon: '💧', label: 'Water Barrels', have: playerBarrels,               qty: ironBarrelQty,   setQty: setIronBarrelQty,   max: playerBarrels,               sending: ironSendingBarrel,   msg: ironBarrelMsg,   onSend: handleIronBarrel,   color: '#38bdf8', done: barrelsDone8 },
                      { icon: '🍎', label: 'Food Packets',  have: Math.floor(playerFoodScore), qty: ironFoodQty,     setQty: setIronFoodQty,     max: Math.floor(playerFoodScore), sending: ironSendingFood,     msg: ironFoodMsg,     onSend: handleIronFood,     color: '#f472b6', done: foodDone8    },
                    ];
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {iRows.map(row => row.done ? (
                          <div key={row.label} style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.35)', borderRadius: 10, padding: '11px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 18 }}>{row.icon}</span>
                            <div style={{ flex: 1 }}>
                              <p style={{ color: '#6ee7b7', fontSize: 11, fontWeight: 900, margin: 0 }}>{row.label} — Complete ✓</p>
                              <p style={{ color: '#a7f3d0', fontSize: 9, margin: 0 }}>Quota filled</p>
                            </div>
                            <span style={{ fontSize: 18 }}>✅</span>
                          </div>
                        ) : (
                          <div key={row.label} style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid rgba(255,255,255,0.08)`, borderRadius: 10, padding: '11px 12px' }}>
                            <p style={{ color: row.color, fontSize: 8.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 6px' }}>
                              {row.icon} {row.label} · you have: <span style={{ fontFamily: 'monospace' }}>{typeof row.have === 'number' ? row.have.toLocaleString() : row.have}</span>
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                              <button onClick={() => row.setQty(Math.max(1, row.qty - 1))} disabled={row.qty <= 1}
                                style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: 16, cursor: row.qty <= 1 ? 'default' : 'pointer', opacity: row.qty <= 1 ? 0.3 : 1, lineHeight: 1 }}>−</button>
                              <input type="number" min={1} max={row.max || 1} value={row.qty}
                                onChange={e => { const v = row.isFloat ? parseFloat(e.target.value) : parseInt(e.target.value); if (!isNaN(v) && v > 0) row.setQty(Math.min(v, row.max || 1)); }}
                                style={{ width: 72, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', color: 'white', fontWeight: 900, fontSize: 14, fontFamily: 'monospace', textAlign: 'center', outline: 'none' }}
                              />
                              <button onClick={() => row.setQty(Math.min(row.max, row.qty + 1))} disabled={row.qty >= row.max}
                                style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: 16, cursor: row.qty >= row.max ? 'default' : 'pointer', opacity: row.qty >= row.max ? 0.3 : 1, lineHeight: 1 }}>+</button>
                              <button onClick={() => row.setQty(row.max)} disabled={row.max === 0}
                                style={{ padding: '4px 8px', borderRadius: 6, fontSize: 9, fontWeight: 800, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#ffffff50', cursor: row.max === 0 ? 'default' : 'pointer' }}>max</button>
                            </div>
                            <button onClick={row.onSend} disabled={row.sending || row.max === 0 || row.qty < 1}
                              style={{ width: '100%', padding: '9px', borderRadius: 8, fontSize: 12, fontWeight: 800, background: (row.sending || row.max === 0) ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.07)', border: `1px solid ${(row.sending || row.max === 0) ? 'rgba(255,255,255,0.06)' : row.color + '55'}`, color: (row.sending || row.max === 0) ? '#ffffff25' : row.color, cursor: (row.sending || row.max === 0) ? 'default' : 'pointer', transition: 'all 0.2s' }}>
                              {row.sending ? 'Contributing…' : `${row.icon} Contribute ${row.qty.toLocaleString()} ${row.label}`}
                            </button>
                            {row.msg && <p style={{ color: row.msg.startsWith('✅') ? '#6ee7b7' : '#f87171', fontSize: 10, margin: '6px 0 0', fontWeight: 700 }}>{row.msg}</p>}
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Bioplastics Manufacturing contributions (Task 9) */}
                  {taskNum === 10 && (() => {
                    const pp = pierProgress;
                    const done = pp?.complete ?? false;
                    if (done) return (
                      <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.35)', borderRadius: 10, padding: '12px', textAlign: 'center' }}>
                        <p style={{ color: '#6ee7b7', fontSize: 15, fontWeight: 900, margin: '0 0 4px' }}>⛵ Boat Pier Built!</p>
                        <p style={{ color: '#a7f3d0', fontSize: 10, margin: 0 }}>Completed by {pp?.completed_by} · Stage 0 complete!</p>
                      </div>
                    );
                    if (!waxAcct) return (
                      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '12px' }}>
                        <p style={{ color: '#ffffff35', fontSize: 10, margin: 0 }}>Connect your WAX wallet to contribute.</p>
                      </div>
                    );
                    const opuntiaDone10 = (pp?.opuntia  ?? 0) >= 100000;
                    const neemDone10    = (pp?.neem     ?? 0) >= 80000;
                    const jujubeDone10  = (pp?.jujube   ?? 0) >= 50000;
                    const acaciaDone10  = (pp?.acacia   ?? 0) >= 150000;
                    const mesqDone10    = (pp?.mesquite ?? 0) >= 60000;
                    const morDone10     = (pp?.moringa  ?? 0) >= 80000;
                    const amarDone10    = (pp?.amaranth ?? 0) >= 500000;
                    const vetDone10     = (pp?.vetiver  ?? 0) >= 300000;
                    const ethDone10     = (pp?.ethanol  ?? 0) >= 50000;
                    const barDone10     = (pp?.barrels  ?? 0) >= 20000;
                    const vayalDone10   = (pp?.vayal    ?? 0) >= 1000000;
                    const shramDone10   = (pp?.shram    ?? 0) >= 1000;
                    const wariDone10    = (pp?.wari     ?? 0) >= 200;
                    const foodDone10    = (pp?.food     ?? 0) >= 50000000;
                    type PRow = { icon: string; label: string; have: number | string; qty: number; setQty: (v: number) => void; max: number; sending: boolean; msg: string; onSend: () => void; color: string; done: boolean; isFloat?: boolean };
                    const pRows: PRow[] = [
                      { icon: '🌵', label: 'Opuntia Logs',   have: playerLogs[4] ?? 0,          qty: pierOpuntiaQty,  setQty: setPierOpuntiaQty,  max: playerLogs[4] ?? 0,          sending: pierSendingOpuntia,  msg: pierOpuntiaMsg,  onSend: handlePierOpuntia,  color: '#f59e0b', done: opuntiaDone10 },
                      { icon: '🌴', label: 'Neem Logs',      have: playerLogs[6] ?? 0,          qty: pierNeemQty,     setQty: setPierNeemQty,     max: playerLogs[6] ?? 0,          sending: pierSendingNeem,     msg: pierNeemMsg,     onSend: handlePierNeem,     color: '#84cc16', done: neemDone10    },
                      { icon: '🍈', label: 'Jujube Logs',    have: playerLogs[8] ?? 0,          qty: pierJujubeQty,   setQty: setPierJujubeQty,   max: playerLogs[8] ?? 0,          sending: pierSendingJujube,   msg: pierJujubeMsg,   onSend: handlePierJujube,   color: '#fb923c', done: jujubeDone10  },
                      { icon: '🌳', label: 'Acacia Logs',    have: playerLogs[2] ?? 0,          qty: pierAcaciaQty,   setQty: setPierAcaciaQty,   max: playerLogs[2] ?? 0,          sending: pierSendingAcacia,   msg: pierAcaciaMsg,   onSend: handlePierAcacia,   color: '#a3e635', done: acaciaDone10  },
                      { icon: '🌲', label: 'Mesquite Logs',  have: playerLogs[1] ?? 0,          qty: pierMesquiteQty, setQty: setPierMesquiteQty, max: playerLogs[1] ?? 0,          sending: pierSendingMesquite, msg: pierMesquiteMsg, onSend: handlePierMesquite, color: '#d97706', done: mesqDone10    },
                      { icon: '🌱', label: 'Moringa Logs',   have: playerLogs[3] ?? 0,          qty: pierMoringaQty,  setQty: setPierMoringaQty,  max: playerLogs[3] ?? 0,          sending: pierSendingMoringa,  msg: pierMoringaMsg,  onSend: handlePierMoringa,  color: '#4ade80', done: morDone10     },
                      { icon: '🌾', label: 'Amaranth Logs',  have: playerLogs[5] ?? 0,          qty: pierAmaranthQty, setQty: setPierAmaranthQty, max: playerLogs[5] ?? 0,          sending: pierSendingAmaranth, msg: pierAmaranthMsg, onSend: handlePierAmaranth, color: '#fbbf24', done: amarDone10    },
                      { icon: '🍃', label: 'Vetiver Logs',   have: playerLogs[7] ?? 0,          qty: pierVetiverQty,  setQty: setPierVetiverQty,  max: playerLogs[7] ?? 0,          sending: pierSendingVetiver,  msg: pierVetiverMsg,  onSend: handlePierVetiver,  color: '#2dd4bf', done: vetDone10     },
                      { icon: '⚗️',  label: 'Ethanol (L)',    have: playerEthanol,               qty: pierEthQty,      setQty: setPierEthQty,      max: playerEthanol,               sending: pierSendingEth,      msg: pierEthMsg,      onSend: handlePierEth,      color: '#22d3ee', done: ethDone10     },
                      { icon: '💧', label: 'Water Barrels',  have: playerBarrels,               qty: pierBarrelQty,   setQty: setPierBarrelQty,   max: playerBarrels,               sending: pierSendingBarrel,   msg: pierBarrelMsg,   onSend: handlePierBarrel,   color: '#38bdf8', done: barDone10     },
                      { icon: '🌊', label: 'Vayal',          have: Math.floor(playerVayal),     qty: pierVayalQty,    setQty: setPierVayalQty,    max: Math.floor(playerVayal),     sending: pierSendingVayal,    msg: pierVayalMsg,    onSend: handlePierVayal,    color: '#34d399', done: vayalDone10   },
                      { icon: '⚒️',  label: 'SHRAM',          have: playerShram.toFixed(4),      qty: pierShramQty,    setQty: setPierShramQty,    max: Math.floor(playerShram),     sending: pierSendingShram,    msg: pierShramMsg,    onSend: handlePierShram,    color: '#e879f9', done: shramDone10, isFloat: true },
                      { icon: '🌊', label: 'WARI',           have: playerWari.toFixed(4),       qty: pierWariQty,     setQty: setPierWariQty,     max: Math.floor(playerWari * 10000) / 10000, sending: pierSendingWari, msg: pierWariMsg, onSend: handlePierWari,    color: '#818cf8', done: wariDone10,  isFloat: true },
                      { icon: '🍎', label: 'Food Packets',   have: Math.floor(playerFoodScore), qty: pierFoodQty,     setQty: setPierFoodQty,     max: Math.floor(playerFoodScore), sending: pierSendingFood,     msg: pierFoodMsg,     onSend: handlePierFood,     color: '#f472b6', done: foodDone10    },
                    ];
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '8px 12px' }}>
                          <p style={{ color: '#fca5a5', fontSize: 10, fontWeight: 900, margin: 0 }}>⛵ FINAL TASK — All 8 species + all resources required</p>
                        </div>
                        {pRows.map(row => row.done ? (
                          <div key={row.label} style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.35)', borderRadius: 10, padding: '11px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 18 }}>{row.icon}</span>
                            <div style={{ flex: 1 }}>
                              <p style={{ color: '#6ee7b7', fontSize: 11, fontWeight: 900, margin: 0 }}>{row.label} — Complete ✓</p>
                              <p style={{ color: '#a7f3d0', fontSize: 9, margin: 0 }}>Quota filled</p>
                            </div>
                            <span style={{ fontSize: 18 }}>✅</span>
                          </div>
                        ) : (
                          <div key={row.label} style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid rgba(255,255,255,0.08)`, borderRadius: 10, padding: '11px 12px' }}>
                            <p style={{ color: row.color, fontSize: 8.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 6px' }}>
                              {row.icon} {row.label} · you have: <span style={{ fontFamily: 'monospace' }}>{typeof row.have === 'number' ? row.have.toLocaleString() : row.have}</span>
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                              <button onClick={() => row.setQty(Math.max(1, row.qty - 1))} disabled={row.qty <= 1}
                                style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: 16, cursor: row.qty <= 1 ? 'default' : 'pointer', opacity: row.qty <= 1 ? 0.3 : 1, lineHeight: 1 }}>−</button>
                              <input type="number" min={1} max={row.max || 1} value={row.qty}
                                onChange={e => { const v = row.isFloat ? parseFloat(e.target.value) : parseInt(e.target.value); if (!isNaN(v) && v > 0) row.setQty(Math.min(v, row.max || 1)); }}
                                style={{ width: 72, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', color: 'white', fontWeight: 900, fontSize: 14, fontFamily: 'monospace', textAlign: 'center', outline: 'none' }}
                              />
                              <button onClick={() => row.setQty(Math.min(row.max, row.qty + 1))} disabled={row.qty >= row.max}
                                style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: 16, cursor: row.qty >= row.max ? 'default' : 'pointer', opacity: row.qty >= row.max ? 0.3 : 1, lineHeight: 1 }}>+</button>
                              <button onClick={() => row.setQty(row.max)} disabled={row.max === 0}
                                style={{ padding: '4px 8px', borderRadius: 6, fontSize: 9, fontWeight: 800, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#ffffff50', cursor: row.max === 0 ? 'default' : 'pointer' }}>max</button>
                            </div>
                            <button onClick={row.onSend} disabled={row.sending || row.max === 0 || row.qty < 1}
                              style={{ width: '100%', padding: '9px', borderRadius: 8, fontSize: 12, fontWeight: 800, background: (row.sending || row.max === 0) ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.07)', border: `1px solid ${(row.sending || row.max === 0) ? 'rgba(255,255,255,0.06)' : row.color + '55'}`, color: (row.sending || row.max === 0) ? '#ffffff25' : row.color, cursor: (row.sending || row.max === 0) ? 'default' : 'pointer', transition: 'all 0.2s' }}>
                              {row.sending ? 'Contributing…' : `${row.icon} Contribute ${row.qty.toLocaleString()} ${row.label}`}
                            </button>
                            {row.msg && <p style={{ color: row.msg.startsWith('✅') ? '#6ee7b7' : '#f87171', fontSize: 10, margin: '6px 0 0', fontWeight: 700 }}>{row.msg}</p>}
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {taskNum === 9 && (() => {
                    const bp = bioProgress;
                    const done = bp?.complete ?? false;
                    if (done) return (
                      <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.35)', borderRadius: 10, padding: '12px', textAlign: 'center' }}>
                        <p style={{ color: '#6ee7b7', fontSize: 15, fontWeight: 900, margin: '0 0 4px' }}>🏭 Bioplastics Manufacturing Live!</p>
                        <p style={{ color: '#a7f3d0', fontSize: 10, margin: 0 }}>Completed by {bp?.completed_by}</p>
                      </div>
                    );
                    if (!waxAcct) return (
                      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '12px' }}>
                        <p style={{ color: '#ffffff35', fontSize: 10, margin: 0 }}>Connect your WAX wallet to contribute.</p>
                      </div>
                    );
                    const mesquiteDone = (bp?.mesquite ?? 0) >= 8000;
                    const acaciaDone   = (bp?.acacia   ?? 0) >= 30000;
                    const moringaDone  = (bp?.moringa  ?? 0) >= 30000;
                    const amaranthDone = (bp?.amaranth ?? 0) >= 150000;
                    const vetiverDone  = (bp?.vetiver  ?? 0) >= 100000;
                    const ethanolDone9 = (bp?.ethanol  ?? 0) >= 15000;
                    const barrelsDone9 = (bp?.barrels  ?? 0) >= 5000;
                    const vayalDone9   = (bp?.vayal    ?? 0) >= 300000;
                    const shramDone9   = (bp?.shram    ?? 0) >= 300;
                    const wariDone9    = (bp?.wari     ?? 0) >= 50;
                    const foodDone9    = (bp?.food     ?? 0) >= 15000000;
                    type BRow = { icon: string; label: string; have: number | string; qty: number; setQty: (v: number) => void; max: number; sending: boolean; msg: string; onSend: () => void; color: string; done: boolean; isFloat?: boolean };
                    const bRows: BRow[] = [
                      { icon: '🌿', label: 'Mesquite Logs',  have: playerLogs[1] ?? 0,          qty: bioMesquiteQty,  setQty: setBioMesquiteQty,  max: playerLogs[1] ?? 0,          sending: bioSendingMesquite,  msg: bioMesquiteMsg,  onSend: handleBioMesquite,  color: '#d97706', done: mesquiteDone },
                      { icon: '🌳', label: 'Acacia Logs',    have: playerLogs[2] ?? 0,          qty: bioAcaciaQty,    setQty: setBioAcaciaQty,    max: playerLogs[2] ?? 0,          sending: bioSendingAcacia,    msg: bioAcaciaMsg,    onSend: handleBioAcacia,    color: '#84cc16', done: acaciaDone   },
                      { icon: '🌱', label: 'Moringa Logs',   have: playerLogs[3] ?? 0,          qty: bioMoringaQty,   setQty: setBioMoringaQty,   max: playerLogs[3] ?? 0,          sending: bioSendingMoringa,   msg: bioMoringaMsg,   onSend: handleBioMoringa,   color: '#4ade80', done: moringaDone  },
                      { icon: '🌾', label: 'Amaranth Logs',  have: playerLogs[5] ?? 0,          qty: bioAmaranthQty,  setQty: setBioAmaranthQty,  max: playerLogs[5] ?? 0,          sending: bioSendingAmaranth,  msg: bioAmaranthMsg,  onSend: handleBioAmaranth,  color: '#f59e0b', done: amaranthDone },
                      { icon: '🍃', label: 'Vetiver Logs',   have: playerLogs[7] ?? 0,          qty: bioVetiverQty,   setQty: setBioVetiverQty,   max: playerLogs[7] ?? 0,          sending: bioSendingVetiver,   msg: bioVetiverMsg,   onSend: handleBioVetiver,   color: '#2dd4bf', done: vetiverDone  },
                      { icon: '⚗️',  label: 'Ethanol (L)',    have: playerEthanol,               qty: bioEthQty,       setQty: setBioEthQty,       max: playerEthanol,               sending: bioSendingEth,       msg: bioEthMsg,       onSend: handleBioEth,       color: '#22d3ee', done: ethanolDone9 },
                      { icon: '💧', label: 'Water Barrels',  have: playerBarrels,               qty: bioBarrelQty,    setQty: setBioBarrelQty,    max: playerBarrels,               sending: bioSendingBarrel,    msg: bioBarrelMsg,    onSend: handleBioBarrel,    color: '#38bdf8', done: barrelsDone9 },
                      { icon: '🌊', label: 'Vayal',          have: Math.floor(playerVayal),     qty: bioVayalQty,     setQty: setBioVayalQty,     max: Math.floor(playerVayal),     sending: bioSendingVayal,     msg: bioVayalMsg,     onSend: handleBioVayal,     color: '#34d399', done: vayalDone9   },
                      { icon: '⚒️',  label: 'SHRAM',          have: playerShram.toFixed(4),      qty: bioShramQty,     setQty: setBioShramQty,     max: Math.floor(playerShram),     sending: bioSendingShram,     msg: bioShramMsg,     onSend: handleBioShram,     color: '#e879f9', done: shramDone9,  isFloat: true },
                      { icon: '🌊', label: 'WARI',           have: playerWari.toFixed(4),       qty: bioWariQty,      setQty: setBioWariQty,      max: Math.floor(playerWari * 10000) / 10000, sending: bioSendingWari, msg: bioWariMsg, onSend: handleBioWari,      color: '#818cf8', done: wariDone9,   isFloat: true },
                      { icon: '🍎', label: 'Food Packets',   have: Math.floor(playerFoodScore), qty: bioFoodQty,      setQty: setBioFoodQty,      max: Math.floor(playerFoodScore), sending: bioSendingFood,      msg: bioFoodMsg,      onSend: handleBioFood,      color: '#f472b6', done: foodDone9    },
                    ];
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {bRows.map(row => row.done ? (
                          <div key={row.label} style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.35)', borderRadius: 10, padding: '11px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 18 }}>{row.icon}</span>
                            <div style={{ flex: 1 }}>
                              <p style={{ color: '#6ee7b7', fontSize: 11, fontWeight: 900, margin: 0 }}>{row.label} — Complete ✓</p>
                              <p style={{ color: '#a7f3d0', fontSize: 9, margin: 0 }}>Quota filled</p>
                            </div>
                            <span style={{ fontSize: 18 }}>✅</span>
                          </div>
                        ) : (
                          <div key={row.label} style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid rgba(255,255,255,0.08)`, borderRadius: 10, padding: '11px 12px' }}>
                            <p style={{ color: row.color, fontSize: 8.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 6px' }}>
                              {row.icon} {row.label} · you have: <span style={{ fontFamily: 'monospace' }}>{typeof row.have === 'number' ? row.have.toLocaleString() : row.have}</span>
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                              <button onClick={() => row.setQty(Math.max(1, row.qty - 1))} disabled={row.qty <= 1}
                                style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: 16, cursor: row.qty <= 1 ? 'default' : 'pointer', opacity: row.qty <= 1 ? 0.3 : 1, lineHeight: 1 }}>−</button>
                              <input type="number" min={1} max={row.max || 1} value={row.qty}
                                onChange={e => { const v = row.isFloat ? parseFloat(e.target.value) : parseInt(e.target.value); if (!isNaN(v) && v > 0) row.setQty(Math.min(v, row.max || 1)); }}
                                style={{ width: 72, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', color: 'white', fontWeight: 900, fontSize: 14, fontFamily: 'monospace', textAlign: 'center', outline: 'none' }}
                              />
                              <button onClick={() => row.setQty(Math.min(row.max, row.qty + 1))} disabled={row.qty >= row.max}
                                style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: 16, cursor: row.qty >= row.max ? 'default' : 'pointer', opacity: row.qty >= row.max ? 0.3 : 1, lineHeight: 1 }}>+</button>
                              <button onClick={() => row.setQty(row.max)} disabled={row.max === 0}
                                style={{ padding: '4px 8px', borderRadius: 6, fontSize: 9, fontWeight: 800, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#ffffff50', cursor: row.max === 0 ? 'default' : 'pointer' }}>max</button>
                            </div>
                            <button onClick={row.onSend} disabled={row.sending || row.max === 0 || row.qty < 1}
                              style={{ width: '100%', padding: '9px', borderRadius: 8, fontSize: 12, fontWeight: 800, background: (row.sending || row.max === 0) ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.07)', border: `1px solid ${(row.sending || row.max === 0) ? 'rgba(255,255,255,0.06)' : row.color + '55'}`, color: (row.sending || row.max === 0) ? '#ffffff25' : row.color, cursor: (row.sending || row.max === 0) ? 'default' : 'pointer', transition: 'all 0.2s' }}>
                              {row.sending ? 'Contributing…' : `${row.icon} Contribute ${row.qty.toLocaleString()} ${row.label}`}
                            </button>
                            {row.msg && <p style={{ color: row.msg.startsWith('✅') ? '#6ee7b7' : '#f87171', fontSize: 10, margin: '6px 0 0', fontWeight: 700 }}>{row.msg}</p>}
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Place packets (task 1 only — others show coming soon) */}
                  {taskNum === 1 ? (
                    <div style={{ background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: 10, padding: '12px' }}>
                      <p style={{ color: '#6ee7b7', fontSize: 8.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 8px' }}>
                        {waxAcct ? `Place Packets · ${waxAcct}` : 'Place Packets'}
                      </p>
                      {!waxAcct ? (
                        <p style={{ color: '#ffffff30', fontSize: 11, margin: 0 }}>Connect your WAX wallet to place packets.</p>
                      ) : (
                        <>
                          <p style={{ color: '#ffffff50', fontSize: 10, margin: '0 0 10px' }}>
                            Your packets: <span style={{ color: '#a7f3d0', fontWeight: 800 }}>{packets}</span>
                          </p>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                            <button onClick={() => setPlaceQty(q => Math.max(1, q - 1))} disabled={placeQty <= 1}
                              style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: 16, cursor: placeQty <= 1 ? 'default' : 'pointer', opacity: placeQty <= 1 ? 0.3 : 1, lineHeight: 1 }}>−</button>
                            <input
                              type="number" min={1} max={packets || 1} value={placeQty}
                              onChange={e => { const v = parseInt(e.target.value); if (!isNaN(v)) setPlaceQty(Math.min(Math.max(1, v), packets || 1)); }}
                              style={{ width: 64, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', color: 'white', fontWeight: 900, fontSize: 14, fontFamily: 'monospace', textAlign: 'center', outline: 'none' }}
                            />
                            <button onClick={() => setPlaceQty(q => Math.min(packets, q + 1))} disabled={placeQty >= packets}
                              style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: 16, cursor: placeQty >= packets ? 'default' : 'pointer', opacity: placeQty >= packets ? 0.3 : 1, lineHeight: 1 }}>+</button>
                            <button onClick={() => setPlaceQty(packets)} disabled={packets === 0}
                              style={{ padding: '4px 8px', borderRadius: 6, fontSize: 9, fontWeight: 800, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#ffffff50', cursor: packets === 0 ? 'default' : 'pointer', letterSpacing: 0.5 }}>max</button>
                          </div>
                          <button onClick={handlePlace} disabled={placing || packets === 0 || placeQty < 1}
                            style={{ width: '100%', padding: '9px', borderRadius: 8, fontSize: 12, fontWeight: 800, background: (placing || packets === 0) ? 'rgba(16,185,129,0.1)' : 'rgba(16,185,129,0.18)', border: `1px solid ${(placing || packets === 0) ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.4)'}`, color: (placing || packets === 0) ? '#10b98150' : '#6ee7b7', cursor: (placing || packets === 0) ? 'default' : 'pointer', transition: 'all 0.2s' }}>
                            {placing ? 'Placing…' : `📦 Place ${placeQty} Packet${placeQty > 1 ? 's' : ''}`}
                          </button>
                          {placeMsg && <p style={{ color: placeMsg.startsWith('✅') ? '#6ee7b7' : '#f87171', fontSize: 10, margin: '8px 0 0', fontWeight: 700 }}>{placeMsg}</p>}
                        </>
                      )}
                    </div>
                  ) : (() => {
                    // Tasks 2–4: show contribution UI when worker is present, else gate message
                    const myWorker2 = waxAcct ? workers.find(w => w.wax_account === waxAcct) : null;
                    const workerHere = myWorker2
                      ? myWorker2.dest_x === activeZone.cx && myWorker2.dest_y === activeZone.cy && myWorker2.state !== 0
                      : false;
                    const task1Done = task1ZoneTotal >= 10000;

                    if (task1Done && workerHere && waxAcct) {
                      // ── Live contribution UI ──
                      return (
                        <div style={{ background: `${tc.faint}0.05)`, border: `1px solid ${tc.faint}0.2)`, borderRadius: 10, padding: '12px' }}>
                          <p style={{ color: `${tc.faint}0.7)`, fontSize: 8.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 8px' }}>
                            🧑 Worker Active · Place Packets
                          </p>
                          <p style={{ color: '#ffffff50', fontSize: 10, margin: '0 0 10px' }}>
                            Your packets: <span style={{ color: '#a7f3d0', fontWeight: 800 }}>{packets}</span>
                          </p>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                            <button onClick={() => setContribQty(q => Math.max(1, q - 1))} disabled={contribQty <= 1}
                              style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: 16, cursor: contribQty <= 1 ? 'default' : 'pointer', opacity: contribQty <= 1 ? 0.3 : 1, lineHeight: 1 }}>−</button>
                            <input
                              type="number" min={1} max={packets || 1} value={contribQty}
                              onChange={e => { const v = parseInt(e.target.value); if (!isNaN(v)) setContribQty(Math.min(Math.max(1, v), packets || 1)); }}
                              style={{ width: 64, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', color: 'white', fontWeight: 900, fontSize: 14, fontFamily: 'monospace', textAlign: 'center', outline: 'none' }}
                            />
                            <button onClick={() => setContribQty(q => Math.min(packets, q + 1))} disabled={contribQty >= packets}
                              style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: 16, cursor: contribQty >= packets ? 'default' : 'pointer', opacity: contribQty >= packets ? 0.3 : 1, lineHeight: 1 }}>+</button>
                            <button onClick={() => setContribQty(packets)} disabled={packets === 0}
                              style={{ padding: '4px 8px', borderRadius: 6, fontSize: 9, fontWeight: 800, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#ffffff50', cursor: packets === 0 ? 'default' : 'pointer', letterSpacing: 0.5 }}>max</button>
                          </div>
                          <button
                            onClick={() => handleContrib(activeZone.zone_id)}
                            disabled={contributing || packets === 0 || contribQty < 1}
                            style={{
                              width: '100%', padding: '9px', borderRadius: 8, fontSize: 12, fontWeight: 800,
                              background: (contributing || packets === 0) ? `${tc.faint}0.07)` : `${tc.faint}0.18)`,
                              border: `1px solid ${tc.faint}${(contributing || packets === 0) ? 0.1 : 0.4})`,
                              color: (contributing || packets === 0) ? `${tc.faint}0.35)` : tc.accent,
                              cursor: (contributing || packets === 0) ? 'default' : 'pointer', transition: 'all 0.2s',
                            }}>
                            {contributing ? 'Placing…' : `📦 Place ${contribQty} Packet${contribQty > 1 ? 's' : ''}`}
                          </button>
                          {contribMsg && <p style={{ color: contribMsg.startsWith('✅') ? '#6ee7b7' : '#f87171', fontSize: 10, margin: '8px 0 0', fontWeight: 700 }}>{contribMsg}</p>}

                          {/* Log delivery form — Task 3 (acacia) and Task 4 */}
                          {(taskNum === 3 || taskNum === 4) && (
                            <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${tc.faint}0.15)` }}>
                              <p style={{ color: `${tc.faint}0.7)`, fontSize: 8.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 8px' }}>
                                🪵 Deliver Logs
                              </p>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                                {(taskNum === 3 ? [
                                  { si: 2, name: 'Acacia',   emoji: '🌳', need: 15 },
                                ] : [
                                  { si: 3, name: 'Moringa',  emoji: '🌿', need: 20 },
                                  { si: 1, name: 'Mesquite', emoji: '🌲', need: 30 },
                                  { si: 6, name: 'Neem',     emoji: '🌴', need: 10 },
                                ] as { si: number; name: string; emoji: string; need: number }[]).map(sp => {
                                  const have = playerLogs[sp.si] ?? 0;
                                  const active = logSpecies === sp.si;
                                  return (
                                    <button key={sp.si} onClick={() => { setLogSpecies(sp.si); setLogQty(1); setLogMsg(''); }}
                                      style={{
                                        padding: '6px 10px', borderRadius: 8, fontSize: 10, fontWeight: 700,
                                        background: active ? `${tc.faint}0.15)` : 'rgba(255,255,255,0.03)',
                                        border: `1px solid ${active ? tc.faint + '0.4)' : 'rgba(255,255,255,0.08)'}`,
                                        color: active ? tc.accent : '#ffffff60', cursor: 'pointer',
                                      }}>
                                      {sp.emoji} {sp.name} <span style={{ fontFamily: 'monospace', marginLeft: 4, color: have > 0 ? '#a7f3d0' : '#ffffff30' }}>{have}</span>
                                    </button>
                                  );
                                })}
                              </div>
                              {(() => {
                                const available = playerLogs[logSpecies] ?? 0;
                                const speciesName = ({ 1:'Mesquite', 2:'Acacia', 3:'Moringa', 6:'Neem' } as Record<number, string>)[logSpecies] ?? 'logs';
                                return (
                                  <>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                      <button onClick={() => setLogQty(q => Math.max(1, q - 1))} disabled={logQty <= 1}
                                        style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: 16, cursor: logQty <= 1 ? 'default' : 'pointer', opacity: logQty <= 1 ? 0.3 : 1, lineHeight: 1 }}>−</button>
                                      <input type="number" min={1} max={available || 1} value={logQty}
                                        onChange={e => { const v = parseInt(e.target.value); if (!isNaN(v)) setLogQty(Math.min(Math.max(1, v), available || 1)); }}
                                        style={{ width: 64, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', color: 'white', fontWeight: 900, fontSize: 14, fontFamily: 'monospace', textAlign: 'center', outline: 'none' }}
                                      />
                                      <button onClick={() => setLogQty(q => Math.min(available, q + 1))} disabled={logQty >= available}
                                        style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: 16, cursor: logQty >= available ? 'default' : 'pointer', opacity: logQty >= available ? 0.3 : 1, lineHeight: 1 }}>+</button>
                                      <button onClick={() => setLogQty(available)} disabled={available === 0}
                                        style={{ padding: '4px 8px', borderRadius: 6, fontSize: 9, fontWeight: 800, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#ffffff50', cursor: available === 0 ? 'default' : 'pointer', letterSpacing: 0.5 }}>max</button>
                                    </div>
                                    <button onClick={() => handleLogDeliver(activeZone.zone_id)}
                                      disabled={logSending || available === 0 || logQty < 1}
                                      style={{
                                        width: '100%', padding: '9px', borderRadius: 8, fontSize: 12, fontWeight: 800,
                                        background: (logSending || available === 0) ? `${tc.faint}0.07)` : `${tc.faint}0.18)`,
                                        border: `1px solid ${tc.faint}${(logSending || available === 0) ? 0.1 : 0.4})`,
                                        color: (logSending || available === 0) ? `${tc.faint}0.35)` : tc.accent,
                                        cursor: (logSending || available === 0) ? 'default' : 'pointer', transition: 'all 0.2s',
                                      }}>
                                      {logSending ? 'Delivering…' : `🪵 Deliver ${logQty} ${speciesName} log${logQty > 1 ? 's' : ''}`}
                                    </button>
                                    {logMsg && <p style={{ color: logMsg.startsWith('✅') ? '#6ee7b7' : '#f87171', fontSize: 10, margin: '8px 0 0', fontWeight: 700 }}>{logMsg}</p>}
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    } else if (task1Done && !workerHere) {
                      return (
                        <div style={{ background: 'rgba(255,255,255,0.02)', border: 'rgba(16,185,129,0.2) 1px solid', borderRadius: 10, padding: '12px' }}>
                          <p style={{ color: '#6ee7b7', fontSize: 10, lineHeight: 1.6, margin: '0 0 6px' }}>
                            ✓ Task 1 complete — this task is now open.
                          </p>
                          <p style={{ color: '#ffffff35', fontSize: 9.5, lineHeight: 1.6, margin: 0 }}>
                            {waxAcct
                              ? 'Send your council worker to this zone to start contributing.'
                              : 'Connect your WAX wallet, then send a council worker here.'}
                          </p>
                        </div>
                      );
                    } else {
                      return (
                        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '12px' }}>
                          <p style={{ color: '#ffffff20', fontSize: 10, lineHeight: 1.6, margin: 0 }}>
                            Complete Task 1 (10,000 food packets) first to unlock this task.
                            <span style={{ display: 'block', marginTop: 4, color: '#f59e0b50', fontFamily: 'monospace' }}>
                              {task1ZoneTotal.toLocaleString()} / 10,000
                            </span>
                          </p>
                        </div>
                      );
                    }
                  })()}

                  {/* Send Worker Here */}
                  {(() => {
                    const myWorker = waxAcct ? workers.find(w => w.wax_account === waxAcct) : null;
                    if (!myWorker) return null;
                    const atZone = myWorker.dest_x === activeZone.cx && myWorker.dest_y === activeZone.cy && myWorker.state !== 0;
                    return (
                      <div style={{ background: `${tc.faint}0.06)`, border: `1px solid ${tc.faint}0.2)`, borderRadius: 10, padding: '11px 12px' }}>
                        <p style={{ color: `${tc.faint}0.7)`, fontSize: 8.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 8px' }}>Your Council Worker</p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <span style={{ fontSize: 18 }}>🧑</span>
                          <div>
                            <p style={{ color: '#ffffffcc', fontSize: 11, fontWeight: 700, margin: 0 }}>{myWorker.worker_name}</p>
                            <p style={{ color: '#ffffff40', fontSize: 9.5, margin: 0, fontFamily: 'monospace' }}>
                              {myWorker.state === 0 ? 'Idle' : myWorker.state === 1 ? 'Moving…' : myWorker.state === 3 ? '🎓 In Training' : 'Working'}
                              {' · '}({myWorker.pos_x}, {myWorker.pos_y})
                            </p>
                          </div>
                        </div>
                        {myWorker.state === 3 ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', borderRadius: 7, background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.25)' }}>
                            <span style={{ fontSize: 12 }}>🎓</span>
                            <span style={{ color: '#a78bfa', fontSize: 10.5, fontWeight: 700 }}>Worker is in Training Academy — immobilised</span>
                          </div>
                        ) : atZone ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', borderRadius: 7, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)' }}>
                            <span style={{ fontSize: 12 }}>✓</span>
                            <span style={{ color: '#6ee7b7', fontSize: 10.5, fontWeight: 700 }}>Worker active at this zone</span>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleSendWorker(activeZone)}
                            disabled={workerSending || myWorker.state === 1}
                            style={{
                              width: '100%', padding: '8px', borderRadius: 8, fontSize: 11, fontWeight: 800,
                              background: (workerSending || myWorker.state === 1) ? `${tc.faint}0.06)` : `${tc.faint}0.15)`,
                              border: `1px solid ${(workerSending || myWorker.state === 1) ? `${tc.faint}0.1)` : `${tc.faint}0.4)`}`,
                              color: (workerSending || myWorker.state === 1) ? `${tc.faint}0.35)` : tc.accent,
                              cursor: (workerSending || myWorker.state === 1) ? 'default' : 'pointer',
                              transition: 'all 0.2s',
                            }}>
                            {workerSending ? 'Sending…' : myWorker.state === 1 ? '🧑 Worker en route…' : '🧑 Send Worker Here'}
                          </button>
                        )}
                        {workerMsg && <p style={{ color: workerMsg.startsWith('✅') ? '#6ee7b7' : '#f87171', fontSize: 10, margin: '7px 0 0', fontWeight: 700 }}>{workerMsg}</p>}
                      </div>
                    );
                  })()}

                  {/* Zone leaderboard — hidden for task 7 (chem factory uses per-resource progress instead) */}
                  {taskNum !== 7 && <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '12px', flex: 1 }}>
                    <p style={{ color: '#ffffff35', fontSize: 8.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 8px' }}>Task {taskNum} Leaderboard</p>
                    {lbZoneId !== activeZone.zone_id ? (
                      <p style={{ color: '#ffffff15', fontSize: 10, margin: 0 }}>Loading…</p>
                    ) : lbRows.length === 0 ? (
                      <p style={{ color: '#ffffff20', fontSize: 10, margin: 0 }}>No placements yet</p>
                    ) : (
                      lbRows.map((r, i) => {
                        const isMe = r.wax_account === waxAcct;
                        return (
                          <div key={r.wax_account} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', borderBottom: i < lbRows.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                            <span style={{ color: i < 3 ? ['#f59e0b','#94a3b8','#b45309'][i] : '#ffffff25', fontSize: 9.5, fontWeight: 900, width: 14, textAlign: 'right' }}>{i + 1}</span>
                            <span style={{ flex: 1, color: isMe ? tc.accent : '#ffffffaa', fontSize: 10, fontFamily: 'monospace', fontWeight: isMe ? 800 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.wax_account}</span>
                            <span style={{ color: isMe ? tc.accent : '#ffffff55', fontSize: 10, fontWeight: 800, fontFamily: 'monospace' }}>{r.placed.toLocaleString()}</span>
                          </div>
                        );
                      })
                    )}
                  </div>}
                </>
              );
            })()
          ) : (
            <p style={{ color: '#ffffff15', fontSize: 11, margin: 0, lineHeight: 1.7 }}>
              Click a highlighted zone on the map to see details.
            </p>
          )}
        </div>

        {/* ── Map area ── */}
        <div style={{
          flex: isMobile ? 'none' : 1,
          order: isMobile ? 0 : 1,
          overflow: isMobile ? 'hidden' : 'auto',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: isMobile ? '0' : '24px',
          background: 'radial-gradient(ellipse 80% 70% at 50% 55%, #2a1e0a 0%, #1a1208 60%, #0d0a04 100%)',
        }}
          onTouchStart={isMobile ? handleMapTouchStart : undefined}
          onTouchMove={isMobile ? handleMapTouchMove : undefined}
          onTouchEnd={isMobile ? handleMapTouchEnd : undefined}
        >
          {/* Map wrapper: <img> base + SVG overlay */}
          <div style={{
            position: 'relative',
            lineHeight: 0,
            boxShadow: isMobile ? '0 0 20px 6px #f59e0b14' : '0 0 80px 20px #f59e0b18, 0 0 0 2px #f59e0b30, 0 8px 60px rgba(0,0,0,0.8)',
            borderRadius: 4,
            width: isMobile ? '100%' : undefined,
            transform: isMobile ? `scale(${mapScale}) translate(${mapTx / mapScale}px, ${mapTy / mapScale}px)` : undefined,
            transformOrigin: 'center center',
            transition: pinchRef.current ? 'none' : 'transform 0.15s ease',
            willChange: 'transform',
          }}>
            {/* ── Base map image — swaps on task completion ── */}
            <img
              src={busStopDone
                ? `${process.env.PUBLIC_URL}/images/samatvam-base-map-water-harvester-bus-stop.png`
                : harvestWaterDone
                ? `${process.env.PUBLIC_URL}/images/samatvam-base-map-water-harvester.png`
                : '/samatvam/samatvam-1.png'}
              alt="Samatvam City Map"
              style={{
                display: 'block',
                width: '100%',
                height: 'auto',
                maxHeight: isMobile ? undefined : 'calc(100vh - 130px)',
                borderRadius: 4,
                userSelect: 'none',
                objectFit: 'contain',
              }}
            />
            {/* ── SVG overlay: zones, workers, labels (transparent background) ── */}
            <svg
              ref={svgRef}
              viewBox={`0 0 ${MAP_W} ${MAP_H}`}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                borderRadius: 4,
                userSelect: 'none',
                overflow: 'visible',
              }}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >

              {/* ── Clickable zones ── */}
              {zones.map(zone => {
                const isHovered = hoverZone === zone.zone_id;
                const isActive  = activeZone?.zone_id === zone.zone_id;
                // Convert bottom-left cy → SVG top-left y
                const svgX = zone.cx - zone.w / 2;
                const svgY = (MAP_H - zone.cy) - zone.h / 2;
                return (
                  <g key={zone.id}>
                    {/* Glow blur behind the rect */}
                    {(isActive || isHovered) && (
                      <rect
                        x={svgX - 6} y={svgY - 6}
                        width={zone.w + 12} height={zone.h + 12}
                        rx={10}
                        fill="rgba(16,185,129,0.18)"
                        style={{ filter: 'blur(8px)' }}
                        pointerEvents="none"
                      />
                    )}
                    <rect
                      x={svgX} y={svgY}
                      width={zone.w} height={zone.h}
                      rx={6}
                      fill={isActive ? 'rgba(16,185,129,0.20)' : isHovered ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.05)'}
                      stroke={isActive ? 'rgba(16,185,129,0.95)' : isHovered ? 'rgba(16,185,129,0.65)' : 'rgba(16,185,129,0.35)'}
                      strokeWidth={isActive ? 2.5 : 1.5}
                      cursor="pointer"
                      onMouseEnter={() => setHoverZone(zone.zone_id)}
                      onMouseLeave={() => setHoverZone(null)}
                      onClick={(e) => { e.stopPropagation(); setActiveZone(prev => { const next = prev?.zone_id === zone.zone_id ? null : zone; if (next) fetchLeaderboard(next.zone_id); return next; }); }}
                    />
                    {/* Pulse dot at centre */}
                    <circle
                      cx={zone.cx} cy={MAP_H - zone.cy}
                      r={isActive ? 5 : 3.5}
                      fill={isActive ? '#10b981' : '#34d399'}
                      opacity={isActive ? 0.9 : 0.6}
                      pointerEvents="none"
                    />
                  </g>
                );
              })}

              {/* ── Council Workers (DB-driven, animated) ── */}
              {workers.map(w => (
                <WorkerIcon key={w.id} w={w} tick={workerTick} />
              ))}

              {/* ── DB-driven labels (all building pins, including Council House) ── */}
              {dbLabels.map(lm => {
                const sx = lm.cx;
                const sy = MAP_H - lm.cy;
                const isHov = hoveredLandmark === lm.label_id;
                const hasAction = !!lm.action;
                const lblW = lm.label.length * 6.5 + 28;
                const lblX = Math.min(Math.max(sx - lblW / 2, 4), MAP_W - lblW - 4);
                const lblY = sy - 26;
                const handleLabelClick = () => {
                  if (lm.action === 'council-house') setCouncilModalOpen(true);
                  if (lm.action === 'biorefinery') setBiorefineryModalOpen(true);
                  if (lm.action === 'power-house') setPowerHouseModalOpen(true);
                  if (lm.action === 'bus-stop') setBusStopModalOpen(true);
                  if (lm.action === 'water-harvester') setWaterHarvesterModalOpen(true);
                  if (lm.action === 'university') onOpenUniversity?.();
                  if (lm.action === 'warehouse') onOpenWarehouse?.();
                };
                return (
                  <g key={lm.label_id}
                    onMouseEnter={() => setHoveredLandmark(lm.label_id)}
                    onMouseLeave={() => setHoveredLandmark(null)}
                    onClick={hasAction ? handleLabelClick : undefined}
                    style={{ cursor: hasAction ? 'pointer' : 'default' }}
                  >
                    <circle cx={sx} cy={sy} r={14} fill="transparent" />
                    <circle cx={sx} cy={sy} r={isHov ? 5 : 3.5}
                      fill={isHov ? '#fde68a' : '#f59e0b80'}
                      stroke={isHov ? '#f59e0b' : 'rgba(245,158,11,0.3)'}
                      strokeWidth={1.5}
                      style={{ transition: 'r 0.15s, fill 0.15s' }}
                    />
                    <text x={sx} y={sy - 6} textAnchor="middle" fontSize={12} style={{ userSelect: 'none' }}>{lm.icon}</text>
                    {isHov && (
                      <g pointerEvents="none">
                        <rect x={lblX} y={lblY - 14} width={lblW} height={18} rx={5}
                          fill="rgba(10,8,3,0.92)" stroke="rgba(245,158,11,0.45)" strokeWidth={1}
                        />
                        <text x={lblX + 14} y={lblY - 2} fontFamily="system-ui,sans-serif" fontSize={10} fontWeight={800} fill="#fde68a">
                          {lm.icon} {lm.label}
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}

              {/* ── Active City Task — wooden map-board style ── */}
              {activeTask && !taskCompletion && (
                <g style={{ cursor: 'pointer' }} transform={`translate(${Math.max(2, Math.min(activeTask.map_x - 112, MAP_W - 226))},${Math.max(2, (896 - activeTask.map_y) - 427)})`} onClick={e => { e.stopPropagation(); setTaskModalOpen(true); }}>
                  {/* Slow warm pulse ring */}
                  <rect x={0} y={348} width={224} height={158} rx={10}
                    fill="none" stroke="#8B5E3C" strokeWidth={2} opacity={0.6}>
                    <animate attributeName="opacity" values="0.25;0.75;0.25" dur="3.2s" repeatCount="indefinite"/>
                    <animate attributeName="stroke-width" values="1.5;3;1.5" dur="3.2s" repeatCount="indefinite"/>
                  </rect>

                  {/* Wood plank — dark base */}
                  <rect x={3} y={351} width={218} height={152} rx={8} fill="#1c0f07"/>
                  {/* Wood grain lines */}
                  <line x1={3} y1={363} x2={221} y2={363} stroke="#2a1509" strokeWidth={1.5}/>
                  <line x1={3} y1={378} x2={221} y2={378} stroke="#251208" strokeWidth={1}/>
                  <line x1={3} y1={420} x2={221} y2={420} stroke="#2a1509" strokeWidth={1}/>
                  <line x1={3} y1={455} x2={221} y2={455} stroke="#251208" strokeWidth={1}/>
                  <line x1={3} y1={488} x2={221} y2={488} stroke="#2a1509" strokeWidth={1}/>
                  {/* Warm wood mid-tone overlay */}
                  <rect x={3} y={351} width={218} height={152} rx={8} fill="url(#woodGrain)" opacity={0.18}/>

                  {/* Outer frame — carved edge (double border) */}
                  <rect x={3} y={351} width={218} height={152} rx={8} fill="none" stroke="#6b3d1e" strokeWidth={3}/>
                  <rect x={6} y={354} width={212} height={146} rx={6} fill="none" stroke="#c8956c" strokeWidth={0.8} opacity={0.5}/>

                  {/* Corner nail dots */}
                  <circle cx={12} cy={360} r={2.5} fill="#a0622a" opacity={0.8}/>
                  <circle cx={212} cy={360} r={2.5} fill="#a0622a" opacity={0.8}/>
                  <circle cx={12} cy={494} r={2.5} fill="#a0622a" opacity={0.8}/>
                  <circle cx={212} cy={494} r={2.5} fill="#a0622a" opacity={0.8}/>

                  {/* Parchment label pinned to top */}
                  <rect x={18} y={356} width={188} height={38} rx={4} fill="#d4a855" opacity={0.92}/>
                  <rect x={18} y={356} width={188} height={38} rx={4} fill="none" stroke="#8B5E3C" strokeWidth={1}/>
                  {/* Parchment texture lines */}
                  <line x1={22} y1={366} x2={202} y2={366} stroke="#b88a3a" strokeWidth={0.5} opacity={0.4}/>
                  <line x1={22} y1={374} x2={202} y2={374} stroke="#b88a3a" strokeWidth={0.5} opacity={0.4}/>
                  <line x1={22} y1={382} x2={202} y2={382} stroke="#b88a3a" strokeWidth={0.5} opacity={0.4}/>
                  {/* Title on parchment — dark ink */}
                  <text x={112} y={369} textAnchor="middle" fontFamily="Georgia,serif" fontSize={10} fontWeight={700} fill="#1a0a04" letterSpacing={0.5}>{activeTask.label.length > 20 ? activeTask.label.substring(0, 20) + '…' : activeTask.label}</text>
                  <text x={112} y={382} textAnchor="middle" fontFamily="Georgia,serif" fontSize={7.5} fontWeight={400} fill="#4a2010" letterSpacing={1.8}>CITY TASK  ·  FIRST COME  ·  ONE TIME</text>

                  {/* Pin holding parchment */}
                  <circle cx={112} cy={357} r={3} fill="#c0392b" stroke="#8B1a10" strokeWidth={0.8}/>
                  <circle cx={112} cy={357} r={1.2} fill="#ff6b5b" opacity={0.8}/>

                  {/* Divider — rope/cord style */}
                  <line x1={18} y1={402} x2={206} y2={402} stroke="#6b3d1e" strokeWidth={1.5}/>
                  <line x1={18} y1={403.5} x2={206} y2={403.5} stroke="#c8956c" strokeWidth={0.5} opacity={0.4}/>
                  <text x={112} y={401} textAnchor="middle" fontFamily="Georgia,serif" fontSize={9} fill="#8B5E3C" opacity={0.7}>✦ Requirements ✦</text>

                  {/* Requirements — dynamic */}
                  <text x={24} y={418} fontFamily="Georgia,serif" fontSize={10} fill="#c8956c">▸  {activeTask.food_req} Food Packets</text>
                  {activeTask.cart_req && <text x={24} y={434} fontFamily="Georgia,serif" fontSize={10} fill="#c8956c">▸  Hand Cart</text>}
                  {activeTask.machine_req && <text x={24} y={activeTask.cart_req ? 450 : 434} fontFamily="Georgia,serif" fontSize={10} fill="#c8956c">▸  Water Harvesting Machine</text>}

                  {/* Reward line */}
                  <line x1={18} y1={444} x2={206} y2={444} stroke="#6b3d1e" strokeWidth={1} opacity={0.6}/>
                  <text x={24} y={456} fontFamily="Georgia,serif" fontSize={10} fontWeight={700} fill="#d4a855">✦  Reward:  Season Zero Champion</text>

                  {/* Stamped button — leather look */}
                  <rect x={34} y={463} width={156} height={24} rx={5} fill="#3d1f0a" stroke="#8B5E3C" strokeWidth={1.5}/>
                  <rect x={35} y={464} width={154} height={11} rx={3} fill="#5c2e0f" opacity={0.5}/>
                  <text x={112} y={479} textAnchor="middle" fontFamily="Georgia,serif" fontSize={9.5} fontWeight={700} fill="#d4a855" letterSpacing={1.2}>▶  CLICK TO COMPLETE</text>
                </g>
              )}
              {activeTask && taskCompletion && (
                /* Completed — aged parchment sealed board */
                <g style={{ cursor: 'pointer' }} transform={`translate(${Math.max(2, Math.min(activeTask.map_x - 112, MAP_W - 226))},${Math.max(2, (896 - activeTask.map_y) - 427)})`} onClick={e => { e.stopPropagation(); setTaskModalOpen(true); }}>
                  <rect x={3} y={378} width={218} height={88} rx={8} fill="#0f1f0d" stroke="#2d5a27" strokeWidth={2.5}/>
                  <rect x={6} y={381} width={212} height={82} rx={6} fill="none" stroke="#4a8a42" strokeWidth={0.7} opacity={0.5}/>
                  <circle cx={12} cy={387} r={2.5} fill="#2d5a27" opacity={0.8}/>
                  <circle cx={212} cy={387} r={2.5} fill="#2d5a27" opacity={0.8}/>
                  <circle cx={12} cy={457} r={2.5} fill="#2d5a27" opacity={0.8}/>
                  <circle cx={212} cy={457} r={2.5} fill="#2d5a27" opacity={0.8}/>
                  {/* Parchment seal */}
                  <rect x={24} y={384} width={176} height={28} rx={4} fill="#c8d4a0" opacity={0.88}/>
                  <rect x={24} y={384} width={176} height={28} rx={4} fill="none" stroke="#4a8a42" strokeWidth={0.8}/>
                  <text x={112} y={394} textAnchor="middle" fontFamily="Georgia,serif" fontSize={9} fill="#1a3a14" letterSpacing={1.5}>✦  TASK  CLEARED  ✦</text>
                  <text x={112} y={404} textAnchor="middle" fontFamily="Georgia,serif" fontSize={7.5} fill="#2d5a27" letterSpacing={0.8}>{activeTask.label.substring(0, 22)} · S0</text>
                  <text x={112} y={426} textAnchor="middle" fontFamily="Georgia,serif" fontSize={9} fill="#6aaa62">Cleared by</text>
                  <text x={112} y={440} textAnchor="middle" fontFamily="monospace" fontSize={10} fontWeight={700} fill="#86efac">{taskCompletion.completed_by}</text>
                  <text x={112} y={456} textAnchor="middle" fontFamily="Georgia,serif" fontSize={8} fill="#3a6a34" opacity={0.7}>tap for details</text>
                </g>
              )}

              {/* ── Coordinate readout (SVG text, bottom-left) ── */}
              {coords && (
                <g pointerEvents="none">
                  <rect x={8} y={MAP_H - 28} width={96} height={20} rx={4} fill="rgba(10,8,3,0.82)" stroke="rgba(245,158,11,0.35)" strokeWidth={1}/>
                  <text x={56} y={MAP_H - 14} fontFamily="monospace" fontSize={11} fontWeight={700} fill="#f59e0b" textAnchor="middle">
                    {coords.x}, {coords.y}
                  </text>
                </g>
              )}
            </svg>
          </div>
        </div>

        {/* ── Right panel: Council Workers ── */}
        <div style={{
          width: isMobile ? '100%' : 262,
          flexShrink: 0,
          order: isMobile ? 2 : 2,
          borderLeft: isMobile ? 'none' : '1px solid rgba(245,158,11,0.1)',
          borderTop: isMobile ? '1px solid rgba(245,158,11,0.1)' : 'none',
          background: 'rgba(10,8,3,0.72)',
          display: 'flex', flexDirection: 'column',
          padding: '16px 14px',
          gap: 10,
          overflowY: isMobile ? 'visible' : 'auto',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <span style={{ fontSize: 18 }}>🏛️</span>
            <div>
              <p style={{ color: '#fde68a', fontSize: 13, fontWeight: 900, margin: 0, letterSpacing: -0.2 }}>Council Workers</p>
              <p style={{ color: '#f59e0b50', fontSize: 9, margin: 0, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase' }}>Distribution Queue</p>
            </div>
          </div>

          {/* Task 1 progress */}
          <div style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.18)', borderRadius: 10, padding: '10px 12px' }}>
            <p style={{ color: '#f59e0b', fontSize: 8.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 5px' }}>Task 1 Progress</p>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 6 }}>
              <span style={{ color: '#fff', fontSize: 18, fontWeight: 900, fontFamily: 'monospace' }}>{task1ZoneTotal.toLocaleString()}</span>
              <span style={{ color: '#f59e0b40', fontSize: 11, fontWeight: 700 }}>/ 10,000</span>
            </div>
            <div style={{ height: 4, background: 'rgba(255,255,255,0.07)', borderRadius: 999, overflow: 'hidden', marginBottom: 5 }}>
              <div style={{ height: '100%', width: `${Math.min(100, (task1ZoneTotal / 10000) * 100)}%`, background: task1ZoneTotal >= 10000 ? 'linear-gradient(90deg,#10b981,#34d399)' : 'linear-gradient(90deg,#b45309,#f59e0b)', borderRadius: 999, transition: 'width 0.6s ease' }} />
            </div>
            {task1ZoneTotal >= 10000 ? (
              <span style={{ fontSize: 9.5, fontWeight: 800, color: '#10b981', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 5, padding: '2px 7px' }}>✓ Task Complete — Distribution Active</span>
            ) : (
              <span style={{ fontSize: 9.5, color: '#f59e0b80', fontWeight: 600 }}>Distribution begins at 10,000</span>
            )}
          </div>

          {/* Distribution rules */}
          <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '10px 12px' }}>
            <p style={{ color: '#ffffff40', fontSize: 8.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 8px' }}>How It Works</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                { icon: '🏛️', text: '30 council workers for the city' },
                { icon: '📦', text: '1 worker per 1,000 packets · max 3 per player' },
                { icon: '⚡', text: '5 workers distributed per day' },
                { icon: '🕛', text: 'Distributed at 12:00 AM daily · max 1 per player per day' },
                { icon: '⏱', text: '≥1,000 pkts → Day 0 · <1,000 → wait days = ⌈1000÷pkts⌉−1' },
              ].map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
                  <span style={{ fontSize: 12, flexShrink: 0, marginTop: 1 }}>{r.icon}</span>
                  <span style={{ color: '#ffffffaa', fontSize: 10.5, lineHeight: 1.5 }}>{r.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Total city workers */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.12)', borderRadius: 8, padding: '7px 12px' }}>
            <span style={{ color: '#f59e0b70', fontSize: 10, fontWeight: 700 }}>City worker pool</span>
            <span style={{ color: '#fde68a', fontSize: 13, fontWeight: 900, fontFamily: 'monospace' }}>30 🧑</span>
          </div>
        </div>

      </div>

      {/* ── Season Tasks ── scrollable below the map ── */}
      <div style={{ flexShrink: 0, padding: isMobile ? '20px 14px 40px' : '36px 32px 48px', background: '#0d0a04', borderTop: '1px solid rgba(245,158,11,0.12)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>

          {/* Section header */}
          <div style={{ marginBottom: 28 }}>
            <p style={{ color: '#f59e0b', fontSize: 9.5, fontWeight: 900, letterSpacing: 3, textTransform: 'uppercase', margin: '0 0 6px' }}>Samatvam I · Season Zero</p>
            <h2 style={{ color: '#fde68a', fontSize: 22, fontWeight: 900, margin: 0, letterSpacing: -0.5 }}>City Tasks</h2>
            <p style={{ color: '#ffffff40', fontSize: 12, margin: '6px 0 0', lineHeight: 1.6 }}>
              Complete Task 1 first to unlock the city. Tasks 2–4 can be worked on simultaneously — players choose which to pursue.
            </p>
          </div>

          {/* Task cards grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>

            {/* Task 1 */}
            <div style={{ borderRadius: 14, border: `1px solid ${task1ZoneTotal >= 10000 ? 'rgba(16,185,129,0.4)' : 'rgba(245,158,11,0.35)'}`, background: task1ZoneTotal >= 10000 ? 'rgba(16,185,129,0.06)' : 'rgba(245,158,11,0.05)', padding: '20px 18px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 12, right: 14 }}>
                <span style={{ fontSize: 9, fontWeight: 900, letterSpacing: 1.5, textTransform: 'uppercase', padding: '3px 8px', borderRadius: 5, background: task1ZoneTotal >= 10000 ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.12)', color: task1ZoneTotal >= 10000 ? '#10b981' : '#f59e0b', border: `1px solid ${task1ZoneTotal >= 10000 ? 'rgba(16,185,129,0.3)' : 'rgba(245,158,11,0.25)'}` }}>
                  {task1ZoneTotal >= 10000 ? '✓ Complete' : 'Active'}
                </span>
              </div>
              <p style={{ color: '#f59e0b80', fontSize: 10, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 6px' }}>Task 1</p>
              <p style={{ color: '#fde68a', fontSize: 15, fontWeight: 900, margin: '0 0 14px', lineHeight: 1.3 }}>Activate the Season 0 Map</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14 }}>📦</span>
                  <span style={{ color: '#ffffffaa', fontSize: 11 }}>10,000 food packets</span>
                </div>
              </div>
              {/* progress bar */}
              <div style={{ height: 4, background: 'rgba(255,255,255,0.07)', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(100, (task1ZoneTotal / 10000) * 100)}%`, background: task1ZoneTotal >= 10000 ? '#10b981' : 'linear-gradient(90deg,#b45309,#f59e0b)', borderRadius: 999, transition: 'width 0.6s ease' }} />
              </div>
              <p style={{ color: '#ffffff30', fontSize: 10, margin: '6px 0 0', fontFamily: 'monospace' }}>{task1ZoneTotal.toLocaleString()} / 10,000</p>
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <p style={{ color: '#ffffff25', fontSize: 10, margin: 0, fontStyle: 'italic' }}>Must be completed before other tasks begin.</p>
              </div>
            </div>

            {/* Task 2 */}
            {[
              {
                num: 2, title: 'Water Harvesting Zone',
                icon: '💧',
                reqs: [
                  { icon: '🧑', text: '1 council worker' },
                  { icon: '🛒', text: '1 hand cart' },
                  { icon: '📦', text: '20,000 food packets' },
                ],
                color: '#0ea5e9', colorFaint: 'rgba(14,165,233,',
              },
              {
                num: 3, title: 'Connect Power Supply',
                icon: '⚡',
                reqs: [
                  { icon: '🧑', text: '2 council workers' },
                  { icon: '📦', text: '50,000 food packets' },
                  { icon: '📗', text: 'Electrician Guide Book' },
                  { icon: '🌳', text: '15 Acacia logs (chopped & carted)' },
                ],
                color: '#a78bfa', colorFaint: 'rgba(167,139,250,',
              },
              {
                num: 4, title: 'Start the Biorefinery',
                icon: '🏭',
                reqs: [
                  { icon: '🧑', text: '5 council workers' },
                  { icon: '📦', text: '100,000 food packets' },
                  { icon: '📘', text: 'Biofuel Synthesis Recipe' },
                  { icon: '🌿', text: '20 Moringa bundles (chopped & carted)' },
                  { icon: '🌲', text: '30 Mesquite logs (chopped & carted)' },
                  { icon: '🌴', text: '10 Neem logs (chopped & carted)' },
                ],
                color: '#f97316', colorFaint: 'rgba(249,115,22,',
              },
            ].map(task => (
              <div key={task.num} style={{ borderRadius: 14, border: `1px solid ${task.colorFaint}0.25)`, background: `${task.colorFaint}0.04)`, padding: '20px 18px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 12, right: 14 }}>
                  <span style={{ fontSize: 9, fontWeight: 900, letterSpacing: 1.5, textTransform: 'uppercase', padding: '3px 8px', borderRadius: 5, background: `${task.colorFaint}0.1)`, color: task.color, border: `1px solid ${task.colorFaint}0.2)` }}>
                    Parallel
                  </span>
                </div>
                <p style={{ color: `${task.colorFaint}0.6)`, fontSize: 10, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 6px' }}>Task {task.num}</p>
                <p style={{ color: '#fff', fontSize: 15, fontWeight: 900, margin: '0 0 14px', lineHeight: 1.3 }}>
                  <span style={{ marginRight: 8 }}>{task.icon}</span>{task.title}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {task.reqs.map((r, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14 }}>{r.icon}</span>
                      <span style={{ color: '#ffffffaa', fontSize: 11 }}>{r.text}</span>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 14, paddingTop: 10, borderTop: `1px solid ${task.colorFaint}0.08)` }}>
                  <p style={{ color: '#ffffff20', fontSize: 10, margin: 0, fontStyle: 'italic' }}>Can run in parallel with other tasks.</p>
                </div>
              </div>
            ))}

          </div>
        </div>
      </div>

      {councilModalOpen && (
        <CouncilHouseModal
          onClose={() => setCouncilModalOpen(false)}
          waxAcct={waxAcct}
          workers={workers}
          zones={zones}
        />
      )}

      {biorefineryModalOpen && (
        <BiorefineryModal onClose={() => setBiorefineryModalOpen(false)} onEnter={onEnterBiorefinery} />
      )}

      {busStopModalOpen && (
        <BusStopModal onClose={() => setBusStopModalOpen(false)} waxAcct={waxAcct} />
      )}
      {birchBusModalOpen && (
        <BirchBusStopModal onClose={() => setBirchBusModalOpen(false)} waxAcct={waxAcct} />
      )}
      {powerHouseModalOpen && (
        <PowerHouseModal onClose={() => setPowerHouseModalOpen(false)} />
      )}

      {waterHarvesterModalOpen && (
        <WaterHarvesterModal onClose={() => setWaterHarvesterModalOpen(false)} waxAcct={waxAcct} />
      )}

      {taskModalOpen && !taskCompletion && (
        <SecureLandTaskModal
          onClose={() => setTaskModalOpen(false)}
          waxAcct={waxAcct}
          activeTask={activeTask}
          onCompleted={(info) => { setTaskCompletion(info); setTaskModalOpen(false); setTimeout(() => setTaskModalOpen(true), 50); }}
        />
      )}

      {taskModalOpen && taskCompletion && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(5,4,2,0.88)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setTaskModalOpen(false)}
        >
          <div style={{ background: 'rgba(5,30,15,0.98)', border: '2px solid rgba(34,197,94,0.45)', borderRadius: 20, padding: '30px 34px', width: 440, maxWidth: '92vw', boxShadow: '0 0 60px rgba(34,197,94,0.15), 0 24px 80px rgba(0,0,0,0.85)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ textAlign: 'center', marginBottom: 22 }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
              <p style={{ color: '#4ade80', fontSize: 20, fontWeight: 900, margin: 0 }}>Task Completed</p>
              <p style={{ color: '#86efac60', fontSize: 9, fontWeight: 700, letterSpacing: 2.5, textTransform: 'uppercase', margin: '4px 0 0' }}>{activeTask?.label ?? 'City Task'} · Season Zero</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ padding: '12px 16px', borderRadius: 10, background: 'rgba(34,197,94,0.07)', border: '1px solid rgba(34,197,94,0.2)' }}>
                <p style={{ color: '#64748b', fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 4px' }}>Completed by</p>
                <p style={{ color: '#86efac', fontSize: 14, fontWeight: 900, margin: 0, fontFamily: 'monospace' }}>{taskCompletion.completed_by}</p>
              </div>
              <div style={{ padding: '12px 16px', borderRadius: 10, background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.18)' }}>
                <p style={{ color: '#64748b', fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 4px' }}>Reward Sent</p>
                <p style={{ color: '#fde68a', fontSize: 14, fontWeight: 900, margin: 0 }}>Task completed by {taskCompletion.completed_by}</p>
              </div>
              <div style={{ padding: '12px 16px', borderRadius: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <p style={{ color: '#64748b', fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 4px' }}>Transaction ID</p>
                <p style={{ color: '#94a3b8', fontSize: 10.5, margin: 0, fontFamily: 'monospace', wordBreak: 'break-all' }}>{taskCompletion.tx_id}</p>
              </div>
            </div>
            <button onClick={() => setTaskModalOpen(false)} style={{ marginTop: 22, width: '100%', padding: '11px', borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#ffffff60', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Privacy Policy Page ─────────────────────────────────────────────────────

function PrivacyPolicyPage({ onBack }: { onBack: () => void }) {
  const section = (title: string, children: React.ReactNode) => (
    <div className="mb-8">
      <h2 className="text-base font-black text-amber-300 uppercase tracking-widest mb-3 pb-2 border-b border-amber-500/20">{title}</h2>
      <div className="text-sm text-slate-300 leading-relaxed space-y-3">{children}</div>
    </div>
  );

  const updated = '6 May 2026';

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-slate-900/95 backdrop-blur-sm border-b border-white/10">
        <div className="w-full px-6 py-4 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center space-x-2 text-slate-400 hover:text-white transition-colors">
            <ArrowDownLeft className="w-5 h-5 rotate-45" />
            <span className="text-sm font-medium">Back</span>
          </button>
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-slate-700 rounded-lg flex items-center justify-center">
              <span className="text-base">🔒</span>
            </div>
            <div>
              <h1 className="text-base font-black text-white leading-none">Privacy Policy</h1>
              <p className="text-xs text-slate-400 mt-0.5">AhantraVyuh Ltd · Last updated {updated}</p>
            </div>
          </div>
          <div className="w-24" />
        </div>
      </div>

      <div className="w-full px-6 py-10">

        {/* Company badge */}
        <div className="bg-white/3 border border-white/8 rounded-2xl px-5 py-4 mb-8 flex items-start space-x-4">
          <span className="text-2xl flex-shrink-0 mt-0.5">🏢</span>
          <div className="text-sm text-slate-300 leading-relaxed">
            <p className="font-black text-white mb-1">AhantraVyuh Ltd</p>
            <p>Company number: <span className="font-mono text-amber-300">SC866275</span> (registered in Scotland, United Kingdom)</p>
            <p className="mt-1">ICO registration pending · Application no. <span className="font-mono text-amber-300">C1925848</span></p>
            <p className="mt-1">This policy applies to the <strong className="text-white">Samatvam</strong> game and the <strong className="text-white">AhantraVyuh</strong> platform accessible at <span className="font-mono text-amber-300">ahantravyuh.com</span>.</p>
          </div>
        </div>

        {section('1. Who We Are', <>
          <p>AhantraVyuh Ltd ("we", "us", "our") is the data controller for personal data collected through Samatvam and the AhantraVyuh platform. We are registered in Scotland under company number SC866275. Our ICO registration application is currently pending (application number <span className="font-mono text-amber-300">C1925848</span>).</p>
          <p>Contact us at: <a href="mailto:ahantravyuh@gmail.com" className="text-amber-300 hover:underline">ahantravyuh@gmail.com</a></p>
        </>)}

        {section('2. What Data We Collect', <>
          <p>We collect and process the following categories of personal data:</p>
          <ul className="list-none space-y-2 mt-2">
            {[
              ['Account data', 'Email address and password (bcrypt-hashed) provided at registration.'],
              ['WAX blockchain account', 'Your WAX wallet address (e.g. yourname.wam) when you connect it to the game. WAX addresses are public by nature of the blockchain.'],
              ['Game activity', 'Land plots owned, trees planted, food scores, bhoomi scores, infrastructure owned, city task completions, and in-game currency balances (AHN, URJA).'],
              ['Transaction data', 'On-chain WAX transactions related to your game actions (land purchase, token transfers). These are permanently public on the WAX blockchain.'],
              ['Technical data', 'Server logs including IP addresses and browser user-agent strings. These are retained solely to detect and block malicious traffic, bots, and unauthorised access attempts targeting our platform.'],
            ].map(([label, desc]) => (
              <li key={label as string} className="flex items-start space-x-3">
                <span className="text-amber-400 mt-0.5 flex-shrink-0">▸</span>
                <span><strong className="text-white">{label}:</strong> {desc}</span>
              </li>
            ))}
          </ul>
        </>)}

        {section('3. How We Use Your Data', <>
          <p>We use your personal data for the following purposes:</p>
          <ul className="list-none space-y-2 mt-2">
            {[
              ['Game operation', 'To run the Samatvam game, track scores and land ownership, and process in-game transactions.'],
              ['Account management', 'To authenticate your login and maintain your account.'],
              ['On-chain rewards', 'To send VAYAL and other on-chain tokens to your WAX wallet when you earn in-game rewards.'],
              ['Security', 'To detect and prevent fraud, abuse, or unauthorised access.'],
              ['Platform improvement', 'Aggregated, anonymised usage data may be used to improve game features and performance.'],
            ].map(([label, desc]) => (
              <li key={label as string} className="flex items-start space-x-3">
                <span className="text-amber-400 mt-0.5 flex-shrink-0">▸</span>
                <span><strong className="text-white">{label}:</strong> {desc}</span>
              </li>
            ))}
          </ul>
        </>)}

        {section('4. Legal Basis for Processing (UK GDPR)', <>
          <p>We rely on the following legal bases under UK GDPR Article 6:</p>
          <ul className="list-none space-y-2 mt-2">
            {[
              ['Contract (Art. 6(1)(b))', 'Processing your account data and game activity is necessary to provide the Samatvam service you signed up for.'],
              ['Legitimate interests (Art. 6(1)(f))', 'Security logging and fraud prevention are in our legitimate interests and do not override your rights.'],
              ['Legal obligation (Art. 6(1)(c))', 'We may process data where required by law.'],
            ].map(([label, desc]) => (
              <li key={label as string} className="flex items-start space-x-3">
                <span className="text-amber-400 mt-0.5 flex-shrink-0">▸</span>
                <span><strong className="text-white">{label}:</strong> {desc}</span>
              </li>
            ))}
          </ul>
        </>)}

        {section('5. Blockchain Data', <>
          <p>Some game actions (land purchase, token transfers, task rewards) are recorded on the <strong className="text-white">WAX blockchain</strong>. Blockchain transactions are <strong className="text-white">permanent and public</strong> by design — they cannot be altered or deleted by us or anyone else.</p>
          <p>By connecting your WAX wallet and making on-chain transactions, you acknowledge this irreversible nature. Your WAX account address and transaction amounts are visible to anyone on the public blockchain.</p>
        </>)}

        {section('6. Nature of In-Game Land Ownership', <>
          <p>Land plots, territories, and all assets within Samatvam are <strong className="text-white">abstract, in-game constructs</strong>. They do not represent, confer, or imply any rights over real-world land, physical property, or tangible assets of any kind.</p>
          <p>Owning a plot in Samatvam means you hold a record in our game database (and where applicable, on the WAX blockchain) that tracks your position within the game world. This record:</p>
          <ul className="list-none space-y-2 mt-2">
            <li className="flex items-start space-x-3"><span className="text-amber-400 mt-0.5 flex-shrink-0">▸</span><span>Has no legal standing as real property ownership under any jurisdiction</span></li>
            <li className="flex items-start space-x-3"><span className="text-amber-400 mt-0.5 flex-shrink-0">▸</span><span>Does not entitle the holder to any physical land, building, or resource in the real world</span></li>
            <li className="flex items-start space-x-3"><span className="text-amber-400 mt-0.5 flex-shrink-0">▸</span><span>Exists solely for the purpose of gameplay within the Samatvam virtual environment</span></li>
            <li className="flex items-start space-x-3"><span className="text-amber-400 mt-0.5 flex-shrink-0">▸</span><span>May be subject to game rule changes, resets, or modifications at the discretion of AhantraVyuh Ltd</span></li>
          </ul>
          <p>In-game currencies (AHN, URJA, Food tokens) are platform constructs earned through contributions and gameplay. AHN is not purchasable for cash and cannot be withdrawn as currency. Where AHN holds redemption value, it may only be exchanged for gift cards, shopping vouchers, or equivalent non-cash rewards at our discretion. None of these tokens should be construed as financial instruments, securities, investments, or legal tender.</p>
        </>)}

        {section('7. Data Sharing', <>
          <p>We do not sell your personal data. We may share data with:</p>
          <ul className="list-none space-y-2 mt-2">
            {[
              ['WAX blockchain network', 'Transaction data is broadcast to the public blockchain when you make on-chain actions.'],
              ['Infrastructure providers', 'Our server infrastructure is provided by a third-party hosting provider. Data processed on our servers remains under our control and is not shared with the provider beyond what is technically necessary to operate the service.'],
              ['Legal authorities', 'We may disclose data if required by UK law or a valid court order.'],
            ].map(([label, desc]) => (
              <li key={label as string} className="flex items-start space-x-3">
                <span className="text-amber-400 mt-0.5 flex-shrink-0">▸</span>
                <span><strong className="text-white">{label}:</strong> {desc}</span>
              </li>
            ))}
          </ul>
        </>)}

        {section('8. Data Retention', <>
          <p>We retain your account data and game records for as long as your account is active. If you request deletion, we will remove your personal data from our systems within 30 days, except:</p>
          <ul className="list-none space-y-2 mt-2 pl-1">
            <li className="flex items-start space-x-3"><span className="text-amber-400 mt-0.5 flex-shrink-0">▸</span><span>On-chain data (WAX blockchain) which is <strong className="text-white">permanent and outside our control</strong>.</span></li>
            <li className="flex items-start space-x-3"><span className="text-amber-400 mt-0.5 flex-shrink-0">▸</span><span>Data we are required to retain by law.</span></li>
          </ul>
          <p>Server logs are retained for a maximum of 90 days.</p>
        </>)}

        {section('9. Your Rights (UK GDPR)', <>
          <p>Under UK GDPR you have the following rights. To exercise any of them, contact us at <a href="mailto:ahantravyuh@gmail.com" className="text-amber-300 hover:underline">ahantravyuh@gmail.com</a>:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            {[
              ['Access', 'Request a copy of the personal data we hold about you.'],
              ['Rectification', 'Ask us to correct inaccurate or incomplete data.'],
              ['Erasure', 'Request deletion of your personal data ("right to be forgotten"), subject to legal and blockchain limitations.'],
              ['Restriction', 'Ask us to restrict processing of your data in certain circumstances.'],
              ['Portability', 'Receive your data in a structured, machine-readable format.'],
              ['Objection', 'Object to processing based on legitimate interests.'],
            ].map(([right, desc]) => (
              <div key={right as string} className="bg-white/3 border border-white/8 rounded-xl p-3">
                <p className="font-black text-white text-xs mb-1">{right}</p>
                <p className="text-xs text-slate-400">{desc}</p>
              </div>
            ))}
          </div>
          <p className="mt-3">You also have the right to lodge a complaint with the <strong className="text-white">Information Commissioner's Office (ICO)</strong> at <span className="font-mono text-amber-300">ico.org.uk</span>.</p>
        </>)}

        {section('10. Cookies and Local Storage', <>
          <p>We use <strong className="text-white">browser local storage</strong> (not cookies) to store your authentication token and session preferences. No third-party tracking or advertising cookies are used on this platform.</p>
        </>)}

        {section('11. Children', <>
          <p>Samatvam is not directed at children under 13. We do not knowingly collect personal data from children. If you believe a child has provided us with personal data, contact us and we will delete it promptly.</p>
        </>)}

        {section('12. Changes to This Policy', <>
          <p>We may update this privacy policy from time to time. The updated date at the top of this page will reflect any changes. For material changes — such as new categories of data collected or new purposes for processing — we will notify users via in-platform notice or email before the changes take effect. Continued use of the platform following notification constitutes acceptance of the updated policy.</p>
        </>)}

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-white/8 text-center text-xs text-slate-500">
          <p>AhantraVyuh Ltd · Company No. SC866275 · Scotland, United Kingdom</p>
          <p className="mt-1">Last updated: {updated} · Contact: <a href="mailto:ahantravyuh@gmail.com" className="text-amber-400 hover:underline">ahantravyuh@gmail.com</a></p>
        </div>
      </div>
    </div>
  );
}

// ─── Season 0 Rank Page ───────────────────────────────────────────────────────

interface RankRow {
  wax_account: string;
  plot_count: number;
  land_score: number;
  machine_score: number;
  cart_score: number;
  total_score: number;
}

function CityChampionsPage({ onBack }: { onBack: () => void }) {
  const [players, setPlayers] = React.useState<{ wax_account: string; tasks: { task_id: string; label: string; completed_at: string; tx_id: string }[] }[]>([]);
  const [totalCompleted, setTotalCompleted] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    fetch('/samatvam/api/samatvam/city-champions')
      .then(r => r.json())
      .then(d => { if (d.success) { setPlayers(d.players); setTotalCompleted(d.total_tasks_completed); } else setError('Failed to load'); })
      .catch(() => setError('Network error'))
      .finally(() => setLoading(false));
  }, []);

  const medals = ['🥇', '🥈', '🥉'];

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <div className="sticky top-0 z-20 bg-slate-900/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center space-x-2 text-slate-400 hover:text-white transition-colors">
            <ArrowDownLeft className="w-5 h-5 rotate-45" />
            <span className="text-sm font-medium">Dashboard</span>
          </button>
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center">
              <span className="text-base">🏛</span>
            </div>
            <div>
              <h1 className="text-lg font-black text-white leading-none">City Champions</h1>
              <p className="text-xs text-amber-400/80 mt-0.5">{totalCompleted} / 10 tasks completed · Season Zero</p>
            </div>
          </div>
          <div className="w-24" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {loading && <div className="text-center text-slate-400 py-20">Loading champions…</div>}
        {error && <div className="text-center text-red-400 py-20">{error}</div>}

        {!loading && !error && players.length === 0 && (
          <div className="text-center py-20">
            <div className="text-5xl mb-4">🏜️</div>
            <p className="text-slate-400">No tasks completed yet. Be the first!</p>
          </div>
        )}

        {!loading && !error && players.length > 0 && (
          <div className="space-y-4">
            {players.map((p, i) => (
              <div key={p.wax_account} className={`rounded-2xl border overflow-hidden ${i === 0 ? 'border-amber-400/40 bg-amber-950/30' : 'border-white/8 bg-white/3'}`}>
                {/* Player header */}
                <div className="flex items-center justify-between px-5 py-4">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{medals[i] ?? `#${i + 1}`}</span>
                    <div>
                      <p className="font-black text-white text-base font-mono">{p.wax_account}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{p.tasks.length} task{p.tasks.length !== 1 ? 's' : ''} completed</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500 uppercase tracking-widest font-black">Tasks done</p>
                    <p className="text-2xl font-black text-amber-300">{p.tasks.length}</p>
                  </div>
                </div>
                {/* Task list */}
                <div className="border-t border-white/6 divide-y divide-white/5">
                  {p.tasks.map(t => (
                    <div key={t.task_id} className="flex items-center justify-between px-5 py-2.5">
                      <div className="flex items-center space-x-2">
                        <span className="text-green-400 text-xs font-black">✓</span>
                        <span className="text-sm text-slate-300">{t.label}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-xs text-slate-500">{new Date(t.completed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Progress bar */}
            <div className="mt-6 bg-white/3 border border-white/8 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-black uppercase tracking-widest text-slate-400">Season 0 Progress</span>
                <span className="text-xs font-black text-amber-300">{totalCompleted} / 10 tasks</span>
              </div>
              <div className="w-full bg-slate-700/50 rounded-full h-2.5">
                <div className="bg-gradient-to-r from-amber-500 to-orange-400 h-2.5 rounded-full transition-all" style={{ width: `${(totalCompleted / 10) * 100}%` }} />
              </div>
              <p className="text-xs text-slate-500 mt-2">{10 - totalCompleted} tasks remaining in Season Zero</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Season0RankPage({ onBack }: { onBack: () => void }) {
  const [rows, setRows] = React.useState<RankRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [myAcct, setMyAcct] = React.useState('');

  // Load connected WAX account for highlighting
  React.useEffect(() => {
    getSessionKit().restore().then((s) => {
      if (s) setMyAcct(String(s.actor));
    }).catch(() => {});
  }, []);

  React.useEffect(() => {
    fetch('/samatvam/api/samatvam/season0-rank')
      .then(r => r.json())
      .then(d => {
        if (d.success) setRows(d.rows);
        else setError('Failed to load rankings');
      })
      .catch(() => setError('Network error'))
      .finally(() => setLoading(false));
  }, []);

  const myRank = myAcct ? rows.findIndex(r => r.wax_account === myAcct) + 1 : 0;
  const myRow = myAcct ? rows.find(r => r.wax_account === myAcct) : null;

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-slate-900/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center space-x-2 text-slate-400 hover:text-white transition-colors">
            <ArrowDownLeft className="w-5 h-5 rotate-45" />
            <span className="text-sm font-medium">Dashboard</span>
          </button>
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center">
              <span className="text-base">🏆</span>
            </div>
            <div>
              <h1 className="text-lg font-black text-white leading-none">Season 0 Rankings</h1>
              <p className="text-xs text-amber-400/80 mt-0.5">Land Position Score · Updated live</p>
            </div>
          </div>
          <div className="w-24" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* Score formula legend */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { icon: '🏜️', label: 'Land Score', desc: 'Origin plot = 10 pts, last = 1 pt, linear decay across 10,000 plots', color: 'from-amber-500/20 to-orange-500/10', border: 'border-amber-500/20', text: 'text-amber-300' },
            { icon: '⚙️', label: 'Water Machine', desc: '+10 bonus points for owning an installed water harvester', color: 'from-blue-500/20 to-cyan-500/10', border: 'border-blue-500/20', text: 'text-blue-300' },
            { icon: '🛒', label: 'Hand Cart', desc: '+20 bonus points for owning an installed hand cart', color: 'from-purple-500/20 to-indigo-500/10', border: 'border-purple-500/20', text: 'text-purple-300' },
          ].map(c => (
            <div key={c.label} className={`relative bg-gradient-to-br ${c.color} border ${c.border} rounded-2xl p-5 overflow-hidden`}>
              <div className="text-3xl mb-3">{c.icon}</div>
              <p className={`text-sm font-bold ${c.text} mb-1`}>{c.label}</p>
              <p className="text-xs text-slate-400 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>

        {/* My rank card (if connected) */}
        {myRow && (
          <div className="mb-8 bg-gradient-to-r from-amber-500/15 via-orange-500/10 to-amber-500/15 border border-amber-400/30 rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/30">
                  <span className="text-xl font-black text-white">#{myRank}</span>
                </div>
                <div>
                  <p className="text-xs text-amber-400/70 font-medium uppercase tracking-wider mb-0.5">Your Rank</p>
                  <p className="text-lg font-black text-white font-mono">{myRow.wax_account}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{myRow.plot_count} plot{Number(myRow.plot_count) !== 1 ? 's' : ''} owned</p>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4 text-right">
                {[
                  { label: 'Land', val: Number(myRow.land_score).toFixed(1), color: 'text-amber-300' },
                  { label: 'Machine', val: `+${myRow.machine_score}`, color: 'text-blue-300' },
                  { label: 'Cart', val: `+${myRow.cart_score}`, color: 'text-purple-300' },
                  { label: 'Total', val: Number(myRow.total_score).toFixed(1), color: 'text-white text-xl font-black' },
                ].map(s => (
                  <div key={s.label}>
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-0.5">{s.label}</p>
                    <p className={`font-bold ${s.color}`}>{s.val}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-12 h-12 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />
              <p className="text-slate-400 text-sm">Loading rankings…</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-16 text-red-400">{error}</div>
        ) : (
          <div className="bg-slate-800/50 border border-white/10 rounded-2xl overflow-hidden">
            {/* Table header */}
            <div className="grid grid-cols-[3rem_1fr_5rem_6rem_6rem_6rem_7rem] gap-0 bg-slate-800 border-b border-white/10 px-5 py-3">
              {['#', 'Account', 'Plots', 'Land', 'Machine', 'Cart', 'Total'].map((h, i) => (
                <div key={h} className={`text-[10px] font-bold uppercase tracking-widest text-slate-500 ${i > 1 ? 'text-right' : ''}`}>{h}</div>
              ))}
            </div>

            {/* Table rows */}
            <div className="divide-y divide-white/5">
              {rows.map((row, idx) => {
                const rank = idx + 1;
                const isMe = row.wax_account === myAcct;
                const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null;
                const rowBg = isMe
                  ? 'bg-amber-500/10 hover:bg-amber-500/15'
                  : rank <= 3
                    ? 'hover:bg-white/5 bg-white/2'
                    : 'hover:bg-white/5';

                return (
                  <div
                    key={row.wax_account}
                    className={`grid grid-cols-[3rem_1fr_5rem_6rem_6rem_6rem_7rem] gap-0 px-5 py-4 items-center transition-colors ${rowBg} ${isMe ? 'border-l-2 border-amber-400' : ''}`}
                  >
                    {/* Rank */}
                    <div>
                      {medal
                        ? <span className="text-xl">{medal}</span>
                        : <span className={`text-sm font-bold ${rank <= 10 ? 'text-slate-300' : 'text-slate-500'}`}>{rank}</span>
                      }
                    </div>

                    {/* Account */}
                    <div className="flex items-center space-x-2 min-w-0">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black flex-shrink-0 ${
                        rank === 1 ? 'bg-amber-400/20 text-amber-300' :
                        rank === 2 ? 'bg-slate-400/20 text-slate-300' :
                        rank === 3 ? 'bg-orange-700/20 text-orange-400' :
                        'bg-slate-700/50 text-slate-400'
                      }`}>
                        {row.wax_account.slice(0, 2).toUpperCase()}
                      </div>
                      <span className={`text-sm font-mono truncate ${isMe ? 'text-amber-300 font-bold' : rank <= 3 ? 'text-white font-semibold' : 'text-slate-300'}`}>
                        {row.wax_account}
                        {isMe && <span className="ml-2 text-[10px] bg-amber-400/20 text-amber-300 px-1.5 py-0.5 rounded-full font-bold">YOU</span>}
                      </span>
                    </div>

                    {/* Plots */}
                    <div className="text-right">
                      <span className="text-sm text-slate-300">{row.plot_count}</span>
                    </div>

                    {/* Land score */}
                    <div className="text-right">
                      <span className="text-sm text-amber-300 font-semibold">{Number(row.land_score).toFixed(1)}</span>
                    </div>

                    {/* Machine */}
                    <div className="text-right">
                      <span className={`text-sm font-semibold ${Number(row.machine_score) > 0 ? 'text-blue-300' : 'text-slate-600'}`}>
                        {Number(row.machine_score) > 0 ? `+${row.machine_score}` : '—'}
                      </span>
                    </div>

                    {/* Cart */}
                    <div className="text-right">
                      <span className={`text-sm font-semibold ${Number(row.cart_score) > 0 ? 'text-purple-300' : 'text-slate-600'}`}>
                        {Number(row.cart_score) > 0 ? `+${row.cart_score}` : '—'}
                      </span>
                    </div>

                    {/* Total */}
                    <div className="text-right">
                      <span className={`font-black ${
                        rank === 1 ? 'text-amber-300 text-base' :
                        rank <= 3 ? 'text-white text-sm' :
                        rank <= 10 ? 'text-slate-200 text-sm' :
                        'text-slate-400 text-sm'
                      }`}>
                        {Number(row.total_score).toFixed(1)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer note */}
            <div className="px-5 py-4 border-t border-white/5 bg-slate-800/30">
              <p className="text-xs text-slate-600 text-center">
                Rankings update in real-time · {rows.length} players · Season 0 closed — all 1,000 plots claimed
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Admin Zone Manager ────────────────────────────────────────────────────────

// ─── Admin Label Page ─────────────────────────────────────────────────────────
interface AdminLabelRow {
  id: number;
  label_id: string;
  label: string;
  cx: number;
  cy: number;
  icon: string;
  action: string | null;
  is_active: boolean;
  created_at: string;
}

function AdminLabelPage({ onBack }: { onBack: () => void }) {
  const token = localStorage.getItem('token') || '';
  const svgRef = React.useRef<SVGSVGElement>(null);

  const [labels, setLabels] = React.useState<AdminLabelRow[]>([]);
  const [hoverLabel, setHoverLabel] = React.useState<string | null>(null);
  const [coords, setCoords] = React.useState<{ x: number; y: number } | null>(null);
  const [msg, setMsg] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const [form, setForm] = React.useState({
    label_id: '', label: '', cx: '', cy: '', icon: '📍', action: '',
  });

  // Edit state
  const [editingId, setEditingId] = React.useState<number | null>(null);
  const [editForm, setEditForm] = React.useState({ label: '', cx: '', cy: '', icon: '', action: '' });
  const [editSaving, setEditSaving] = React.useState(false);
  const [editMsg, setEditMsg] = React.useState('');

  const [activeMapSrc, setActiveMapSrc] = React.useState('/samatvam/samatvam-1.png');

  React.useEffect(() => {
    fetch('/samatvam/api/samatvam/map/bus-stop-progress')
      .then(r => r.json()).then(d => {
        if (d.complete) { setActiveMapSrc(`${process.env.PUBLIC_URL}/images/samatvam-base-map-water-harvester-bus-stop.png`); return; }
        fetch('/samatvam/api/samatvam/map/harvest-water-progress')
          .then(r2 => r2.json()).then(d2 => {
            if (d2.complete) setActiveMapSrc(`${process.env.PUBLIC_URL}/images/samatvam-base-map-water-harvester.png`);
          }).catch(() => {});
      }).catch(() => {});
  }, []);

  const fetchLabels = React.useCallback(() => {
    fetch('/samatvam/api/samatvam/admin/map-labels', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { if (d.success) setLabels(d.labels); })
      .catch(() => {});
  }, [token]);

  React.useEffect(() => { fetchLabels(); }, [fetchLabels]);

  const handleMouseMove = React.useCallback((e: React.MouseEvent) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * MAP_W);
    const y = Math.round(MAP_H - ((e.clientY - rect.top) / rect.height) * MAP_H);
    if (x >= 0 && y >= 0 && x <= MAP_W && y <= MAP_H) setCoords({ x, y });
    else setCoords(null);
  }, []);

  const handleMapClick = React.useCallback((e: React.MouseEvent) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * MAP_W);
    const y = Math.round(MAP_H - ((e.clientY - rect.top) / rect.height) * MAP_H);
    setEditingId(cur => {
      if (cur !== null) { setEditForm(f => ({ ...f, cx: String(x), cy: String(y) })); return cur; }
      setForm(f => ({ ...f, cx: String(x), cy: String(y) }));
      return null;
    });
  }, []);

  const startEdit = (l: AdminLabelRow) => {
    setEditingId(l.id);
    setEditForm({ label: l.label, cx: String(l.cx), cy: String(l.cy), icon: l.icon, action: l.action || '' });
    setEditMsg('');
  };

  const cancelEdit = () => { setEditingId(null); setEditMsg(''); };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    if (!editForm.label.trim() || !editForm.cx || !editForm.cy) { setEditMsg('❌ label, cx, cy required'); return; }
    setEditSaving(true); setEditMsg('');
    try {
      const r = await fetch(`/samatvam/api/samatvam/admin/map-labels/${editingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          label: editForm.label.trim(),
          cx: parseInt(editForm.cx), cy: parseInt(editForm.cy),
          icon: editForm.icon.trim() || '📍',
          action: editForm.action.trim() || null,
        }),
      });
      const d = await r.json();
      if (d.success) { setEditingId(null); setEditMsg(''); fetchLabels(); }
      else { setEditMsg(`❌ ${d.error}`); }
    } catch { setEditMsg('❌ Network error'); }
    setEditSaving(false);
  };

  const handleSubmit = async () => {
    const { label_id, label, cx, cy, icon, action } = form;
    if (!label_id.trim() || !label.trim() || !cx || !cy) {
      setMsg('❌ label_id, label, cx, cy are required'); return;
    }
    setSaving(true); setMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/admin/map-labels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          label_id: label_id.trim(),
          label: label.trim(),
          cx: parseInt(cx), cy: parseInt(cy),
          icon: icon.trim() || '📍',
          action: action.trim() || null,
        }),
      });
      const d = await r.json();
      if (d.success) {
        setMsg('✅ Label created!');
        setForm({ label_id: '', label: '', cx: '', cy: '', icon: '📍', action: '' });
        fetchLabels();
      } else { setMsg(`❌ ${d.error}`); }
    } catch { setMsg('❌ Network error'); }
    setSaving(false);
  };

  const handleToggle = async (id: number, is_active: boolean) => {
    await fetch(`/samatvam/api/samatvam/admin/map-labels/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ is_active: !is_active }),
    });
    fetchLabels();
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '7px 10px', borderRadius: 7, fontSize: 12,
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
    color: 'white', outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = { color: '#ffffff55', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4, display: 'block' };

  return (
    <div style={{ height: '100vh', background: '#0d0a04', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ flexShrink: 0, padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(5,4,2,0.9)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(139,92,246,0.15)', zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: 'linear-gradient(135deg,#4c1d95,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 14px 3px #8b5cf640', fontSize: 16 }}>🏷️</div>
          <div>
            <h2 style={{ color: '#c4b5fd', fontWeight: 900, fontSize: 15, margin: 0, letterSpacing: -0.3 }}>Map Label Manager</h2>
            <p style={{ color: '#8b5cf655', fontSize: 9.5, margin: 0, fontWeight: 700, letterSpacing: 2 }}>ADMIN · SAMATVAM CITY MAP</p>
          </div>
        </div>
        <button onClick={onBack} style={{ background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.3)', borderRadius: 9, padding: '6px 16px', color: '#c4b5fd', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>← Back</button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Left panel: form + list */}
        <div style={{ width: 300, flexShrink: 0, borderRight: '1px solid rgba(139,92,246,0.1)', background: 'rgba(5,4,2,0.75)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Add Label form */}
          <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
            <p style={{ color: '#8b5cf6', fontSize: 9.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 10px' }}>Add New Label</p>
            <p style={{ color: '#8b5cf670', fontSize: 10, margin: '0 0 10px', lineHeight: 1.5 }}>Click on the map to set coordinates, then fill details below.</p>

            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>Label ID</label>
              <input style={inputStyle} value={form.label_id} onChange={e => setForm(f => ({ ...f, label_id: e.target.value }))} placeholder="e.g. bakery" />
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>Display Name</label>
              <input style={inputStyle} value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} placeholder="e.g. Village Bakery" />
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>CX (click map)</label>
                <input style={{ ...inputStyle, borderColor: form.cx ? 'rgba(139,92,246,0.5)' : 'rgba(255,255,255,0.12)' }} type="number" value={form.cx} onChange={e => setForm(f => ({ ...f, cx: e.target.value }))} placeholder="x" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>CY (click map)</label>
                <input style={{ ...inputStyle, borderColor: form.cy ? 'rgba(139,92,246,0.5)' : 'rgba(255,255,255,0.12)' }} type="number" value={form.cy} onChange={e => setForm(f => ({ ...f, cy: e.target.value }))} placeholder="y" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Icon Emoji</label>
                <input style={inputStyle} value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} placeholder="📍" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Action (on-click)</label>
                <input style={inputStyle} value={form.action} onChange={e => setForm(f => ({ ...f, action: e.target.value }))} placeholder="e.g. council-house" />
              </div>
            </div>

            {msg && <p style={{ color: msg.startsWith('✅') ? '#a78bfa' : '#f87171', fontSize: 10, margin: '0 0 8px', fontWeight: 700 }}>{msg}</p>}
            <button onClick={handleSubmit} disabled={saving} style={{ width: '100%', padding: '9px', borderRadius: 8, fontSize: 12, fontWeight: 800, background: saving ? 'rgba(139,92,246,0.08)' : 'rgba(139,92,246,0.2)', border: `1px solid ${saving ? 'rgba(139,92,246,0.15)' : 'rgba(139,92,246,0.45)'}`, color: saving ? '#8b5cf650' : '#c4b5fd', cursor: saving ? 'default' : 'pointer', transition: 'all 0.2s' }}>
              {saving ? 'Creating…' : '+ Create Label'}
            </button>
          </div>

          {/* Label list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px' }}>
            <p style={{ color: '#ffffff25', fontSize: 9.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 8px' }}>Existing Labels ({labels.length})</p>
            {labels.length === 0 ? (
              <p style={{ color: '#ffffff15', fontSize: 11 }}>No labels yet. Add one above.</p>
            ) : labels.map(l => {
              const isEditing = editingId === l.id;
              return (
                <div key={l.id}
                  onMouseEnter={() => setHoverLabel(l.label_id)}
                  onMouseLeave={() => setHoverLabel(null)}
                  style={{ marginBottom: 8, padding: '9px 10px', borderRadius: 8, background: isEditing ? 'rgba(139,92,246,0.12)' : hoverLabel === l.label_id ? 'rgba(139,92,246,0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${isEditing ? 'rgba(139,92,246,0.5)' : l.is_active ? 'rgba(139,92,246,0.25)' : 'rgba(255,255,255,0.07)'}`, transition: 'all 0.15s' }}>

                  {isEditing ? (
                    /* ── Inline edit form ── */
                    <>
                      <p style={{ color: '#a78bfa', fontSize: 9, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 8px' }}>Editing: {l.label_id}</p>
                      <div style={{ marginBottom: 6 }}>
                        <label style={labelStyle}>Display Name</label>
                        <input style={inputStyle} value={editForm.label} onChange={e => setEditForm(f => ({ ...f, label: e.target.value }))} />
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                        <div style={{ flex: 1 }}>
                          <label style={labelStyle}>CX</label>
                          <input style={{ ...inputStyle, borderColor: 'rgba(139,92,246,0.5)' }} type="number" value={editForm.cx} onChange={e => setEditForm(f => ({ ...f, cx: e.target.value }))} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={labelStyle}>CY</label>
                          <input style={{ ...inputStyle, borderColor: 'rgba(139,92,246,0.5)' }} type="number" value={editForm.cy} onChange={e => setEditForm(f => ({ ...f, cy: e.target.value }))} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={labelStyle}>Icon</label>
                          <input style={inputStyle} value={editForm.icon} onChange={e => setEditForm(f => ({ ...f, icon: e.target.value }))} />
                        </div>
                      </div>
                      <div style={{ marginBottom: 6 }}>
                        <label style={labelStyle}>Action (on-click)</label>
                        <input style={inputStyle} value={editForm.action} onChange={e => setEditForm(f => ({ ...f, action: e.target.value }))} placeholder="e.g. council-house (leave blank for no action)" />
                      </div>
                      <p style={{ color: '#8b5cf670', fontSize: 9, margin: '0 0 6px' }}>Click map to reposition</p>
                      {editMsg && <p style={{ color: editMsg.startsWith('✅') ? '#a78bfa' : '#f87171', fontSize: 10, margin: '0 0 6px', fontWeight: 700 }}>{editMsg}</p>}
                      <div style={{ display: 'flex', gap: 5 }}>
                        <button onClick={handleSaveEdit} disabled={editSaving} style={{ flex: 1, padding: '5px 0', borderRadius: 5, fontSize: 9.5, fontWeight: 800, background: editSaving ? 'rgba(139,92,246,0.08)' : 'rgba(139,92,246,0.25)', border: '1px solid rgba(139,92,246,0.5)', color: editSaving ? '#8b5cf650' : '#c4b5fd', cursor: editSaving ? 'default' : 'pointer' }}>
                          {editSaving ? 'Saving…' : '✓ Save'}
                        </button>
                        <button onClick={cancelEdit} style={{ padding: '5px 10px', borderRadius: 5, fontSize: 9.5, fontWeight: 700, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#ffffff50', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    /* ── Normal view ── */
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                        <span style={{ fontSize: 15 }}>{l.icon}</span>
                        <span style={{ flex: 1, color: l.is_active ? '#ddd6fe' : '#ffffff40', fontSize: 11, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.label}</span>
                        <span style={{ fontSize: 9, fontWeight: 700, color: l.is_active ? '#8b5cf6' : '#ffffff30', background: l.is_active ? 'rgba(139,92,246,0.1)' : 'rgba(255,255,255,0.04)', border: `1px solid ${l.is_active ? 'rgba(139,92,246,0.2)' : 'rgba(255,255,255,0.06)'}`, borderRadius: 4, padding: '1px 6px' }}>{l.is_active ? 'active' : 'off'}</span>
                      </div>
                      <p style={{ color: '#ffffff30', fontSize: 9.5, margin: '0 0 3px', fontFamily: 'monospace' }}>{l.label_id} · ({l.cx},{l.cy})</p>
                      {l.action && <p style={{ color: '#f59e0b80', fontSize: 9, margin: '0 0 5px', fontFamily: 'monospace' }}>⚡ {l.action}</p>}
                      <div style={{ display: 'flex', gap: 5 }}>
                        <button onClick={() => startEdit(l)} style={{ flex: 1, padding: '4px 0', borderRadius: 5, fontSize: 9.5, fontWeight: 700, background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', color: '#a78bfa', cursor: 'pointer' }}>
                          Edit
                        </button>
                        <button onClick={() => handleToggle(l.id, l.is_active)} style={{ flex: 1, padding: '4px 0', borderRadius: 5, fontSize: 9.5, fontWeight: 700, background: l.is_active ? 'rgba(245,158,11,0.08)' : 'rgba(139,92,246,0.08)', border: `1px solid ${l.is_active ? 'rgba(245,158,11,0.2)' : 'rgba(139,92,246,0.2)'}`, color: l.is_active ? '#f59e0b' : '#8b5cf6', cursor: 'pointer' }}>
                          {l.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Map */}
        <div style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', background: 'radial-gradient(ellipse 80% 70% at 50% 55%, #0d0a1a 0%, #0a080d 60%, #050603 100%)' }}>
          <div style={{ lineHeight: 0, boxShadow: '0 0 60px 15px #8b5cf612, 0 0 0 1.5px #8b5cf628, 0 6px 40px rgba(0,0,0,0.9)', borderRadius: 4 }}>
            <svg
              ref={svgRef}
              viewBox={`0 0 ${MAP_W} ${MAP_H}`}
              width={MAP_W}
              height={MAP_H}
              style={{ display: 'block', maxWidth: '100%', maxHeight: 'calc(100vh - 100px)', width: '100%', height: 'auto', borderRadius: 4, userSelect: 'none', cursor: 'crosshair' }}
              onMouseMove={handleMouseMove}
              onMouseLeave={() => setCoords(null)}
              onClick={handleMapClick}
            >
              <image href={activeMapSrc} xlinkHref={activeMapSrc} width={MAP_W} height={MAP_H} preserveAspectRatio="xMidYMid meet" />

              {/* Render all existing labels */}
              {labels.map(l => {
                const sx = l.cx;
                const sy = MAP_H - l.cy;
                const isHov = hoverLabel === l.label_id;
                const activeCol = l.is_active ? '#8b5cf6' : '#64748b';
                const lblW = l.label.length * 6.5 + 28;
                const lblX = Math.min(Math.max(sx - lblW / 2, 4), MAP_W - lblW - 4);
                return (
                  <g key={l.id}
                    onMouseEnter={() => setHoverLabel(l.label_id)}
                    onMouseLeave={() => setHoverLabel(null)}
                  >
                    {/* Hit area */}
                    <circle cx={sx} cy={sy} r={12} fill="transparent" />
                    {/* Pin dot */}
                    <circle cx={sx} cy={sy} r={isHov ? 6 : 4}
                      fill={isHov ? activeCol : `${activeCol}80`}
                      stroke={isHov ? activeCol : `${activeCol}50`}
                      strokeWidth={1.5}
                      style={{ transition: 'r 0.15s, fill 0.15s' }}
                    />
                    {/* Icon text */}
                    <text x={sx} y={sy - 9} textAnchor="middle" fontSize={12} style={{ userSelect: 'none' }}>{l.icon}</text>
                    {/* Tooltip on hover */}
                    {isHov && (
                      <g pointerEvents="none">
                        <rect x={lblX} y={sy - 38} width={lblW} height={18} rx={5}
                          fill="rgba(10,8,3,0.92)" stroke={`${activeCol}70`} strokeWidth={1}
                        />
                        <text x={lblX + 14} y={sy - 26} fontFamily="system-ui,sans-serif" fontSize={10} fontWeight={800} fill="#ddd6fe">
                          {l.icon} {l.label}
                        </text>
                      </g>
                    )}
                    {/* label_id below dot on hover */}
                    {isHov && (
                      <text x={sx} y={sy + 16} textAnchor="middle" fontFamily="monospace" fontSize={8} fill="#8b5cf690">{l.label_id}</text>
                    )}
                  </g>
                );
              })}

              {/* Preview pin for current form cx/cy */}
              {form.cx && form.cy && (
                <g pointerEvents="none">
                  <circle cx={parseInt(form.cx)} cy={MAP_H - parseInt(form.cy)} r={7}
                    fill="rgba(251,191,36,0.25)" stroke="rgba(251,191,36,0.9)" strokeWidth={2} strokeDasharray="4 2"
                  />
                  <circle cx={parseInt(form.cx)} cy={MAP_H - parseInt(form.cy)} r={2.5} fill="#fbbf24" />
                  <text x={parseInt(form.cx)} y={MAP_H - parseInt(form.cy) - 12} textAnchor="middle" fontSize={14} style={{ userSelect: 'none' }}>{form.icon || '📍'}</text>
                  {form.label && (
                    <>
                      <rect
                        x={Math.min(Math.max(parseInt(form.cx) - (form.label.length * 6.5 + 28) / 2, 4), MAP_W - (form.label.length * 6.5 + 28) - 4)}
                        y={MAP_H - parseInt(form.cy) - 38}
                        width={form.label.length * 6.5 + 28} height={18} rx={5}
                        fill="rgba(10,8,3,0.92)" stroke="rgba(251,191,36,0.6)" strokeWidth={1}
                      />
                      <text
                        x={parseInt(form.cx)} y={MAP_H - parseInt(form.cy) - 26}
                        textAnchor="middle" fontFamily="system-ui,sans-serif" fontSize={10} fontWeight={800} fill="#fde68a"
                      >
                        {form.icon} {form.label}
                      </text>
                    </>
                  )}
                </g>
              )}

              {/* Coordinate readout */}
              {coords && (
                <g pointerEvents="none">
                  <rect x={8} y={MAP_H - 28} width={100} height={20} rx={4} fill="rgba(5,4,2,0.85)" stroke="rgba(139,92,246,0.35)" strokeWidth={1} />
                  <text x={58} y={MAP_H - 14} fontFamily="monospace" fontSize={11} fontWeight={700} fill="#a78bfa" textAnchor="middle">
                    {coords.x}, {coords.y}
                  </text>
                </g>
              )}
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

interface AdminZoneRow {
  id: number;
  zone_id: string;
  label: string;
  cx: number;
  cy: number;
  w: number;
  h: number;
  note: string;
  counter_target: number;
  counter_unit: string;
  task_number: number | null;
  is_active: boolean;
  created_at: string;
}

function AdminZonePage({ onBack }: { onBack: () => void }) {
  const token = localStorage.getItem('token') || '';
  const svgRef = React.useRef<SVGSVGElement>(null);

  const [zones, setZones] = React.useState<AdminZoneRow[]>([]);
  const [hoverZone, setHoverZone] = React.useState<string | null>(null);
  const [coords, setCoords] = React.useState<{ x: number; y: number } | null>(null);
  const [msg, setMsg] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [activeMapSrc, setActiveMapSrc] = React.useState('/samatvam/samatvam-1.png');

  React.useEffect(() => {
    fetch('/samatvam/api/samatvam/map/bus-stop-progress')
      .then(r => r.json()).then(d => {
        if (d.complete) { setActiveMapSrc(`${process.env.PUBLIC_URL}/images/samatvam-base-map-water-harvester-bus-stop.png`); return; }
        fetch('/samatvam/api/samatvam/map/harvest-water-progress')
          .then(r2 => r2.json()).then(d2 => {
            if (d2.complete) setActiveMapSrc(`${process.env.PUBLIC_URL}/images/samatvam-base-map-water-harvester.png`);
          }).catch(() => {});
      }).catch(() => {});
  }, []);

  const [form, setForm] = React.useState({
    zone_id: '', label: '', cx: '', cy: '', w: '60', h: '60',
    note: '', counter_target: '10000', counter_unit: 'packets', task_number: '',
  });

  const fetchZones = React.useCallback(() => {
    fetch('/samatvam/api/samatvam/admin/map-zones', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { if (d.success) setZones(d.zones); })
      .catch(() => {});
  }, [token]);

  React.useEffect(() => { fetchZones(); }, [fetchZones]);

  const handleMouseMove = React.useCallback((e: React.MouseEvent) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * MAP_W);
    const y = Math.round(MAP_H - ((e.clientY - rect.top) / rect.height) * MAP_H);
    if (x >= 0 && y >= 0 && x <= MAP_W && y <= MAP_H) setCoords({ x, y });
    else setCoords(null);
  }, []);

  // Click on map → fill cx/cy in form
  const handleMapClick = React.useCallback((e: React.MouseEvent) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * MAP_W);
    const y = Math.round(MAP_H - ((e.clientY - rect.top) / rect.height) * MAP_H);
    setForm(f => ({ ...f, cx: String(x), cy: String(y) }));
  }, []);

  const handleSubmit = async () => {
    const { zone_id, label, cx, cy, w, h, note, counter_target, counter_unit, task_number } = form;
    if (!zone_id.trim() || !label.trim() || !cx || !cy) {
      setMsg('❌ zone_id, label, cx, cy are required'); return;
    }
    setSaving(true); setMsg('');
    try {
      const r = await fetch('/samatvam/api/samatvam/admin/map-zones', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          zone_id: zone_id.trim(),
          label: label.trim(),
          cx: parseInt(cx), cy: parseInt(cy),
          w: parseInt(w) || 60, h: parseInt(h) || 60,
          note: note.trim(),
          counter_target: parseInt(counter_target) || 10000,
          counter_unit: counter_unit.trim() || 'packets',
          task_number: task_number ? parseInt(task_number) : null,
        }),
      });
      const d = await r.json();
      if (d.success) {
        setMsg('✅ Zone created!');
        setForm({ zone_id: '', label: '', cx: '', cy: '', w: '60', h: '60', note: '', counter_target: '10000', counter_unit: 'packets', task_number: '' });
        fetchZones();
      } else { setMsg(`❌ ${d.error}`); }
    } catch { setMsg('❌ Network error'); }
    setSaving(false);
  };

  const handleToggle = async (id: number, is_active: boolean) => {
    await fetch(`/samatvam/api/samatvam/admin/map-zones/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ is_active: !is_active }),
    });
    fetchZones();
  };

  const handleDelete = async (id: number, zone_id: string) => {
    if (!window.confirm(`Delete zone "${zone_id}" permanently?`)) return;
    await fetch(`/samatvam/api/samatvam/admin/map-zones/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchZones();
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '7px 10px', borderRadius: 7, fontSize: 12,
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
    color: 'white', outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = { color: '#ffffff55', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4, display: 'block' };

  return (
    <div style={{ height: '100vh', background: '#0d0a04', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ flexShrink: 0, padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(5,4,2,0.9)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(16,185,129,0.15)', zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: 'linear-gradient(135deg,#065f46,#10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 14px 3px #10b98140', fontSize: 16 }}>📍</div>
          <div>
            <h2 style={{ color: '#6ee7b7', fontWeight: 900, fontSize: 15, margin: 0, letterSpacing: -0.3 }}>Task Zone Manager</h2>
            <p style={{ color: '#10b98155', fontSize: 9.5, margin: 0, fontWeight: 700, letterSpacing: 2 }}>ADMIN · SAMATVAM CITY MAP</p>
          </div>
        </div>
        <button onClick={onBack} style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 9, padding: '6px 16px', color: '#6ee7b7', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>← Back</button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Left panel: form + list */}
        <div style={{ width: 320, flexShrink: 0, borderRight: '1px solid rgba(16,185,129,0.1)', background: 'rgba(5,4,2,0.75)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Add Zone form */}
          <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
            <p style={{ color: '#10b981', fontSize: 9.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 10px' }}>Add New Zone</p>
            <p style={{ color: '#10b98170', fontSize: 10, margin: '0 0 10px', lineHeight: 1.5 }}>Click on the map to set coordinates, then fill details below.</p>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Zone ID</label>
                <input style={inputStyle} value={form.zone_id} onChange={e => setForm(f => ({ ...f, zone_id: e.target.value }))} placeholder="e.g. task1-food-drop" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Task #</label>
                <input style={inputStyle} type="number" value={form.task_number} onChange={e => setForm(f => ({ ...f, task_number: e.target.value }))} placeholder="1" />
              </div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>Label</label>
              <input style={inputStyle} value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} placeholder="e.g. Food Drop Zone" />
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>CX (click map)</label>
                <input style={{ ...inputStyle, borderColor: form.cx ? 'rgba(16,185,129,0.4)' : 'rgba(255,255,255,0.12)' }} type="number" value={form.cx} onChange={e => setForm(f => ({ ...f, cx: e.target.value }))} placeholder="x" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>CY (click map)</label>
                <input style={{ ...inputStyle, borderColor: form.cy ? 'rgba(16,185,129,0.4)' : 'rgba(255,255,255,0.12)' }} type="number" value={form.cy} onChange={e => setForm(f => ({ ...f, cy: e.target.value }))} placeholder="y" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>W</label>
                <input style={inputStyle} type="number" value={form.w} onChange={e => setForm(f => ({ ...f, w: e.target.value }))} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>H</label>
                <input style={inputStyle} type="number" value={form.h} onChange={e => setForm(f => ({ ...f, h: e.target.value }))} />
              </div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>Note</label>
              <input style={inputStyle} value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="Short description" />
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <div style={{ flex: 2 }}>
                <label style={labelStyle}>Target</label>
                <input style={inputStyle} type="number" value={form.counter_target} onChange={e => setForm(f => ({ ...f, counter_target: e.target.value }))} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Unit</label>
                <input style={inputStyle} value={form.counter_unit} onChange={e => setForm(f => ({ ...f, counter_unit: e.target.value }))} />
              </div>
            </div>
            {msg && <p style={{ color: msg.startsWith('✅') ? '#6ee7b7' : '#f87171', fontSize: 10, margin: '0 0 8px', fontWeight: 700 }}>{msg}</p>}
            <button onClick={handleSubmit} disabled={saving} style={{ width: '100%', padding: '9px', borderRadius: 8, fontSize: 12, fontWeight: 800, background: saving ? 'rgba(16,185,129,0.08)' : 'rgba(16,185,129,0.2)', border: `1px solid ${saving ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.45)'}`, color: saving ? '#10b98150' : '#6ee7b7', cursor: saving ? 'default' : 'pointer', transition: 'all 0.2s' }}>
              {saving ? 'Creating…' : '+ Create Zone'}
            </button>
          </div>

          {/* Zone list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px' }}>
            <p style={{ color: '#ffffff25', fontSize: 9.5, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 8px' }}>Existing Zones ({zones.length})</p>
            {zones.length === 0 ? (
              <p style={{ color: '#ffffff15', fontSize: 11 }}>No zones yet. Add one above.</p>
            ) : zones.map(z => (
              <div key={z.id}
                onMouseEnter={() => setHoverZone(z.zone_id)}
                onMouseLeave={() => setHoverZone(null)}
                style={{ marginBottom: 8, padding: '9px 10px', borderRadius: 8, background: hoverZone === z.zone_id ? 'rgba(16,185,129,0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${z.is_active ? 'rgba(16,185,129,0.25)' : 'rgba(255,255,255,0.07)'}`, transition: 'all 0.15s' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <span style={{ flex: 1, color: z.is_active ? '#a7f3d0' : '#ffffff40', fontSize: 11, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{z.label}</span>
                  {z.task_number != null && <span style={{ fontSize: 9, fontWeight: 900, color: '#f59e0b', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 4, padding: '1px 5px' }}>T{z.task_number}</span>}
                  <span style={{ fontSize: 9, fontWeight: 700, color: z.is_active ? '#10b981' : '#ffffff30', background: z.is_active ? 'rgba(16,185,129,0.1)' : 'rgba(255,255,255,0.04)', border: `1px solid ${z.is_active ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.06)'}`, borderRadius: 4, padding: '1px 6px' }}>{z.is_active ? 'active' : 'off'}</span>
                </div>
                <p style={{ color: '#ffffff30', fontSize: 9.5, margin: '0 0 5px', fontFamily: 'monospace' }}>{z.zone_id} · ({z.cx},{z.cy}) · {z.w}×{z.h}</p>
                <div style={{ display: 'flex', gap: 5 }}>
                  <button onClick={() => handleToggle(z.id, z.is_active)} style={{ flex: 1, padding: '4px 0', borderRadius: 5, fontSize: 9.5, fontWeight: 700, background: z.is_active ? 'rgba(245,158,11,0.08)' : 'rgba(16,185,129,0.08)', border: `1px solid ${z.is_active ? 'rgba(245,158,11,0.2)' : 'rgba(16,185,129,0.2)'}`, color: z.is_active ? '#f59e0b' : '#10b981', cursor: 'pointer' }}>
                    {z.is_active ? 'Disable' : 'Enable'}
                  </button>
                  <button onClick={() => handleDelete(z.id, z.zone_id)} style={{ padding: '4px 10px', borderRadius: 5, fontSize: 9.5, fontWeight: 700, background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.18)', color: '#f87171', cursor: 'pointer' }}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Map */}
        <div style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', background: 'radial-gradient(ellipse 80% 70% at 50% 55%, #0d1a0d 0%, #0a0d04 60%, #050603 100%)' }}>
          <div style={{ lineHeight: 0, boxShadow: '0 0 60px 15px #10b98112, 0 0 0 1.5px #10b98128, 0 6px 40px rgba(0,0,0,0.9)', borderRadius: 4 }}>
            <svg
              ref={svgRef}
              viewBox={`0 0 ${MAP_W} ${MAP_H}`}
              width={MAP_W}
              height={MAP_H}
              style={{ display: 'block', maxWidth: '100%', maxHeight: 'calc(100vh - 100px)', width: '100%', height: 'auto', borderRadius: 4, userSelect: 'none', cursor: 'crosshair' }}
              onMouseMove={handleMouseMove}
              onMouseLeave={() => setCoords(null)}
              onClick={handleMapClick}
            >
              <image href={activeMapSrc} xlinkHref={activeMapSrc} width={MAP_W} height={MAP_H} preserveAspectRatio="xMidYMid meet" />

              {/* Render all zones */}
              {zones.map(zone => {
                const isHov = hoverZone === zone.zone_id;
                const svgX = zone.cx - zone.w / 2;
                const svgY = (MAP_H - zone.cy) - zone.h / 2;
                const activeCol = zone.is_active ? '#10b981' : '#94a3b8';
                return (
                  <g key={zone.id}>
                    <rect x={svgX} y={svgY} width={zone.w} height={zone.h} rx={5}
                      fill={isHov ? `${activeCol}22` : `${activeCol}08`}
                      stroke={isHov ? `${activeCol}cc` : `${activeCol}55`}
                      strokeWidth={isHov ? 2 : 1.5}
                      strokeDasharray={zone.is_active ? 'none' : '4 3'}
                    />
                    <circle cx={zone.cx} cy={MAP_H - zone.cy} r={3} fill={activeCol} opacity={0.8} />
                    {isHov && (
                      <text x={zone.cx} y={MAP_H - zone.cy - 14} textAnchor="middle" fontFamily="system-ui" fontSize={10} fontWeight={700} fill={activeCol}>
                        {zone.label}
                      </text>
                    )}
                  </g>
                );
              })}

              {/* Preview zone for current form cx/cy */}
              {form.cx && form.cy && (
                <g>
                  <rect
                    x={parseInt(form.cx) - (parseInt(form.w) || 60) / 2}
                    y={(MAP_H - parseInt(form.cy)) - (parseInt(form.h) || 60) / 2}
                    width={parseInt(form.w) || 60}
                    height={parseInt(form.h) || 60}
                    rx={5}
                    fill="rgba(251,191,36,0.12)"
                    stroke="rgba(251,191,36,0.8)"
                    strokeWidth={2}
                    strokeDasharray="6 3"
                  />
                  <circle cx={parseInt(form.cx)} cy={MAP_H - parseInt(form.cy)} r={4} fill="#fbbf24" />
                </g>
              )}

              {/* Coordinate readout */}
              {coords && (
                <g pointerEvents="none">
                  <rect x={8} y={MAP_H - 28} width={100} height={20} rx={4} fill="rgba(5,4,2,0.85)" stroke="rgba(16,185,129,0.35)" strokeWidth={1} />
                  <text x={58} y={MAP_H - 14} fontFamily="monospace" fontSize={11} fontWeight={700} fill="#10b981" textAnchor="middle">
                    {coords.x}, {coords.y}
                  </text>
                </g>
              )}
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Vayal Farmland Grid ──────────────────────────────────────────────────────

const VAYAL_W = 200;
const VAYAL_H = 125;
const VP_COLS = 8;
const VP_ROWS = 8;
// Isometric geometry — measured from vayal-blank.png (1024×1024 source image)
// Diamond N peak is at row 43, equator (widest) at row 366, S peak at row 689 (=2*366-43)
const VAYAL_TOP_PAD = 43  / 1024;  // fraction of image height above diamond N peak
const VAYAL_DFH     = 646 / 1024;  // diamond face height as fraction of image size
const VAYAL_STEP    = 323 / 1024;  // = VAYAL_DFH / 2 — vertical step per diagonal
// Per-stage anchor [s_row, imgSz].
// Full image drawn at natural square scale (itw×itw), positioned so image row s_row → dS.y.
// s_row = theoretical diamond S corner = 2*peak - N (symmetric diamond).
// Dark soil bands are handled by image-level blending (see Python processing script).
// [s_row, imgSz, anchor]
// anchor = equator - 40 (blend zone offset); anchoring this row → dS.y
// ensures the dark bottom-of-top-face rows fall below dS.y and are auto-clipped
const VAYAL_SRC: Array<[number, number, number]> = [
  [ 689, 1024, 366], // 0  blank tile (unchanged)
  [ 689, 1024, 366], // 1  blank-style (matched geometry)
  [1016, 1189,1016], // 2  padded to match stage-1 margin (9 rows)
  [ 835, 1024, 835], // 3  s_peak=835
  [ 859, 1024, 859], // 4  s_peak=859
  [ 857, 1024, 857], // 5  s_peak=857
  [ 856, 1024, 856], // 6  s_peak=856
  [ 868, 1024, 868], // 7  s_peak=868
  [ 923,  976, 923], // 8  s_peak=923
  [ 926,  976, 926], // 9  s_peak=926
  [ 887,  976, 887], // 10 s_peak=887
];

/* ── Image cache (module-level, persists across renders) ── */
const _vayalImgCache: Record<string, HTMLImageElement> = {};
function vayalLoadImg(src: string): Promise<HTMLImageElement> {
  const c = _vayalImgCache[src];
  if (c?.complete && c.naturalWidth > 0) return Promise.resolve(c);
  return new Promise((resolve, reject) => {
    if (c) {
      c.addEventListener('load', () => resolve(c), { once: true });
      c.addEventListener('error', reject, { once: true });
      return;
    }
    const img = new window.Image();
    _vayalImgCache[src] = img;
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// ─── My NFTs Page ─────────────────────────────────────────────────────────────

type NftAsset = {
  asset_id: number;
  plot_x: number;
  plot_y: number;
  mint_tier: string;
  minted_at: string;
  stage: number;
  collection_name: string;
  schema_name: string;
  template_name: string;
  vayal_rate: number;
};

const STAGE_LABELS = ['','Bare','Tilled','Sown','Sprout','Early','Mid','Advanced','Harvest','Cutting','Replenish'];
const TIER_LABELS: Record<string, string> = { wax: '10 WAX', food: 'Food', shram: 'Food + Shram', wari: 'Food + WARI', bhoomi: 'Bhoomi' };

function MyNftsPage({ onBack, onOpenMint }: { onBack: () => void; onOpenMint: () => void }) {
  const [account, setAccount] = React.useState('');
  const [nfts, setNfts] = React.useState<NftAsset[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const stored = localStorage.getItem('samatvam_active_account') || '';
    setAccount(stored);
    if (!stored) { setLoading(false); return; }
    fetch(`/samatvam/api/samatvam/vayal/my-nfts/${stored}`)
      .then(r => r.json())
      .then(d => { if (d.success) setNfts(d.nfts); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const totalRate = nfts.reduce((s, n) => s + n.vayal_rate, 0);

  return (
    <div className="min-h-screen" style={{ background: '#020617', color: '#e2e8f0' }}>
      {/* Header */}
      <div className="sticky top-0 z-50 flex items-center justify-between px-5 py-4"
        style={{ background: 'rgba(2,6,23,0.95)', borderBottom: '1px solid rgba(255,255,255,0.07)', backdropFilter: 'blur(8px)' }}>
        <div className="flex items-center gap-3">
          <button onClick={onBack}
            className="w-9 h-9 rounded-xl flex items-center justify-center transition-colors hover:bg-white/5"
            style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
          </button>
          <div>
            <div className="text-white font-black text-base leading-tight">My NFTs</div>
            <div className="text-slate-600 text-[11px]">On-chain assets · WAX AtomicAssets</div>
          </div>
        </div>
        {account && (
          <div className="text-[11px] font-bold px-3 py-1.5 rounded-full"
            style={{ background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.25)', color: '#a78bfa' }}>
            {account}
          </div>
        )}
      </div>

      <div className="px-5 py-6 max-w-5xl mx-auto">

        {/* No wallet connected */}
        {!account && (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <div className="text-5xl">◈</div>
            <p className="text-white font-black text-xl">No wallet connected</p>
            <p className="text-slate-500 text-sm">Connect your WAX wallet via Atavi Forest to view your NFTs.</p>
          </div>
        )}

        {/* Loading */}
        {account && loading && (
          <div className="flex items-center justify-center py-24">
            <div className="text-slate-500 text-sm animate-pulse">Loading your assets…</div>
          </div>
        )}

        {/* Empty */}
        {account && !loading && nfts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 gap-5">
            <div className="text-6xl">🌱</div>
            <p className="text-white font-black text-xl">No NFTs yet</p>
            <p className="text-slate-500 text-sm text-center max-w-xs">You don't own any on-chain assets yet. Craft or buy a Vayal farmland to get started.</p>
            <button onClick={onOpenMint}
              className="px-6 py-3 rounded-2xl font-black text-sm mt-2 transition-all hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #fcd34d, #f59e0b)', color: '#451a03' }}>
              Mint Vayal Farmland →
            </button>
          </div>
        )}

        {/* Has NFTs */}
        {account && !loading && nfts.length > 0 && (
          <>
            {/* Summary bar */}
            <div className="flex flex-wrap gap-4 mb-6">
              {[
                { label: 'Total Assets', value: String(nfts.length), color: '#c4b5fd', bg: 'rgba(139,92,246,0.08)', border: 'rgba(139,92,246,0.2)' },
                { label: 'VAYAL / hr', value: `+${totalRate}`, color: '#4ade80', bg: 'rgba(34,197,94,0.08)', border: 'rgba(34,197,94,0.2)' },
                { label: 'Collection', value: 'ahantravyuhw', color: '#fcd34d', bg: 'rgba(251,191,36,0.08)', border: 'rgba(251,191,36,0.2)' },
              ].map(s => (
                <div key={s.label} className="flex-1 min-w-[140px] rounded-2xl px-4 py-3"
                  style={{ background: s.bg, border: `1px solid ${s.border}` }}>
                  <div className="text-[10px] font-bold uppercase tracking-widest mb-1 text-slate-500">{s.label}</div>
                  <div className="text-xl font-black" style={{ color: s.color }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* NFT grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {nfts.map(nft => (
                <div key={nft.asset_id} className="rounded-2xl overflow-hidden flex flex-col"
                  style={{ background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(255,255,255,0.07)' }}>

                  {/* Image */}
                  <div className="relative w-full" style={{ aspectRatio: '1/1', background: 'rgba(2,6,23,0.8)' }}>
                    <img
                      src={`${process.env.PUBLIC_URL}/images/farmland-stage-${Math.min(nft.stage, 10)}.jpg`}
                      alt={`Stage ${nft.stage}`}
                      className="w-full h-full object-cover"
                      onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                    {/* Stage badge */}
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-lg text-[10px] font-black"
                      style={{ background: 'rgba(0,0,0,0.7)', color: '#fcd34d', border: '1px solid rgba(251,191,36,0.3)' }}>
                      Stage {nft.stage}
                    </div>
                    {/* Rate badge */}
                    <div className="absolute top-2 right-2 px-2 py-0.5 rounded-lg text-[10px] font-black"
                      style={{ background: 'rgba(0,0,0,0.7)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)' }}>
                      +{nft.vayal_rate}/hr
                    </div>
                  </div>

                  {/* Info */}
                  <div className="px-3 py-3 flex flex-col gap-1.5 flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-white font-black text-sm">{nft.template_name}</span>
                      <span className="text-[10px] text-slate-600 font-mono">#{nft.asset_id}</span>
                    </div>
                    <div className="text-[11px] text-slate-500">
                      Plot ({nft.plot_x}, {nft.plot_y}) · {STAGE_LABELS[nft.stage] || `Stage ${nft.stage}`}
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-bold"
                        style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.15)', color: '#fcd34d' }}>
                        {TIER_LABELS[nft.mint_tier] || nft.mint_tier}
                      </span>
                      <a
                        href={`https://gyn.ahantravyuh.com/nft?id=${nft.asset_id}`}
                        target="_blank" rel="noreferrer"
                        className="text-[10px] text-slate-600 hover:text-slate-400 transition-colors"
                        onClick={e => e.stopPropagation()}>
                        view ↗
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Mint more */}
            <div className="mt-8 flex justify-center">
              <button onClick={onOpenMint}
                className="flex items-center gap-2 px-6 py-3 rounded-2xl font-black text-sm transition-all hover:scale-105"
                style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)', color: '#fcd34d' }}>
                + Mint more farmland
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function VayalPage({ onBack, onOpenMint }: { onBack: () => void; onOpenMint: () => void }) {
  const [vpX, setVpX] = React.useState(0);
  const [vpY, setVpY] = React.useState(0);
  const [plots, setPlots] = React.useState<{ plot_x: number; plot_y: number; owner: string; stage: number }[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [canvasW, setCanvasW] = React.useState(0);
  const [selectedTile, setSelectedTile] = React.useState<{ gx: number; gy: number; owner: string; stage: number; px: number; py: number } | null>(null);
  const canvasRef    = React.useRef<HTMLCanvasElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const tileDims     = React.useRef({ tw: 1, th: 1, stepY: 1 });
  const plotMapRef   = React.useRef<Record<string, { owner: string; stage: number }>>({});

  const fetchViewport = React.useCallback((x: number, y: number) => {
    setLoading(true);
    fetch(`/samatvam/api/samatvam/vayal/viewport?x=${x}&y=${y}&w=${VP_COLS}&h=${VP_ROWS}`)
      .then(r => r.json())
      .then(d => { if (d.success) setPlots(d.plots); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { fetchViewport(0, 0); }, [fetchViewport]);

  const move = (dx: number, dy: number) => {
    const nx = Math.max(0, Math.min(VAYAL_W - VP_COLS, vpX + dx));
    const ny = Math.max(0, Math.min(VAYAL_H - VP_ROWS, vpY + dy));
    setVpX(nx); setVpY(ny);
    fetchViewport(nx, ny);
  };

  /* keep plotMapRef current every render */
  plotMapRef.current = {};
  plots.forEach(p => { plotMapRef.current[`${p.plot_x},${p.plot_y}`] = p; });

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const cx = (e.clientX - rect.left) * scaleX;
    const cy = (e.clientY - rect.top)  * scaleY;

    const { tw, stepY } = tileDims.current;
    const originX = (VP_ROWS - 1) * tw / 2;
    const topPad  = tw * VAYAL_TOP_PAD;
    const dFH     = tw * VAYAL_DFH;

    // Approximate cell from reverse math
    const approxCPR = cy / stepY;
    const approxCMR = 2 * (cx - originX) / tw;
    const approxCol = Math.round((approxCPR + approxCMR) / 2);
    const approxRow = Math.round((approxCPR - approxCMR) / 2);

    // Build a small candidate set around the estimate, sorted frontmost-first (highest col+row)
    const cands: [number, number][] = [];
    for (let dc = -1; dc <= 1; dc++)
      for (let dr = -1; dr <= 1; dr++)
        cands.push([approxCol + dc, approxRow + dr]);
    cands.sort((a, b) => (b[0] + b[1]) - (a[0] + a[1]));

    for (const [col, row] of cands) {
      if (col < 0 || row < 0 || col >= VP_COLS || row >= VP_ROWS) continue;
      // Pixel origin of this tile
      const px = Math.round(originX + (col - row) * tw / 2);
      const py = Math.round((col + row) * stepY);
      // Diamond hit test: |dx|/(tw/2) + |dy|/(dFH/2) <= 1
      const dx = cx - (px + tw / 2);
      const dy = cy - (py + topPad + dFH / 2);
      if (Math.abs(dx) / (tw / 2) + Math.abs(dy) / (dFH / 2) > 1) continue;
      // Click is geometrically inside this tile's diamond — this is the correct tile
      const gx = vpX + col;
      const gy = vpY + row;
      const plot = plotMapRef.current[`${gx},${gy}`];
      if (plot?.owner) {
        setSelectedTile({ gx, gy, owner: plot.owner, stage: plot.stage, px: e.clientX, py: e.clientY });
      } else {
        setSelectedTile(null);
      }
      return;
    }
    setSelectedTile(null);
  };

  /* ── Resize: derive tile dimensions from container width ── */
  React.useEffect(() => {
    const canvas    = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const sync = () => {
      const cw = container.clientWidth;
      if (!cw) return;
      /*
       * Canvas width spans (VP_COLS + VP_ROWS) half-tile-widths:
       *   leftmost tile  (col=0, row=VP_ROWS-1): screen x = originX - (VP_ROWS-1)*tw/2 = 0
       *   rightmost edge (col=VP_COLS-1, row=0) + tw: originX + (VP_COLS-1)*tw/2 + tw = cw
       *   → originX = (VP_ROWS-1)*tw/2
       *   → cw = (VP_ROWS-1)*tw/2 + (VP_COLS-1)*tw/2 + tw = (VP_COLS+VP_ROWS)/2 * tw
       *   → tw = 2*cw / (VP_COLS+VP_ROWS)
       */
      const tw    = (2 * cw) / (VP_COLS + VP_ROWS);
      const stepY = tw * VAYAL_STEP;  // measured: dFH/2 = 323/1024 of image size
      tileDims.current = { tw, th: tw, stepY };
      canvas.width  = Math.round(cw);
      canvas.height = Math.round((VP_COLS + VP_ROWS - 2) * stepY + tw);
      setCanvasW(cw);
    };

    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  /* ── Draw: isometric diamond projection with per-tile clip ── */
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || canvas.width === 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const BLANK = `${process.env.PUBLIC_URL}/images/vayal-blank.png`;
    const { tw, stepY } = tileDims.current;

    /*
     * originX = (VP_ROWS-1)*tw/2 so leftmost tile (col=0, row=VP_ROWS-1) starts at x=0.
     * Each tile is clipped to its diamond face (measured from blank tile):
     *   N peak at (tw/2, topPad), E at (tw, topPad+dFH/2), S at (tw/2, topPad+dFH), W at (0, topPad+dFH/2)
     * Tiles drawn back→front (ascending col+row) so front tiles' faces render over back tiles.
     */
    const originX = (VP_ROWS - 1) * tw / 2;
    const topPad  = tw * VAYAL_TOP_PAD;
    const dFH     = tw * VAYAL_DFH;

    const tiles: { col: number; row: number; gx: number; gy: number }[] = [];
    for (let row = 0; row < VP_ROWS; row++)
      for (let col = 0; col < VP_COLS; col++)
        tiles.push({ col, row, gx: vpX + col, gy: vpY + row });
    tiles.sort((a, b) => (a.col + a.row) - (b.col + b.row));

    let cancelled = false;

    (async () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      /* Dark wooden background with grain stripes */
      ctx.fillStyle = '#1a0c04';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      /* Subtle grain lines */
      const grainCount = Math.ceil(canvas.width / 6);
      for (let i = 0; i < grainCount; i++) {
        const x = i * 6 + (i % 3) * 1.5;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x + canvas.height * 0.15, canvas.height);
        ctx.strokeStyle = i % 5 === 0 ? 'rgba(80,35,5,0.35)' : 'rgba(60,25,4,0.18)';
        ctx.lineWidth = i % 7 === 0 ? 1.5 : 0.8;
        ctx.stroke();
      }

      for (const { col, row, gx, gy } of tiles) {
        if (cancelled) return;

        const plot = plotMapRef.current[`${gx},${gy}`];
        const stage = plot?.stage ?? 0;
        const src = stage === 0
          ? BLANK
          : `${process.env.PUBLIC_URL}/images/farmland-stage-${stage}.png?v=3`;

        const px  = Math.round(originX + (col - row) * tw / 2);
        const py  = Math.round((col + row) * stepY);
        const itw = Math.round(tw);

        /* Diamond corner coordinates (clip based on blank tile geometry) */
        const dN = { x: px + itw / 2, y: py + topPad           };
        const dE = { x: px + itw,     y: py + topPad + dFH / 2  };
        const dS = { x: px + itw / 2, y: py + topPad + dFH      };
        const dW = { x: px,           y: py + topPad + dFH / 2  };

        let img: HTMLImageElement | null = null;
        try { img = await vayalLoadImg(src); } catch {
          try { img = await vayalLoadImg(BLANK); } catch {}
        }
        if (cancelled) return;

        /* Anchor: for stage tiles, equator row → dS.y so front-face falls outside diamond clip.
           For blank tile, s_row → dS.y as before. */
        const [s_row, imgSz, equator] = VAYAL_SRC[Math.min(stage, 10)] ?? VAYAL_SRC[0];
        // Blank: scale = itw/imgSz, dy = py
        const b_scale = itw / imgSz;
        const b_dy    = Math.round(dN.y + dFH - s_row * b_scale);

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(dN.x, dN.y);
        ctx.lineTo(dE.x, dE.y);
        ctx.lineTo(dS.x, dS.y);
        ctx.lineTo(dW.x, dW.y);
        ctx.closePath();
        ctx.clip();

        /* Draw blank ground first (full diamond) */
        try {
          const blankImg = await vayalLoadImg(BLANK);
          const [b_srow2, b_sz2] = VAYAL_SRC[0];
          const bs2 = itw / b_sz2;
          const bdy2 = Math.round(dN.y + dFH - b_srow2 * bs2);
          ctx.drawImage(blankImg, 0, 0, b_sz2, b_sz2, px, bdy2, itw, itw);
        } catch {}

        if (img && stage === 1) {
          /* Stage 1: blank-style rendering, 4% oversized to eliminate junction gaps */
          const OVER = 1.04;
          const dW1 = itw * OVER;
          const dOff = (dW1 - itw) / 2;
          const sc1 = dW1 / imgSz;
          const dy1 = Math.round(dN.y + dFH - s_row * sc1);
          ctx.drawImage(img, 0, 0, imgSz, imgSz, px - dOff, dy1, dW1, dW1);
        } else if (img && stage > 1) {
          /* Stage 2+: anchor equator → dS.y; clip S tip so dark shadows don't bleed */
          const eqScale = itw / equator;
          const drawSz  = imgSz * eqScale;
          const drawX   = px + (itw - drawSz) / 2;
          const drawY   = Math.round(dN.y + dFH - itw);
          const tipCut  = dFH * 0.12;
          const clipSy  = dS.y - tipCut;
          const clipHW  = tipCut; // diamond slope 1:1 x:y
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(dN.x, dN.y);
          ctx.lineTo(dE.x, dE.y);
          ctx.lineTo(dS.x + clipHW, clipSy);
          ctx.lineTo(dS.x - clipHW, clipSy);
          ctx.lineTo(dW.x, dW.y);
          ctx.closePath();
          ctx.clip();
          ctx.drawImage(img, 0, 0, imgSz, imgSz, drawX, drawY, drawSz, drawSz);
          ctx.restore();
        } else if (!img) {
          ctx.fillStyle = '#0f172a';
          ctx.fill();
        }

        ctx.restore();
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plots, vpX, vpY, canvasW]);

  const NavBtn = ({ label, onClick, disabled }: { label: string; onClick: () => void; disabled: boolean }) => (
    <button onClick={onClick} disabled={disabled} style={{
      background: disabled ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.07)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: 8, color: disabled ? '#334155' : '#94a3b8',
      fontSize: 13, fontWeight: 900, cursor: disabled ? 'default' : 'pointer',
      padding: '8px 14px', transition: 'all 0.15s',
    }}>{label}</button>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#020617', color: '#e2e8f0' }}>

      {/* Header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 40, background: 'rgba(2,6,23,0.96)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '14px 28px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>←</button>
        <div>
          <p style={{ color: '#fbbf24', fontSize: 17, fontWeight: 900, margin: 0, letterSpacing: 1 }}>VAYAL FARMLANDS</p>
          <p style={{ color: '#475569', fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', margin: 0 }}>25,000 Farmlands · 200 × 125 Grid · 10 Growth Stages</p>
        </div>
        <button onClick={onOpenMint}
          style={{ marginLeft: 'auto', background: 'linear-gradient(135deg, #d97706, #b45309)', border: 'none', borderRadius: 10, padding: '8px 16px', fontSize: 12, color: '#fff', fontWeight: 900, cursor: 'pointer', letterSpacing: 0.5, boxShadow: '0 2px 12px rgba(217,119,6,0.35)' }}>
          🌾 Mint Vayal
        </button>
        <div style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 10, padding: '6px 14px', fontSize: 11, color: '#fbbf24', fontWeight: 700 }}>
          ({vpX}–{vpX + VP_COLS - 1}, {vpY}–{vpY + VP_ROWS - 1}) / {VAYAL_W}×{VAYAL_H}
        </div>
      </div>

      <div style={{ padding: '24px 28px' }}>

        {/* Canvas — tiles drawn flush, isometric stagger */}
        <div ref={containerRef} style={{ width: '100%', lineHeight: 0, marginBottom: 4, position: 'relative' }}>
          <canvas ref={canvasRef} onClick={handleCanvasClick} style={{ display: 'block', width: '100%', cursor: 'pointer' }} />
        </div>

        {/* Owner popup */}
        {selectedTile && (
          <div
            onClick={() => setSelectedTile(null)}
            style={{
              position: 'fixed', top: selectedTile.py - 80, left: selectedTile.px - 90,
              zIndex: 200, pointerEvents: 'auto',
            }}>
            <div style={{
              background: 'rgba(2,6,23,0.97)', border: '1px solid rgba(251,191,36,0.3)',
              borderRadius: 12, padding: '10px 14px', minWidth: 160,
              boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
            }}>
              <div style={{ fontSize: 9, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#fbbf24', marginBottom: 6 }}>
                Farmland ({selectedTile.gx}, {selectedTile.gy})
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9', fontFamily: 'monospace', marginBottom: 4 }}>
                {selectedTile.owner}
              </div>
              <div style={{ fontSize: 10, color: '#64748b' }}>
                Stage {selectedTile.stage}
              </div>
              <button onClick={() => setSelectedTile(null)} style={{
                position: 'absolute', top: 6, right: 8, background: 'none', border: 'none',
                color: '#475569', fontSize: 14, cursor: 'pointer', lineHeight: 1,
              }}>✕</button>
            </div>
          </div>
        )}
        {loading && <p style={{ color: '#475569', fontSize: 11, textAlign: 'center', margin: '4px 0 16px' }}>Loading…</p>}

        {/* Navigation */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginBottom: 24 }}>
          <NavBtn label="▲" onClick={() => move(0, -VP_ROWS)} disabled={vpY === 0} />
          <div style={{ display: 'flex', gap: 8 }}>
            <NavBtn label="◄" onClick={() => move(-VP_COLS, 0)} disabled={vpX === 0} />
            <div style={{ width: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 10, color: '#334155', fontWeight: 700, textAlign: 'center', lineHeight: 1.5 }}>
                {Math.round(((vpX + vpY * VAYAL_W) / 25000) * 100)}%<br/>explored
              </span>
            </div>
            <NavBtn label="►" onClick={() => move(VP_COLS, 0)} disabled={vpX + VP_COLS >= VAYAL_W} />
          </div>
          <NavBtn label="▼" onClick={() => move(0, VP_ROWS)} disabled={vpY + VP_ROWS >= VAYAL_H} />
        </div>

        {/* Mini-map */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }}>
            <svg width={200} height={125} style={{ display: 'block' }}>
              <rect width={200} height={125} fill="rgba(15,15,30,0.9)" rx={6} />
              {plots.map(p => (
                <rect key={`${p.plot_x},${p.plot_y}`} x={p.plot_x} y={p.plot_y} width={1} height={1}
                  fill={p.stage >= 8 ? '#6ee7b7' : p.stage >= 5 ? '#fbbf24' : '#38bdf8'} opacity={0.9} />
              ))}
              <rect x={vpX} y={vpY} width={VP_COLS} height={VP_ROWS}
                fill="none" stroke="#fbbf24" strokeWidth={1} opacity={0.8} />
            </svg>
            <p style={{ fontSize: 8, color: '#334155', textAlign: 'center', margin: '4px 0', letterSpacing: 1 }}>MINIMAP · 200×125</p>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── Vayal Test Map ──────────────────────────────────────────────────────────

const VT_COLS = 8;
const VT_ROWS = 8;
const VT_GX = 16;   // fixed viewport: x 16–23
const VT_GY = 0;    // fixed viewport: y 0–7

/* Test map uses its own image set: vayal-test-stage-N.png
   These are completely separate from the main farmland-stage-N.png images. */
const VT_SRC: Array<[number, number, number]> = [
  [ 689, 1024, 366], // 0 blank
  [ 689, 1024, 366], // 1 same geometry as blank
  [ 689, 1024, 366], // 2 same geometry as blank
  [ 689, 1024, 366], // 3 same geometry as blank
  [ 689, 1024, 366], // 4 same geometry as blank
  [ 940, 1024, 940], // 5
  [ 940, 1024, 940], // 6
  [ 940, 1024, 940], // 7
  [ 940, 1024, 940], // 8
  [ 940, 1024, 940], // 9
  [ 940, 1024, 940], // 10
];

const _vtImgCache: Record<string, HTMLImageElement> = {};
function vtLoadImg(src: string): Promise<HTMLImageElement> {
  const c = _vtImgCache[src];
  if (c?.complete && c.naturalWidth > 0) return Promise.resolve(c);
  return new Promise((resolve, reject) => {
    if (c) {
      c.addEventListener('load', () => resolve(c), { once: true });
      c.addEventListener('error', reject, { once: true });
      return;
    }
    const img = new window.Image();
    _vtImgCache[src] = img;
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function VayalTestPage({ onBack }: { onBack: () => void }) {
  const [plots, setPlots] = React.useState<{ plot_x: number; plot_y: number; owner: string; stage: number }[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [canvasW, setCanvasW] = React.useState(0);
  const canvasRef    = React.useRef<HTMLCanvasElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const tileDims     = React.useRef({ tw: 1, th: 1, stepY: 1 });
  const plotMapRef   = React.useRef<Record<string, { owner: string; stage: number }>>({});

  React.useEffect(() => {
    setLoading(true);
    fetch(`/samatvam/api/samatvam/vayal/viewport?x=${VT_GX}&y=${VT_GY}&w=${VT_COLS}&h=${VT_ROWS}`)
      .then(r => r.json())
      .then(d => { if (d.success) setPlots(d.plots); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  plotMapRef.current = {};
  plots.forEach(p => { plotMapRef.current[`${p.plot_x},${p.plot_y}`] = p; });

  /* Sizing */
  React.useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;
    const sync = () => {
      const cw = container.clientWidth;
      const tw = (2 * cw) / (VT_COLS + VT_ROWS);
      const stepY = tw * VAYAL_STEP;
      tileDims.current = { tw, th: tw, stepY };
      canvas.width  = Math.round(cw);
      canvas.height = Math.round((VT_COLS + VT_ROWS - 2) * stepY + tw);
      setCanvasW(cw);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  /* Draw */
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || canvas.width === 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const BLANK = `${process.env.PUBLIC_URL}/images/vayal-blank.png`;
    const { tw, stepY } = tileDims.current;
    const originX = (VT_ROWS - 1) * tw / 2;
    const topPad  = tw * VAYAL_TOP_PAD;
    const dFH     = tw * VAYAL_DFH;

    let cancelled = false;
    (async () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const tiles: { col: number; row: number; gx: number; gy: number }[] = [];
      for (let r = 0; r < VT_ROWS; r++)
        for (let c = 0; c < VT_COLS; c++)
          tiles.push({ col: c, row: r, gx: VT_GX + c, gy: VT_GY + r });
      tiles.sort((a, b) => (a.col + a.row) - (b.col + b.row));

      for (const { col, row, gx, gy } of tiles) {
        if (cancelled) return;

        const plot = plotMapRef.current[`${gx},${gy}`];
        const stage = plot?.stage ?? 0;
        /* Test map uses vayal-test-stage-N.png */
        const src = stage === 0
          ? BLANK
          : `${process.env.PUBLIC_URL}/images/vayal-test-stage-${stage}.png?v=1`;

        const px  = Math.round(originX + (col - row) * tw / 2);
        const py  = Math.round((col + row) * stepY);
        const itw = Math.round(tw);
        const OVER = 1.06; // draw 6% larger so tiles overlap at junctions
        const drawW = itw * OVER;
        const drawOff = (drawW - itw) / 2; // offset to keep centered

        const dN = { x: px + itw / 2, y: py + topPad };
        const dE = { x: px + itw,     y: py + topPad + dFH / 2 };
        const dS = { x: px + itw / 2, y: py + topPad + dFH };
        const dW = { x: px,           y: py + topPad + dFH / 2 };

        let img: HTMLImageElement | null = null;
        try { img = await vtLoadImg(src); } catch {
          try { img = await vtLoadImg(BLANK); } catch {}
        }
        if (cancelled) return;

        const [s_row, imgSz, equator] = VT_SRC[Math.min(stage, 10)] ?? VT_SRC[0];

        /* Normal diamond clip — no expansion */
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(dN.x, dN.y);
        ctx.lineTo(dE.x, dE.y);
        ctx.lineTo(dS.x, dS.y);
        ctx.lineTo(dW.x, dW.y);
        ctx.closePath();
        ctx.clip();

        /* Draw blank ground first — 4% oversized so it bleeds into neighbours */
        try {
          const blankImg = await vtLoadImg(BLANK);
          const [b_srow2, b_sz2] = VT_SRC[0];
          const bs2 = drawW / b_sz2;
          const bdy2 = Math.round(dN.y + dFH - b_srow2 * bs2);
          ctx.drawImage(blankImg, 0, 0, b_sz2, b_sz2, px - drawOff, bdy2, drawW, drawW);
        } catch {}

        if (img && stage > 0) {
          const vt_scale = drawW / imgSz;
          const vt_dy = Math.round(dN.y + dFH - s_row * vt_scale);
          ctx.drawImage(img, 0, 0, imgSz, imgSz, px - drawOff, vt_dy, drawW, drawW);
        } else if (!img) {
          ctx.fillStyle = '#0f172a';
          ctx.fill();
        }

        ctx.restore();
      }
    })();

    return () => { cancelled = true; };
  }, [plots, canvasW]);

  return (
    <div style={{ minHeight: '100vh', background: '#020617', color: '#e2e8f0' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 40, background: 'rgba(2,6,23,0.96)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '14px 28px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>←</button>
        <div>
          <p style={{ color: '#f97316', fontSize: 17, fontWeight: 900, margin: 0, letterSpacing: 1 }}>VAYAL TEST MAP</p>
          <p style={{ color: '#475569', fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', margin: 0 }}>Tile Testing · Grid ({VT_GX}–{VT_GX+VT_COLS-1}, {VT_GY}–{VT_GY+VT_ROWS-1}) · Uses vayal-test-stage-N.png</p>
        </div>
      </div>
      <div style={{ padding: '24px 28px' }}>
        <div ref={containerRef} style={{ width: '100%', lineHeight: 0, marginBottom: 4 }}>
          <canvas ref={canvasRef} style={{ display: 'block', width: '100%' }} />
        </div>
        {loading && <p style={{ color: '#475569', fontSize: 11, textAlign: 'center', margin: '4px 0 16px' }}>Loading…</p>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function EdinburghPlotsPage({ onBack, waxAcct }: { onBack: () => void; waxAcct: string }) {
  type EPlot = { plot_index: number; coord_x: number; coord_y: number; state: string; assigned_at: string; species_index: number | null; planted_at: string | null; infrastructure_type: string | null };
  const [plots, setPlots] = React.useState<EPlot[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!waxAcct) return;
    fetch(`/samatvam/api/samatvam/edinburgh/plots/${waxAcct}`)
      .then(r => r.json())
      .then(d => { if (d.success) setPlots(d.plots); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [waxAcct]);

  return (
    <div style={{ minHeight: '100vh', background: '#020617', display: 'flex', flexDirection: 'column' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 40, background: 'rgba(2,6,23,0.96)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>←</button>
        <div>
          <p style={{ color: '#22d3ee', fontSize: 10, fontWeight: 900, letterSpacing: 3, textTransform: 'uppercase', margin: 0 }}>Edinburgh · Stage 1</p>
          <p style={{ color: 'white', fontSize: 17, fontWeight: 900, margin: '2px 0 0' }}>My Plots — {waxAcct}</p>
        </div>
        <div style={{ marginLeft: 'auto', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8, padding: '4px 14px' }}>
          <span style={{ color: '#4ade80', fontSize: 12, fontWeight: 900 }}>{plots.length} plots owned</span>
        </div>
      </div>

      <div style={{ flex: 1, padding: '24px 28px' }}>
        {loading && <p style={{ color: '#475569', fontSize: 13 }}>Loading…</p>}
        {!loading && plots.length === 0 && (
          <div style={{ textAlign: 'center', marginTop: 60 }}>
            <p style={{ color: '#475569', fontSize: 14 }}>No Edinburgh plots yet.</p>
            <p style={{ color: '#334155', fontSize: 12, marginTop: 8 }}>Start claiming from the Bus Stop page.</p>
          </div>
        )}
        {plots.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  {['Plot #', 'X', 'Y', 'State', 'Assigned'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 14px', color: '#475569', fontSize: 10, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 1 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {plots.map((p, i) => (
                  <tr key={p.plot_index} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                    <td style={{ padding: '10px 14px', color: '#22d3ee', fontWeight: 700 }}>#{p.plot_index}</td>
                    <td style={{ padding: '10px 14px', color: '#94a3b8', fontFamily: 'monospace' }}>{p.coord_x}</td>
                    <td style={{ padding: '10px 14px', color: '#94a3b8', fontFamily: 'monospace' }}>{p.coord_y}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 5, padding: '2px 8px', color: '#4ade80', fontSize: 11, fontWeight: 700 }}>{p.state}</span>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#64748b', fontSize: 11 }}>{p.assigned_at ? new Date(p.assigned_at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function EdinburghBusStopPage({ onBack, onOpenPlots }: { onBack: () => void; onOpenPlots: (acct: string) => void }) {
  const TRACK_COLOR: Record<string, string> = { express: '#f59e0b', boost: '#22d3ee', standard: '#94a3b8' };
  const TRACK_ICON:  Record<string, string> = { express: '⚡', boost: '🚀', standard: '🚌' };
  type Operative = { wax_account: string; operative_index: number; track: string; seeds_loaded: number; wax_loaded: number; vayal_loaded: number; wari_loaded: number; food_loaded: number };
  type ClaimSession = { plots_to_claim: number; plots_claimed: number; next_claim_at: string; status: string; completed_at: string | null };

  type ReturningOp = { wax_account: string; operative_index: number; track: string; status: string; wax_loaded: number; seeds_loaded: number; vayal_loaded: number; wari_loaded: number; food_loaded: number };

  const [operatives,    setOperatives]    = React.useState<Operative[]>([]);
  const [waxAcct,       setWaxAcct]       = React.useState('');
  const [sessions,      setSessions]      = React.useState<Record<number, ClaimSession>>({});
  const [claimingMap,   setClaimingMap]   = React.useState<Record<number, boolean>>({});
  const [claimMsgMap,   setClaimMsgMap]   = React.useState<Record<number, string>>({});
  const [secsMap,       setSecsMap]       = React.useState<Record<number, number>>({});
  const [returningOps,  setReturningOps]  = React.useState<ReturningOp[]>([]);
  const [recallingMap,  setRecallingMap]  = React.useState<Record<number, boolean>>({});

  const loadLobby = React.useCallback(() => {
    fetch('/samatvam/api/samatvam/bus/edinburgh-lobby')
      .then(r => r.json()).then(d => { if (d.success) setOperatives(d.operatives); }).catch(() => {});
  }, []);

  const loadReturning = React.useCallback(() => {
    fetch('/samatvam/api/samatvam/bus/returning-queue')
      .then(r => r.json()).then(d => { if (d.success) setReturningOps(d.queue); }).catch(() => {});
  }, []);

  const loadSessions = React.useCallback((acct: string) => {
    fetch(`/samatvam/api/samatvam/edinburgh/claim-status/${acct}`)
      .then(r => r.json()).then(d => {
        if (d.success) {
          setSessions(d.sessions ?? {});
          const newSecs: Record<number, number> = {};
          for (const [idx, s] of Object.entries(d.sessions ?? {})) {
            const sess = s as ClaimSession;
            if (sess.status === 'active') {
              newSecs[parseInt(idx)] = Math.max(0, Math.round((new Date(sess.next_claim_at).getTime() - Date.now()) / 1000));
            }
          }
          setSecsMap(newSecs);
        }
      }).catch(() => {});
  }, []);

  // Restore WAX session for claim panel
  React.useEffect(() => {
    loadLobby();
    loadReturning();
    getSessionKit().restore().then((s: any) => {
      if (s) { const actor = String(s.actor); setWaxAcct(actor); loadSessions(actor); }
    }).catch(() => {});
  }, [loadLobby, loadReturning, loadSessions]);

  // Countdown timers for all active sessions
  React.useEffect(() => {
    const iv = setInterval(() => {
      setSecsMap(prev => {
        const next = { ...prev };
        let needReload = false;
        for (const idx of Object.keys(next)) {
          const i = parseInt(idx);
          if (next[i] <= 1) { next[i] = 0; needReload = true; }
          else next[i] = next[i] - 1;
        }
        if (needReload && waxAcct) { loadSessions(waxAcct); loadLobby(); loadReturning(); }
        return next;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [waxAcct, loadSessions, loadLobby]);

  const handleClaim = async (operative_index: number) => {
    if (!waxAcct) return;
    setClaimingMap(p => ({ ...p, [operative_index]: true }));
    setClaimMsgMap(p => ({ ...p, [operative_index]: '' }));
    try {
      const r = await fetch('/samatvam/api/samatvam/edinburgh/start-claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxAcct, operative_index }),
      });
      const d = await r.json();
      if (d.success) { setClaimMsgMap(p => ({ ...p, [operative_index]: 'Started!' })); loadSessions(waxAcct); }
      else setClaimMsgMap(p => ({ ...p, [operative_index]: d.error || 'Failed' }));
    } catch { setClaimMsgMap(p => ({ ...p, [operative_index]: 'Error' })); }
    setClaimingMap(p => ({ ...p, [operative_index]: false }));
  };

  const handleRecall = async (operative_index: number) => {
    if (!waxAcct) return;
    setRecallingMap(p => ({ ...p, [operative_index]: true }));
    try {
      const r = await fetch('/samatvam/api/samatvam/bus/recall', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: waxAcct, operative_index }),
      });
      const d = await r.json();
      if (d.success) { loadLobby(); loadReturning(); }
    } catch {}
    setRecallingMap(p => ({ ...p, [operative_index]: false }));
  };

  const fmtTime = (secs: number) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return h > 0 ? `${h}h ${m}m ${s}s` : m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const myOps = waxAcct ? operatives.filter(o => o.wax_account === waxAcct) : [];

  return (
    <div style={{ minHeight: '100vh', background: '#020617', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 40, background: 'rgba(2,6,23,0.96)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>←</button>
        <div>
          <p style={{ color: '#f59e0b', fontSize: 10, fontWeight: 900, letterSpacing: 3, textTransform: 'uppercase', margin: 0 }}>Stage 1 · Edinburgh</p>
          <p style={{ color: 'white', fontSize: 17, fontWeight: 900, margin: '2px 0 0' }}>Bus Stop</p>
        </div>
      </div>

      {/* Body — lobby | claim | image */}
      <div style={{ flex: 1, display: 'flex', gap: 0, overflow: 'hidden' }}>

        {/* Lobby panel */}
        <div style={{ width: 240, minWidth: 200, background: 'rgba(15,23,42,0.95)', borderRight: '1px solid rgba(255,255,255,0.07)', padding: '20px 16px', overflowY: 'auto' }}>
          <p style={{ color: '#f59e0b', fontSize: 10, fontWeight: 900, letterSpacing: 3, textTransform: 'uppercase', margin: '0 0 4px' }}>Lobby</p>
          <p style={{ color: '#475569', fontSize: 11, margin: '0 0 16px' }}>Operatives in Edinburgh</p>
          {operatives.length === 0 && <p style={{ color: '#475569', fontSize: 11 }}>No operatives currently in Edinburgh.</p>}
          {operatives.map(op => (
            <div key={op.operative_index} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '10px 12px', marginBottom: 10 }}>
              <p style={{ color: TRACK_COLOR[op.track] ?? '#94a3b8', fontSize: 13, fontWeight: 900, margin: 0 }}>
                {TRACK_ICON[op.track] ?? '🚌'} Op #{op.operative_index}
              </p>
              <p style={{ color: '#64748b', fontSize: 10, margin: '3px 0 0', textTransform: 'uppercase', letterSpacing: 1 }}>{op.track}</p>
              <p style={{ color: '#94a3b8', fontSize: 10, margin: '6px 0 4px', fontFamily: 'monospace' }}>{op.wax_account}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                {op.wax_loaded   > 0 && <span style={{ background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 5, padding: '2px 7px', color: '#fbbf24', fontSize: 10, fontWeight: 700 }}>{op.wax_loaded} WAX</span>}
                {op.seeds_loaded > 0 && <span style={{ background: 'rgba(34,197,94,0.12)',  border: '1px solid rgba(34,197,94,0.3)',  borderRadius: 5, padding: '2px 7px', color: '#4ade80', fontSize: 10, fontWeight: 700 }}>{op.seeds_loaded} Seeds</span>}
                {op.food_loaded  > 0 && <span style={{ background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.3)', borderRadius: 5, padding: '2px 7px', color: '#fb923c', fontSize: 10, fontWeight: 700 }}>{op.food_loaded} Food</span>}
                {op.vayal_loaded > 0 && <span style={{ background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.3)', borderRadius: 5, padding: '2px 7px', color: '#a78bfa', fontSize: 10, fontWeight: 700 }}>{op.vayal_loaded} Vayal</span>}
                {op.wari_loaded  > 0 && <span style={{ background: 'rgba(14,165,233,0.12)', border: '1px solid rgba(14,165,233,0.3)', borderRadius: 5, padding: '2px 7px', color: '#38bdf8', fontSize: 10, fontWeight: 700 }}>{op.wari_loaded} WARI</span>}
                {op.wax_loaded === 0 && op.seeds_loaded === 0 && op.food_loaded === 0 && op.vayal_loaded === 0 && op.wari_loaded === 0 && (
                  <span style={{ color: '#334155', fontSize: 10 }}>Nothing loaded</span>
                )}
              </div>
            </div>
          ))}

          {/* Returning queue */}
          {returningOps.length > 0 && (
            <>
              <p style={{ color: '#f59e0b', fontSize: 10, fontWeight: 900, letterSpacing: 3, textTransform: 'uppercase', margin: '20px 0 4px' }}>Returning</p>
              <p style={{ color: '#475569', fontSize: 11, margin: '0 0 12px' }}>Heading back to Glasgow</p>
              {returningOps.map(op => (
                <div key={`ret-${op.operative_index}`} style={{ background: 'rgba(251,191,36,0.04)', border: '1px solid rgba(251,191,36,0.12)', borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <p style={{ color: TRACK_COLOR[op.track] ?? '#94a3b8', fontSize: 12, fontWeight: 900, margin: 0 }}>
                      {TRACK_ICON[op.track] ?? '🚌'} Op #{op.operative_index}
                    </p>
                    <span style={{ background: op.status === 'returning_transit' ? 'rgba(34,197,94,0.12)' : 'rgba(251,191,36,0.12)', border: `1px solid ${op.status === 'returning_transit' ? 'rgba(34,197,94,0.3)' : 'rgba(251,191,36,0.3)'}`, borderRadius: 4, padding: '2px 6px', color: op.status === 'returning_transit' ? '#4ade80' : '#fbbf24', fontSize: 9, fontWeight: 700 }}>
                      {op.status === 'returning_transit' ? 'In transit' : 'Waiting for bus'}
                    </span>
                  </div>
                  <p style={{ color: '#475569', fontSize: 10, margin: '2px 0 0', fontFamily: 'monospace' }}>{op.wax_account}</p>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Claim panel */}
        <div style={{ width: 300, minWidth: 260, background: 'rgba(2,6,23,0.98)', borderRight: '1px solid rgba(255,255,255,0.07)', padding: '20px 16px', overflowY: 'auto' }}>
          <p style={{ color: '#22d3ee', fontSize: 10, fontWeight: 900, letterSpacing: 3, textTransform: 'uppercase', margin: '0 0 4px' }}>Claim Plots</p>
          <p style={{ color: '#475569', fontSize: 11, margin: '0 0 12px' }}>Edinburgh · Stage 1</p>

          <a href="/edinburgh-smt/" target="_blank" rel="noopener noreferrer"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, background: 'linear-gradient(135deg, rgba(22,101,52,0.3) 0%, rgba(6,95,70,0.2) 100%)', border: '1px solid rgba(52,211,153,0.35)', borderRadius: 10, padding: '10px 12px', textDecoration: 'none', cursor: 'pointer' }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(52,211,153,0.75)')}
            onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(52,211,153,0.35)')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>🌲</span>
              <div>
                <p style={{ color: '#34d399', fontSize: 11, fontWeight: 900, margin: 0 }}>Edinburgh Forest</p>
                <p style={{ color: '#34d39960', fontSize: 9, margin: '1px 0 0', letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700 }}>Plant · Grow · Harvest</p>
              </div>
            </div>
            <span style={{ color: '#34d399', fontSize: 11, fontWeight: 900 }}>Go to your plots →</span>
          </a>

          {myOps.length === 0 ? (
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: '16px 14px' }}>
              <button disabled style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#334155', fontSize: 12, fontWeight: 800, padding: '10px 0', cursor: 'not-allowed' }}>
                Claim Plots
              </button>
              <p style={{ color: '#475569', fontSize: 11, margin: '12px 0 0', textAlign: 'center', lineHeight: 1.5 }}>
                No operative stationed here.<br />Please send one from Glasgow (old city).
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {myOps.map(op => {
                const opSess = sessions[op.operative_index];
                const secs = secsMap[op.operative_index] ?? 0;
                const isClaiming = claimingMap[op.operative_index] ?? false;
                const msg = claimMsgMap[op.operative_index] ?? '';
                const remaining = opSess ? (opSess.plots_to_claim - opSess.plots_claimed) * 60 - (60 - secs) : 0;
                const trackColors: Record<string, string> = { food: '#4ade80', bhoomi: '#f59e0b', water: '#22d3ee', energy: '#a78bfa' };
                const tc = trackColors[op.track] ?? '#94a3b8';
                return (
                  <div key={op.operative_index} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '12px 12px 10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: tc, flexShrink: 0 }} />
                      <span style={{ color: '#94a3b8', fontSize: 10, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase' }}>{op.track} operative</span>
                      <span style={{ marginLeft: 'auto', color: '#475569', fontSize: 10 }}>#{op.operative_index}</span>
                    </div>

                    {opSess && opSess.status === 'active' ? (
                      <>
                        <p style={{ color: '#4ade80', fontSize: 10, fontWeight: 900, margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: 1 }}>Claiming in Progress</p>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ color: '#64748b', fontSize: 11 }}>Plots claimed</span>
                          <span style={{ color: 'white', fontSize: 11, fontWeight: 700 }}>{opSess.plots_claimed} / {opSess.plots_to_claim}</span>
                        </div>
                        <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 5, overflow: 'hidden', marginBottom: 8 }}>
                          <div style={{ height: '100%', borderRadius: 4, background: tc, width: `${(opSess.plots_claimed / Math.max(1, opSess.plots_to_claim)) * 100}%`, transition: 'width 0.3s' }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                          <span style={{ color: '#64748b', fontSize: 11 }}>Next plot in</span>
                          <span style={{ color: '#f59e0b', fontSize: 11, fontWeight: 900, fontFamily: 'monospace' }}>{fmtTime(secs)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                          <span style={{ color: '#64748b', fontSize: 11 }}>Time to complete</span>
                          <span style={{ color: '#94a3b8', fontSize: 11, fontFamily: 'monospace' }}>{fmtTime(Math.max(0, remaining))}</span>
                        </div>
                      </>
                    ) : opSess && opSess.status === 'completed' ? (
                      <>
                        <div style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.18)', borderRadius: 8, padding: '10px', textAlign: 'center', marginBottom: 8 }}>
                          <p style={{ color: '#fbbf24', fontSize: 12, fontWeight: 900, margin: '0 0 4px' }}>All Plots Claimed!</p>
                          <p style={{ color: '#64748b', fontSize: 11, margin: 0 }}>{opSess.plots_to_claim} plots secured.</p>
                        </div>
                        {(op.wax_loaded ?? 0) > 0 && (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                              <span style={{ color: '#64748b', fontSize: 11 }}>New WAX loaded</span>
                              <span style={{ color: '#fbbf24', fontSize: 11, fontWeight: 700 }}>{op.wax_loaded} WAX</span>
                            </div>
                            <button onClick={() => handleClaim(op.operative_index)} disabled={isClaiming}
                              style={{ width: '100%', background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.4)', borderRadius: 7, color: '#4ade80', fontSize: 12, fontWeight: 900, padding: '10px 0', cursor: 'pointer' }}>
                              {isClaiming ? 'Starting…' : 'Claim New Plots'}
                            </button>
                            {msg && <p style={{ color: '#f87171', fontSize: 11, margin: '6px 0 0', textAlign: 'center' }}>{msg}</p>}
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ color: '#64748b', fontSize: 11 }}>WAX loaded</span>
                          <span style={{ color: '#fbbf24', fontSize: 11, fontWeight: 700 }}>{op.wax_loaded ?? 0} WAX</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                          <span style={{ color: '#64748b', fontSize: 11 }}>Plots available</span>
                          <span style={{ color: 'white', fontSize: 11, fontWeight: 700 }}>{op.wax_loaded ?? 0}</span>
                        </div>
                        <button onClick={() => handleClaim(op.operative_index)} disabled={isClaiming || (op.wax_loaded ?? 0) < 1}
                          style={{ width: '100%', background: (op.wax_loaded ?? 0) < 1 ? 'rgba(255,255,255,0.04)' : 'rgba(34,197,94,0.15)', border: `1px solid ${(op.wax_loaded ?? 0) < 1 ? 'rgba(255,255,255,0.08)' : 'rgba(34,197,94,0.4)'}`, borderRadius: 7, color: (op.wax_loaded ?? 0) < 1 ? '#334155' : '#4ade80', fontSize: 12, fontWeight: 900, padding: '10px 0', cursor: (op.wax_loaded ?? 0) < 1 ? 'not-allowed' : 'pointer' }}>
                          {isClaiming ? 'Starting…' : 'Claim Plots'}
                        </button>
                        {msg && <p style={{ color: '#f87171', fontSize: 11, margin: '6px 0 0', textAlign: 'center' }}>{msg}</p>}
                      </>
                    )}

                    {/* Return to Glasgow — always shown at bottom of card */}
                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', marginTop: 10, paddingTop: 10 }}>
                      <button
                        onClick={() => handleRecall(op.operative_index)}
                        disabled={recallingMap[op.operative_index] || returningOps.some(r => r.operative_index === op.operative_index)}
                        style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, color: returningOps.some(r => r.operative_index === op.operative_index) ? '#475569' : '#64748b', fontSize: 11, fontWeight: 700, padding: '8px 0', cursor: returningOps.some(r => r.operative_index === op.operative_index) ? 'not-allowed' : 'pointer' }}>
                        {recallingMap[op.operative_index] ? 'Recalling…' : returningOps.some(r => r.operative_index === op.operative_index) ? 'Waiting for bus…' : '← Return to Glasgow'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </div>

        {/* Bus stop image */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <img src="/samatvam/edinburgh-stage-1-bus-stop.png" alt="Edinburgh Bus Stop" style={{ display: 'block', maxWidth: '100%', maxHeight: '80vh', borderRadius: 12, objectFit: 'contain' }} />
        </div>
      </div>
    </div>
  );
}

function EdinburghPage({ onBack, onOpenBusStop }: { onBack: () => void; onOpenBusStop: () => void }) {
  return (
    <div style={{ minHeight: '100vh', background: '#020617', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 40, background: 'rgba(2,6,23,0.96)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>←</button>
        <div>
          <p style={{ color: '#f59e0b', fontSize: 10, fontWeight: 900, letterSpacing: 3, textTransform: 'uppercase', margin: 0 }}>Stage 1</p>
          <p style={{ color: 'white', fontSize: 17, fontWeight: 900, margin: '2px 0 0' }}>Edinburgh</p>
        </div>
      </div>

      {/* Map */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div style={{ position: 'relative', width: '100%', maxWidth: 1200 }}>
          {/* Stage 0 — blank canvas (deactivated) */}
          {/* <img src="/samatvam/edinburgh-canvas.png" alt="Edinburgh Stage 0" style={{ display: 'block', width: '100%', borderRadius: 12 }} /> */}
          {/* Stage 1 */}
          <img src="/samatvam/edinburgh-stage-1.jpg" alt="Edinburgh Stage 1" style={{ display: 'block', width: '100%', borderRadius: 12 }} />
          {/* Green patch clickable zone — transparent, no visual */}
          <div onClick={onOpenBusStop} style={{
            position: 'absolute',
            left: '10%',
            top: '28%',
            width: '8%',
            height: '20%',
            cursor: 'pointer',
          }} />
        </div>
      </div>
    </div>
  );
}

function AppContent() {
  const { isAuthenticated } = useAuth();
  const waxBalance = useWaxBalance();
  const { txs, loading, lastUpdated, error, fetchTxs } = useWaxIncoming();
  const [page, setPage] = useState<'dashboard' | 'game' | 'leaderboard' | 'warehouse' | 'samrajya' | 'university' | 'exchange' | 'crafting' | 'season0rank' | 'citymap' | 'citymapeditor' | 'adminzones' | 'adminlabels' | 'atavi' | 'adminpage' | 'citychampions' | 'privacy' | 'vayal' | 'mynfts' | 'vayaltest' | 'ataviforest' | 'biorefinery' | 'edinburgh' | 'edinburgh-busstop' | 'edinburgh-plots'>('dashboard');
  const [edinburghPlotsAcct, setEdinburghPlotsAcct] = useState('');
  const [samrajyaInitialTab, setSamrajyaInitialTab] = useState<'overview'|'infra'|'forest'|'knowledge'|'vayal'|'atavi'>('overview');

  if (!isAuthenticated) return <Landing />;
  if (page === 'game') return <GamePage txs={txs} onBack={() => setPage('dashboard')} onLeaderboard={() => setPage('leaderboard')} />;
  if (page === 'leaderboard') return <LeaderboardPage txs={txs} onBack={() => setPage('game')} />;
  if (page === 'warehouse') return <WarehousePage onBack={() => setPage('dashboard')} />;
  if (page === 'samrajya') return <SamrajyaPage onBack={() => setPage('dashboard')} onOpenWarehouse={() => setPage('warehouse')} onOpenUniversity={() => setPage('university')} onOpenAtaviForest={() => setPage('ataviforest')} initialTab={samrajyaInitialTab} />;
  if (page === 'university') return <UniversityPage onBack={() => setPage('samrajya')} onOpenCityMap={() => setPage('citymap')} />;
  if (page === 'exchange') return null;
  if (page === 'crafting') return <CraftingPage onBack={() => setPage('dashboard')} />;
  if (page === 'season0rank') return <Season0RankPage onBack={() => setPage('dashboard')} />;
  if (page === 'citymap') return <CityMapPage onBack={() => setPage('dashboard')} onOpenUniversity={() => setPage('university')} onOpenWarehouse={() => setPage('warehouse')} onEnterBiorefinery={() => setPage('biorefinery')} />;
  if (page === 'citymapeditor') return <CityMapCreator onBack={() => setPage('dashboard')} />;
  if (page === 'adminzones') return <AdminZonePage onBack={() => setPage('dashboard')} />;
  if (page === 'adminlabels') return <AdminLabelPage onBack={() => setPage('dashboard')} />;
  if (page === 'atavi') return <AtaviGuide onBack={() => setPage('dashboard')} />;
  if (page === 'citychampions') return <CityChampionsPage onBack={() => setPage('dashboard')} />;
  if (page === 'privacy') return <PrivacyPolicyPage onBack={() => setPage('dashboard')} />;
  if (page === 'vayaltest') return <VayalTestPage onBack={() => setPage('dashboard')} />;
  if (page === 'ataviforest') return <AtaviForestPage onBack={() => setPage('dashboard')} onOpenAtaviAssets={() => { setSamrajyaInitialTab('atavi'); setPage('samrajya'); }} />;
  if (page === 'biorefinery') return <BiorefineryPage onBack={() => setPage('citymap')} />;
  if (page === 'edinburgh') return <EdinburghPage onBack={() => setPage('dashboard')} onOpenBusStop={() => setPage('edinburgh-busstop')} />;
  if (page === 'edinburgh-busstop') return <EdinburghBusStopPage onBack={() => setPage('edinburgh')} onOpenPlots={(acct) => { setEdinburghPlotsAcct(acct); setPage('edinburgh-plots'); }} />;
  if (page === 'edinburgh-plots') return <EdinburghPlotsPage onBack={() => setPage('edinburgh-busstop')} waxAcct={edinburghPlotsAcct} />;
  if (page === 'vayal') return <VayalPage onBack={() => setPage('dashboard')} onOpenMint={() => { setSamrajyaInitialTab('vayal'); setPage('samrajya'); }} />;
  if (page === 'mynfts') return <MyNftsPage onBack={() => setPage('dashboard')} onOpenMint={() => { setSamrajyaInitialTab('vayal'); setPage('samrajya'); }} />;
  if (page === 'adminpage') {
    const token = localStorage.getItem('token') || '';
    return <AdminPage onBack={() => setPage('dashboard')} token={token} onOpenCityMapEditor={() => setPage('citymapeditor')} onOpenAdminZones={() => setPage('adminzones')} onOpenAdminLabels={() => setPage('adminlabels')} />;
  }
  return (
    <Dashboard
      txs={txs}
      loading={loading}
      lastUpdated={lastUpdated}
      error={error}
      fetchTxs={fetchTxs}
      waxBalance={waxBalance}
      onOpenGame={() => setPage('game')}
      onOpenWarehouse={() => setPage('warehouse')}
      onOpenSamrajya={() => setPage('samrajya')}
      onOpenExchange={() => setPage('exchange')}
      onOpenCrafting={() => setPage('crafting')}
      onOpenSeason0Rank={() => setPage('season0rank')}
      onOpenCityMap={() => setPage('citymap')}
      onOpenCityMapEditor={() => setPage('citymapeditor')}
      onOpenAdminZones={() => setPage('adminzones')}
      onOpenAdminLabels={() => setPage('adminlabels')}
      onOpenAtavi={() => setPage('atavi')}
      onOpenAdminPage={() => setPage('adminpage')}
      onOpenCityChampions={() => setPage('citychampions')}
      onOpenPrivacy={() => setPage('privacy')}
      onOpenVayal={() => setPage('vayal')}
      onOpenMyNfts={() => setPage('mynfts')}
      onOpenVayalTest={() => setPage('vayaltest')}
      onOpenAtaviForest={() => setPage('ataviforest')}
      onOpenEdinburgh={() => setPage('edinburgh')}
    />
  );
}

// ─── Crafting Page ────────────────────────────────────────────────────────────

function CraftingPage({ onBack }: { onBack: () => void }) {
  const [myAcct, setMyAcct] = React.useState('');
  const [waxSession, setWaxSession] = React.useState<any>(null);
  const [waxConnecting, setWaxConnecting] = React.useState(false);
  const [mesquiteLogs, setMesquiteLogs] = React.useState(0);
  const [moringaBundles, setMoringaBundles] = React.useState(0);
  const [firewood, setFirewood] = React.useState(0);
  const [yeastCulture, setYeastCulture] = React.useState(0);
  const [craftingBio, setCraftingBio] = React.useState<'firewood' | 'yeast' | null>(null);
  const [bioMsg, setBioMsg] = React.useState('');

  const loadData = React.useCallback((acct: string) => {
    if (!acct) return;
    Promise.all([
      fetch(`/samatvam/api/samatvam/workshop/logs/${acct}`).then(r => r.json()),
      fetch(`/samatvam/api/samatvam/materials/${acct}`).then(r => r.json()),
    ]).then(([logs, mats]) => {
      if (logs.success) { setMesquiteLogs(logs.mesquite_logs); setMoringaBundles(logs.moringa_bundles); }
      if (mats.success) {
        const fw = mats.materials.find((m: any) => m.material_id === 'firewood')?.quantity ?? 0;
        const yc = mats.materials.find((m: any) => m.material_id === 'yeast_culture')?.quantity ?? 0;
        setFirewood(fw); setYeastCulture(yc);
      }
    }).catch(() => {});
  }, []);

  React.useEffect(() => {
    getSessionKit().restore().then((s: any) => {
      if (s) {
        const acct = String(s.actor);
        setWaxSession(s);
        setMyAcct(acct);
        loadData(acct);
      } else {
        const acct = localStorage.getItem('samatvam_active_account') || '';
        setMyAcct(acct);
        if (acct) loadData(acct);
      }
    }).catch(() => {
      const acct = localStorage.getItem('samatvam_active_account') || '';
      setMyAcct(acct);
      if (acct) loadData(acct);
    });
  }, [loadData]);

  const handleWaxConnect = async () => {
    setWaxConnecting(true);
    try {
      const { session: s } = await getSessionKit().login();
      const acct = String(s.actor);
      setWaxSession(s);
      setMyAcct(acct);
      localStorage.setItem('samatvam_active_account', acct);
      loadData(acct);
    } catch { /* cancelled */ }
    setWaxConnecting(false);
  };

  const handleWaxDisconnect = async () => {
    if (waxSession) await getSessionKit().logout(waxSession);
    setWaxSession(null);
    setMyAcct('');
  };

  const handleCraftBio = async (type: 'firewood' | 'yeast') => {
    if (!myAcct || craftingBio) return;
    setCraftingBio(type); setBioMsg('');
    const endpoint = type === 'firewood' ? 'craft-firewood' : 'craft-yeast-culture';
    try {
      const r = await fetch(`/samatvam/api/samatvam/workshop/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wax_account: myAcct }),
      });
      const d = await r.json();
      if (d.success) {
        if (type === 'firewood') { setMesquiteLogs(d.new_mesquite_logs); setFirewood(d.new_firewood); setBioMsg(`✅ Crafted 1 firewood · ${d.new_mesquite_logs} mesquite logs remaining`); }
        else { setMoringaBundles(d.new_moringa_bundles); setYeastCulture(d.new_yeast_culture); setBioMsg(`✅ Crafted 1 yeast culture · ${d.new_moringa_bundles} moringa bundles remaining`); }
      } else setBioMsg(`❌ ${d.error}`);
    } catch { setBioMsg('❌ Network error'); }
    setCraftingBio(null);
  };

  return (
    <div className="min-h-screen bg-slate-900" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Header */}
      <div className="sticky top-0 z-40 backdrop-blur-md border-b border-slate-800/60" style={{ background: 'rgba(2,6,23,0.92)' }}>
        <div className="px-3 py-3 flex items-center gap-3">
          <button onClick={onBack} className="text-slate-400 hover:text-white transition-colors p-1">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
          </button>
          <div>
            <h1 className="text-white font-black text-base leading-tight">Crafting Workshop</h1>
            <p className="text-slate-500 text-xs">Craft Biorefinery materials from logs</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {myAcct ? (
              <>
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-bold" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', color: '#fcd34d' }}>
                  <span>⬡</span><span className="font-mono">{myAcct}</span>
                </div>
                <button onClick={handleWaxDisconnect} className="text-xs text-slate-500 hover:text-slate-300 transition-colors font-bold">Disconnect</button>
              </>
            ) : (
              <button onClick={handleWaxConnect} disabled={waxConnecting} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all disabled:opacity-50" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', color: '#fcd34d' }}>
                <span>⬡</span>{waxConnecting ? '…' : 'Connect WAX'}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="px-3 py-4 flex flex-col gap-4">

        {!myAcct && (
          <div className="rounded-2xl border border-slate-700/40 bg-slate-800/40 p-10 flex flex-col items-center gap-4 text-center">
            <div className="text-4xl">⚒️</div>
            <p className="text-white font-black text-lg">Connect your WAX wallet</p>
            <p className="text-slate-400 text-sm">Sign in to craft Biorefinery materials.</p>
            <button onClick={handleWaxConnect} disabled={waxConnecting} className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm disabled:opacity-50" style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)', color: '#fcd34d' }}>
              <span style={{ fontSize: 18 }}>⬡</span>{waxConnecting ? 'Connecting…' : 'Connect WAX Wallet'}
            </button>
          </div>
        )}

        {myAcct && (
          <div className="rounded-2xl border border-amber-700/30 overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(120,53,15,0.35) 0%, rgba(15,23,42,0.95) 100%)' }}>
            <div className="px-5 py-3 border-b border-amber-700/20 flex items-center gap-2">
              <span className="text-amber-500 text-xs">🌲</span>
              <span className="text-xs font-bold uppercase tracking-widest text-amber-500/70">Biorefinery Materials</span>
            </div>
            <div className="p-4 flex flex-col gap-3">

              {/* Craft Firewood */}
              <div className="rounded-2xl border border-amber-800/40 overflow-hidden" style={{ background: 'rgba(15,23,42,0.7)' }}>
                <div className="flex items-center gap-4 px-4 py-3">
                  <span style={{ fontSize: 36 }}>🌲</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-black text-sm">Craft Firewood</div>
                    <div className="text-slate-400 text-xs mt-0.5">100 mesquite logs → 1 firewood · used in Biorefinery batches</div>
                    <div className="flex gap-4 mt-1.5">
                      <span className="text-xs text-amber-300">🌲 {mesquiteLogs} mesquite logs</span>
                      <span className="text-xs text-orange-300">🔥 {firewood} firewood</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleCraftBio('firewood')}
                    disabled={mesquiteLogs < 100 || !!craftingBio}
                    className="px-5 py-2 rounded-xl text-xs font-bold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                    style={{ background: mesquiteLogs >= 100 ? 'linear-gradient(135deg, #b45309, #92400e)' : 'rgba(51,65,85,0.8)' }}
                  >
                    {craftingBio === 'firewood' ? 'Crafting…' : 'Craft'}
                  </button>
                </div>
              </div>

              {/* Craft Yeast Culture */}
              <div className="rounded-2xl border border-purple-900/40 overflow-hidden" style={{ background: 'rgba(15,23,42,0.7)' }}>
                <div className="flex items-center gap-4 px-4 py-3">
                  <span style={{ fontSize: 36 }}>🧫</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-black text-sm">Craft Yeast Culture</div>
                    <div className="text-slate-400 text-xs mt-0.5">100 moringa bundles → 1 yeast culture · used in Biorefinery batches</div>
                    <div className="flex gap-4 mt-1.5">
                      <span className="text-xs text-green-400">🌿 {moringaBundles} moringa bundles</span>
                      <span className="text-xs text-purple-300">🧫 {yeastCulture} yeast culture</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleCraftBio('yeast')}
                    disabled={moringaBundles < 100 || !!craftingBio}
                    className="px-5 py-2 rounded-xl text-xs font-bold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                    style={{ background: moringaBundles >= 100 ? 'linear-gradient(135deg, #7c3aed, #5b21b6)' : 'rgba(51,65,85,0.8)' }}
                  >
                    {craftingBio === 'yeast' ? 'Crafting…' : 'Craft'}
                  </button>
                </div>
              </div>

              {bioMsg && (
                <div className={`rounded-xl px-4 py-3 text-sm text-center font-medium ${bioMsg.startsWith('✅') ? 'text-green-300 bg-green-900/20 border border-green-500/30' : 'text-red-300 bg-red-900/20 border border-red-500/30'}`}>
                  {bioMsg}
                </div>
              )}

            </div>
          </div>
        )}

      </div>
    </div>
  );
}

function GrihaLiveWidget() {
  const [expanded, setExpanded] = React.useState(false);
  const parent = window.location.hostname;
  const twitchSrc = `https://player.twitch.tv/?channel=griha_samatvam&parent=${parent}&muted=true&autoplay=true`;
  return (
    <div style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999, fontFamily: 'Segoe UI, monospace' }}>
      <div style={{
        width: expanded ? 400 : 1, height: expanded ? 300 : 1,
        overflow: 'hidden', borderRadius: expanded ? 12 : 0,
        boxShadow: expanded ? '0 8px 32px rgba(0,0,0,0.6)' : 'none',
        border: expanded ? '1px solid rgba(145,71,255,0.4)' : 'none',
        marginBottom: expanded ? 6 : 0, transition: 'all 0.2s ease',
      }}>
        <iframe src={twitchSrc} width="400" height="300" frameBorder="0"
          scrolling="no" allowFullScreen allow="autoplay" title="GRIHA Live"
          style={{ display: 'block' }} />
      </div>
      <button onClick={() => setExpanded(o => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: 'rgba(14,10,30,0.92)', border: '1px solid rgba(145,71,255,0.5)',
        borderRadius: 10, padding: '6px 12px', cursor: 'pointer', color: '#fff',
        fontSize: 12, fontWeight: 700, backdropFilter: 'blur(8px)',
        boxShadow: '0 4px 16px rgba(145,71,255,0.25)',
      }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#ff4444', boxShadow: '0 0 6px #ff4444', display: 'inline-block' }} />
        🏗 GRIHA · LIVE
        <span style={{ color: '#9147ff', fontSize: 10 }}>{expanded ? '▼' : '▲'}</span>
      </button>
    </div>
  );
}

function App() {
  return (
    <>
      <WaxProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </WaxProvider>
      <GrihaLiveWidget />
    </>
  );
}

export default App;
